import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Camera,
  CheckCircle2,
  ChevronLeft,
  Clapperboard,
  CloudUpload,
  Film,
  Smartphone,
  Image as ImageIcon,
  LayoutDashboard,
  LayoutGrid,
  Mic,
  Moon,
  Pause,
  Pencil,
  Play,
  Plus,
  Square,
  SwitchCamera,
  Trophy,
  Undo2,
  Users,
  Video,
  X,
  Zap,
  Minus,
  CameraOff,
  Crop,
  Ban,
  Crosshair,
  TriangleAlert,
  Shield,
  Star,
  CornerDownLeft,
  CircleDot,
  Flag,
  Eye,
  EyeOff,
  Hash,
  Wifi,
  User,
  ArrowLeftRight,
  Tag,
  Trash2,
  Scan,
  ScanSearch,
  Loader2,
  ArrowRightToLine,
  ArrowLeftToLine,
  Check,
  ChevronRight,
  Maximize2,
  Minimize2,
  RectangleHorizontal,
  RectangleVertical,
  List,
  MoreVertical,
} from "lucide-react";
import { ReviewVideoPlayer } from "../../components/ReviewVideoPlayer";
import { ChampionshipScorePad } from "../../components/ChampionshipScorePad";
import PointOutcomeBar from "../../components/PointOutcomeBar";
import { ip_back } from "../../config";
import { useBrand } from "../../context/BrandContext";
import { useClubLicense } from "../../context/ClubLicenseContext";
import { useScreenWakeLock } from "../../hooks/useScreenWakeLock";
import { resolveClubImageUrl } from "../../utils/resolveClubImageUrl";
import {
  dismissLiveFullscreenGate,
  isLiveFullscreenGateDismissed,
  pageFullscreenLikelyWorks,
  exitLiveFullscreen,
  requestElementFullscreen,
  requestLiveFullscreen,
  useIsLiveFullscreen,
  useLiveFullscreen,
} from "../../hooks/useLiveFullscreen";
import { useChampionshipMatchSession } from "../../hooks/useChampionshipMatchSession";
import { isMobileDevice } from "../../utils/isMobileDevice";
import {
  canManageChampionshipMatchPresence,
  readActiveHomeRole,
  userHasModeratorRole,
} from "../../utils/homeRole";
import {
  MATCH_RECORD_DEFAULT_FPS,
  MATCH_RECORD_MIN_SHORT,
  WEB_EVAL_FPS_OPTIONS,
  blobWithCanonicalEvalMime,
  buildWebEvalCameraOptions,
  canonicalEvalVideoMime,
  cloneLiveMediaStream,
  createEvalMediaRecorder,
  evalVideoExtension,
  evalVideoUserMediaConstraints,
  isWebEvalCameraSelected,
  isWideArrayCameraLabel,
  matchCaptureAdjustment,
  matchRecordBitRate,
  matchRecordMegabytesPerMinute,
  matchRecordNeedsCanvas,
  matchRecordTierForFrame,
  matchRecordVideoConstraints,
  matchUploadFrameSize,
  mixEvalAudioIntoCaptureStream,
  normalizeMatchRecordFps,
  normalizeMatchRecordResolution,
  readMatchTrackCapture,
  shouldRotateMatchRecordingChunk,
  normalizeEvalVideoQuality,
  normalizeWebEvalFacingMode,
  pickEvalVideoMimeType,
  webEvalCameraButtonLabel,
  webEvalCameraSetupLabel,
  webEvalCameraLensMark,
} from "../../utils/evalVideoEncoding";
import { createLiveH264Session } from "../../utils/liveH264Capture";
import { createSidecarAudioRecorder } from "../../utils/sidecarAudio";
import { getNetworkTransport } from "../../utils/videoUploadNetwork";
import {
  appendCaptureQos,
  effectiveBitrateBps,
  resolveCaptureQosRunId,
  setCaptureQosContext,
  startCaptureQosSampling,
  stopCaptureQosSampling,
} from "../../utils/captureQos";
import {
  MATCH_UPLOAD_MODES,
  captureClipThumb,
  isLocalClipStorageFull,
  normalizeMatchUploadMode,
  uploadModeForMatchClip,
} from "../../utils/championshipLocalClips";
import {
  clipUploadErrorText,
  clipUploadStatusText,
  flushChampionshipClips,
  keepMatchClipOnPhone,
  kickChampionshipClipUploads,
  onChampionshipClipUploaded,
  setChampionshipRecordingActive,
  uploadOrKeepMatchClip,
} from "../../utils/championshipClipUploader";
import { useChampionshipClipUploads } from "../../hooks/useChampionshipClipUploads";
import {
  DEFAULT_CAMERA_ZOOM,
  applyTrackZoom,
  clampCameraZoom,
  digitalCropSourceRect,
  formatCameraZoomLabel,
  logicalZoomRange,
  nextPinchZoom,
  normalizeCameraZoom,
  pinchDistance,
  readTrackZoomRange,
} from "../../utils/cameraTrackZoom";
import {
  blobToJpegBase64,
  roiFingerprint,
  roiToJpegBlob,
  videoRoiToJpegBase64,
  normalizeRoi,
  isFreshBoardScore,
  scoreTimelineAt,
} from "../../utils/umpireScoreCrop";
import {
  pointerToFitNorm,
  videoNormToFitRect,
} from "../../utils/videoContainCoords";
import { reactionsNearPlayhead } from "../../utils/matchReviewReactions";
import { planCameraLeave } from "../../utils/championshipCameraLeave";
import { thisPhoneShouldRecordTable } from "../../utils/championshipRecordingTarget";
import {
  FINGERPRINT_H,
  FINGERPRINT_W,
  UMPIRE_SCORE_POLL_MS,
  createBoardChangeGate,
  summarizeCompare,
} from "../../utils/umpireScoreChange";
import {
  annotateChampionshipPoint,
  claimChampionshipCamera,
  releaseChampionshipCamera,
  claimChampionshipScorer,
  completeChampionshipMatch,
  resetChampionshipMatch,
  fetchChampionshipMatch,
  listChampionshipPlayerNotes,
  postChampionshipPoint,
  releaseChampionshipScorer,
  removeChampionshipPoint,
  postChampionshipSheetPhoto,
  saveChampionshipLineup,
  setChampionshipOpponentAvailability,
  setChampionshipScore,
  setChampionshipFinalResult,
  postChampionshipScoreOcr,
  clearChampionshipRubberPoints,
  postChampionshipReviewPoint,
  postChampionshipReviewReaction,
  probeChampionshipScore,
  fingerprintChampionshipScore,
  scanChampionshipScoreSegment,
  labelChampionshipScoreVideo,
  getChampionshipScoreLabel,
  startChampionshipRubber,
  assignChampionshipRubberTable,
  startChampionshipSet,
  syncChampionshipMediaClock,
  undoChampionshipPoint,
  uploadChampionshipMedia,
} from "../../components/funtionsBD/competitionService";
import {
  CORE_POINT_TAGS,
  EXTRA_POINT_TAGS,
  GAME_SCHEMAS,
  TIMELINE_MARKER_TAGS,
  lastPointForRubber,
  mediaForPoint,
  clipForPoint,
  locatePointOnTape,
  locateTapeSegment,
  tapeSegmentStartsSec,
  formatTms,
  groupPointsBySet,
  orphanMediaForRubber,
  setReviewSources,
  rubberReviewTapes,
  defaultRubberTapeId,
  cueIndexForPlayback,
  broadcastScoreAtPlayhead,
  planLiveScoreStamp,
  teamBoardScoreAtPlayhead,
  rubberReviewReady,
  rubberReviewUploading,
  rubberHasStoredClips,
  seatsInSameTie,
  swapSeats,
  seekSecondsForPoint,
  playbackAtomicMs,
  activeCueIndex,
  atomicOffsetFromSync,
  matchPlaybackMedia,
  isScorecamMedia,
  formatSetCell,
} from "../../utils/championshipMatchSheet";
import {
  isOutcomeTag,
  outcomeMeta,
  pointLabelWithOutcome,
  pointMatchesOutcome,
  POINT_OUTCOMES,
  readPointOutcome,
  togglePointOutcome,
} from "../../utils/matchPointLabel";
import ChampionshipPlayerNotes from "../../components/championship/ChampionshipPlayerNotes";
import "./ChampionshipMatchPage.css";
import ChampionshipCheerBar from "./ChampionshipCheerBar";
import ChampionshipControlRoom from "./ChampionshipControlRoom";
import {
  ChampionshipMatchOnTable,
  ChampionshipTablePicker,
} from "../../components/championship/ChampionshipTableAssign";
import {
  TableCameraGuide,
  useTableCameraGuide,
} from "../../components/championship/TableCameraGuide";
import {
  championshipTablesFromSnapshot,
  tableHasOnlineCamera,
} from "../../utils/championshipTableAssign";
import {
  tableIsRecording as captureTableIsRecording,
  tableIsStarting as captureTableIsStarting,
} from "../../utils/championshipCaptureState";
import { playCheerSound, stopCheerKind, stopMatchSounds, cheerDelayMs, CHEER_EMOJIS } from "../../utils/championshipCheer";

function readUser() {
  try {
    return JSON.parse(localStorage.getItem("user") || "null");
  } catch {
    return null;
  }
}

function mediaSrc(url) {
  if (!url) return "";
  if (/^https?:/i.test(url)) return url;
  let base = String(ip_back || "").replace(/\/?$/, "");
  // OVH frontend origin proxies /api but not always /uploadFiles — load media from API host.
  if (
    typeof window !== "undefined" &&
    String(url).startsWith("/uploadFiles/")
  ) {
    const host = String(window.location.hostname || "").toLowerCase();
    if (host === "dev.smartttclub.com" || host.endsWith(".dev.smartttclub.com")) {
      base = "https://api-dev.smartttclub.com";
    } else if (host === "193.70.75.208") {
      base = `${window.location.protocol}//193.70.75.208:3030`;
    }
  }
  return `${base}${url.startsWith("/") ? "" : "/"}${url}`;
}

function BoardScoreLabel({ reading, t, className }) {
  if (!reading || reading.board_left == null || reading.board_right == null) {
    return null;
  }
  const games =
    reading.games_left != null && reading.games_right != null
      ? ` · ${reading.games_left}–${reading.games_right}`
      : "";
  return (
    <span className={className}>
      {t("cmBoardScore", "Board")}{" "}
      <strong>
        {reading.board_left}–{reading.board_right}
      </strong>
      {games}
    </span>
  );
}

function scoreGlyph(value) {
  return value == null ? "–" : String(value);
}

/** Broadcast plate: club mark, team rubbers, and the detected game on screen. */
function LeagueScoreOverlay({
  clubLogo,
  clubName,
  homeClub,
  awayClub,
  homeTeamScore,
  awayTeamScore,
  division = "",
  homeName,
  awayName,
  homeScore,
  awayScore,
  gamesHome,
  gamesAway,
  setLabel = "",
  brandColor = "",
  label,
}) {
  const divisionLabel = String(division || "").trim();
  const brand = String(brandColor || "").trim();
  return (
    <div
      className="cm-league"
      role="status"
      aria-label={label}
      style={brand ? { "--cm-league-brand": brand } : undefined}
    >
      <div className="cm-league__brand">
        {clubLogo ? (
          <img className="cm-league__logo" src={clubLogo} alt="" />
        ) : (
          <span className="cm-league__mark" aria-hidden>
            {String(clubName || "Club").slice(0, 2)}
          </span>
        )}
        {clubName ? <span className="cm-league__club">{clubName}</span> : null}
      </div>
      <div className="cm-league__board">
        <div
          className={`cm-league__teams${divisionLabel ? " has-division" : ""}`}
          aria-hidden="true"
        >
          <span className="cm-league__team cm-league__team--home">{homeClub}</span>
          <span className="cm-league__team-score">{scoreGlyph(homeTeamScore)}</span>
          {divisionLabel ? (
            <span className="cm-league__division">{divisionLabel}</span>
          ) : null}
          <span className="cm-league__team-score">{scoreGlyph(awayTeamScore)}</span>
          <span className="cm-league__team cm-league__team--away">{awayClub}</span>
        </div>
        {setLabel ? (
          <div className="cm-league__set" aria-hidden="true">
            {setLabel}
          </div>
        ) : null}
        <div className="cm-league__player cm-league__player--home" aria-hidden="true">
          <span className="cm-league__games">{scoreGlyph(gamesHome)}</span>
          <span className="cm-league__name">{homeName}</span>
          <span className="cm-league__pts">{scoreGlyph(homeScore)}</span>
        </div>
        <div className="cm-league__player cm-league__player--away" aria-hidden="true">
          <span className="cm-league__games">{scoreGlyph(gamesAway)}</span>
          <span className="cm-league__name">{awayName}</span>
          <span className="cm-league__pts">{scoreGlyph(awayScore)}</span>
        </div>
      </div>
    </div>
  );
}

/**
 * Board left/right onto home/away.
 * `swapSides: false` locks the left card to home. `true` locks it to away.
 * Omit it and, once a score exists, pick the orientation closer to that score.
 */
function boardAsHomeAway(reading, officialScore, { swapSides = null } = {}) {
  const left = Number(reading?.board_left);
  const right = Number(reading?.board_right);
  if (!Number.isFinite(left) || !Number.isFinite(right)) return null;
  const direct = {
    home: left,
    away: right,
    gamesHome: reading?.games_left,
    gamesAway: reading?.games_right,
  };
  const flipped = {
    home: right,
    away: left,
    gamesHome: reading?.games_right,
    gamesAway: reading?.games_left,
  };
  if (swapSides === true) return flipped;
  if (swapSides === false) return direct;
  const oh = Number(officialScore?.live_home) || 0;
  const oa = Number(officialScore?.live_away) || 0;
  if (oh + oa > 0) {
    const directDist = Math.abs(left - oh) + Math.abs(right - oa);
    const flippedDist = Math.abs(left - oa) + Math.abs(right - oh);
    if (flippedDist < directDist) return flipped;
  }
  return direct;
}

function tableTennisSetOver(home, away) {
  const h = Math.floor(Number(home));
  const a = Math.floor(Number(away));
  if (!Number.isFinite(h) || !Number.isFinite(a)) return false;
  const hi = Math.max(h, a);
  const lo = Math.min(h, a);
  return hi >= 11 && hi - lo >= 2;
}

/** Next board may repeat, add one point, or return to 0–0 after a finished set. */
function isConsecutivePoint(prev, next) {
  if (!prev) return true;
  const h = Number(prev.home) || 0;
  const a = Number(prev.away) || 0;
  const th = Number(next?.home);
  const ta = Number(next?.away);
  if (!Number.isFinite(th) || !Number.isFinite(ta)) return false;
  if (th === h && ta === a) return true;
  if (tableTennisSetOver(h, a) && th === 0 && ta === 0) return true;
  if (th === h + 1 && ta === a) return true;
  if (ta === a + 1 && th === h) return true;
  return false;
}

const SCORE_RECHECK_MS = 3000;

function clipSetCount(value) {
  if (value == null || value === "") return null;
  const n = Math.floor(Number(value));
  if (!Number.isFinite(n)) return null;
  return Math.max(0, Math.min(7, n));
}

function pointSetPair(point) {
  const home = clipSetCount(point?.sets_home);
  const away = clipSetCount(point?.sets_away);
  if (home == null || away == null) return null;
  return `${home}–${away}`;
}

/**
 * score_timeline rows are usually file-local milliseconds. Point clocks can
 * include the capture origin, which would otherwise land on a later reading.
 * Use the clock whose hit sits closer to the playhead.
 */
function resolveBoardReading(timeline, fileMs, atomicMs) {
  if (!Array.isArray(timeline) || !timeline.length) return null;
  const file = Number.isFinite(Number(fileMs)) ? Number(fileMs) : 0;
  const atomic = Number(atomicMs);
  let minT = Infinity;
  for (const row of timeline) {
    const ts = Number(row?.t_ms);
    if (Number.isFinite(ts) && ts < minT) minT = ts;
  }
  if (!Number.isFinite(minT)) return null;
  const useAtomic =
    minT > 120000 &&
    Number.isFinite(atomic) &&
    Math.abs(atomic - minT) < Math.abs(file - minT);
  return scoreTimelineAt(timeline, useAtomic ? atomic : file);
}

function finiteBoardCount(value, fallback) {
  if (value == null || value === "") return fallback;
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function boardSetIsOver(home, away) {
  const h = Number(home) || 0;
  const a = Number(away) || 0;
  return Math.max(h, a) >= 11 && Math.abs(h - a) >= 2;
}

function scoreAiStatusMeta(phase, t) {
  switch (phase) {
    case "reading":
      return {
        icon: Loader2,
        spin: true,
        label: t("cmScoreAiReading", "Score AI reading"),
      };
    case "motion":
      return {
        icon: EyeOff,
        spin: false,
        label: t("cmScoreAiMotion", "Motion / occlusion"),
      };
    case "occlusion":
      return {
        icon: EyeOff,
        spin: false,
        label: t("cmScoreAiOcclusion", "Occlusion · not visible"),
      };
    case "hit":
      return {
        icon: CheckCircle2,
        spin: false,
        label: t("cmScoreAiHit", "Score detected"),
      };
    case "confirm":
      return {
        icon: TriangleAlert,
        spin: false,
        label: t("cmScoreAiConfirm", "Confirm score jump"),
      };
    case "blank":
      return {
        icon: Ban,
        spin: false,
        label: t("cmScoreAiBlank", "Blank 0–0 ignored"),
      };
    case "error":
      return {
        icon: TriangleAlert,
        spin: false,
        label: t("cmScoreAiError", "Score AI error"),
      };
    case "watching":
      return {
        icon: ScanSearch,
        spin: false,
        label: t("cmScoreAiWatching", "Watching board"),
      };
    default:
      return {
        icon: Scan,
        spin: false,
        label: t("cmScoreAiIdle", "Score AI idle"),
      };
  }
}

function formatScoreAiLogTime(ts) {
  try {
    return new Date(ts).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch {
    return "";
  }
}

function shortNames(list, seats = [], prefix = "") {
  const named = (list || [])
    .filter(Boolean)
    .map((name) => String(name).split(/\s+/)[0]);
  if (named.length) return named.join("/");
  if (seats?.length) return seats.map((s) => `${prefix}${s}`).join("/");
  return "";
}

/** Full first + last names for the broadcast plate (not first-name only). */
function fullNames(list, seats = [], prefix = "") {
  const named = (list || [])
    .map((name) => String(name || "").trim())
    .filter(Boolean);
  if (named.length) return named.join(" / ");
  if (seats?.length) return seats.map((s) => `${prefix}${s}`).join(" / ");
  return "";
}

/** Live (or next) rubber matchup label for a table — used in camera status. */
function rubberMatchupForTable(snapshot, tableNumber) {
  const table = Number(tableNumber);
  const rubbers = snapshot?.rubbers || [];
  const row =
    rubbers.find((r) => Number(r.table_number) === table && r.state === "live") ||
    rubbers.find(
      (r) => Number(r.table_number) === table && r.state !== "done"
    ) ||
    rubbers.find((r) => Number(r.table_number) === table) ||
    null;
  if (!row) return null;
  const home =
    shortNames(row.home_names, row.home_seats, "") ||
    snapshot?.fixture?.home?.display_name ||
    "Home";
  const away =
    shortNames(row.away_names, row.away_seats, "") ||
    snapshot?.fixture?.away?.display_name ||
    "Away";
  return {
    rubber: row,
    label: `${home} vs ${away}`,
    code: row.code,
    state: row.state,
    awaiting_next_set: Boolean(row.set_scores?.awaiting_next_set),
  };
}

function normalizeSeatList(raw) {
  if (Array.isArray(raw)) {
    return raw.map(Number).filter((n) => Number.isFinite(n) && n > 0);
  }
  if (typeof raw === "string") {
    try {
      return normalizeSeatList(JSON.parse(raw));
    } catch {
      return [];
    }
  }
  if (raw && typeof raw === "object") {
    return Object.values(raw)
      .map(Number)
      .filter((n) => Number.isFinite(n) && n > 0);
  }
  return [];
}

function lineupPlayersForSeats(lineupSide, seats, names = []) {
  const seatList = normalizeSeatList(seats);
  const nameList = Array.isArray(names) ? names : [];
  const slots = Array.isArray(lineupSide) ? lineupSide : [];
  const bySeat = new Map(
    slots.map((slot) => [Number(slot.seat), slot])
  );
  const resolvedSeats =
    seatList.length > 0
      ? seatList
      : nameList.length > 0
        ? nameList.map((_, idx) => idx + 1)
        : [];
  return resolvedSeats.map((seat, idx) => {
    const slot = bySeat.get(Number(seat));
    const fromLineup = String(slot?.display_name || slot?.name || "").trim();
    const fromRubber = String(nameList[idx] || "").trim();
    return {
      seat: Number(seat) || idx + 1,
      name: fromLineup || fromRubber || `Seat ${seat}`,
      user_id: slot?.user_id || null,
      player_id: slot?.player_id || null,
      classement: slot?.classement || null,
      ranking_points:
        slot?.ranking_points != null ? Number(slot.ranking_points) : null,
      ranking_place:
        slot?.ranking_place != null ? Number(slot.ranking_place) : null,
    };
  });
}

function formatPlayerMeta(player, t) {
  const bits = [];
  if (player.classement) bits.push(String(player.classement));
  if (player.ranking_points != null && !Number.isNaN(player.ranking_points)) {
    bits.push(
      t("cmRatingPoints", "{{n}} pts", { n: Math.round(player.ranking_points) })
    );
  }
  if (player.ranking_place != null && Number.isFinite(Number(player.ranking_place))) {
    bits.push(
      t("cmNationalRank", "#{{n}}", { n: Math.round(Number(player.ranking_place)) })
    );
  }
  return bits.join(" · ");
}

function formatBoardPlayerNames(players, fallbackNames, seats, t) {
  const list = Array.isArray(players) ? players.filter((p) => p?.name) : [];
  if (list.length) {
    return list
      .map((player) => {
        const meta = formatPlayerMeta(player, t);
        return meta ? `${player.name} · ${meta}` : player.name;
      })
      .join(" / ");
  }
  return fullNames(fallbackNames, seats, "");
}

function rubberCodeLabel(code) {
  if (!code) return "";
  return code.startsWith("D") ? code : String(code).replace("x", "–");
}

/** Segment length lives in evalVideoEncoding (1 minute). */
const CM_ENCODE_PREFS_KEY = "cm-camera-encode-prefs";
/** Match-side cameras. Scorecam also gets 480p. */
const CM_RECORD_RES_OPTIONS = Object.freeze([720, 1080]);
const CM_SCORECAM_RES_OPTIONS = Object.freeze([480, 720, 1080]);
const CM_RECORD_QUALITY_OPTIONS = Object.freeze(["low", "medium"]);
const DEFAULT_RECORD_RESOLUTION = 720;
const DEFAULT_SET_CLOSE_STABLE_SEC = 15;
const SET_CLOSE_STABLE_SEC_OPTIONS = Object.freeze([10, 15, 20, 30]);

function normalizeSetCloseStableSec(raw, fallback = DEFAULT_SET_CLOSE_STABLE_SEC) {
  const n = Number(raw);
  if (SET_CLOSE_STABLE_SEC_OPTIONS.includes(n)) return n;
  const sec = Number.isFinite(n) && n > 120 ? Math.round(n / 1000) : Math.round(n);
  if (sec >= 5 && sec <= 60) return sec;
  return fallback;
}

function normalizeChampionshipQuality(raw, fallback = "low") {
  const q = normalizeEvalVideoQuality(raw, fallback);
  // Match camera only exposes low / medium — clamp legacy "high".
  return q === "high" ? "medium" : q;
}

function normalizeRecordResolution(raw, fallback = DEFAULT_RECORD_RESOLUTION) {
  return normalizeMatchRecordResolution(raw, fallback);
}

/** Scorecam may use 480p; spectator/referee stay ≥720p. */
function clampResolutionForCameraRole(resolution, isScorecam) {
  const res = normalizeRecordResolution(resolution, DEFAULT_RECORD_RESOLUTION);
  if (isScorecam) return res;
  return res === 480 ? MATCH_RECORD_MIN_SHORT : res;
}

function readEncodePrefs() {
  try {
    const raw = localStorage.getItem(CM_ENCODE_PREFS_KEY);
    if (!raw) {
      return {
        quality: "low",
        fps: MATCH_RECORD_DEFAULT_FPS,
        resolution: DEFAULT_RECORD_RESOLUTION,
        setCloseStableSec: DEFAULT_SET_CLOSE_STABLE_SEC,
        uploadMode: "live",
        scorecamRecordVideo: false,
      };
    }
    const parsed = JSON.parse(raw);
    return {
      quality: normalizeChampionshipQuality(parsed?.quality, "low"),
      fps: normalizeMatchRecordFps(parsed?.fps, MATCH_RECORD_DEFAULT_FPS),
      resolution: normalizeRecordResolution(
        parsed?.resolution,
        DEFAULT_RECORD_RESOLUTION
      ),
      setCloseStableSec: normalizeSetCloseStableSec(
        parsed?.setCloseStableSec,
        DEFAULT_SET_CLOSE_STABLE_SEC
      ),
      uploadMode: normalizeMatchUploadMode(
        parsed?.uploadMode,
        parsed?.wifiOnlyUpload === true
      ),
      scorecamRecordVideo: parsed?.scorecamRecordVideo === true,
    };
  } catch {
    return {
      quality: "low",
      fps: MATCH_RECORD_DEFAULT_FPS,
      resolution: DEFAULT_RECORD_RESOLUTION,
      setCloseStableSec: DEFAULT_SET_CLOSE_STABLE_SEC,
      uploadMode: "live",
      scorecamRecordVideo: false,
    };
  }
}

function writeEncodePrefs(prefs) {
  try {
    localStorage.setItem(
      CM_ENCODE_PREFS_KEY,
      JSON.stringify({
        quality: normalizeChampionshipQuality(prefs?.quality, "low"),
        fps: normalizeMatchRecordFps(prefs?.fps, MATCH_RECORD_DEFAULT_FPS),
        resolution: normalizeRecordResolution(
          prefs?.resolution,
          DEFAULT_RECORD_RESOLUTION
        ),
        setCloseStableSec: normalizeSetCloseStableSec(
          prefs?.setCloseStableSec,
          DEFAULT_SET_CLOSE_STABLE_SEC
        ),
        uploadMode: normalizeMatchUploadMode(prefs?.uploadMode),
        scorecamRecordVideo: prefs?.scorecamRecordVideo === true,
      })
    );
  } catch {
    /* ignore */
  }
}

function formatClipBytes(bytes) {
  const n = Number(bytes) || 0;
  if (n < 1024 * 1024) return `${Math.max(1, Math.round(n / 1024))} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function recorderExt(mimeType) {
  return evalVideoExtension(mimeType);
}

/** Friendly copy when axios loses the response (common on nginx 413). */
function mediaUploadErrorMessage(err, t) {
  const status = err?.response?.status;
  const raw = err?.response?.data?.message || err?.message || "";
  const msg = String(raw);
  if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError") {
    return t(
      "cmUploadStalled",
      "Upload stopped before it finished. Check the connection and tap Upload now again."
    );
  }
  if (
    status === 413 ||
    /network error/i.test(msg) ||
    err?.code === "ERR_NETWORK" ||
    /entity too large|payload too large|file too large/i.test(msg)
  ) {
    return t(
      "cmVideoTooLarge",
      "Video is too large to upload. Recording will use a lower quality next time — try a shorter set, or check your connection."
    );
  }
  return msg || t("cmVideoSaveFailed", "Could not save video — try again");
}

function cameraClaimStorageKey(matchId) {
  return matchId ? `cm-camera-claim:${matchId}` : null;
}

const CM_PHONE_ID_KEY = "cm-phone-id";

function championshipPhoneId() {
  try {
    const existing = String(localStorage.getItem(CM_PHONE_ID_KEY) || "").trim();
    if (existing) return existing.slice(0, 64);
    const created =
      (typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `web-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
      ).slice(0, 64);
    localStorage.setItem(CM_PHONE_ID_KEY, created);
    return created;
  } catch {
    return "";
  }
}

function cameraIsOnline(cam) {
  return Boolean(cam?.online || cam?.socket_id);
}

function recordingTablesLabel(tables, t) {
  const nums = [
    ...new Set(
      (tables || [])
        .map(Number)
        .filter((n) => Number.isFinite(n) && n > 0)
    ),
  ].sort((a, b) => a - b);
  if (!nums.length) return "";
  if (nums.length === 1) {
    return t("cmRecordingTable", "Recording · Table {{n}}", { n: nums[0] });
  }
  return t("cmRecordingTables", "Recording · Tables {{n}}", {
    n: nums.join(", "),
  });
}

function cameraIsMine(cam, userId) {
  return Boolean(userId && String(cam?.user_id) === String(userId));
}

function cameraIsThisPhone(cam, userId, phoneId) {
  if (!cameraIsMine(cam, userId)) return false;
  const device = String(cam?.device_id || "");
  const mine = String(phoneId || "");
  if (mine) return device === mine;
  return !device;
}

function sortMatchCameras(cameras, userId, phoneId) {
  return [...(cameras || [])].sort((a, b) => {
    const rank = (cam) =>
      cameraIsThisPhone(cam, userId, phoneId) ? 0 : cameraIsMine(cam, userId) ? 1 : 2;
    const diff = rank(a) - rank(b);
    if (diff) return diff;
    return Number(a.table_number) - Number(b.table_number);
  });
}

/**
 * Clip label from a "Score + video" scorecam. Not a scorecam label, so the clip
 * plays, aligns and stitches like referee/spectator footage.
 */
const SCORECAM_VIDEO_MEDIA_LABEL = "scorecam_video";

/** Camera position relative to the table (stored as angle_label). */
function normalizeCamPosition(value) {
  const raw = String(value || "").toLowerCase().trim();
  if (raw === "referee" || raw === "side" || raw === "long") return "referee";
  if (raw === "scorecam" || raw === "score" || raw === "score_camera") {
    return "scorecam";
  }
  return "spectator";
}

function camRoleFromPosition(value) {
  return normalizeCamPosition(value) === "scorecam" ? "score" : "match";
}

function camPositionLabel(value, t) {
  if (String(value || "").toLowerCase().trim() === SCORECAM_VIDEO_MEDIA_LABEL) {
    return t("cmCamPositionScorecamVideo", "Scorecam (players)");
  }
  const pos = normalizeCamPosition(value);
  if (pos === "referee") return t("cmCamPositionReferee", "Referee");
  if (pos === "scorecam") return t("cmCamPositionScorecam", "Scorecam");
  return t("cmCamPositionSpectator", "Spectator");
}

function cameraIsScoreCam(cam) {
  if (!cam) return false;
  if (normalizeCamPosition(cam.angle_label) === "scorecam") return true;
  return cam.camera_role === "score" || Boolean(cam.score_camera_armed);
}

/** Scorecam only drives the table it is connected to. */
function scoreCameraOnTable(cameras, tableNumber) {
  const table = Number(tableNumber);
  if (!Number.isFinite(table) || table < 1) return false;
  return (cameras || []).some(
    (cam) =>
      Number(cam?.table_number) === table &&
      (Boolean(cam?.score_camera_armed) ||
        (cameraIsScoreCam(cam) && Boolean(cam?.score_roi)))
  );
}

function ChampionshipFullscreenGate({ enabled }) {
  const { t } = useTranslation();
  const fullscreen = useIsLiveFullscreen();
  const [dismissed, setDismissed] = useState(() => isLiveFullscreenGateDismissed());

  const hideGate = useCallback(() => {
    dismissLiveFullscreenGate();
    setDismissed(true);
  }, []);

  useEffect(() => {
    if (!enabled || dismissed || fullscreen) return undefined;
    const onFirstGesture = () => hideGate();
    document.addEventListener("pointerdown", onFirstGesture, { capture: true });
    return () => {
      document.removeEventListener("pointerdown", onFirstGesture, { capture: true });
    };
  }, [enabled, dismissed, fullscreen, hideGate]);

  const standalone =
    typeof window !== "undefined" &&
    (window.navigator.standalone === true ||
      window.matchMedia("(display-mode: standalone)").matches ||
      window.matchMedia("(display-mode: fullscreen)").matches);

  if (
    !enabled ||
    dismissed ||
    fullscreen ||
    standalone ||
    !pageFullscreenLikelyWorks()
  ) {
    return null;
  }

  return (
    <button
      type="button"
      className="cm-fs-gate"
      onPointerDown={() => {
        requestLiveFullscreen();
        hideGate();
      }}
    >
      <span className="cm-fs-gate__label">
        {t("liveTrainingTapFullscreen", "Tap to hide the browser bar")}
      </span>
    </button>
  );
}

function ActionLabel({ children, landscape }) {
  if (landscape) return <span className="cm-sr-only">{children}</span>;
  return <span className="cm-btn__label">{children}</span>;
}

const POINT_TAG_META = {
  point_start: { cat: "moment", Icon: Play, mark: "▶" },
  amazing_rally: { cat: "moment", Icon: Zap, mark: "R" },
  service_missed: { cat: "serve", Icon: Ban, mark: "M" },
  service_ace: { cat: "serve", Icon: Star, mark: "A" },
  service_attack_3rd: { cat: "serve", Icon: Crosshair, mark: "3" },
  unforced_error: { cat: "error", Icon: TriangleAlert, mark: "E" },
  forced_error: { cat: "error", Icon: Shield, mark: "F" },
  winner_open_play: { cat: "winner", Icon: Trophy, mark: "W" },
  return_winner: { cat: "winner", Icon: CornerDownLeft, mark: "RW" },
  return_error: { cat: "error", Icon: CornerDownLeft, mark: "RE" },
  net: { cat: "moment", Icon: CircleDot, mark: "N" },
  edge: { cat: "moment", Icon: CircleDot, mark: "Ed" },
  let_serve: { cat: "serve", Icon: Flag, mark: "L" },
  timeout_on: { cat: "moment", Icon: Pause, mark: "T+" },
  timeout_off: { cat: "moment", Icon: Play, mark: "T−" },
  set_point: { cat: "moment", Icon: Flag, mark: "SP" },
  match_point: { cat: "moment", Icon: Trophy, mark: "MP" },
  court_coupe: { cat: "schema", Icon: Crosshair, mark: "SC" },
  long_coupe: { cat: "schema", Icon: Crosshair, mark: "LC" },
  long_rapide: { cat: "schema", Icon: Zap, mark: "LF" },
  court_lateral: { cat: "schema", Icon: Crosshair, mark: "SL" },
  long_varie: { cat: "schema", Icon: Crosshair, mark: "LM" },
};

const REVIEW_EMOJIS = ["🔥", "👏", "😮", "😂", "👍", "❤️", "💪", "🏓"];

const POINT_LABEL_GROUPS = [
  { id: "serve", titleKey: "cmTagGroupServe", title: "Serve" },
  { id: "winner", titleKey: "cmTagGroupWinner", title: "Winner" },
  { id: "error", titleKey: "cmTagGroupError", title: "Error" },
  { id: "moment", titleKey: "cmTagGroupMoment", title: "Moment" },
  { id: "schema", titleKey: "cmTagGroupSchema", title: "Pattern" },
];

function groupedPointLabels(tags) {
  const buckets = new Map();
  for (const tag of tags || []) {
    if (isOutcomeTag(tag)) continue;
    const cat = POINT_TAG_META[tag]?.cat || "moment";
    if (!buckets.has(cat)) buckets.set(cat, []);
    buckets.get(cat).push(tag);
  }
  return POINT_LABEL_GROUPS.filter((group) => buckets.has(group.id)).map((group) => ({
    ...group,
    tags: buckets.get(group.id),
  }));
}

function normBoxFromDrag(drag) {
  if (!drag?.a || !drag?.b) return null;
  return {
    x: Math.min(drag.a.x, drag.b.x),
    y: Math.min(drag.a.y, drag.b.y),
    w: Math.abs(drag.a.x - drag.b.x),
    h: Math.abs(drag.a.y - drag.b.y),
  };
}

function videoObjectFit(videoEl, fallback) {
  try {
    const fit = window.getComputedStyle(videoEl).objectFit;
    if (fit === "cover" || fit === "contain") return fit;
  } catch {
    /* ignore */
  }
  return fallback === "cover" ? "cover" : "contain";
}

/**
 * Score box drawn in the video's own pixels.
 * The SVG shares the <video> layout box and CSS transform, and the mapping
 * uses that element's object-fit, so the saved ROI is the scoreboard the
 * operator boxed — not the letterbox or the cropped preview margins.
 */
function ScoreBoxLayer({
  videoEl,
  fit = "contain",
  drawing = false,
  roi = null,
  tone = "",
  pauseOnDraw = false,
  onCommit,
}) {
  const svgRef = useRef(null);
  const dragRef = useRef(null);
  const pointersRef = useRef(new Set());
  const [drag, setDrag] = useState(null);
  const [size, setSize] = useState({ w: 0, h: 0 });

  useEffect(() => {
    if (!videoEl) return undefined;
    const measure = () => {
      setSize({
        w: Math.max(1, videoEl.clientWidth || 0),
        h: Math.max(1, videoEl.clientHeight || 0),
      });
    };
    measure();
    let observer;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(measure);
      observer.observe(videoEl);
    }
    videoEl.addEventListener("loadedmetadata", measure);
    return () => {
      observer?.disconnect();
      videoEl.removeEventListener("loadedmetadata", measure);
    };
  }, [videoEl]);

  if (!videoEl?.parentElement || size.w < 2 || size.h < 2) return null;
  const frameW = Number(videoEl.videoWidth) || 0;
  const frameH = Number(videoEl.videoHeight) || 0;
  if (frameW < 2 || frameH < 2) return null;

  const usedFit = videoObjectFit(videoEl, fit);
  const live = drawing ? normBoxFromDrag(drag) : null;
  // Hide the committed box after selection — only show while drawing (live
  // drag, or the previous box as a guide until a new drag starts).
  const shown =
    live && live.w > 0 && live.h > 0
      ? live
      : drawing && roi && roi.w > 0 && roi.h > 0
        ? roi
        : null;
  const rect =
    shown && shown.w > 0 && shown.h > 0
      ? videoNormToFitRect(shown, size.w, size.h, frameW, frameH, usedFit)
      : null;
  const transform = videoEl.style?.transform || "";

  const readHit = (event) =>
    pointerToFitNorm(event, svgRef.current, frameW, frameH, usedFit);

  return createPortal(
    <svg
      ref={svgRef}
      className={`cm-video-roi-svg${drawing ? " is-draw" : ""}`}
      viewBox={`0 0 ${size.w} ${size.h}`}
      preserveAspectRatio="none"
      style={{
        width: size.w,
        height: size.h,
        transform: transform && transform !== "none" ? transform : undefined,
        transformOrigin: "center center",
      }}
      onTouchStart={(event) => {
        if (!drawing || event.touches.length >= 2) return;
        event.stopPropagation();
      }}
      onTouchMove={(event) => {
        if (!drawing || event.touches.length >= 2) return;
        event.stopPropagation();
      }}
      onPointerDown={(event) => {
        if (!drawing) return;
        pointersRef.current.add(event.pointerId);
        if (pointersRef.current.size > 1) {
          dragRef.current = null;
          setDrag(null);
          return;
        }
        if (pauseOnDraw) videoEl.pause?.();
        const hit = readHit(event);
        if (!hit.inside) return;
        event.preventDefault();
        event.currentTarget.setPointerCapture?.(event.pointerId);
        const next = { a: hit, b: hit };
        dragRef.current = next;
        setDrag(next);
      }}
      onPointerMove={(event) => {
        if (!drawing || pointersRef.current.size > 1 || !dragRef.current) return;
        event.preventDefault();
        const hit = readHit(event);
        const next = { ...dragRef.current, b: hit };
        dragRef.current = next;
        setDrag(next);
      }}
      onPointerUp={(event) => {
        pointersRef.current.delete(event.pointerId);
        if (!drawing) return;
        if (pointersRef.current.size > 0) {
          dragRef.current = null;
          setDrag(null);
          return;
        }
        const hadDrag = Boolean(dragRef.current);
        const box = normBoxFromDrag(dragRef.current);
        dragRef.current = null;
        setDrag(null);
        if (hadDrag) onCommit?.(box);
      }}
      onPointerCancel={(event) => {
        if (event?.pointerId != null) pointersRef.current.delete(event.pointerId);
        dragRef.current = null;
        setDrag(null);
        if (drawing && pointersRef.current.size === 0) onCommit?.(null);
      }}
    >
      {rect && rect.width > 1 && rect.height > 1 ? (
        <rect
          className={`cm-video-roi-rect${tone ? ` is-${tone}` : ""}`}
          x={rect.x}
          y={rect.y}
          width={rect.width}
          height={rect.height}
          rx="4"
        />
      ) : null}
    </svg>,
    videoEl.parentElement
  );
}

function PointTagChip({
  tagKey,
  label,
  active,
  onClick,
  tone = "tag",
  disabled = false,
}) {
  const meta = POINT_TAG_META[tagKey] || { cat: "moment", Icon: CircleDot, mark: "?" };
  const Icon = meta.Icon;
  return (
    <button
      type="button"
      className={`cm-tagchip cm-tagchip--${meta.cat}${active ? " is-on" : ""}${
        tone === "more" ? " cm-tagchip--more" : ""
      }`}
      onClick={onClick}
      aria-pressed={active}
      title={label}
      disabled={disabled}
    >
      <span className="cm-tagchip__mark" aria-hidden>
        <Icon size={14} strokeWidth={2.4} />
      </span>
      <span className="cm-tagchip__text">{label}</span>
    </button>
  );
}

function opponentPoolPlayerKey(player) {
  if (player?.user_id) return `u:${player.user_id}`;
  if (player?.player_id) return `p:${player.player_id}`;
  return `n:${String(player?.display_name || player?.name || "").toLowerCase()}-${
    player?.ranking_points ?? ""
  }`;
}

function OpponentPoolPanel({
  poolPlayers,
  lineupSide,
  onToggleSwap,
  onSetAvailability,
  t,
  extrasOnly = false,
  readOnly = false,
  availabilityMode = false,
  swapPlayerKey = null,
}) {
  const pool = Array.isArray(poolPlayers) ? poolPlayers : [];
  if (!pool.length) return null;

  const opening = extrasOnly ? [] : pool.filter((p) => p.expected);
  const extras = pool.filter((p) => !p.expected);
  if (!opening.length && !extras.length) return null;

  const slots = Array.isArray(lineupSide) ? lineupSide : [];

  const renderPlayer = (p) => {
    const playerKey = opponentPoolPlayerKey(p);
    const selectedForSwap = swapPlayerKey === playerKey;
    const inLineup = slots.some(
      (s) =>
        (p.user_id && String(s.user_id) === String(p.user_id)) ||
        (p.player_id && String(s.player_id) === String(p.player_id)) ||
        (p.display_name &&
          String(s.display_name || "").toLowerCase() ===
            String(p.display_name || p.name || "").toLowerCase())
    );
    const tags = [
      p.expected
        ? t("cmPoolExpected", "Expected")
        : p.band === "above"
          ? t("cmPoolAbove", "Above")
          : p.band === "below"
            ? t("cmPoolBelow", "Below")
            : null,
      p.in_last_match ? t("cmPoolRecent", "Last match") : null,
    ].filter(Boolean);

    const rowClass = [
      p.expected ? "is-expected" : "",
      selectedForSwap ? "is-swap-source" : "",
    ]
      .filter(Boolean)
      .join(" ");

    return (
      <li key={playerKey} className={rowClass || undefined}>
        <div>
          <strong>{p.display_name || p.name || "—"}</strong>
          <small>
            {[
              p.classement,
              p.ranking_points != null ? `${p.ranking_points} pts` : null,
            ]
              .filter(Boolean)
              .join(" · ") || "—"}
            {tags.length ? ` · ${tags.join(" · ")}` : ""}
          </small>
        </div>
        {!readOnly ? (
          <div className="cm-opp-pool__acts">
            {!inLineup ? (
              <button
                type="button"
                className={`cm-opp-pool__swap${selectedForSwap ? " is-on" : ""}`}
                aria-pressed={selectedForSwap}
                aria-label={
                  selectedForSwap
                    ? t("cmPoolCancelSwap", "Cancel selection")
                    : t("cmPoolSelectSwap", "Select to swap")
                }
                title={
                  selectedForSwap
                    ? t("cmPoolCancelSwap", "Cancel selection")
                    : t("cmPoolSelectSwap", "Select to swap")
                }
                onClick={() => onToggleSwap?.(p)}
              >
                <ArrowLeftRight size={14} strokeWidth={2.4} aria-hidden />
                <span>
                  {selectedForSwap
                    ? t("cmPoolSelected", "Selected")
                    : t("cmPoolSwap", "Swap")}
                </span>
              </button>
            ) : availabilityMode ? (
              <button
                type="button"
                className="cm-opp-avail cm-opp-avail--bench cm-opp-avail--icon"
                onClick={() => onSetAvailability?.(p, "bench")}
                aria-label={t("cmOpponentNotPresent", "Not present")}
                title={t("cmOpponentNotPresent", "Not present")}
              >
                <X size={14} strokeWidth={2.4} aria-hidden />
              </button>
            ) : (
              <span className="cm-opp-pool__in">
                {t("cmPoolInLineup", "In line-up")}
              </span>
            )}
          </div>
        ) : inLineup ? (
          <span className="cm-opp-pool__in">
            {t("cmPoolInLineup", "In line-up")}
          </span>
        ) : null}
      </li>
    );
  };

  return (
    <div className="cm-opp-pool" translate="no">
      {!readOnly ? (
        <p className="cm-opp-pool__hint">
          {swapPlayerKey
            ? t("cmPoolSwapPending", "Now tap the starter to replace.")
            : t(
                "cmPoolSwapHelp",
                "Select a potential player, then tap the starter to replace."
              )}
        </p>
      ) : null}
      {opening.length ? (
        <div className="cm-opp-pool__group" key="opening">
          <p className="cm-opp-pool__label">
            {t("cmTitularTeam", "Titular team (theoretical)")}
          </p>
          <ul className="cm-opp-pool__list">{opening.map(renderPlayer)}</ul>
        </div>
      ) : null}
      {extras.length ? (
        <div className="cm-opp-pool__group" key="extras">
          <p className="cm-opp-pool__label">
            {t("cmOtherPotential", "Other potential players")}
          </p>
          <ul className="cm-opp-pool__list">{extras.map(renderPlayer)}</ul>
        </div>
      ) : null}
    </div>
  );
}

function portraitStageNow() {
  try {
    return Boolean(window.matchMedia?.("(orientation: portrait)")?.matches);
  } catch {
    return window.innerHeight > window.innerWidth;
  }
}

function lockPlaybackOrientation(kind) {
  try {
    const lock = window.screen?.orientation?.lock?.(kind);
    if (lock && typeof lock.catch === "function") lock.catch(() => {});
  } catch {
    /* orientation lock needs a user gesture and browser fullscreen */
  }
}

function unlockPlaybackOrientation() {
  try {
    window.screen?.orientation?.unlock?.();
  } catch {
    /* ignore */
  }
}

const SCORE_FLIP_MAX = 40;

function ScoreFlip({
  side,
  name,
  value,
  onChange,
  disabled = false,
  t,
  max = SCORE_FLIP_MAX,
  compact = false,
}) {
  const cap = Math.max(0, Number(max) || SCORE_FLIP_MAX);
  const n = String(value).trim() === "" ? null : Number(value);
  const step = (delta) => {
    const base = n == null || !Number.isFinite(n) ? (delta > 0 ? -1 : 0) : n;
    onChange(String(Math.min(cap, Math.max(0, base + delta))));
  };
  return (
    <div
      className={`cm-flip cm-flip--${side}${compact ? " cm-flip--compact" : ""}`}
    >
      <span className="cm-flip__name" title={name}>
        {name}
      </span>
      <div className="cm-flip__row">
        <button
          type="button"
          className="cm-flip__step"
          disabled={disabled || n == null || n <= 0}
          onClick={() => step(-1)}
          aria-label={t("cmScoreMinusOne", "{{name}} minus one", { name })}
        >
          <Minus size={14} strokeWidth={2.6} aria-hidden />
        </button>
        <input
          className="cm-flip__card"
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={2}
          placeholder="–"
          value={value}
          disabled={disabled}
          onFocus={(event) => event.target.select()}
          onChange={(event) => {
            const digits = event.target.value.replace(/\D/g, "").slice(0, 2);
            onChange(
              digits === "" ? "" : String(Math.min(cap, Number(digits)))
            );
          }}
          aria-label={t("cmScoreOf", "{{name}} score", { name })}
        />
        <button
          type="button"
          className="cm-flip__step"
          disabled={disabled || (n != null && n >= cap)}
          onClick={() => step(1)}
          aria-label={t("cmScorePlusOne", "{{name}} plus one", { name })}
        >
          <Plus size={14} strokeWidth={2.6} aria-hidden />
        </button>
      </div>
    </div>
  );
}

function ChronoTimelineViewer({
  rubber,
  points,
  media,
  rubbers = [],
  tagLabel,
  homeLabel,
  awayLabel,
  homePlayers = null,
  awayPlayers = null,
  showRubberHead = false,
  canTag = false,
  canModerate = false,
  canLabelScore = false,
  onSetPointOutcome,
  onSavePointScore,
  onStampReviewPoint,
  onSendReaction,
  onClearPoints,
  onProbeScore,
  onFingerprintFrame,
  onScanScore,
  onLabelScore,
  onLabelStatus,
  onReassignMedia,
  preferredMediaId = null,
  clubLogo = null,
  clubName = "",
  brandColor = "",
  homeClubName = "",
  awayClubName = "",
  homeTeamScore = 0,
  awayTeamScore = 0,
  matchPoints = null,
  setsToWin = 3,
  division = "",
  /** "timeline" keeps the match tab. "review" is the full-screen match review dialog. */
  layout = "timeline",
  t,
}) {
  const sets = useMemo(() => {
    return groupPointsBySet(points).map((group) => ({
      ...group,
      points: [...group.points].sort((a, b) => {
        const ta = a?.t_ms == null ? Number.POSITIVE_INFINITY : Number(a.t_ms);
        const tb = b?.t_ms == null ? Number.POSITIVE_INFINITY : Number(b.t_ms);
        if (ta !== tb) return ta - tb;
        return (Number(a?.seq) || 0) - (Number(b?.seq) || 0);
      }),
    }));
  }, [points]);
  const [setNumber, setSetNumber] = useState(() => sets[0]?.setNumber || 1);
  const [cueIndex, setCueIndex] = useState(-1);
  const [selectedCue, setSelectedCue] = useState(-1);
  const [mediaError, setMediaError] = useState("");
  const [boardNow, setBoardNow] = useState(null);
  const [playheadSec, setPlayheadSec] = useState(0);
  const [activePhoto, setActivePhoto] = useState(null);
  const [srcIndex, setSrcIndex] = useState(0);
  const [seekToken, setSeekToken] = useState({ sec: null, play: false });
  /** "embedded" | "portrait" | "landscape" */
  const [stageMode, setStageMode] = useState("embedded");
  const playerExpanded = stageMode !== "embedded";
  const [stagePortrait, setStagePortrait] = useState(false);
  const [frameRatio, setFrameRatio] = useState(16 / 9);
  const [pointsOpen, setPointsOpen] = useState(false);
  const [wideReview, setWideReview] = useState(
    () =>
      layout === "review" &&
      typeof window !== "undefined" &&
      window.matchMedia("(min-width: 1024px)").matches
  );
  const [labelerOpen, setLabelerOpen] = useState(
    () =>
      layout === "review" &&
      typeof window !== "undefined" &&
      window.matchMedia("(min-width: 1024px)").matches
  );
  const [toolsOpen, setToolsOpen] = useState(
    () =>
      layout === "review" &&
      typeof window !== "undefined" &&
      window.matchMedia("(min-width: 1024px)").matches
  );
  const [tagBusy, setTagBusy] = useState(false);
  const [scoreHome, setScoreHome] = useState("");
  const [scoreAway, setScoreAway] = useState("");
  const [scoreSetsHome, setScoreSetsHome] = useState("0");
  const [scoreSetsAway, setScoreSetsAway] = useState("0");
  const [pendingTags, setPendingTags] = useState([]);
  const [scoreBusy, setScoreBusy] = useState(false);
  const [reviewNote, setReviewNote] = useState("");
  const [roiMode, setRoiMode] = useState(false);
  const [roiForScan, setRoiForScan] = useState(false);
  const [probeReading, setProbeReading] = useState(null);
  const [probeError, setProbeError] = useState("");
  const [probeBusy, setProbeBusy] = useState(false);
  const [scoreAiOpen, setScoreAiOpen] = useState(false);
  const [scoreAiInSec, setScoreAiInSec] = useState(null);
  const [scoreAiOutSec, setScoreAiOutSec] = useState(null);
  const [scoreAiRoi, setScoreAiRoi] = useState(null);
  const [scoreAiScanBusy, setScoreAiScanBusy] = useState(false);
  const [scoreAiLabelBusy, setScoreAiLabelBusy] = useState(false);
  const [scoreAiLabelNote, setScoreAiLabelNote] = useState("");
  const [localLabelJob, setLocalLabelJob] = useState(null);
  const [playAtomicMs, setPlayAtomicMs] = useState(0);
  const [scoreAiScanPct, setScoreAiScanPct] = useState(0);
  const [scoreAiScanDone, setScoreAiScanDone] = useState(0);
  const [scoreAiScanTotal, setScoreAiScanTotal] = useState(0);
  const [scoreAiScanEtaSec, setScoreAiScanEtaSec] = useState(0);
  const scoreAiScanTimerRef = useRef(null);
  const scoreAiScanBusyRef = useRef(false);
  const [scoreAiReview, setScoreAiReview] = useState(false);
  const [scoreAiCandidates, setScoreAiCandidates] = useState([]);
  const [scoreAiSkipped, setScoreAiSkipped] = useState(0);
  const [scoreAiSkippedDead, setScoreAiSkippedDead] = useState(0);
  const [scoreAiTiming, setScoreAiTiming] = useState(null);
  const [scoreAiIndex, setScoreAiIndex] = useState(0);
  const [scoreAiReading, setScoreAiReading] = useState(null);
  const [scoreAiError, setScoreAiError] = useState("");
  const [scoreAiOcrBusy, setScoreAiOcrBusy] = useState(false);
  const [scoreAiLiveSim, setScoreAiLiveSim] = useState(false);
  const [scoreAiLiveRunning, setScoreAiLiveRunning] = useState(false);
  const [scoreAiLiveLogs, setScoreAiLiveLogs] = useState([]);
  const scoreAiLiveSimRef = useRef(false);
  const scoreAiLiveStopRef = useRef(false);
  const scoreAiLiveTimerRef = useRef(null);
  const scoreAiLiveLastKeyRef = useRef("");
  const scoreAiLiveLastScoreRef = useRef(null);
  const scoreAiLiveSetsRef = useRef({ home: 0, away: 0 });
  const scoreAiLiveSetNumberRef = useRef(1);
  const scoreAiLiveSuspectRef = useRef(null);
  const scoreAiLiveGateRef = useRef(null);
  const scoreSideFlipRef = useRef(false);
  const [scoreSideFlip, setScoreSideFlip] = useState(false);
  /** Set number we already flipped ends for (avoid double flip on 0–0). */
  const scoreAiLiveFlippedAfterSetRef = useRef(0);
  const [scoreAiLiveBoard, setScoreAiLiveBoard] = useState(null);
  const stopScoreAiLiveRef = useRef(() => {});
  const [reassignBusy, setReassignBusy] = useState(false);
  const rootRef = useRef(null);
  const videoRef = useRef(null);
  const [roiVideoEl, setRoiVideoEl] = useState(null);
  const bindRoiVideo = useCallback((node) => {
    videoRef.current = node;
    setRoiVideoEl(node);
  }, []);
  const cueListRef = useRef(null);
  const pillStripRef = useRef(null);
  const [reviewFs, setReviewFs] = useState(false);
  const stageModeRef = useRef("embedded");
  /** Landscape opened by turning the phone; turning back returns to returnModeRef. */
  const autoLandscapeRef = useRef(false);
  const returnModeRef = useRef("embedded");
  const atomicMsRef = useRef(0);
  const filePlayMsRef = useRef(0);

  useEffect(() => {
    if (!sets.length) return;
    if (!sets.some((s) => s.setNumber === setNumber)) {
      setSetNumber(sets[0].setNumber);
    }
  }, [sets, setNumber]);

  const setPoints = useMemo(
    () => sets.find((s) => s.setNumber === setNumber)?.points || [],
    [sets, setNumber]
  );
  const playbackMedia = useMemo(() => matchPlaybackMedia(media), [media]);
  const tapes = useMemo(
    () => rubberReviewTapes(media, rubber?.rubber_id),
    [media, rubber?.rubber_id]
  );
  const [tapeId, setTapeId] = useState("");
  const chosenTapeId = tapes.some((tape) => tape.id === tapeId)
    ? tapeId
    : defaultRubberTapeId(tapes, points);
  const activeTape = tapes.find((tape) => tape.id === chosenTapeId) || null;
  useEffect(() => {
    if (!preferredMediaId) return;
    const hit = tapes.find((tape) =>
      (tape.clips || []).some(
        (row) => String(row.media_id) === String(preferredMediaId)
      )
    );
    if (hit) setTapeId(hit.id);
  }, [preferredMediaId, tapes]);
  const reviewMedia = useMemo(() => {
    if (!activeTape) return playbackMedia;
    const ids = new Set(
      (activeTape.clips || []).map((row) => String(row.media_id))
    );
    return playbackMedia.filter((item) => {
      if (item?.kind !== "clip" || !item.file_url) return true;
      if (
        !item.rubber_id ||
        String(item.rubber_id) !== String(rubber?.rubber_id)
      ) {
        return true;
      }
      return ids.has(String(item.media_id));
    });
  }, [activeTape, playbackMedia, rubber?.rubber_id]);
  const clip = useMemo(
    () => clipForPoint(reviewMedia, setPoints[0], setPoints),
    [reviewMedia, setPoints]
  );
  const sources = useMemo(() => {
    const picked = setReviewSources(reviewMedia, {
      rubberId: rubber?.rubber_id,
      setNumber,
      setPoints,
      allPoints: points,
    });
    // A moved full file can sit beside point-aligned chunks. Play the tape
    // the moderator is looking at, even when stitch prefers the other capture.
    const tapeClips = (activeTape?.clips || []).filter((row) => row?.file_url);
    if (!tapeClips.length) return picked;
    const ids = new Set(tapeClips.map((row) => String(row.media_id)));
    const onTape = picked.filter((row) => ids.has(String(row?.media_id)));
    return onTape.length ? onTape : tapeClips;
  }, [activeTape, points, reviewMedia, rubber?.rubber_id, setNumber, setPoints]);
  const activeSource = sources[Math.min(srcIndex, Math.max(0, sources.length - 1))] || null;
  const sourceStarts = useMemo(
    () => tapeSegmentStartsSec(sources),
    [sources]
  );
  const sourcesKey = useMemo(
    () => sources.map((s) => s?.media_id || "").join("|"),
    [sources]
  );

  useEffect(() => {
    setSrcIndex(0);
    setCueIndex(-1);
    setSelectedCue(-1);
    setBoardNow(null);
    setPlayheadSec(0);
    filePlayMsRef.current = 0;
    setPlayAtomicMs(0);
    setActivePhoto(null);
    setMediaError("");
    atomicMsRef.current = 0;
    setSeekToken({ sec: null, play: false });
  }, [setNumber, sourcesKey]);

  const remoteLabelJob =
    activeSource?.score_label &&
    typeof activeSource.score_label === "object" &&
    !Array.isArray(activeSource.score_label)
      ? activeSource.score_label
      : null;
  const labelJob = (() => {
    if (localLabelJob && remoteLabelJob) {
      return String(localLabelJob.updated_at || "") >= String(remoteLabelJob.updated_at || "")
        ? localLabelJob
        : remoteLabelJob;
    }
    return localLabelJob || remoteLabelJob;
  })();
  const scoreLabelActive =
    labelJob?.status === "queued" || labelJob?.status === "running";
  const labelProgressPct = Math.max(0, Math.min(100, Number(labelJob?.progress) || 0));

  useEffect(() => {
    const mediaId = String(activeSource?.media_id || "");
    setLocalLabelJob((prev) => {
      const queue = Array.isArray(prev?.queue) ? prev.queue.map(String) : [];
      if (mediaId && queue.includes(mediaId)) return prev;
      return null;
    });
    setScoreAiRoi(normalizeRoi(activeSource?.score_roi));
    scoreSideFlipRef.current = false;
    setScoreSideFlip(false);
  }, [activeSource?.media_id]);

  const onLabelStatusRef = useRef(onLabelStatus);
  onLabelStatusRef.current = onLabelStatus;

  useEffect(() => {
    if (!scoreLabelActive || !activeSource?.media_id) return undefined;
    let stop = false;
    const tick = async () => {
      const read = onLabelStatusRef.current;
      if (!read) return;
      try {
        const data = await read({ media_id: activeSource.media_id });
        if (stop || !data?.job) return;
        setLocalLabelJob(data.job);
      } catch (err) {
        if (!stop) {
          setScoreAiError(err?.response?.data?.message || err.message || String(err));
        }
      }
    };
    tick();
    const id = window.setInterval(tick, 2000);
    return () => {
      stop = true;
      window.clearInterval(id);
    };
  }, [scoreLabelActive, activeSource?.media_id]);

  const syncPlayhead = useCallback(
    (localSecOverride = null) => {
      const local =
        localSecOverride != null && Number.isFinite(Number(localSecOverride))
          ? Number(localSecOverride)
          : Number(videoRef.current?.currentTime) || 0;
      const marked = Array.isArray(activeSource?.point_marks) && activeSource.point_marks.length;
      const pointClip = sources.length > 1 && activeSource?.point_id;
      const ownTimeline = activeSource?.score_timeline;
      const timeline =
        (Array.isArray(ownTimeline) && ownTimeline.length ? ownTimeline : null) ||
        clip?.score_timeline ||
        sources[0]?.score_timeline;
      if (marked || pointClip) {
        const fileMs = Math.round(local * 1000);
        filePlayMsRef.current = fileMs;
        if (scoreAiScanBusyRef.current) {
          atomicMsRef.current = fileMs;
          setBoardNow(resolveBoardReading(timeline, fileMs, fileMs));
          return;
        }
        setPlayheadSec(local);
        const idx = cueIndexForPlayback(setPoints, {
          localSec: local,
          clip: activeSource,
          sourcePointId: activeSource?.point_id,
        });
        const markedPoint = idx >= 0 ? setPoints[idx] : null;
        const atomic =
          markedPoint?.t_ms != null ? Number(markedPoint.t_ms) : fileMs;
        atomicMsRef.current = atomic;
        setBoardNow(resolveBoardReading(timeline, fileMs, atomic));
        setPlayAtomicMs(atomic);
        setCueIndex(idx);
        if (idx >= 0) setSelectedCue(idx);
        const point = idx >= 0 ? setPoints[idx] : null;
        if (point) {
          const photos = mediaForPoint(playbackMedia, point).filter(
            (m) => m.kind === "photo"
          );
          setActivePhoto(photos[0] || null);
        } else {
          setActivePhoto(null);
        }
        return;
      }
      const start = sourceStarts[srcIndex] || 0;
      const tapeMs = Math.round((start + local) * 1000);
      const captureOrigin = Number(activeSource?.capture_t_ms);
      const atomic =
        Number.isFinite(captureOrigin)
          ? Math.round(captureOrigin + local * 1000)
          : sources.length > 1
            ? tapeMs
            : playbackAtomicMs(activeSource || clip, local);
      const fileMs =
        Array.isArray(ownTimeline) && ownTimeline.length
          ? Math.round(local * 1000)
          : tapeMs;
      filePlayMsRef.current = fileMs;
      atomicMsRef.current = atomic;
      setBoardNow(resolveBoardReading(timeline, fileMs, atomic));
      // Scan scrub seeks every few ms — skip cue/photo layout updates so the
      // player shell keeps a stable size.
      if (scoreAiScanBusyRef.current) return;
      setPlayAtomicMs(atomic);
      setPlayheadSec((sources.length > 1 ? tapeMs : atomic) / 1000);
      const idx = activeCueIndex(setPoints, atomic);
      setCueIndex(idx);
      if (idx >= 0) setSelectedCue(idx);
      const point = idx >= 0 ? setPoints[idx] : null;
      if (point) {
        const photos = mediaForPoint(playbackMedia, point).filter(
          (m) => m.kind === "photo"
        );
        setActivePhoto(photos[0] || null);
      } else {
        setActivePhoto(null);
      }
    },
    [activeSource, clip, playbackMedia, setPoints, sourceStarts, sources, srcIndex]
  );

  const boardTimelineKey = useMemo(() => {
    const own = activeSource?.score_timeline;
    const timeline =
      (Array.isArray(own) && own.length ? own : null) ||
      clip?.score_timeline ||
      sources[0]?.score_timeline;
    if (!Array.isArray(timeline) || !timeline.length) return "";
    const last = timeline[timeline.length - 1];
    return `${timeline.length}:${last?.t_ms}:${last?.board_left}:${last?.board_right}:${last?.games_left}:${last?.games_right}`;
  }, [activeSource, clip, sources]);

  useEffect(() => {
    const local = Number(videoRef.current?.currentTime);
    syncPlayhead(Number.isFinite(local) ? local : filePlayMsRef.current / 1000);
  }, [boardTimelineKey, syncPlayhead]);

  useEffect(() => {
    if (cueIndex < 0 || !cueListRef.current || scoreAiScanBusyRef.current) return;
    const node = cueListRef.current.querySelector(`[data-cue="${cueIndex}"]`);
    if (!node || node.offsetParent == null) return;
    node?.scrollIntoView?.({ block: "center", behavior: "smooth", inline: "nearest" });
  }, [cueIndex, pointsOpen]);

  const enterStage = useCallback((next) => {
    // Portrait stage is phone-only. Desktop review may still go landscape
    // fullscreen on demand while keeping the staff tools when embedded.
    const resolved =
      layout === "review" && next === "portrait" ? "embedded" : next;
    if (resolved === stageModeRef.current) return;
    stageModeRef.current = resolved;
    if (layout === "review") {
      // Stay in the dialog. A second fullscreen plus a CSS rotation blanks the
      // video when the phone returns to portrait.
      setStagePortrait(false);
      setStageMode(resolved);
      if (resolved === "embedded") unlockPlaybackOrientation();
      return;
    }
    const node = rootRef.current;
    const current =
      document.fullscreenElement || document.webkitFullscreenElement || null;
    const ownsFullscreen = Boolean(
      current && node && (current === node || node.contains(current))
    );
    if (next === "embedded") {
      setStageMode("embedded");
      setStagePortrait(false);
      unlockPlaybackOrientation();
      if (ownsFullscreen) {
        const exit = document.exitFullscreen || document.webkitExitFullscreen;
        try {
          exit?.call(document);
        } catch {
          /* ignore */
        }
      }
      return;
    }
    setStagePortrait(next === "landscape" && portraitStageNow());
    setStageMode(next);
    if (ownsFullscreen) {
      lockPlaybackOrientation(next);
      return;
    }
    // Fullscreen + orientation lock must start in this turn (user gesture).
    const req = node?.requestFullscreen || node?.webkitRequestFullscreen;
    try {
      const pending = req?.call(node);
      if (pending && typeof pending.then === "function") {
        pending.then(() => lockPlaybackOrientation(next)).catch(() => {});
      } else {
        lockPlaybackOrientation(next);
      }
    } catch {
      lockPlaybackOrientation(next);
    }
  }, [layout]);

  const chooseStage = useCallback(
    (next) => {
      autoLandscapeRef.current = false;
      enterStage(next);
    },
    [enterStage]
  );

  useEffect(() => {
    if (!playerExpanded) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const mq = window.matchMedia("(orientation: portrait)");
    const syncPortrait = () => setStagePortrait(mq.matches);
    syncPortrait();
    const onKey = (event) => {
      if (event.key === "Escape") chooseStage("embedded");
    };
    mq.addEventListener?.("change", syncPortrait);
    window.addEventListener("keydown", onKey);
    let ownedFullscreen = false;
    const onFs = () => {
      const current =
        document.fullscreenElement || document.webkitFullscreenElement || null;
      const node = rootRef.current;
      if (node && current && (current === node || node.contains(current))) {
        ownedFullscreen = true;
        return;
      }
      if (ownedFullscreen && !current) {
        autoLandscapeRef.current = false;
        stageModeRef.current = "embedded";
        setStageMode("embedded");
        setStagePortrait(false);
        unlockPlaybackOrientation();
      }
    };
    document.addEventListener("fullscreenchange", onFs);
    document.addEventListener("webkitfullscreenchange", onFs);
    return () => {
      document.body.style.overflow = prev;
      mq.removeEventListener?.("change", syncPortrait);
      window.removeEventListener("keydown", onKey);
      document.removeEventListener("fullscreenchange", onFs);
      document.removeEventListener("webkitfullscreenchange", onFs);
    };
  }, [playerExpanded, chooseStage]);

  const seek = useCallback(
    (point, { select = true } = {}) => {
      if (!point || !sources.length) return;
      const pointClipIdx = sources.findIndex(
        (source) =>
          source?.point_id && String(source.point_id) === String(point.point_id)
      );
      if (sources.length > 1 && pointClipIdx >= 0) {
        const nextIdx = pointClipIdx;
        const local = 0;
        if (nextIdx !== srcIndex) {
          setSrcIndex(nextIdx);
          setSeekToken({ sec: local, play: true });
        } else if (videoRef.current) {
          try {
            videoRef.current.currentTime = local;
          } catch {
            /* ignore */
          }
          videoRef.current.play?.().catch(() => {});
          syncPlayhead(local);
        } else {
          setSeekToken({ sec: local, play: true });
        }
      } else if (sources.length > 1 && point.t_ms != null) {
        const atomicSec = Math.max(0, Number(point.t_ms) || 0) / 1000;
        const hit =
          locatePointOnTape(sources, point) ||
          locateTapeSegment(sources, atomicSec);
        if (!hit) return;
        const nextIdx = hit.index;
        const local = hit.localSec;
        if (nextIdx !== srcIndex) {
          setSrcIndex(nextIdx);
          setSeekToken({ sec: local, play: true });
        } else if (videoRef.current) {
          try {
            videoRef.current.currentTime = local;
          } catch {
            /* ignore */
          }
          videoRef.current.play?.().catch(() => {});
          syncPlayhead(local);
        } else {
          setSeekToken({ sec: local, play: true });
        }
      } else {
        const sec = seekSecondsForPoint(point, activeSource || sources[0] || clip);
        if (sec == null) return;
        if (videoRef.current) {
          try {
            videoRef.current.currentTime = sec;
          } catch {
            /* ignore */
          }
          videoRef.current.play?.().catch(() => {});
          syncPlayhead(sec);
        } else {
          setSeekToken({ sec, play: true });
        }
      }
      if (select) {
        const idx = setPoints.findIndex(
          (p) => String(p.point_id) === String(point.point_id)
        );
        if (idx >= 0) setSelectedCue(idx);
      }
    },
    [activeSource, clip, setPoints, sources, srcIndex, syncPlayhead]
  );

  const showPlayer =
    Boolean(activeSource?.file_url) || Boolean(activePhoto?.file_url);
  const focusIdx = selectedCue >= 0 ? selectedCue : cueIndex;
  const focusPoint = focusIdx >= 0 ? setPoints[focusIdx] : null;
  const focusLabels = groupedPointLabels(focusPoint?.tags);

  useEffect(() => {
    const root = pillStripRef.current;
    if (!root || focusIdx < 0 || scoreAiScanBusyRef.current) return;
    const node = root.querySelector(`[data-pill="${focusIdx}"]`);
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    node?.scrollIntoView?.({
      inline: "center",
      block: "nearest",
      behavior: reduce ? "auto" : "smooth",
    });
  }, [focusIdx, setNumber]);

  useEffect(() => {
    const sync = () => {
      const node = rootRef.current;
      const target = node?.closest?.(".cm-review") || node;
      const current =
        document.fullscreenElement || document.webkitFullscreenElement || null;
      setReviewFs(
        Boolean(target && current && (current === target || target.contains(current)))
      );
    };
    document.addEventListener("fullscreenchange", sync);
    document.addEventListener("webkitfullscreenchange", sync);
    return () => {
      document.removeEventListener("fullscreenchange", sync);
      document.removeEventListener("webkitfullscreenchange", sync);
    };
  }, []);

  const handleOutcome = useCallback(
    async (outcomeId) => {
      if (!canTag || tagBusy) return;
      if (!focusPoint) {
        setPendingTags((prev) => togglePointOutcome({ tags: prev }, outcomeId).tags);
        return;
      }
      if (!onSetPointOutcome) return;
      setTagBusy(true);
      try {
        await onSetPointOutcome(focusPoint, outcomeId);
      } finally {
        setTagBusy(false);
      }
    },
    [canTag, focusPoint, onSetPointOutcome, tagBusy]
  );

  useEffect(() => {
    setScoreHome(focusPoint ? String(focusPoint.home_score ?? "") : "");
    setScoreAway(focusPoint ? String(focusPoint.away_score ?? "") : "");
    if (focusPoint) setPendingTags([]);
  }, [focusPoint?.point_id, focusPoint?.home_score, focusPoint?.away_score]);

  useEffect(() => {
    if (scoreAiLiveBoard) {
      setScoreSetsHome(String(scoreAiLiveBoard.gamesHome ?? 0));
      setScoreSetsAway(String(scoreAiLiveBoard.gamesAway ?? 0));
      return;
    }
    if (focusPoint) {
      const sh = clipSetCount(focusPoint.sets_home);
      const sa = clipSetCount(focusPoint.sets_away);
      setScoreSetsHome(sh != null ? String(sh) : "0");
      setScoreSetsAway(sa != null ? String(sa) : "0");
      return;
    }
    setScoreSetsHome("0");
    setScoreSetsAway("0");
  }, [
    focusPoint?.point_id,
    focusPoint?.sets_home,
    focusPoint?.sets_away,
    scoreAiLiveBoard,
  ]);

  const liveReactions = useMemo(
    () => reactionsNearPlayhead(rubber?.review_reactions, playheadSec * 1000),
    [rubber?.review_reactions, playheadSec]
  );

  const saveScore = useCallback(async () => {
    if (!canTag || scoreBusy) return;
    const home = Number(scoreHome);
    const away = Number(scoreAway);
    const setsHome = clipSetCount(scoreSetsHome);
    const setsAway = clipSetCount(scoreSetsAway);
    if (
      String(scoreHome).trim() === "" ||
      String(scoreAway).trim() === "" ||
      !Number.isFinite(home) ||
      !Number.isFinite(away)
    ) {
      setReviewNote(t("cmScoreNeeded", "Enter both scores first."));
      return;
    }
    if (setsHome == null || setsAway == null) {
      setReviewNote(t("cmSetsNeeded", "Enter both set counts first."));
      return;
    }
    const setScores = { sets_home: setsHome, sets_away: setsAway };
    const nextSetNumber = Math.max(
      1,
      Math.min(9, setsHome + setsAway + 1)
    );
    setScoreBusy(true);
    setReviewNote("");
    try {
      if (focusPoint && onSavePointScore) {
        await onSavePointScore(focusPoint, {
          home_score: home,
          away_score: away,
          ...setScores,
        });
        setReviewNote(t("cmScoreSaved", "Score saved on this point."));
      } else if (onStampReviewPoint) {
        await onStampReviewPoint({
          t_ms: Math.max(0, Math.round(atomicMsRef.current || 0)),
          set_number: setNumber || nextSetNumber,
          home_score: home,
          away_score: away,
          ...setScores,
          tags: pendingTags,
          outcome: readPointOutcome({ tags: pendingTags }),
          label: pointLabelWithOutcome(
            null,
            readPointOutcome({ tags: pendingTags })
          ),
        });
        setPendingTags([]);
        setReviewNote(t("cmScoreStamped", "Score tagged at this moment."));
      }
      scoreAiLiveSetsRef.current = { home: setsHome, away: setsAway };
      scoreAiLiveSetNumberRef.current = nextSetNumber;
      setScoreAiLiveBoard((prev) =>
        prev
          ? {
              ...prev,
              home,
              away,
              gamesHome: setsHome,
              gamesAway: setsAway,
              setNumber: nextSetNumber,
            }
          : {
              home,
              away,
              gamesHome: setsHome,
              gamesAway: setsAway,
              setNumber: nextSetNumber,
            }
      );
      setSetNumber(nextSetNumber);
    } catch (err) {
      setReviewNote(err?.response?.data?.message || err.message || String(err));
    } finally {
      setScoreBusy(false);
    }
  }, [
    canTag,
    focusPoint,
    onSavePointScore,
    onStampReviewPoint,
    pendingTags,
    scoreAway,
    scoreBusy,
    scoreHome,
    scoreSetsAway,
    scoreSetsHome,
    setNumber,
    t,
  ]);

  const sendEmoji = useCallback(
    async (emoji) => {
      if (!canTag || !onSendReaction) return;
      try {
        await onSendReaction({
          emoji,
          t_ms: Math.max(0, Math.round(atomicMsRef.current || 0)),
        });
      } catch (err) {
        setReviewNote(err?.response?.data?.message || err.message || String(err));
      }
    },
    [canTag, onSendReaction]
  );

  const finishRoi = useCallback(
    async (roi) => {
      setRoiMode(false);
      if (!roi) {
        setRoiForScan(false);
        return;
      }
      if (roi.w < 0.02 || roi.h < 0.02) {
        setRoiForScan(false);
        return;
      }
      if (roiForScan) {
        setScoreAiRoi(roi);
        setRoiForScan(false);
        setScoreAiError("");
        return;
      }
      if (!onProbeScore) return;
      setProbeBusy(true);
      setProbeError("");
      setProbeReading(null);
      try {
        const image = await videoRoiToJpegBase64(videoRef.current, roi);
        const data = await onProbeScore({
          image_jpeg_base64: image || undefined,
          media_id: activeSource?.media_id,
          t_sec: Number(videoRef.current?.currentTime) || 0,
          roi,
        });
        const reading = data?.reading || data;
        setProbeReading(reading || null);
        if (!reading?.visible) {
          setProbeError(t("cmScoreUnread", "No score could be read in that box."));
        }
      } catch (err) {
        setProbeError(err?.response?.data?.message || err.message || String(err));
      } finally {
        setProbeBusy(false);
      }
    },
    [activeSource?.media_id, onProbeScore, roiForScan, t]
  );

  const seekClipSec = useCallback((sec, { play = false } = {}) => {
    const target = Math.max(0, Number(sec) || 0);
    setSeekToken({ sec: target, play });
    const video = videoRef.current;
    if (!video) return;
    try {
      video.currentTime = target;
      if (play) video.play?.().catch(() => {});
      else video.pause?.();
    } catch {
      /* ignore */
    }
    syncPlayhead(target);
  }, [syncPlayhead]);

  const closeScoreAi = useCallback(() => {
    if (scoreAiScanTimerRef.current) {
      window.clearInterval(scoreAiScanTimerRef.current);
      scoreAiScanTimerRef.current = null;
    }
    scoreAiScanBusyRef.current = false;
    setScoreAiOpen(false);
    setScoreAiReview(false);
    setScoreAiScanBusy(false);
    setScoreAiScanPct(0);
    setScoreAiScanDone(0);
    setScoreAiScanTotal(0);
    setScoreAiScanEtaSec(0);
    setScoreAiOcrBusy(false);
    setScoreAiCandidates([]);
    setScoreAiIndex(0);
    setScoreAiSkippedDead(0);
    setScoreAiTiming(null);
    setScoreAiReading(null);
    setScoreAiError("");
    setScoreAiLabelNote("");
    stopScoreAiLiveRef.current();
    setRoiForScan(false);
    setRoiMode(false);
  }, []);

  const launchScoreAiScan = useCallback(async () => {
    stopScoreAiLiveRef.current();
    if (!onScanScore || !activeSource?.media_id || !scoreAiRoi) return;
    if (
      scoreAiInSec == null ||
      scoreAiOutSec == null ||
      !Number.isFinite(scoreAiInSec) ||
      !Number.isFinite(scoreAiOutSec)
    ) {
      setScoreAiError(t("cmScoreAiNeedSegment", "Mark in and out on this clip first."));
      return;
    }
    const tIn = Math.min(Number(scoreAiInSec), Number(scoreAiOutSec));
    const tOut = Math.max(Number(scoreAiInSec), Number(scoreAiOutSec));
    const span = Math.max(0.2, tOut - tIn);
    // Backend hunts at 0.75s; decode of the marked window dominates. Cached
    // downloads / HTTPS range reads make repeats faster than this ETA.
    const sampleTotal = Math.min(600, Math.max(1, Math.ceil(span / 0.75) + 1));
    const expectedMs = Math.round(4000 + span * 500 + sampleTotal * 20);
    const startedAt = Date.now();
    if (scoreAiScanTimerRef.current) {
      window.clearInterval(scoreAiScanTimerRef.current);
      scoreAiScanTimerRef.current = null;
    }
    scoreAiScanBusyRef.current = true;
    setScoreAiScanBusy(true);
    setScoreAiScanPct(2);
    setScoreAiScanDone(0);
    setScoreAiScanTotal(sampleTotal);
    setScoreAiScanEtaSec(Math.max(1, Math.round(expectedMs / 1000)));
    setScoreAiError("");
    setScoreAiReading(null);
    setScoreAiReview(false);
    setScoreAiTiming(null);
    setScoreAiSkippedDead(0);
    setActivePhoto(null);
    scoreAiScanTimerRef.current = window.setInterval(() => {
      const elapsed = Date.now() - startedAt;
      const raw = Math.min(0.92, elapsed / expectedMs);
      const pct = Math.max(2, Math.round(raw * 100));
      const done = Math.min(sampleTotal, Math.floor(raw * sampleTotal));
      const remainMs = Math.max(0, expectedMs - elapsed);
      setScoreAiScanPct(pct);
      setScoreAiScanDone(done);
      setScoreAiScanEtaSec(Math.max(1, Math.ceil(remainMs / 1000)));
      const video = videoRef.current;
      if (video && Number.isFinite(tIn) && Number.isFinite(tOut)) {
        try {
          video.currentTime = tIn + (tOut - tIn) * raw;
        } catch {
          /* ignore */
        }
      }
    }, 180);
    try {
      const data = await onScanScore({
        media_id: activeSource.media_id,
        t_in_sec: scoreAiInSec,
        t_out_sec: scoreAiOutSec,
        roi: scoreAiRoi,
      });
      const list = Array.isArray(data?.candidates) ? data.candidates : [];
      setScoreAiCandidates(list);
      setScoreAiSkipped(
        Array.isArray(data?.skipped_occlusion) ? data.skipped_occlusion.length : 0
      );
      setScoreAiSkippedDead(Number(data?.skipped_dead_count) || 0);
      setScoreAiTiming(data?.timing_ms || null);
      setScoreAiIndex(0);
      setScoreAiScanPct(100);
      setScoreAiScanDone(
        Number(data?.fingerprinted_count) ||
          Number(data?.sample_count) ||
          sampleTotal
      );
      setScoreAiScanTotal(Number(data?.sample_count) || sampleTotal);
      setScoreAiScanEtaSec(0);
      if (!list.length) {
        setScoreAiError(
          t(
            "cmScoreAiNoCandidates",
            "No score updates found in that segment (or all changes looked like occlusion)."
          )
        );
        return;
      }
      setScoreAiReview(true);
      seekClipSec(list[0].t_sec, { play: false });
    } catch (err) {
      setScoreAiError(err?.response?.data?.message || err.message || String(err));
    } finally {
      if (scoreAiScanTimerRef.current) {
        window.clearInterval(scoreAiScanTimerRef.current);
        scoreAiScanTimerRef.current = null;
      }
      scoreAiScanBusyRef.current = false;
      setScoreAiScanBusy(false);
    }
  }, [
    activeSource?.media_id,
    onScanScore,
    scoreAiInSec,
    scoreAiOutSec,
    scoreAiRoi,
    seekClipSec,
    t,
  ]);

  const launchScoreLabel = useCallback(async () => {
    if (!onLabelScore || !activeSource?.media_id || !scoreAiRoi) return;
    const duration = Number(videoRef.current?.duration);
    const hasIn = scoreAiInSec != null && Number.isFinite(Number(scoreAiInSec));
    const hasOut = scoreAiOutSec != null && Number.isFinite(Number(scoreAiOutSec));
    const tIn = hasIn ? Number(scoreAiInSec) : 0;
    const tOut = hasOut
      ? Number(scoreAiOutSec)
      : Number.isFinite(duration) && duration > 0.2
        ? duration
        : null;
    if (tOut == null || Math.abs(tOut - tIn) < 0.2) {
      setScoreAiError(
        t(
          "cmScoreLabelNeedVideo",
          "Wait for the video to load, or mark in and out on this clip."
        )
      );
      return;
    }
    setScoreAiLabelBusy(true);
    setScoreAiError("");
    setScoreAiLabelNote("");
    setScoreAiReading(null);
    try {
      const data = await onLabelScore({
        media_id: activeSource.media_id,
        t_in_sec: Math.min(tIn, tOut),
        t_out_sec: Math.max(tIn, tOut),
        roi: scoreAiRoi,
      });
      if (data?.job) setLocalLabelJob(data.job);
      if (data?.job?.status === "failed" && data.job.error) {
        setScoreAiError(String(data.job.error));
      }
    } catch (err) {
      setScoreAiError(err?.response?.data?.message || err.message || String(err));
    } finally {
      setScoreAiLabelBusy(false);
    }
  }, [
    activeSource?.media_id,
    onLabelScore,
    scoreAiInSec,
    scoreAiOutSec,
    scoreAiRoi,
    t,
  ]);

  useEffect(
    () => () => {
      if (scoreAiScanTimerRef.current) {
        window.clearInterval(scoreAiScanTimerRef.current);
        scoreAiScanTimerRef.current = null;
      }
      scoreAiLiveStopRef.current = true;
      if (scoreAiLiveTimerRef.current) {
        window.clearInterval(scoreAiLiveTimerRef.current);
        scoreAiLiveTimerRef.current = null;
      }
    },
    []
  );

  const confirmScoreAiCandidate = useCallback(async () => {
    const hit = scoreAiCandidates[scoreAiIndex];
    if (!hit || !onProbeScore || !activeSource?.media_id || !scoreAiRoi) return;
    setScoreAiOcrBusy(true);
    setScoreAiError("");
    setScoreAiReading(null);
    try {
      const image = await videoRoiToJpegBase64(videoRef.current, scoreAiRoi);
      const data = await onProbeScore({
        image_jpeg_base64: image || undefined,
        media_id: activeSource.media_id,
        t_sec: Number(hit.t_sec) || 0,
        roi: scoreAiRoi,
      });
      const reading = data?.reading || data;
      setScoreAiReading(reading || null);
      if (!reading?.visible) {
        setScoreAiError(t("cmScoreUnread", "No score could be read in that box."));
      }
    } catch (err) {
      setScoreAiError(err?.response?.data?.message || err.message || String(err));
    } finally {
      setScoreAiOcrBusy(false);
    }
  }, [
    activeSource?.media_id,
    onProbeScore,
    scoreAiCandidates,
    scoreAiIndex,
    scoreAiRoi,
    t,
  ]);

  const nextScoreAiCandidate = useCallback(() => {
    setScoreAiReading(null);
    setScoreAiError("");
    const next = scoreAiIndex + 1;
    if (next >= scoreAiCandidates.length) {
      setScoreAiReview(false);
      setScoreAiError(
        t("cmScoreAiDone", "No more flagged frames in this segment.")
      );
      return;
    }
    setScoreAiIndex(next);
    seekClipSec(scoreAiCandidates[next].t_sec, { play: false });
  }, [scoreAiCandidates, scoreAiIndex, seekClipSec, t]);

  const pushScoreAiLiveLog = useCallback((msg, level = "info") => {
    const line = String(msg || "");
    // eslint-disable-next-line no-console
    console.debug(`[scorecam replay] ${line}`);
    setScoreAiLiveLogs((prev) =>
      [...prev, { t: Date.now(), level, msg: line }].slice(-80)
    );
  }, []);

  const stopScoreAiLive = useCallback(() => {
    scoreAiLiveStopRef.current = true;
    scoreAiLiveSimRef.current = false;
    if (scoreAiLiveTimerRef.current) {
      window.clearInterval(scoreAiLiveTimerRef.current);
      scoreAiLiveTimerRef.current = null;
    }
    setScoreAiLiveRunning(false);
    setScoreAiLiveSim(false);
    // Drop the ephemeral live plate so the board rebuilds from saved stamps.
    setScoreAiLiveBoard(null);
    scoreAiLiveGateRef.current = null;
    scoreAiLiveSuspectRef.current = null;
    videoRef.current?.pause?.();
    const local = Number(videoRef.current?.currentTime);
    if (Number.isFinite(local)) {
      // Refresh playhead → stamped plate after the live overlay clears.
      window.requestAnimationFrame(() => syncPlayhead(local));
    }
    if (layout === "review") setPointsOpen(true);
  }, [layout, syncPlayhead]);
  stopScoreAiLiveRef.current = stopScoreAiLive;

  const resetScoreAiLiveScoreState = useCallback((setN = 1) => {
    scoreAiLiveLastKeyRef.current = "";
    scoreAiLiveLastScoreRef.current = null;
    scoreAiLiveSuspectRef.current = null;
    scoreAiLiveSetsRef.current = { home: 0, away: 0 };
    scoreAiLiveSetNumberRef.current = Math.max(1, Math.min(9, Number(setN) || 1));
    scoreAiLiveFlippedAfterSetRef.current = 0;
    setScoreAiLiveBoard(null);
  }, []);

  /**
   * Flip which team owns the physical left score card.
   * End-change after a set: mapping only (home/away labels stay).
   * Manual correction: also swap the live plate digits.
   */
  const flipScoreSide = useCallback(
    ({ swapBoard = false, reason = "flip" } = {}) => {
      const next = !scoreSideFlipRef.current;
      scoreSideFlipRef.current = next;
      setScoreSideFlip(next);
      scoreAiLiveSuspectRef.current = null;
      scoreAiLiveGateRef.current?.reset?.();
      if (swapBoard) {
        const last = scoreAiLiveLastScoreRef.current;
        if (last) {
          scoreAiLiveLastScoreRef.current = {
            home: last.away,
            away: last.home,
          };
        }
        const sets = scoreAiLiveSetsRef.current;
        scoreAiLiveSetsRef.current = {
          home: sets.away,
          away: sets.home,
        };
        setScoreAiLiveBoard((prev) => {
          if (!prev) return prev;
          return {
            home: prev.away,
            away: prev.home,
            gamesHome: prev.gamesAway,
            gamesAway: prev.gamesHome,
            setNumber: prev.setNumber,
          };
        });
        const swapped = scoreAiLiveLastScoreRef.current;
        if (swapped) {
          setScoreHome(String(swapped.home));
          setScoreAway(String(swapped.away));
          const s = scoreAiLiveSetsRef.current;
          const stampSet = Math.max(
            1,
            Number(scoreAiLiveSetNumberRef.current) || 1
          );
          scoreAiLiveLastKeyRef.current = `${stampSet}|${s.home}–${s.away}|${swapped.home}-${swapped.away}`;
        } else {
          scoreAiLiveLastKeyRef.current = "";
        }
      }
      const name =
        (next ? awayLabel : homeLabel) || (next ? "away" : "home");
      const sets = scoreAiLiveSetsRef.current;
      pushScoreAiLiveLog(
        swapBoard
          ? `Left score → ${name} · board swapped · sets ${sets.home}–${sets.away}`
          : `${reason} · left score → ${name} · sets ${sets.home}–${sets.away}`,
        "ok"
      );
    },
    [awayLabel, homeLabel, pushScoreAiLiveLog]
  );

  const startScoreAiLive = useCallback(async () => {
    if (!canModerate) return;
    if (!onProbeScore || !activeSource?.media_id || !scoreAiRoi) {
      setScoreAiError(
        t("cmScoreAiLiveNeedRoi", "Draw the umpire score box before playing as live.")
      );
      stopScoreAiLive();
      return;
    }
    const video = videoRef.current;
    if (!video) {
      stopScoreAiLive();
      return;
    }
    scoreAiLiveStopRef.current = false;
    scoreAiLiveSimRef.current = true;
    resetScoreAiLiveScoreState(setNumber);
    setScoreAiLiveLogs([]);
    setScoreAiError("");
    setScoreAiReview(false);
    const gate = createBoardChangeGate();
    scoreAiLiveGateRef.current = gate;
    const hasIn = scoreAiInSec != null && Number.isFinite(Number(scoreAiInSec));
    const hasOut = scoreAiOutSec != null && Number.isFinite(Number(scoreAiOutSec));
    const duration = Number(video.duration);
    const tIn = hasIn ? Number(scoreAiInSec) : Number(video.currentTime) || 0;
    const tOut = hasOut
      ? Number(scoreAiOutSec)
      : Number.isFinite(duration) && duration > 0.2
        ? duration
        : null;
    const start = Math.max(0, tOut == null ? tIn : Math.min(tIn, tOut));
    const end = tOut == null ? null : Math.max(start, Math.max(tIn, tOut));
    const fileMs = (sec) => {
      const local = Math.max(0, Number(sec) || 0);
      const marked =
        Array.isArray(activeSource?.point_marks) && activeSource.point_marks.length;
      const pointClip = sources.length > 1 && activeSource?.point_id;
      if (marked || pointClip) return Math.round(local * 1000);
      const tapeStart = sourceStarts[srcIndex] || 0;
      const tapeMs = Math.round((tapeStart + local) * 1000);
      const captureOrigin = Number(activeSource?.capture_t_ms);
      if (Number.isFinite(captureOrigin)) return Math.round(captureOrigin + local * 1000);
      if (sources.length > 1) return tapeMs;
      return Math.round(playbackAtomicMs(activeSource || clip, local));
    };
    seekClipSec(start, { play: false });
    try {
      await video.play();
    } catch (err) {
      pushScoreAiLiveLog(`Play blocked · ${err?.message || "allow playback"}`, "err");
      stopScoreAiLive();
      return;
    }
    if (scoreAiLiveStopRef.current) return;
    setScoreAiLiveRunning(true);
    const leftName =
      (scoreSideFlipRef.current ? awayLabel : homeLabel) ||
      (scoreSideFlipRef.current ? "away" : "home");
    pushScoreAiLiveLog(
      `Live scorecam · ${formatTms(Math.round(start * 1000))} → ${
        end == null ? "end" : formatTms(Math.round(end * 1000))
      } · left score → ${leftName} · poll ${UMPIRE_SCORE_POLL_MS}ms`,
      "ok"
    );
    let busy = false;
    let pausedLogged = false;
    let serverLogged = false;
    let serverErrAt = 0;
    let noFrameAt = 0;
    let quietAt = 0;
    let motionAt = 0;
    const fingerprintLen = FINGERPRINT_W * FINGERPRINT_H;
    const pixelLine = (decision) => {
      const commit = summarizeCompare(decision?.vsCommit);
      const prev = summarizeCompare(decision?.vsPrev);
      return [commit ? `vs commit ${commit}` : "", prev ? `vs prev ${prev}` : ""]
        .filter(Boolean)
        .join(" · ");
    };
    const planStamp = (mapped, prev) =>
      planLiveScoreStamp(
        mapped,
        prev,
        {
          home: scoreAiLiveSetsRef.current.home,
          away: scoreAiLiveSetsRef.current.away,
          setNumber: scoreAiLiveSetNumberRef.current,
        },
        { home: homeLabel, away: awayLabel }
      );
    const stampMapped = async (
      mapped,
      sampleSec,
      at,
      decision,
      plan,
      note
    ) => {
      const tMs = fileMs(sampleSec);
      const body = {
        t_ms: tMs,
        set_number: plan.stampSet,
        home_score: mapped.home,
        away_score: mapped.away,
        sets_home: plan.setsHome,
        sets_away: plan.setsAway,
      };
      if (!onStampReviewPoint) {
        if (decision) gate.rejectRead(decision.read_seq);
        pushScoreAiLiveLog(
          `Score ${mapped.home}–${mapped.away} · label failed · ${at}`,
          "err"
        );
        return;
      }
      const stamped = await onStampReviewPoint(body);
      if (!stamped?.point && !stamped?.snapshot?.match && !stamped?.match) {
        if (decision) gate.rejectRead(decision.read_seq);
        pushScoreAiLiveLog(
          `Score ${mapped.home}–${mapped.away} · label not saved · ${at}`,
          "err"
        );
        return;
      }
      scoreAiLiveLastKeyRef.current = plan.scoreKey;
      scoreAiLiveLastScoreRef.current = { home: mapped.home, away: mapped.away };
      scoreAiLiveSetsRef.current = { home: plan.setsHome, away: plan.setsAway };
      scoreAiLiveSuspectRef.current = null;
      scoreAiLiveSetNumberRef.current = plan.stampSet;
      setSetNumber(plan.stampSet);
      setScoreHome(String(mapped.home));
      setScoreAway(String(mapped.away));
      setScoreAiLiveBoard({
        home: mapped.home,
        away: mapped.away,
        gamesHome: plan.setsHome,
        gamesAway: plan.setsAway,
        setNumber: plan.stampSet,
      });
      pushScoreAiLiveLog(note, "ok");
      // Players change ends after every set — flip left-card mapping once.
      if (plan.finished) {
        flipScoreSide({ swapBoard: false, reason: "End change" });
        scoreAiLiveFlippedAfterSetRef.current = plan.stampSet;
      } else if (
        plan.restart &&
        scoreAiLiveFlippedAfterSetRef.current !== plan.stampSet - 1
      ) {
        flipScoreSide({ swapBoard: false, reason: "End change" });
        scoreAiLiveFlippedAfterSetRef.current = plan.stampSet - 1;
      }
    };
    const settleMapped = async (mapped, sampleSec, at, decision, { confirm = false } = {}) => {
      const prev = scoreAiLiveLastScoreRef.current;
      const plan = planStamp(mapped, prev);
      // Label opening 0–0 and post-set 0–0. Duplicate keys are skipped below.
      if (scoreAiLiveLastKeyRef.current === plan.scoreKey) {
        scoreAiLiveSuspectRef.current = null;
        pushScoreAiLiveLog(
          `Same score ${plan.scoreLabel} · no new label · ${at}`,
          "info"
        );
        return;
      }
      const openingBlank =
        mapped.home === 0 && mapped.away === 0 && !prev;
      if (
        !prev ||
        openingBlank ||
        isConsecutivePoint(prev, mapped) ||
        plan.restart
      ) {
        await stampMapped(
          mapped,
          sampleSec,
          at,
          decision,
          plan,
          `Labeled ${plan.scoreLabel}${plan.setNote} · ${at}`
        );
        return;
      }
      if (confirm && scoreAiLiveSuspectRef.current?.key === plan.scoreKey) {
        await stampMapped(
          mapped,
          sampleSec,
          at,
          decision,
          plan,
          `Confirmed ${plan.scoreLabel}${plan.setNote} · still the same · ${at}`
        );
        return;
      }
      const pending = scoreAiLiveSuspectRef.current;
      if (!pending || pending.key !== plan.scoreKey) {
        scoreAiLiveSuspectRef.current = {
          key: plan.scoreKey,
          at: Date.now(),
          home: mapped.home,
          away: mapped.away,
        };
        pushScoreAiLiveLog(
          `Not the next point ${prev.home}–${prev.away} → ${mapped.home}–${mapped.away} · recheck in 3s · ${at}`,
          "warn"
        );
      }
    };
    const readMapped = async (sampleSec) => {
      const blob = await roiToJpegBlob(videoRef.current, scoreAiRoi);
      const image = blob ? await blobToJpegBase64(blob) : "";
      const data = await onProbeScore({
        image_jpeg_base64: image || undefined,
        media_id: activeSource.media_id,
        t_sec: sampleSec,
        roi: scoreAiRoi,
      });
      const reading = data?.reading || data;
      const at = formatTms(Math.round(sampleSec * 1000));
      if (!reading?.visible) return { at, mapped: null };
      return {
        at,
        mapped: boardAsHomeAway(reading, null, {
          swapSides: scoreSideFlipRef.current,
        }),
      };
    };
    const tick = async () => {
      if (scoreAiLiveStopRef.current || !scoreAiLiveSimRef.current) return;
      const v = videoRef.current;
      if (!v) return;
      const nowSec = Number(v.currentTime) || 0;
      const clock = formatTms(Math.round(nowSec * 1000));
      if (end != null && nowSec >= end - 0.05) {
        pushScoreAiLiveLog(`Segment ended · ${clock}`, "ok");
        stopScoreAiLive();
        return;
      }
      if (v.ended) {
        pushScoreAiLiveLog(`Playback ended · ${clock}`, "ok");
        stopScoreAiLive();
        return;
      }
      if (v.paused) {
        if (!pausedLogged) {
          pausedLogged = true;
          pushScoreAiLiveLog(`Paused · ${clock}`, "info");
        }
        return;
      }
      pausedLogged = false;
      if (busy) return;
      const pending = scoreAiLiveSuspectRef.current;
      if (pending && Date.now() - pending.at >= SCORE_RECHECK_MS) {
        busy = true;
        const sampleSec = Number(v.currentTime) || 0;
        try {
          const at = formatTms(Math.round(sampleSec * 1000));
          pushScoreAiLiveLog(
            `Recheck ${pending.home}–${pending.away} · ${at}`,
            "info"
          );
          const got = await readMapped(sampleSec);
          if (scoreAiLiveStopRef.current || !scoreAiLiveSimRef.current) return;
          if (!got.mapped) {
            scoreAiLiveSuspectRef.current = { ...pending, at: Date.now() };
            pushScoreAiLiveLog(`Recheck missed · board not visible · ${got.at}`, "warn");
            return;
          }
          await settleMapped(got.mapped, sampleSec, got.at, null, { confirm: true });
        } catch (err) {
          if (scoreAiLiveSuspectRef.current?.key === pending.key) {
            scoreAiLiveSuspectRef.current = { ...pending, at: Date.now() };
          }
          pushScoreAiLiveLog(
            `Recheck failed · ${err?.response?.data?.message || err?.message || "error"} · ${clock}`,
            "err"
          );
        } finally {
          busy = false;
        }
        return;
      }
      const sampleSec = Number(v.currentTime) || 0;
      const readDetail = {};
      let gray = roiFingerprint(v, scoreAiRoi, readDetail);
      /** Canvas can't read this clip — use the same server frame as Check board. */
      let preferServerJpeg =
        readDetail.reason === "tainted" || readDetail.reason === "unreadable";
      if (!gray) {
        if (readDetail.reason === "no-frame") {
          if (Date.now() - noFrameAt > 2000) {
            noFrameAt = Date.now();
            pushScoreAiLiveLog(`No frame yet · ${clock}`, "warn");
          }
          return;
        }
        if (readDetail.reason === "bad-roi" || !onFingerprintFrame) {
          pushScoreAiLiveLog(`No pixels in the score box · ${clock}`, "warn");
          return;
        }
        busy = true;
        try {
          if (!serverLogged) {
            serverLogged = true;
            pushScoreAiLiveLog(
              `Reading the score box from the clip · ${clock}`,
              "info"
            );
          }
          const data = await onFingerprintFrame({
            media_id: activeSource.media_id,
            t_sec: sampleSec,
            roi: scoreAiRoi,
          });
          if (scoreAiLiveStopRef.current || !scoreAiLiveSimRef.current) return;
          const bytes = data?.gray;
          if (!bytes || bytes.length < fingerprintLen) {
            pushScoreAiLiveLog(`No pixels in the score box · ${clock}`, "warn");
            return;
          }
          gray = Uint8Array.from(bytes.slice(0, fingerprintLen));
          preferServerJpeg = true;
        } catch (err) {
          if (Date.now() - serverErrAt > 2000) {
            serverErrAt = Date.now();
            pushScoreAiLiveLog(
              `Score box sample failed · ${
                err?.response?.data?.message || err?.message || "error"
              } · ${clock}`,
              "err"
            );
          }
          return;
        } finally {
          busy = false;
        }
      }
      const decision = gate.observe(gray);
      const pixels = pixelLine(decision);
      const reason = decision.reason || "gate";
      if (!decision.read) {
        const quiet = reason === "same_board" || reason === "empty";
        const now = Date.now();
        if (quiet && now - quietAt < 4000) return;
        if (quiet) quietAt = now;
        if (reason === "in_motion") {
          if (Date.now() - motionAt < 2500) return;
          motionAt = Date.now();
          pushScoreAiLiveLog(`Occlusion · ${pixels || "pixels moving"} · ${clock}`, "warn");
        } else if (reason === "same_board") {
          pushScoreAiLiveLog(`No change · ${pixels || "pixels steady"} · ${clock}`, "info");
        } else {
          pushScoreAiLiveLog(`Gate ${reason} · ${pixels} · ${clock}`.trim(), "info");
        }
        return;
      }
      busy = true;
      pushScoreAiLiveLog(
        `Trigger · ${reason} · ${pixels || "pixels changed"} · ${clock}`,
        "ok"
      );
      try {
        const probeAt = async (useClientJpeg) => {
          let image;
          if (useClientJpeg) {
            const blob = await roiToJpegBlob(v, scoreAiRoi);
            image = blob ? await blobToJpegBase64(blob) : "";
          }
          return onProbeScore({
            image_jpeg_base64: image || undefined,
            media_id: activeSource.media_id,
            t_sec: sampleSec,
            roi: scoreAiRoi,
          });
        };
        // Prefer the server crop whenever the browser cannot read pixels —
        // same path as Check board score.
        let data = await probeAt(!preferServerJpeg);
        if (scoreAiLiveStopRef.current) return;
        let reading = data?.reading || data;
        if (!reading?.visible && activeSource.media_id) {
          data = await probeAt(false);
          if (scoreAiLiveStopRef.current) return;
          reading = data?.reading || data;
        }
        const at = formatTms(Math.round(sampleSec * 1000));
        if (!reading?.visible) {
          // Keep the gate committed so we do not re-OCR the same frame in a loop.
          pushScoreAiLiveLog(`Occlusion · board not visible · ${at}`, "warn");
          return;
        }
        const mapped = boardAsHomeAway(reading, null, {
          swapSides: scoreSideFlipRef.current,
        });
        if (!mapped) {
          pushScoreAiLiveLog(`No score digits · ${at}`, "warn");
          return;
        }
        await settleMapped(mapped, sampleSec, at, decision);
      } catch (err) {
        gate.rejectRead(decision.read_seq);
        pushScoreAiLiveLog(`Read failed · ${err?.message || "error"} · ${clock}`, "err");
      } finally {
        busy = false;
      }
    };
    if (scoreAiLiveTimerRef.current) {
      window.clearInterval(scoreAiLiveTimerRef.current);
    }
    scoreAiLiveTimerRef.current = window.setInterval(() => {
      void tick();
    }, UMPIRE_SCORE_POLL_MS);
    void tick();
  }, [
    activeSource,
    awayLabel,
    canModerate,
    clip,
    flipScoreSide,
    homeLabel,
    onProbeScore,
    onFingerprintFrame,
    onStampReviewPoint,
    pushScoreAiLiveLog,
    resetScoreAiLiveScoreState,
    scoreAiInSec,
    scoreAiOutSec,
    scoreAiRoi,
    seekClipSec,
    setNumber,
    sourceStarts,
    sources.length,
    srcIndex,
    stopScoreAiLive,
    t,
  ]);

  const playerUrl = activeSource?.file_url ? mediaSrc(activeSource.file_url) : "";

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const sync = () => setWideReview(layout === "review" && mq.matches);
    sync();
    mq.addEventListener?.("change", sync);
    return () => mq.removeEventListener?.("change", sync);
  }, [layout]);

  useEffect(() => {
    if (layout !== "review" || !wideReview) return;
    setLabelerOpen(true);
    setToolsOpen(true);
  }, [layout, wideReview]);

  useEffect(() => {
    if (!playerUrl) return undefined;
    // Wide match review stays in the staff layout beside the tape.
    // Phones still follow rotation; the timeline tab already skips desktop.
    const desktopReview =
      layout === "review" && window.matchMedia("(min-width: 1024px)").matches;
    if (desktopReview || (layout !== "review" && !isMobileDevice())) {
      return undefined;
    }
    const mq = window.matchMedia("(orientation: landscape)");
    const onChange = () => {
      const mode = stageModeRef.current;
      if (layout === "review") {
        if (mq.matches) {
          if (mode !== "landscape") enterStage("landscape");
        } else if (mode !== "embedded") {
          enterStage("embedded");
        }
        return;
      }
      if (mq.matches) {
        if (mode === "landscape") return;
        returnModeRef.current = mode;
        autoLandscapeRef.current = true;
        enterStage("landscape");
      } else if (autoLandscapeRef.current && mode === "landscape") {
        autoLandscapeRef.current = false;
        enterStage(returnModeRef.current);
      }
    };
    onChange();
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, [playerUrl, enterStage, layout]);
  const playerKey = activeSource?.media_id || "none";

  useEffect(() => {
    if (layout !== "review") return undefined;
    let stop = false;
    const read = () => {
      const video = videoRef.current;
      if (!video || stop) return false;
      const w = Number(video.videoWidth) || 0;
      const h = Number(video.videoHeight) || 0;
      if (w > 0 && h > 0) {
        setFrameRatio(w / h);
        return true;
      }
      return false;
    };
    const video = videoRef.current;
    if (read()) return () => {
      stop = true;
    };
    const onMeta = () => {
      read();
    };
    video?.addEventListener("loadedmetadata", onMeta);
    const id = window.setInterval(() => {
      if (read()) window.clearInterval(id);
    }, 250);
    return () => {
      stop = true;
      video?.removeEventListener("loadedmetadata", onMeta);
      window.clearInterval(id);
    };
  }, [layout, playerUrl, playerKey, stageMode]);

  useEffect(() => {
    if (layout !== "review") return undefined;
    const video = videoRef.current;
    if (!video || video.readyState < 1) return undefined;
    const time = Number(video.currentTime) || 0;
    const id = window.requestAnimationFrame(() => {
      try {
        if (Math.abs((Number(video.currentTime) || 0) - time) < 0.08) {
          video.currentTime = time;
        }
      } catch {
        /* ignore */
      }
    });
    return () => window.cancelAnimationFrame(id);
  }, [layout, stageMode]);

  const scoreAiMode = scoreAiOpen || scoreAiScanBusy || scoreAiReview;
  const taggedCount = setPoints.filter((p) => readPointOutcome(p)).length;
  const canJumpTo = (p) => Boolean(p && showPlayer && p.t_ms != null);
  const prevPoint = focusIdx > 0 ? setPoints[focusIdx - 1] : null;
  const nextPoint = focusIdx < 0 ? setPoints[0] || null : setPoints[focusIdx + 1] || null;
  const scoreDirty = focusPoint
    ? String(focusPoint.home_score ?? "") !== scoreHome ||
      String(focusPoint.away_score ?? "") !== scoreAway ||
      String(clipSetCount(focusPoint.sets_home) ?? 0) !==
        String(clipSetCount(scoreSetsHome) ?? 0) ||
      String(clipSetCount(focusPoint.sets_away) ?? 0) !==
        String(clipSetCount(scoreSetsAway) ?? 0)
    : scoreHome !== "" ||
      scoreAway !== "" ||
      String(clipSetCount(scoreSetsHome) ?? 0) !== "0" ||
      String(clipSetCount(scoreSetsAway) ?? 0) !== "0" ||
      Boolean(scoreAiLiveBoard);
  const deckClock = focusPoint
    ? formatTms(focusPoint.t_ms)
    : playerUrl
      ? formatTms(Math.round(playheadSec * 1000))
      : "";
  const deckOutcome = readPointOutcome(focusPoint ? focusPoint : { tags: pendingTags });
  const reviewLayout = layout === "review";
  const staffDesktop = reviewLayout && wideReview;
  const showLabeler = !reviewLayout || labelerOpen;
  const deckVisible =
    canTag &&
    (staffDesktop || !scoreAiMode) &&
    showLabeler &&
    (reviewLayout || !playerExpanded) &&
    Boolean(playerUrl || focusPoint);
  const landscapeStage = stageMode === "landscape";
  const plate = broadcastScoreAtPlayhead(points, playAtomicMs);
  const labelingPlate =
    scoreLabelActive && Number(labelJob?.points_added) > 0
      ? broadcastScoreAtPlayhead(points, Number.MAX_SAFE_INTEGER)
      : null;
  const detectedSides = boardNow
    ? boardAsHomeAway(boardNow, null, { swapSides: scoreSideFlip })
    : null;
  const liveDetected =
    !detectedSides && isFreshBoardScore(rubber?.detected_score)
      ? boardAsHomeAway(rubber.detected_score, null, { swapSides: scoreSideFlip })
      : null;
  const shownPlate = (() => {
    if (scoreAiLiveBoard) {
      return {
        home: scoreAiLiveBoard.home,
        away: scoreAiLiveBoard.away,
        gamesHome: scoreAiLiveBoard.gamesHome,
        gamesAway: scoreAiLiveBoard.gamesAway,
        setNumber: scoreAiLiveBoard.setNumber,
      };
    }
    if (labelingPlate?.pointId) return labelingPlate;
    // Live scorecam / review stamps beat OCR score_timeline once saved.
    if (
      plate?.pointId &&
      plate.home != null &&
      plate.away != null
    ) {
      return plate;
    }
    if (detectedSides) {
      return {
        home: detectedSides.home,
        away: detectedSides.away,
        gamesHome: finiteBoardCount(detectedSides.gamesHome, plate.gamesHome),
        gamesAway: finiteBoardCount(detectedSides.gamesAway, plate.gamesAway),
        setNumber: plate.setNumber,
      };
    }
    if (liveDetected) {
      return {
        home: liveDetected.home,
        away: liveDetected.away,
        gamesHome: finiteBoardCount(liveDetected.gamesHome, plate.gamesHome),
        gamesAway: finiteBoardCount(liveDetected.gamesAway, plate.gamesAway),
        setNumber: plate.setNumber,
      };
    }
    return plate;
  })();
  const boardSetNumber = Math.max(
    1,
    Number(shownPlate.setNumber) || Number(setNumber) || 1
  );
  const boardHomeName =
    formatBoardPlayerNames(
      homePlayers,
      rubber?.home_names,
      rubber?.home_seats,
      t
    ) ||
    homeLabel ||
    t("cmHome", "Home");
  const boardAwayName =
    formatBoardPlayerNames(
      awayPlayers,
      rubber?.away_names,
      rubber?.away_seats,
      t
    ) ||
    awayLabel ||
    t("cmAway", "Away");

  useEffect(() => {
    if (scoreAiLiveRunning || scoreAiLiveBoard) return;
    const next = Math.max(1, Number(plate.setNumber) || 0);
    if (!next || next === setNumber) return;
    if (!sets.some((s) => s.setNumber === next)) return;
    setSetNumber(next);
  }, [
    plate.setNumber,
    scoreAiLiveBoard,
    scoreAiLiveRunning,
    setNumber,
    sets,
  ]);
  const teamNow = teamBoardScoreAtPlayhead({
    points: matchPoints || points,
    playheadMs: labelingPlate?.pointId
      ? Number.MAX_SAFE_INTEGER
      : playAtomicMs,
    setsToWin,
    rubbers,
    activeRubberId: rubber?.rubber_id,
    sheetHome: homeTeamScore,
    sheetAway: awayTeamScore,
  });
  const labelQueue = Array.isArray(labelJob?.queue) ? labelJob.queue : [];
  const labelPart = labelQueue.length
    ? Math.min(labelQueue.length, (Number(labelJob.queue_index) || 0) + 1)
    : 1;
  const taggerBlock =
    canTag &&
    (staffDesktop || !scoreAiMode) &&
    showLabeler &&
    (playerUrl || focusPoint) ? (
      <div className="cm-chrono__tagger cm-deck">
        <header className="cm-deck__head">
          <button
            type="button"
            className="cm-deck__nav"
            disabled={!canJumpTo(prevPoint)}
            onClick={() => seek(prevPoint)}
            aria-label={t("cmPrevPoint", "Previous point")}
            title={t("cmPrevPoint", "Previous point")}
          >
            <ChevronLeft size={18} strokeWidth={2.4} aria-hidden />
          </button>
          <div className="cm-deck__title">
            <strong>
              {focusPoint
                ? t("cmPointNofTotal", "Point {{n}} of {{total}}", {
                    n: focusIdx + 1,
                    total: setPoints.length,
                  })
                : t("cmTagMoment", "Tag this moment")}
            </strong>
            {deckClock ? <span className="cm-deck__clock">{deckClock}</span> : null}
          </div>
          <button
            type="button"
            className="cm-deck__nav"
            disabled={!canJumpTo(nextPoint)}
            onClick={() => seek(nextPoint)}
            aria-label={t("cmNextPoint", "Next point")}
            title={t("cmNextPoint", "Next point")}
          >
            <ChevronRight size={18} strokeWidth={2.4} aria-hidden />
          </button>
        </header>

        <div className="cm-deck__sets-edit" role="group" aria-label={t("cmSets", "Sets")}>
          <span className="cm-deck__sets-label">{t("cmSets", "Sets")}</span>
          <ScoreFlip
            side="home"
            name={homeLabel || t("cmHome", "Home")}
            value={scoreSetsHome}
            onChange={setScoreSetsHome}
            disabled={scoreBusy}
            max={7}
            compact
            t={t}
          />
          <span className="cm-deck__colon cm-deck__colon--sets" aria-hidden>
            –
          </span>
          <ScoreFlip
            side="away"
            name={awayLabel || t("cmAway", "Away")}
            value={scoreSetsAway}
            onChange={setScoreSetsAway}
            disabled={scoreBusy}
            max={7}
            compact
            t={t}
          />
        </div>
        <div className="cm-deck__score">
          <ScoreFlip
            side="home"
            name={homeLabel || t("cmHome", "Home")}
            value={scoreHome}
            onChange={setScoreHome}
            disabled={scoreBusy}
            t={t}
          />
          <span className="cm-deck__colon" aria-hidden>
            :
          </span>
          <ScoreFlip
            side="away"
            name={awayLabel || t("cmAway", "Away")}
            value={scoreAway}
            onChange={setScoreAway}
            disabled={scoreBusy}
            t={t}
          />
          <button
            type="button"
            className={`cm-deck__save${scoreDirty ? " is-ready" : ""}`}
            disabled={scoreBusy || !scoreDirty}
            onClick={() => void saveScore()}
            title={
              focusPoint
                ? t("cmSaveScore", "Save score")
                : t("cmStampScore", "Tag score here")
            }
          >
            {scoreBusy ? (
              <Loader2 size={16} className="cm-deck__spin" aria-hidden />
            ) : focusPoint ? (
              <Check size={16} strokeWidth={2.8} aria-hidden />
            ) : (
              <Tag size={15} strokeWidth={2.4} aria-hidden />
            )}
            <span>
              {focusPoint
                ? t("cmSaveScore", "Save score")
                : t("cmStampScore", "Tag score here")}
            </span>
          </button>
        </div>

        <p className="cm-deck__label">
          {t("cmHowPointEnded", "How did the point end?")}
        </p>
        <PointOutcomeBar
          t={t}
          className="cm-outcome--deck"
          activeOutcome={deckOutcome}
          disabled={tagBusy}
          onSelect={(outcomeId) => void handleOutcome(outcomeId)}
        />

        {playerUrl ? (
          <div
            className="cm-deck__react"
            role="group"
            aria-label={t("cmSendEmoji", "Send an emoji")}
          >
            {REVIEW_EMOJIS.map((emoji) => (
              <button
                key={emoji}
                type="button"
                className="cm-deck__emoji"
                onClick={() => void sendEmoji(emoji)}
              >
                {emoji}
              </button>
            ))}
          </div>
        ) : null}
        {reviewNote ? (
          <p className="cm-deck__note" role="status">
            {reviewNote}
          </p>
        ) : null}
      </div>
    ) : null;

  const scoreBoard =
    playerUrl && !roiMode ? (
      <LeagueScoreOverlay
        clubLogo={clubLogo}
        clubName={clubName}
        brandColor={brandColor}
        homeClub={homeClubName || homeLabel || t("cmHome", "Home")}
        awayClub={awayClubName || awayLabel || t("cmAway", "Away")}
        homeTeamScore={teamNow.home}
        awayTeamScore={teamNow.away}
        division={division}
        homeName={boardHomeName}
        awayName={boardAwayName}
        homeScore={shownPlate.home}
        awayScore={shownPlate.away}
        gamesHome={shownPlate.gamesHome}
        gamesAway={shownPlate.gamesAway}
        setLabel={t("cmSetN", "Set {{n}}", { n: boardSetNumber })}
        label={t(
          "cmLeagueScore",
          "{{division}} {{home}} {{homeMatches}} matches, {{away}} {{awayMatches}} matches. Set {{set}} · {{homeScore}}–{{awayScore}} · sets {{setsHome}}–{{setsAway}}.",
          {
            division: division || "",
            home: boardHomeName,
            away: boardAwayName,
            homeMatches: teamNow.home,
            awayMatches: teamNow.away,
            set: boardSetNumber,
            homeScore: scoreGlyph(shownPlate.home),
            awayScore: scoreGlyph(shownPlate.away),
            setsHome: scoreGlyph(shownPlate.gamesHome),
            setsAway: scoreGlyph(shownPlate.gamesAway),
          }
        )}
      />
    ) : null;
  const showReviewTools = reviewLayout && (canModerate || canLabelScore);

  return (
    <section
      ref={rootRef}
      className={`cm-chrono${
        playerExpanded
          ? ` is-stage-fs is-stage-${stageMode}`
          : ""
      }${
        !reviewLayout && stageMode === "landscape" && stagePortrait
          ? " is-portrait-stage"
          : ""
      }${canTag && !scoreAiMode ? " has-tagger" : ""}${deckVisible ? " has-deck" : ""}${
        reviewFs ? " is-review-fs" : ""
      }${scoreAiMode ? " is-score-ai" : ""}${reviewLayout ? " is-review-layout" : ""}${
        staffDesktop ? " is-staff-desktop" : ""
      }`}
      style={reviewLayout ? { "--cm-frame": String(frameRatio) } : undefined}
    >
      {showRubberHead ? (
        <header className="cm-chrono__rubber">
          <strong>{rubberCodeLabel(rubber?.code)}</strong>
          <span>
            {homeLabel} vs {awayLabel}
          </span>
        </header>
      ) : null}
      {tapes.length > 1 ? (
        <label className="cm-chrono__reassign">
          <span>{t("cmChooseTape", "Video on this match")}</span>
          <select
            className="cm-chrono__reassign-select"
            value={chosenTapeId}
            onChange={(event) => setTapeId(event.target.value)}
          >
            {tapes.map((tape) => {
              const file = String(tape.clips?.[0]?.file_url || "")
                .split("/")
                .pop();
              const when = tape.capturedAt
                ? new Date(tape.capturedAt).toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                  })
                : "";
              const extra =
                (tape.clips || []).length > 1
                  ? t("cmClipCount", "{{n}} clips", {
                      n: tape.clips.length,
                    })
                  : file;
              return (
                <option key={tape.id} value={tape.id}>
                  {[when, extra].filter(Boolean).join(" · ")}
                </option>
              );
            })}
          </select>
        </label>
      ) : null}

      <div className="cm-chrono__layout">
        <div className="cm-chrono__stage">
          {playerUrl ? (
            <div className="cm-chrono__player-shell">
              <ReviewVideoPlayer
                key={playerKey}
                url={playerUrl}
                mode={playerExpanded ? "fullscreen" : "embedded"}
                className="cm-chrono__eval-player"
                mediaElRef={bindRoiVideo}
                autoPlay={seekToken.play}
                controlsHideMs={2000}
                initialCurrentTime={
                  seekToken.sec != null && Number.isFinite(seekToken.sec)
                    ? seekToken.sec
                    : null
                }
                onMediaTimeUpdate={() => syncPlayhead()}
                onMediaEnded={() => {
                  if (srcIndex < sources.length - 1) {
                    setSrcIndex((i) => i + 1);
                    setSeekToken({ sec: 0, play: true });
                  }
                }}
                onMediaError={() =>
                  setMediaError(
                    t("cmMediaLoadError", "Could not load this video.")
                  )
                }
                twoFingerGesturesOnly={roiMode}
              />
              <div className="cm-chrono__hud" aria-hidden>
                <BoardScoreLabel
                  reading={boardNow}
                  t={t}
                  className="cm-chrono__board"
                />
                {activeSource?.file_url ? (
                  <p className="cm-chrono__tape-id">
                    {camPositionLabel(activeSource.angle_label, t)}
                    {" · "}
                    {String(activeSource.file_url).split("/").pop()}
                  </p>
                ) : null}
                {sources.length > 1 && !playerExpanded ? (
                  <p className="cm-chrono__parts">
                    {t("cmTapeParts", "Part {{n}}/{{total}}", {
                      n: srcIndex + 1,
                      total: sources.length,
                    })}
                  </p>
                ) : null}
              </div>
              {!reviewLayout || landscapeStage ? scoreBoard : null}
              {labelJob?.status === "failed" && labelJob.error && !scoreLabelActive ? (
                <div className="cm-chrono__scan-progress cm-chrono__label-wait" role="status">
                  <p className="cm-chrono__scan-progress-title">{labelJob.error}</p>
                </div>
              ) : null}
              {scoreLabelActive ? (
                <div
                  className="cm-chrono__scan-progress cm-chrono__label-wait"
                  role="status"
                  aria-live="polite"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={Math.round(labelProgressPct)}
                >
                  <p className="cm-chrono__scan-progress-title">
                    {t("cmScoreLabeling", "Reading the video…")}
                  </p>
                  <div className="cm-chrono__scan-progress-track" aria-hidden>
                    <span
                      className="cm-chrono__scan-progress-fill"
                      style={{ width: `${Math.max(4, Math.min(100, labelProgressPct))}%` }}
                    />
                  </div>
                  <p className="cm-chrono__scan-progress-meta">
                    {t(
                      "cmScoreLabelWait",
                      "{{n}} points so far{{part}}",
                      {
                        n: Number(labelJob?.points_added) || 0,
                        part:
                          labelQueue.length > 1
                            ? t("cmScoreLabelPart", " · part {{i}}/{{total}}", {
                                i: labelPart,
                                total: labelQueue.length,
                              })
                            : "",
                      }
                    )}
                  </p>
                </div>
              ) : null}
              {scoreAiScanBusy ? (
                <div
                  className="cm-chrono__scan-progress"
                  role="status"
                  aria-live="polite"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={Math.round(scoreAiScanPct)}
                >
                  <p className="cm-chrono__scan-progress-title">
                    {t("cmScoreAiScanning", "Scanning…")}
                  </p>
                  <div className="cm-chrono__scan-progress-track" aria-hidden>
                    <span
                      className="cm-chrono__scan-progress-fill"
                      style={{ width: `${Math.max(4, Math.min(100, scoreAiScanPct))}%` }}
                    />
                  </div>
                  <p className="cm-chrono__scan-progress-meta">
                    {t(
                      "cmScoreAiScanRemain",
                      "{{done}}/{{total}} frames · ~{{sec}}s left",
                      {
                        done: scoreAiScanDone,
                        total: Math.max(scoreAiScanDone, scoreAiScanTotal),
                        sec: Math.max(1, scoreAiScanEtaSec),
                      }
                    )}
                  </p>
                </div>
              ) : null}
              {mediaError ? <p className="cm-chrono__error">{mediaError}</p> : null}
              {liveReactions.length ? (
                <div className="cm-chrono__emoji-layer" aria-hidden>
                  {liveReactions.slice(0, 6).map((row) => (
                    <span key={row.reaction_id || `${row.t_ms}-${row.emoji}`} className="cm-chrono__emoji-pop">
                      {row.emoji}
                    </span>
                  ))}
                </div>
              ) : null}
              {probeReading?.visible || probeError || probeBusy ? (
                <p className="cm-chrono__probe" role="status">
                  <span>
                    {probeBusy
                      ? t("cmScoreReading", "Reading the score…")
                      : probeReading?.visible
                        ? t("cmScoreRead", "Score read: {{score}}", {
                            score: `${probeReading.board_left}–${probeReading.board_right}${
                              probeReading.games_left != null &&
                              probeReading.games_right != null
                                ? ` · ${probeReading.games_left}–${probeReading.games_right}`
                                : ""
                            }`,
                          })
                        : probeError}
                  </span>
                  {!probeBusy ? (
                    <button
                      type="button"
                      className="cm-chrono__probe-dismiss"
                      onClick={() => {
                        setProbeReading(null);
                        setProbeError("");
                      }}
                      aria-label={t("close", "Close")}
                    >
                      <X size={14} aria-hidden />
                    </button>
                  ) : null}
                </p>
              ) : null}
              {playerUrl && roiMode ? (
                <ScoreBoxLayer
                  videoEl={roiVideoEl}
                  fit="contain"
                  drawing={roiMode}
                  roi={scoreAiRoi}
                  pauseOnDraw
                  onCommit={(box) => void finishRoi(box)}
                />
              ) : null}
              {roiMode ? (
                <div className="cm-chrono__roi">
                  <p className="cm-chrono__roi-hint">
                    {roiForScan
                      ? t(
                          "cmDrawScoreBoxScan",
                          "One finger draws the box. Two fingers zoom and pan."
                        )
                      : t(
                          "cmDrawScoreBox",
                          "One finger draws the box. Two fingers zoom and pan."
                        )}
                  </p>
                  <button
                    type="button"
                    className="cm-chrono__roi-cancel"
                    onPointerDown={(event) => event.stopPropagation()}
                    onClick={(event) => {
                      event.stopPropagation();
                      setRoiMode(false);
                      setRoiForScan(false);
                    }}
                  >
                    {t("cancel", "Cancel")}
                  </button>
                </div>
              ) : null}
              {scoreAiReview && scoreAiRoi && !roiMode ? (
                <div className="cm-chrono__score-ai-review" aria-live="polite">
                  <div className="cm-chrono__score-ai-dock">
                    <p className="cm-chrono__score-ai-dock-title">
                      {t("cmScoreAiCandidate", "Flagged frame {{n}}/{{total}}", {
                        n: scoreAiIndex + 1,
                        total: scoreAiCandidates.length,
                      })}
                      {scoreAiSkipped > 0
                        ? ` · ${t("cmScoreAiSkippedShort", "{{n}} occlusion skipped", {
                            n: scoreAiSkipped,
                          })}`
                        : ""}
                    </p>
                    <p className="cm-chrono__score-ai-dock-meta">
                      {formatTms(
                        Math.round(
                          (Number(scoreAiCandidates[scoreAiIndex]?.t_sec) || 0) *
                            1000
                        )
                      )}{" "}
                      · {scoreAiCandidates[scoreAiIndex]?.reason || "board_changed"}
                      {scoreAiSkippedDead > 0
                        ? ` · ${t("cmScoreAiDeadSkipped", "{{n}} dead-time skipped", {
                            n: scoreAiSkippedDead,
                          })}`
                        : ""}
                    </p>
                    {scoreAiTiming ? (
                      <p className="cm-chrono__score-ai-dock-meta">
                        {t(
                          "cmScoreAiTiming",
                          "Scan {{total}}ms · dl {{dl}} · extract {{ex}} · fp {{fp}} · detect {{det}}{{mode}}",
                          {
                            total: scoreAiTiming.total_ms ?? "—",
                            dl: scoreAiTiming.download_ms ?? "—",
                            ex: scoreAiTiming.extract_ms ?? "—",
                            fp: scoreAiTiming.fingerprint_ms ?? "—",
                            det: scoreAiTiming.detect_ms ?? "—",
                            mode: scoreAiTiming.cache_hit
                              ? " · cache"
                              : scoreAiTiming.input_mode === "https"
                                ? " · https"
                                : "",
                          }
                        )}
                      </p>
                    ) : null}
                    {scoreAiReading?.visible ? (
                      <p className="cm-chrono__score-ai-dock-score">
                        {t("cmScoreRead", "Score read: {{score}}", {
                          score: `${scoreAiReading.board_left}–${scoreAiReading.board_right}${
                            scoreAiReading.games_left != null &&
                            scoreAiReading.games_right != null
                              ? ` · ${scoreAiReading.games_left}–${scoreAiReading.games_right}`
                              : ""
                          }`,
                        })}
                      </p>
                    ) : null}
                    {scoreAiError ? (
                      <p className="cm-chrono__score-ai-err">{scoreAiError}</p>
                    ) : null}
                    <div className="cm-chrono__score-ai-dock-actions">
                      <button
                        type="button"
                        className="cm-btn"
                        disabled={scoreAiOcrBusy}
                        onClick={() => void confirmScoreAiCandidate()}
                      >
                        <CheckCircle2 size={14} aria-hidden />
                        {scoreAiOcrBusy
                          ? t("cmScoreReading", "Reading the score…")
                          : t("cmScoreAiConfirm", "Confirm · read score")}
                      </button>
                      <button
                        type="button"
                        className="cm-btn cm-btn--ghost"
                        disabled={scoreAiOcrBusy}
                        onClick={nextScoreAiCandidate}
                      >
                        {t("cmScoreAiNext", "Next frame")}
                      </button>
                      <button
                        type="button"
                        className="cm-btn cm-btn--ghost"
                        disabled={scoreAiOcrBusy}
                        onClick={closeScoreAi}
                      >
                        {t("cmScoreAiStop", "Stop")}
                      </button>
                    </div>
                  </div>
                </div>
              ) : null}
              <div
                className="cm-stagectl"
                role="toolbar"
                aria-label={t("cmViewMode", "Video view")}
              >
                {!reviewLayout && stageMode !== "portrait" ? (
                  <button
                    type="button"
                    className="cm-stagectl__btn"
                    onClick={() => chooseStage("portrait")}
                    aria-label={t("cmFullscreenPortrait", "Full screen, portrait")}
                    title={t("cmFullscreenPortrait", "Full screen, portrait")}
                  >
                    <RectangleVertical size={15} strokeWidth={2.3} aria-hidden />
                  </button>
                ) : null}
                {stageMode !== "landscape" ? (
                  <button
                    type="button"
                    className="cm-stagectl__btn"
                    onClick={() => chooseStage("landscape")}
                    aria-label={
                      reviewLayout
                        ? t("cmEnterFullscreen", "Full screen")
                        : t("cmFullscreenLandscape", "Full screen, landscape")
                    }
                    title={
                      reviewLayout
                        ? t("cmEnterFullscreen", "Full screen")
                        : t("cmFullscreenLandscape", "Full screen, landscape")
                    }
                  >
                    <RectangleHorizontal size={15} strokeWidth={2.3} aria-hidden />
                  </button>
                ) : null}
                {playerExpanded ? (
                  <button
                    type="button"
                    className="cm-stagectl__btn cm-stagectl__btn--exit"
                    onClick={() => chooseStage("embedded")}
                    aria-label={t("cmExitFullscreen", "Exit full screen")}
                    title={t("cmExitFullscreen", "Exit full screen")}
                  >
                    <Minimize2 size={15} strokeWidth={2.3} aria-hidden />
                  </button>
                ) : null}
              </div>
            </div>
          ) : activePhoto?.file_url ? (
            <div className="cm-chrono__player cm-chrono__player--photo">
              <img src={mediaSrc(activePhoto.file_url)} alt="" />
            </div>
          ) : (
            <div className="cm-chrono__empty-stage">
              <Clapperboard size={28} aria-hidden />
              <p>{t("cmNoClipForSet", "No video clip for this set yet.")}</p>
            </div>
          )}

          {reviewLayout && !landscapeStage && scoreBoard ? (
            <div className="cm-review__board-dock">{scoreBoard}</div>
          ) : null}

          {reviewLayout && (setPoints.length || canTag || showReviewTools) ? (
            <div
              className="cm-reviewbar"
              role="toolbar"
              aria-label={t("cmMatchReview", "Match review")}
            >
              {(points?.length || setPoints.length) ? (
                <button
                  type="button"
                  className={`cm-reviewbar__btn${pointsOpen ? " is-on" : ""}`}
                  aria-expanded={pointsOpen}
                  onClick={() => setPointsOpen((open) => !open)}
                >
                  <List size={15} strokeWidth={2.3} aria-hidden />
                  {pointsOpen
                    ? t("cmHidePoints", "Hide points")
                    : t("cmReviewPoints", "Points")}
                  <span className="cm-reviewbar__count">
                    {points?.length || setPoints.length}
                  </span>
                </button>
              ) : null}
              {canTag ? (
                <button
                  type="button"
                  className={`cm-reviewbar__btn${labelerOpen ? " is-on" : ""}`}
                  aria-expanded={labelerOpen}
                  onClick={() => setLabelerOpen((open) => !open)}
                >
                  <Tag size={15} strokeWidth={2.3} aria-hidden />
                  {labelerOpen
                    ? t("cmHideLabeler", "Hide labeler")
                    : t("cmShowLabeler", "Label point")}
                </button>
              ) : null}
              <button
                type="button"
                className={`cm-reviewbar__btn cm-reviewbar__btn--fs${
                  landscapeStage ? " is-on" : ""
                }`}
                aria-pressed={landscapeStage}
                onClick={() =>
                  chooseStage(landscapeStage ? "embedded" : "landscape")
                }
              >
                {landscapeStage ? (
                  <Minimize2 size={15} strokeWidth={2.3} aria-hidden />
                ) : (
                  <Maximize2 size={15} strokeWidth={2.3} aria-hidden />
                )}
                {landscapeStage
                  ? t("cmExitFullscreen", "Exit full screen")
                  : t("cmEnterFullscreen", "Full screen")}
              </button>
              {showReviewTools ? (
                <button
                  type="button"
                  className={`cm-reviewbar__btn cm-reviewbar__btn--menu${
                    toolsOpen ? " is-on" : ""
                  }`}
                  aria-expanded={toolsOpen}
                  aria-label={t("cmReviewOptions", "Options")}
                  title={t("cmReviewOptions", "Options")}
                  onClick={() => setToolsOpen((open) => !open)}
                >
                  <MoreVertical size={16} strokeWidth={2.3} aria-hidden />
                </button>
              ) : null}
            </div>
          ) : null}

          {reviewLayout && pointsOpen ? (
            <div className="cm-review__sheet">
              {sets.length > 1 ? (
                <div
                  className="cm-review__sheet-sets"
                  role="tablist"
                  aria-label={t("cmSets", "Sets")}
                >
                  {sets.map((s) => (
                    <button
                      key={s.setNumber}
                      type="button"
                      role="tab"
                      aria-selected={s.setNumber === setNumber}
                      className={s.setNumber === setNumber ? "is-on" : undefined}
                      onClick={() => setSetNumber(s.setNumber)}
                    >
                      {t("cmSetN", "Set {{n}}", { n: s.setNumber })}
                    </button>
                  ))}
                </div>
              ) : null}
              {setPoints.length ? (
                <ol className="cm-review__points" ref={cueListRef}>
                  {setPoints.map((p, idx) => {
                    const isPlaying = idx === cueIndex;
                    const isSelected = idx === focusIdx;
                    const scored =
                      Number.isFinite(Number(p.home_score)) &&
                      Number.isFinite(Number(p.away_score));
                    const outcome = readPointOutcome(p);
                    const meta = outcome ? outcomeMeta(outcome) : null;
                    const clock = formatTms(p.t_ms);
                    return (
                      <li key={p.point_id} data-cue={idx}>
                        <button
                          type="button"
                          className={[
                            "cm-review__pt",
                            isPlaying ? "is-active" : "",
                            isSelected ? "is-selected" : "",
                          ]
                            .filter(Boolean)
                            .join(" ")}
                          onClick={() => seek(p)}
                          disabled={!showPlayer || p.t_ms == null}
                        >
                          <span className="cm-review__pt-n">{idx + 1}</span>
                          <span className="cm-review__pt-score">
                            {pointSetPair(p) ? (
                              <span className="cm-review__pt-sets">{pointSetPair(p)}</span>
                            ) : null}
                            <span>{scored ? `${p.home_score}–${p.away_score}` : "–"}</span>
                          </span>
                          <span className="cm-review__pt-meta">
                            {clock || t("cmUntimed", "No time")}
                            {meta ? ` · ${t(meta.titleKey, meta.title)}` : ""}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ol>
              ) : (
                <p className="cm-review__sheet-empty">
                  {t("cmNoTimelineYet", "No points or media for this match yet.")}
                </p>
              )}
            </div>
          ) : null}

          {reviewLayout ? null : (
          <div className="cm-film">
              <div className="cm-film__head">
                <div className="cm-film__sets" role="tablist" aria-label={t("cmSets", "Sets")}>
                  {sets.map((s) => (
                    <button
                      key={s.setNumber}
                      type="button"
                      role="tab"
                      aria-selected={s.setNumber === setNumber}
                      className={
                        s.setNumber === setNumber ? "cm-film__set is-on" : "cm-film__set"
                      }
                      onClick={() => setSetNumber(s.setNumber)}
                    >
                      {t("cmSetN", "Set {{n}}", { n: s.setNumber })}
                    </button>
                  ))}
                </div>
                {canTag && setPoints.length ? (
                  <span
                    className={`cm-film__progress${
                      taggedCount === setPoints.length ? " is-done" : ""
                    }`}
                  >
                    {t("cmTaggedCount", "{{n}}/{{total}} tagged", {
                      n: taggedCount,
                      total: setPoints.length,
                    })}
                  </span>
                ) : null}
              </div>
              {setPoints.length && !landscapeStage ? (
                <div
                  ref={pillStripRef}
                  className="cm-film__points"
                  role="listbox"
                  aria-label={t("cmPointCues", "Points on this tape")}
                >
                  {setPoints.map((p, idx) => {
                    const isPlaying = idx === cueIndex;
                    const isSelected = idx === focusIdx;
                    const scored =
                      Number.isFinite(Number(p.home_score)) &&
                      Number.isFinite(Number(p.away_score));
                    const outcome = readPointOutcome(p);
                    return (
                      <button
                        key={p.point_id}
                        type="button"
                        role="option"
                        data-pill={idx}
                        data-outcome={outcome || "none"}
                        title={
                          outcome
                            ? t(outcomeMeta(outcome)?.titleKey, outcomeMeta(outcome)?.title)
                            : undefined
                        }
                        aria-selected={isSelected}
                        aria-current={isPlaying ? "true" : undefined}
                        className={[
                          "cm-film__point",
                          isPlaying ? "is-active" : "",
                          isSelected ? "is-selected" : "",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                        onClick={() => seek(p)}
                        disabled={!showPlayer || p.t_ms == null}
                      >
                        <span className="cm-film__point-n">{idx + 1}</span>
                        {scored ? (
                          <span className="cm-film__point-score">
                            {pointSetPair(p) ? (
                              <span className="cm-review__pt-sets">{pointSetPair(p)}</span>
                            ) : null}
                            {p.home_score}–{p.away_score}
                          </span>
                        ) : (
                          <span className="cm-film__point-score is-empty">
                            {t("cmScorePending", "—")}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              ) : null}
              {focusPoint &&
              ((readPointOutcome(focusPoint) && !deckVisible) || focusLabels.length) ? (
                <div className="cm-film__labels" aria-label={t("cmPointLabels", "Point labels")}>
                  {readPointOutcome(focusPoint) && !deckVisible ? (
                    <span
                      className={`cm-film__outcome cm-film__outcome--${readPointOutcome(focusPoint)}`}
                    >
                      {outcomeMeta(readPointOutcome(focusPoint))?.mark}{" "}
                      {t(
                        outcomeMeta(readPointOutcome(focusPoint))?.titleKey,
                        outcomeMeta(readPointOutcome(focusPoint))?.title
                      )}
                    </span>
                  ) : null}
                  {focusLabels.length
                    ? focusLabels.map((group) => (
                        <div key={group.id} className="cm-film__label-group">
                          <span className="cm-film__label-kicker">
                            {t(group.titleKey, group.title)}
                          </span>
                          {group.tags.map((tag) => (
                            <span key={tag} className="cm-film__label">
                              {tagLabel ? tagLabel(tag) : tag}
                            </span>
                          ))}
                        </div>
                      ))
                    : null}
                </div>
              ) : null}
            </div>
          )}

        </div>

        <aside className="cm-chrono__side">
          {(canModerate || canLabelScore) && (!reviewLayout || toolsOpen) ? (
            <div className="cm-chrono__mod">
              {reviewLayout ? (
                <p className="cm-review__tools-kicker">
                  {t("cmReviewOptions", "Options")}
                </p>
              ) : null}
              {canModerate ? (
              <button
                type="button"
                className="cm-btn cm-btn--danger"
                onClick={() => onClearPoints?.()}
                disabled={!onClearPoints}
              >
                <Trash2 size={14} aria-hidden />
                {t("cmClearPoints", "Clear points & labels")}
              </button>
              ) : null}
              {canModerate && playerUrl ? (
                <button
                  type="button"
                  className="cm-btn cm-btn--ghost"
                  disabled={probeBusy || roiMode || scoreAiScanBusy || scoreAiLabelBusy || scoreAiReview}
                  onClick={() => {
                    setProbeError("");
                    setProbeReading(null);
                    setRoiForScan(false);
                    setRoiMode(true);
                    videoRef.current?.pause?.();
                  }}
                >
                  <Scan size={14} aria-hidden />
                  {t("cmCheckBoard", "Check board score")}
                </button>
              ) : null}
              {canModerate && playerUrl && onScanScore ? (
                <button
                  type="button"
                  className={`cm-btn cm-btn--ghost${scoreAiOpen ? " is-on" : ""}`}
                  disabled={probeBusy || scoreAiScanBusy || scoreAiLabelBusy}
                  onClick={() => {
                    if (scoreAiOpen) {
                      closeScoreAi();
                      return;
                    }
                    setScoreAiOpen(true);
                    setScoreAiError("");
                    setScoreAiReading(null);
                    videoRef.current?.pause?.();
                  }}
                >
                  <ScanSearch size={14} aria-hidden />
                  {t("cmScoreAiSegment", "Score AI segment")}
                </button>
              ) : null}
              {canLabelScore && playerUrl && onLabelScore && !canModerate ? (
                <button
                  type="button"
                  className={`cm-btn cm-btn--ghost${scoreAiOpen ? " is-on" : ""}`}
                  disabled={scoreAiLabelBusy}
                  onClick={() => {
                    if (scoreAiOpen) {
                      closeScoreAi();
                      return;
                    }
                    setScoreAiOpen(true);
                    setScoreAiError("");
                    setScoreAiLabelNote("");
                    videoRef.current?.pause?.();
                  }}
                >
                  <ScanSearch size={14} aria-hidden />
                  {t("cmScoreLabelOpen", "Label score on this video")}
                </button>
              ) : null}
              {activeSource?.media_id && onReassignMedia && rubbers.length ? (
                <label className="cm-chrono__reassign">
                  <span>
                    {t("cmMoveTapeToRubber", "Move this video to")}
                  </span>
                  <select
                    className="cm-chrono__reassign-select"
                    value={String(activeSource.rubber_id || rubber?.rubber_id || "")}
                    disabled={reassignBusy}
                    onChange={(event) => {
                      const next = event.target.value;
                      const current = String(
                        activeSource.rubber_id || rubber?.rubber_id || ""
                      );
                      if (!next || next === current) return;
                      const moving = activeTape?.clips?.length
                        ? activeTape.clips
                        : sources.length
                          ? sources
                          : [activeSource];
                      const mediaIds = [
                        ...new Set(
                          moving
                            .map((row) => row?.media_id)
                            .filter(Boolean)
                            .map(String)
                        ),
                      ];
                      void (async () => {
                        setReassignBusy(true);
                        setMediaError("");
                        try {
                          await onReassignMedia({
                            media_ids: mediaIds,
                            rubber_id: next,
                          });
                        } catch (err) {
                          setMediaError(
                            err?.message ||
                              t("cmMoveTapeFailed", "Could not move video")
                          );
                        } finally {
                          setReassignBusy(false);
                        }
                      })();
                    }}
                  >
                    {rubbers.map((row) => (
                      <option key={row.rubber_id} value={row.rubber_id}>
                        {rubberCodeLabel(row.code)}
                        {row.table_number != null ? ` · T${row.table_number}` : ""}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}
            </div>
          ) : null}
          {(canModerate || canLabelScore) && scoreAiOpen ? (
            <div className="cm-chrono__score-ai" role="region" aria-label={t("cmScoreAiSegment", "Score AI segment")}>
              <p className="cm-chrono__score-ai-hint">
                {canLabelScore
                  ? t(
                      "cmScoreLabelHint",
                      "Box the umpire score. Processing reads the whole video in order, like a live score camera, and stamps each detected score on that frame. You can wait here while it runs."
                    )
                  : t(
                      "cmScoreAiHint",
                      "Mark in/out on this clip, box the umpire score, then scan. Confirm each flagged frame before AI reads it."
                    )}
              </p>
              {canModerate && onProbeScore ? (
                <label className="cm-chrono__score-ai-accept">
                  <input
                    type="checkbox"
                    checked={scoreAiLiveSim}
                    disabled={scoreAiScanBusy || scoreAiOcrBusy}
                    onChange={(event) => {
                      const on = event.target.checked;
                      scoreAiLiveSimRef.current = on;
                      setScoreAiLiveSim(on);
                      if (on) void startScoreAiLive();
                      else stopScoreAiLive();
                    }}
                  />
                  <span>
                    {t("cmScoreAiLiveSim", "Play as live scorecam")}
                  </span>
                </label>
              ) : null}
              {canModerate && onProbeScore ? (
                <button
                  type="button"
                  className={`cm-btn cm-btn--ghost${scoreSideFlip ? " is-on" : ""}`}
                  onClick={() => flipScoreSide({ swapBoard: true })}
                >
                  <ArrowLeftRight size={14} aria-hidden />
                  {t("cmScoreSideLeft", "Left score → {{name}}", {
                    name:
                      (scoreSideFlip ? awayLabel : homeLabel) ||
                      (scoreSideFlip ? "away" : "home"),
                  })}
                </button>
              ) : null}
              <div className="cm-chrono__score-ai-marks">
                <button
                  type="button"
                  className="cm-btn cm-btn--ghost"
                  disabled={scoreAiScanBusy || scoreAiReview}
                  onClick={() => {
                    const sec =
                      Number(videoRef.current?.currentTime) ||
                      Number(playheadSec) ||
                      0;
                    setScoreAiInSec(sec);
                    setScoreAiError("");
                  }}
                >
                  <ArrowLeftToLine size={14} aria-hidden />
                  {t("cmScoreAiMarkIn", "Mark in")}
                  {scoreAiInSec != null ? (
                    <span className="cm-chrono__score-ai-t">
                      {formatTms(Math.round(scoreAiInSec * 1000))}
                    </span>
                  ) : null}
                </button>
                <button
                  type="button"
                  className="cm-btn cm-btn--ghost"
                  disabled={scoreAiScanBusy || scoreAiReview}
                  onClick={() => {
                    const sec =
                      Number(videoRef.current?.currentTime) ||
                      Number(playheadSec) ||
                      0;
                    setScoreAiOutSec(sec);
                    setScoreAiError("");
                  }}
                >
                  <ArrowRightToLine size={14} aria-hidden />
                  {t("cmScoreAiMarkOut", "Mark out")}
                  {scoreAiOutSec != null ? (
                    <span className="cm-chrono__score-ai-t">
                      {formatTms(Math.round(scoreAiOutSec * 1000))}
                    </span>
                  ) : null}
                </button>
                <button
                  type="button"
                  className="cm-btn cm-btn--ghost"
                  disabled={scoreAiScanBusy || scoreAiReview || roiMode}
                  onClick={() => {
                    setRoiForScan(true);
                    setRoiMode(true);
                    videoRef.current?.pause?.();
                  }}
                >
                  <Crosshair size={14} aria-hidden />
                  {scoreAiRoi
                    ? t("cmScoreAiRoiSet", "Score box set")
                    : t("cmScoreAiDrawRoi", "Draw score box")}
                </button>
              </div>
              <div className="cm-chrono__score-ai-actions">
                {canModerate && onScanScore ? (
                <button
                  type="button"
                  className="cm-btn"
                  disabled={
                    scoreAiScanBusy ||
                    scoreAiLabelBusy ||
                    scoreLabelActive ||
                    scoreAiReview ||
                    !scoreAiRoi ||
                    scoreAiInSec == null ||
                    scoreAiOutSec == null
                  }
                  onClick={() => void launchScoreAiScan()}
                >
                  {scoreAiScanBusy
                    ? t("cmScoreAiScanning", "Scanning…")
                    : t("cmScoreAiLaunch", "Scan segment")}
                </button>
                ) : null}
                {canLabelScore && onLabelScore ? (
                  <button
                    type="button"
                    className="cm-btn"
                    disabled={
                      scoreAiScanBusy ||
                      scoreAiLabelBusy ||
                      scoreLabelActive ||
                      scoreAiReview ||
                      !scoreAiRoi
                    }
                    onClick={() => void launchScoreLabel()}
                  >
                    {scoreAiLabelBusy || scoreLabelActive
                      ? t("cmScoreLabeling", "Reading the video…")
                      : t("cmScoreLabelRun", "Process whole video")}
                  </button>
                ) : null}
                <button
                  type="button"
                  className="cm-btn cm-btn--ghost"
                  disabled={scoreAiScanBusy || scoreAiLabelBusy || scoreAiOcrBusy}
                  onClick={closeScoreAi}
                >
                  {t("close", "Close")}
                </button>
              </div>
              {scoreLabelActive ? (
                <div className="cm-chrono__score-ai-progress" aria-hidden>
                  <div className="cm-chrono__score-ai-progress-track">
                    <span
                      className="cm-chrono__score-ai-progress-fill"
                      style={{
                        width: `${Math.max(4, Math.min(100, labelProgressPct))}%`,
                      }}
                    />
                  </div>
                  <p className="cm-chrono__score-ai-meta">
                    {t(
                      "cmScoreLabelWait",
                      "{{n}} points so far{{part}}",
                      {
                        n: Number(labelJob?.points_added) || 0,
                        part:
                          labelQueue.length > 1
                            ? t("cmScoreLabelPart", " · part {{i}}/{{total}}", {
                                i: labelPart,
                                total: labelQueue.length,
                              })
                            : "",
                      }
                    )}
                  </p>
                </div>
              ) : null}
              {labelJob?.status === "done" ? (
                <p className="cm-chrono__score-ai-meta" role="status">
                  {t(
                    "cmScoreLabelDone",
                    "Labeled {{n}} points on this video.{{tail}}",
                    { n: Number(labelJob.points_added) || 0, tail: "" }
                  )}
                </p>
              ) : scoreAiLabelNote ? (
                <p className="cm-chrono__score-ai-meta" role="status">
                  {scoreAiLabelNote}
                </p>
              ) : null}
              {labelJob?.status === "failed" && labelJob.error ? (
                <p className="cm-chrono__score-ai-err" role="status">
                  {labelJob.error}
                </p>
              ) : null}
              {scoreAiScanBusy ? (
                <div className="cm-chrono__score-ai-progress" aria-hidden>
                  <div className="cm-chrono__score-ai-progress-track">
                    <span
                      className="cm-chrono__score-ai-progress-fill"
                      style={{
                        width: `${Math.max(4, Math.min(100, scoreAiScanPct))}%`,
                      }}
                    />
                  </div>
                  <p className="cm-chrono__score-ai-meta">
                    {t(
                      "cmScoreAiScanRemain",
                      "{{done}}/{{total}} frames · ~{{sec}}s left",
                      {
                        done: scoreAiScanDone,
                        total: Math.max(scoreAiScanDone, scoreAiScanTotal),
                        sec: Math.max(1, scoreAiScanEtaSec),
                      }
                    )}
                  </p>
                </div>
              ) : null}
              {(scoreAiSkipped > 0 || scoreAiSkippedDead > 0) && !scoreAiReview ? (
                <p className="cm-chrono__score-ai-meta">
                  {scoreAiSkipped > 0
                    ? t("cmScoreAiSkipped", "{{n}} occlusion frames filtered", {
                        n: scoreAiSkipped,
                      })
                    : null}
                  {scoreAiSkipped > 0 && scoreAiSkippedDead > 0 ? " · " : null}
                  {scoreAiSkippedDead > 0
                    ? t("cmScoreAiDeadSkipped", "{{n}} dead-time skipped", {
                        n: scoreAiSkippedDead,
                      })
                    : null}
                </p>
              ) : null}
              {scoreAiTiming && !scoreAiReview && !scoreAiScanBusy ? (
                <p className="cm-chrono__score-ai-meta">
                  {t(
                    "cmScoreAiTiming",
                    "Scan {{total}}ms · dl {{dl}} · extract {{ex}} · fp {{fp}} · detect {{det}}{{mode}}",
                    {
                      total: scoreAiTiming.total_ms ?? "—",
                      dl: scoreAiTiming.download_ms ?? "—",
                      ex: scoreAiTiming.extract_ms ?? "—",
                      fp: scoreAiTiming.fingerprint_ms ?? "—",
                      det: scoreAiTiming.detect_ms ?? "—",
                      mode: scoreAiTiming.cache_hit
                        ? " · cache"
                        : scoreAiTiming.input_mode === "https"
                          ? " · https"
                          : "",
                    }
                  )}
                </p>
              ) : null}
              {scoreAiError && !scoreAiReview ? (
                <p className="cm-chrono__score-ai-err" role="status">
                  {scoreAiError}
                </p>
              ) : null}
              {canModerate && (scoreAiLiveRunning || scoreAiLiveLogs.length) ? (
                <div className="cm-chrono__score-ai-live">
                  <p className="cm-chrono__score-ai-meta" role="status">
                    {scoreAiLiveRunning
                      ? t("cmScoreAiLiveRunning", "Playing · scorecam is reading the box")
                      : t("cmScoreAiLiveDone", "Live scorecam log")}
                  </p>
                  {scoreAiLiveLogs.length ? (
                    <ol className="cm-score-ai-hud__log cm-chrono__score-ai-log">
                      {[...scoreAiLiveLogs].reverse().map((row, idx) => (
                        <li
                          key={`${row.t}-${idx}`}
                          className={`is-${row.level || "info"}`}
                        >
                          <time>{formatScoreAiLogTime(row.t)}</time>
                          <span>{row.msg}</span>
                        </li>
                      ))}
                    </ol>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : null}
          {reviewLayout ? (
            <div className="cm-chrono__tagger-slot cm-chrono__tagger-slot--review">
              {taggerBlock}
            </div>
          ) : null}
          {sets.length > 1 ? (
            <div className="cm-chrono__sets" role="tablist">
              {sets.map((s) => (
                <button
                  key={s.setNumber}
                  type="button"
                  role="tab"
                  aria-selected={s.setNumber === setNumber}
                  className={
                    s.setNumber === setNumber
                      ? "cm-chrono__set is-on"
                      : "cm-chrono__set"
                  }
                  onClick={() => setSetNumber(s.setNumber)}
                >
                  {t("cmSetN", "Set {{n}}", { n: s.setNumber })}
                </button>
              ))}
            </div>
          ) : sets.length === 1 ? (
            <p className="cm-chrono__set-label">
              {t("cmSetN", "Set {{n}}", { n: sets[0].setNumber })}
            </p>
          ) : null}

          <div className="cm-chrono__rail">
            <div className="cm-chrono__rail-head">
              <p className="cm-chrono__rail-label">
                {t("cmPointCues", "Points on this tape")}
              </p>
              {cueIndex >= 0 ? (
                <span className="cm-chrono__now-pill">
                  {t("cmNowPlaying", "Now")} · {cueIndex + 1}/{setPoints.length}
                </span>
              ) : null}
            </div>
            {setPoints.length ? (
              <ol className="cm-chrono__cues" ref={reviewLayout ? null : cueListRef}>
                {setPoints.map((p, idx) => {
                  const assets = mediaForPoint(playbackMedia, p);
                  const photos = assets.filter((m) => m.kind === "photo");
                  const voices = assets.filter((m) => m.kind === "voice");
                  const clock = formatTms(p.t_ms);
                  const tags = p.tags || [];
                  const isPlaying = idx === cueIndex;
                  const isSelected = idx === focusIdx;
                  return (
                    <li
                      key={p.point_id}
                      data-cue={idx}
                      className={[
                        "cm-chrono__cue",
                        isPlaying ? "is-active" : "",
                        isSelected ? "is-selected" : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                    >
                      <button
                        type="button"
                        className="cm-chrono__cue-hit"
                        onClick={() => seek(p)}
                        disabled={!showPlayer || p.t_ms == null}
                      >
                        <span className="cm-chrono__cue-index" aria-hidden>
                          {idx + 1}
                        </span>
                        <span className="cm-chrono__cue-score">
                          {pointSetPair(p) ? (
                            <span className="cm-review__pt-sets">{pointSetPair(p)}</span>
                          ) : null}
                          {p.home_score}–{p.away_score}
                        </span>
                        {clock ? (
                          <span className="cm-chrono__cue-clock">{clock}</span>
                        ) : null}
                        {isPlaying ? (
                          <span className="cm-chrono__cue-live">
                            {t("cmNowPlaying", "Now")}
                          </span>
                        ) : null}
                        {tags.length ? (
                          <span className="cm-chrono__cue-tags">
                            {tags.map((tag) => (
                              <span
                                key={tag}
                                className="cm-chrono__cue-tag"
                                data-tag={tag}
                              >
                                {tagLabel(tag)}
                              </span>
                            ))}
                          </span>
                        ) : (
                          <span className="cm-chrono__cue-tags cm-chrono__cue-tags--empty">
                            {t("cmNoTagsYet", "No labels")}
                          </span>
                        )}
                        {p.comment ? (
                          <span className="cm-chrono__cue-note">{p.comment}</span>
                        ) : null}
                        {photos.length || voices.length ? (
                          <span className="cm-chrono__cue-media" aria-hidden>
                            {photos.length ? <ImageIcon size={12} /> : null}
                            {voices.length ? <Mic size={12} /> : null}
                          </span>
                        ) : null}
                      </button>
                      {isSelected && photos.length ? (
                        <div className="cm-chrono__cue-gallery">
                          {photos.map((m) => (
                            <button
                              key={m.media_id}
                              type="button"
                              className="cm-chrono__thumb"
                              onClick={() => setActivePhoto(m)}
                            >
                              <img
                                src={mediaSrc(m.file_url)}
                                alt=""
                                loading="lazy"
                              />
                            </button>
                          ))}
                        </div>
                      ) : null}
                      {isSelected && voices.length ? (
                        <div className="cm-chrono__cue-voices">
                          {voices.map((m) => (
                            <audio
                              key={m.media_id}
                              src={mediaSrc(m.file_url)}
                              controls
                              preload="metadata"
                            />
                          ))}
                        </div>
                      ) : null}
                    </li>
                  );
                })}
              </ol>
            ) : (
              <p className="cm-chrono__empty">
                {t("cmNoTimelineYet", "No points or media for this match yet.")}
              </p>
            )}
          </div>

          <div className="cm-chrono__tagger-slot cm-chrono__tagger-slot--side">
            {taggerBlock}
          </div>
        </aside>
      </div>

      <div className="cm-chrono__tagger-slot cm-chrono__tagger-slot--dock">
        {taggerBlock}
      </div>

    </section>
  );
}

function MatchReviewPlayer({
  rubber,
  points,
  media,
  rubbers = [],
  tagLabel,
  homeLabel,
  awayLabel,
  homePlayers = null,
  awayPlayers = null,
  canTag = false,
  canModerate = false,
  canLabelScore = false,
  onSetPointOutcome,
  onSavePointScore,
  onStampReviewPoint,
  onSendReaction,
  onClearPoints,
  onProbeScore,
  onFingerprintFrame,
  onScanScore,
  onLabelScore,
  onLabelStatus,
  onReassignMedia,
  preferredMediaId = null,
  clubLogo = null,
  clubName = "",
  brandColor = "",
  homeClubName = "",
  awayClubName = "",
  homeTeamScore = 0,
  awayTeamScore = 0,
  matchPoints = null,
  setsToWin = 3,
  division = "",
  onClose,
  t,
}) {
  const rootRef = useRef(null);
  const rubberPoints = useMemo(
    () =>
      (points || []).filter(
        (p) => String(p.rubber_id) === String(rubber?.rubber_id)
      ),
    [points, rubber?.rubber_id]
  );
  const stamped = rubberPoints.filter(
    (p) => p.t_ms != null && Number.isFinite(Number(p.t_ms))
  ).length;

  return (
    <div ref={rootRef} className="cm-review" role="dialog" aria-modal="true">
      <header className="cm-review__head">
        <div>
          {rubberReviewTapes(media, rubber?.rubber_id).length ||
          rubberReviewReady(
            rubber?.state === "done" ? rubber : { ...rubber, state: "done" },
            points,
            media
          ) ? (
            <p className="cm-review__kicker">{t("cmMatchReview", "Match review")}</p>
          ) : null}
          <strong>
            {rubberCodeLabel(rubber?.code)} · {homeLabel} vs {awayLabel}
          </strong>
          <p className="cm-review__sub">
            {t(
              "cmReviewTapeHintShort",
              "{{stamped}}/{{total}} points timed. Open Points to jump, or Label point to tag.",
              { stamped, total: rubberPoints.length }
            )}
          </p>
        </div>
        <button
          type="button"
          className="cm-review__close"
          onClick={onClose}
          aria-label={t("close", "Close")}
        >
          <X size={18} aria-hidden />
        </button>
      </header>

      <div className="cm-review__body">
        {rubberPoints.length ||
        matchPlaybackMedia(media).some(
          (m) =>
            m.kind === "clip" &&
            m.rubber_id &&
            String(m.rubber_id) === String(rubber?.rubber_id)
        ) ? (
          <ChronoTimelineViewer
            key={`${rubber?.rubber_id || ""}:${preferredMediaId || ""}`}
            rubber={rubber}
            points={rubberPoints}
            media={media}
            rubbers={rubbers}
            tagLabel={tagLabel}
            homeLabel={homeLabel}
            awayLabel={awayLabel}
            homePlayers={homePlayers}
            awayPlayers={awayPlayers}
            showRubberHead={false}
            canTag={canTag}
            canModerate={canModerate}
            canLabelScore={canLabelScore}
            onSetPointOutcome={onSetPointOutcome}
            onSavePointScore={onSavePointScore}
            onStampReviewPoint={onStampReviewPoint}
            onSendReaction={onSendReaction}
            onClearPoints={onClearPoints}
            onProbeScore={onProbeScore}
            onFingerprintFrame={onFingerprintFrame}
            onScanScore={onScanScore}
            onLabelScore={onLabelScore}
            onLabelStatus={onLabelStatus}
            onReassignMedia={onReassignMedia}
            preferredMediaId={preferredMediaId}
            clubLogo={clubLogo}
            clubName={clubName}
            brandColor={brandColor}
            homeClubName={homeClubName}
            awayClubName={awayClubName}
            homeTeamScore={homeTeamScore}
            awayTeamScore={awayTeamScore}
            matchPoints={matchPoints}
            setsToWin={setsToWin}
            division={division}
            layout="review"
            t={t}
          />
        ) : (
          <div className="cm-review__empty">
            <Clapperboard size={28} aria-hidden />
            <p>
              {t(
                "cmNoTimelineYet",
                "No points or media for this match yet."
              )}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function readPersistedCameraClaim(matchId) {
  try {
    const raw = localStorage.getItem(cameraClaimStorageKey(matchId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || (parsed.table_number !== 1 && parsed.table_number !== 2)) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function persistCameraClaim(matchId, claim) {
  const key = cameraClaimStorageKey(matchId);
  if (!key) return;
  try {
    if (!claim) localStorage.removeItem(key);
    else localStorage.setItem(key, JSON.stringify(claim));
  } catch {
    /* ignore */
  }
}

function cameraPrefsKey(matchId) {
  return matchId ? `cm-camera-prefs:${matchId}` : null;
}

function readCameraPrefs(matchId) {
  try {
    const raw = localStorage.getItem(cameraPrefsKey(matchId));
    if (!raw) {
      return { facingMode: "environment", deviceId: "", zoom: DEFAULT_CAMERA_ZOOM };
    }
    const parsed = JSON.parse(raw);
    return {
      facingMode: normalizeWebEvalFacingMode(parsed?.facingMode, "environment"),
      deviceId: typeof parsed?.deviceId === "string" ? parsed.deviceId : "",
      zoom: normalizeCameraZoom(parsed?.zoom, DEFAULT_CAMERA_ZOOM),
    };
  } catch {
    return { facingMode: "environment", deviceId: "", zoom: DEFAULT_CAMERA_ZOOM };
  }
}

function writeCameraPrefs(matchId, prefs) {
  const key = cameraPrefsKey(matchId);
  if (!key) return;
  try {
    localStorage.setItem(
      key,
      JSON.stringify({
        facingMode: normalizeWebEvalFacingMode(prefs?.facingMode, "environment"),
        deviceId: typeof prefs?.deviceId === "string" ? prefs.deviceId : "",
        zoom: normalizeCameraZoom(prefs?.zoom, DEFAULT_CAMERA_ZOOM),
      })
    );
  } catch {
    /* ignore */
  }
}

function previewZoomTransform(zoom, hardwareZoom, hasHardwareRange) {
  if (hardwareZoom || hasHardwareRange) return undefined;
  const z = normalizeCameraZoom(zoom);
  if (z <= 1.001) return undefined;
  return `scale(${z})`;
}

/** Prefer the normal rear camera (not ultra-wide / tele / front). */
function preferDefaultBackCamera(options) {
  const list = Array.isArray(options) ? options : [];
  return (
    list.find((o) => o.lens === "back") ||
    list.find(
      (o) =>
        o.facingMode === "environment" &&
        o.lens !== "ultrawide" &&
        o.lens !== "tele" &&
        o.lens !== "front"
    ) ||
    list.find((o) => o.facingMode === "environment") ||
    null
  );
}

function championshipLandscape() {
  return (
    typeof window !== "undefined" &&
    window.matchMedia?.("(orientation: landscape)")?.matches
  );
}

function championshipCameraConstraints(
  facingMode,
  deviceId,
  resolution = DEFAULT_RECORD_RESOLUTION,
  fps = MATCH_RECORD_DEFAULT_FPS,
  deviceIdMode = "exact"
) {
  const landscape = championshipLandscape();
  const res = normalizeRecordResolution(resolution, DEFAULT_RECORD_RESOLUTION);
  const rate = normalizeMatchRecordFps(fps, MATCH_RECORD_DEFAULT_FPS);
  const size = matchRecordVideoConstraints({
    landscape,
    resolution: res,
    fps: rate,
  });
  const base = evalVideoUserMediaConstraints({
    fps: rate,
    deviceId,
    facingMode,
    includeAudio: true,
    deviceIdMode,
    frameRateMode: "min",
  });
  return {
    ...base,
    video: {
      ...base.video,
      width: size.width,
      height: size.height,
      frameRate: size.frameRate,
      aspectRatio: size.aspectRatio,
    },
  };
}

function matchCaptureWarningText(warnings) {
  const notes = [];
  if (warnings.includes("fps")) {
    notes.push("60fps is not available on this camera. Recording at 30fps.");
  }
  if (warnings.includes("resolution")) {
    notes.push(
      "Requested resolution is not available on this camera. Recording at a lower size."
    );
  }
  return notes.join(" ");
}

function isCameraPermissionDenied(err) {
  const name = String(err?.name || "");
  return name === "NotAllowedError" || name === "PermissionDeniedError";
}

/** Camera is already open, so a second getUserMedia cannot start. */
function isCameraBusyError(err) {
  const name = String(err?.name || "");
  return (
    name === "NotReadableError" ||
    name === "TrackStartError" ||
    name === "AbortError"
  );
}

/**
 * The call that must run inside the tap. A saved device id or a microphone
 * track makes Safari and Chrome reject it without showing Allow.
 */
function plainCameraConstraints(facingMode) {
  const facing = facingMode === "user" ? "user" : "environment";
  return {
    audio: false,
    video: { facingMode: { ideal: facing } },
  };
}

function attemptIsPlainCamera(constraints) {
  const video = constraints?.video;
  const hasDevice = Boolean(
    video && typeof video === "object" && video.deviceId
  );
  return constraints?.audio === false && !hasDevice;
}

async function queryCameraPermissionState() {
  if (typeof navigator === "undefined" || !navigator.permissions?.query) {
    return null;
  }
  try {
    const status = await navigator.permissions.query({ name: "camera" });
    return status?.state || null;
  } catch {
    return null;
  }
}

function stopStreamTracks(stream) {
  if (!stream) return;
  stream.getTracks().forEach((track) => {
    try {
      track.stop();
    } catch {
      /* ignore */
    }
  });
}

function sizedCameraAttempts(facing, deviceId, resolution, fps) {
  const attempts = [];
  const id = String(deviceId || "").trim();
  if (id) {
    attempts.push(
      championshipCameraConstraints(facing, id, resolution, fps, "exact")
    );
    attempts.push(
      championshipCameraConstraints(facing, id, resolution, fps, "ideal")
    );
  }
  attempts.push(championshipCameraConstraints(facing, "", resolution, fps));
  const videoOnly = championshipCameraConstraints(facing, "", resolution, fps);
  attempts.push({ ...videoOnly, audio: false });
  if (id) {
    const exact = championshipCameraConstraints(
      facing,
      id,
      resolution,
      fps,
      "exact"
    );
    attempts.push({ ...exact, audio: false });
  }
  return attempts;
}

async function openSizedCameraStream(facing, deviceId, resolution, fps) {
  let lastErr;
  for (const constraints of sizedCameraAttempts(facing, deviceId, resolution, fps)) {
    try {
      return await navigator.mediaDevices.getUserMedia(constraints);
    } catch (err) {
      lastErr = err;
      // Exact lens or microphone failures are not a site-wide camera block.
      // Keep going until the plain video request is denied.
      if (isCameraPermissionDenied(err) && attemptIsPlainCamera(constraints)) {
        break;
      }
    }
  }
  throw lastErr || new Error("Camera unavailable");
}

function queueMatchCaptureFallback(queue, plan) {
  if (plan.fps === 60) {
    queue.push({
      fps: 30,
      resolution: plan.resolution,
      warnings: [...plan.warnings, "fps"],
    });
  }
  if (plan.resolution === 1080) {
    queue.push({
      fps: plan.fps,
      resolution: 720,
      warnings: [...plan.warnings, "resolution"],
    });
  }
}

/**
 * Open the camera at the selected fps and resolution.
 * 60fps falls back to 30fps with a warning. 1080p falls back to 720p.
 * Scorecam may open at 480p; match cameras refuse below 720p.
 */
async function openChampionshipCameraStream({
  facing,
  deviceId,
  resolution,
  fps,
}) {
  const startRes = normalizeRecordResolution(resolution, DEFAULT_RECORD_RESOLUTION);
  const startFps = normalizeMatchRecordFps(fps, MATCH_RECORD_DEFAULT_FPS);
  const queue = [{ resolution: startRes, fps: startFps, warnings: [] }];
  const tried = new Set();
  let lastErr;
  while (queue.length) {
    const plan = queue.shift();
    const key = `${plan.fps}:${plan.resolution}`;
    if (tried.has(key)) continue;
    tried.add(key);
    let stream;
    try {
      stream = await openSizedCameraStream(
        facing,
        deviceId,
        plan.resolution,
        plan.fps
      );
    } catch (err) {
      lastErr = err;
      queueMatchCaptureFallback(queue, plan);
      continue;
    }
    const actual = readMatchTrackCapture(stream.getVideoTracks?.()?.[0]);
    const decision = matchCaptureAdjustment({
      requestedFps: plan.fps,
      requestedResolution: plan.resolution,
      width: actual.width,
      height: actual.height,
      frameRate: actual.frameRate,
    });
    if (decision.reject) {
      stopStreamTracks(stream);
      if (decision.reason === "fps" && plan.fps === 60) {
        queue.push({
          fps: 30,
          resolution: plan.resolution,
          warnings: [...plan.warnings, "fps"],
        });
      } else if (decision.reason === "resolution" && plan.resolution === 1080) {
        queue.push({
          fps: plan.fps,
          resolution: 720,
          warnings: [...plan.warnings, "resolution"],
        });
      } else if (decision.reason === "resolution" && plan.resolution === 720) {
        // Only scorecam requests land here with an intentional 480 fallback queue
        // entry from the caller; otherwise refuse.
        lastErr = new Error("This camera cannot record at 720p.");
      } else {
        lastErr = new Error(
          decision.reason === "fps"
            ? "This camera cannot record at 30fps."
            : plan.resolution === 480
              ? "This camera cannot record at 480p."
              : "This camera cannot record at 720p."
        );
      }
      continue;
    }
    if (!decision.accept) {
      stopStreamTracks(stream);
      queue.unshift({
        fps: decision.fps,
        resolution: decision.resolution,
        warnings: [...new Set([...plan.warnings, ...decision.warnings])],
      });
      continue;
    }
    return {
      stream,
      fps: plan.fps,
      resolution: plan.resolution,
      warnings: [...new Set(plan.warnings)],
    };
  }
  throw lastErr || new Error("Camera unavailable");
}

/** Selected box, scaled down to the camera. Never larger than the camera frame. */
function championshipRecordFrameSize(srcW, srcH, resolution, fps) {
  const frame = matchUploadFrameSize(
    srcW,
    srcH,
    normalizeRecordResolution(resolution, DEFAULT_RECORD_RESOLUTION),
    normalizeMatchRecordFps(fps, MATCH_RECORD_DEFAULT_FPS)
  );
  return { w: frame.width, h: frame.height, fps: frame.fps };
}

export default function ChampionshipMatchPage() {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const { themeMode, setThemeMode, brand } = useBrand();
  const { hasFeature } = useClubLicense();
  const videoFeatureOn = hasFeature("video_review") !== false;
  const isMobileLive = isMobileDevice();
  const user = useMemo(() => readUser(), []);
  const userId = user?.user_id;
  const phoneId = useMemo(() => championshipPhoneId(), []);
  const [snapshot, setSnapshot] = useState(null);
  const [playerNotes, setPlayerNotes] = useState([]);
  const [error, setError] = useState("");
  const [tab, setTab] = useState(() => {
    const q = searchParams.get("tab");
    if (q === "camera" || q === "control" || q === "lineup" || q === "replay") {
      return q;
    }
    return "score";
  });
  const [activeRubberId, setActiveRubberId] = useState(null);
  const [moreTags, setMoreTags] = useState(false);
  const [timeoutActive, setTimeoutActive] = useState(false);
  const [pointStartArmed, setPointStartArmed] = useState(false);
  const [reviewRubberId, setReviewRubberId] = useState(null);
  const [movedMediaId, setMovedMediaId] = useState(null);
  const pendingMoveRef = useRef(null);
  const [comment, setComment] = useState("");
  const [filterTag, setFilterTag] = useState("");
  const [replayRubberFocusId, setReplayRubberFocusId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sheetPhotoBusy, setSheetPhotoBusy] = useState(false);
  const [sheetPhotoNote, setSheetPhotoNote] = useState("");
  const sheetCameraRef = useRef(null);
  const sheetFileRef = useRef(null);
  const [swapPick, setSwapPick] = useState(null);
  const [tableNum, setTableNum] = useState(1);
  // ?cam=scorecam deep-links from cup / tournament cards straight to scorecam.
  const [angle, setAngle] = useState(() =>
    normalizeCamPosition(searchParams.get("cam")) === "scorecam"
      ? "scorecam"
      : "spectator"
  );
  const [tableGuideOn, toggleTableGuide] = useTableCameraGuide();
  const [recording, setRecording] = useState(false);
  const [recordBusy, setRecordBusy] = useState(false);
  const recordActionRef = useRef(false);
  const [mediaStatus, setMediaStatus] = useState("");
  const [scoreMode, setScoreMode] = useState("input");
  const [editHome, setEditHome] = useState("0");
  const [editAway, setEditAway] = useState("0");
  /** Scorer can invert auto table-side orientation on the pad. */
  const [scoreSidesManualFlip, setScoreSidesManualFlip] = useState(false);
  const pendingPointTagsRef = useRef([]);
  const [pendingPointTags, setPendingPointTags] = useState([]);
  const [hasPreviewStream, setHasPreviewStream] = useState(false);
  /** When false and a rubber is selected, the 10-match grid is hidden. */
  const [sheetBrowsing, setSheetBrowsing] = useState(true);
  const [tableEditRubberId, setTableEditRubberId] = useState(null);
  const [tableAssignBusy, setTableAssignBusy] = useState(false);
  const [camLandscape, setCamLandscape] = useState(false);
  const [cameraDocked, setCameraDocked] = useState(false);
  const [cameraPermissionPrompt, setCameraPermissionPrompt] = useState(false);
  const [cameraPermissionBlocked, setCameraPermissionBlocked] = useState(false);
  const [scoreWatch, setScoreWatch] = useState({
    phase: "idle",
    n: 0,
    detail: "",
    scoreText: "",
    logs: [],
    /** Last change-gate sample — shown in HUD while debugging detection. */
    gate: null,
  });
  const [scoreJump, setScoreJump] = useState(null);
  const dismissedJumpRef = useRef("");
  const heldBoardRef = useRef(null);
  const scoreLogAtRef = useRef(0);
  const scoreWatchCtxRef = useRef(null);
  /** Throttle same_board / heartbeat console+HUD spam while still proving the loop is alive. */
  const scoreGateQuietAtRef = useRef(0);
  const [cameraSettingsOpen, setCameraSettingsOpen] = useState(false);
  const [cameraSwitching, setCameraSwitching] = useState(false);
  const [scoreRoiMode, setScoreRoiMode] = useState(false);
  const [cameraOptions, setCameraOptions] = useState(() =>
    buildWebEvalCameraOptions([])
  );
  const initialCamPrefs = useMemo(() => readCameraPrefs(matchId), [matchId]);
  const initialEncodePrefs = useMemo(() => readEncodePrefs(), []);
  const [cameraFacingMode, setCameraFacingMode] = useState(
    initialCamPrefs.facingMode
  );
  const [cameraDeviceId, setCameraDeviceId] = useState(initialCamPrefs.deviceId);
  const [cameraZoom, setCameraZoom] = useState(initialCamPrefs.zoom);
  const [cameraZoomRange, setCameraZoomRange] = useState(null);
  const [cameraHardwareZoom, setCameraHardwareZoom] = useState(false);
  const [recordQuality, setRecordQuality] = useState(initialEncodePrefs.quality);
  const [uploadMode, setUploadMode] = useState(initialEncodePrefs.uploadMode);
  const clipUploads = useChampionshipClipUploads();
  const [recordFps, setRecordFps] = useState(initialEncodePrefs.fps);
  const [recordResolution, setRecordResolution] = useState(
    initialEncodePrefs.resolution
  );
  const [setCloseStableSec, setSetCloseStableSec] = useState(
    initialEncodePrefs.setCloseStableSec
  );
  /** Scorecam: false = score only (no clip upload), true = score + video. */
  const [scorecamRecordVideo, setScorecamRecordVideo] = useState(
    initialEncodePrefs.scorecamRecordVideo
  );
  const scorecamRecordVideoRef = useRef(scorecamRecordVideo);
  scorecamRecordVideoRef.current = scorecamRecordVideo;
  /** This phone reads the board only — never opens the recorder or uploads. */
  const scoreOnlyPhoneRef = useRef(false);
  const [voiceRecording, setVoiceRecording] = useState(false);
  const videoRef = useRef(null);
  const [previewVideoEl, setPreviewVideoEl] = useState(null);
  const bindPreviewVideo = useCallback((node) => {
    // The setup <video> and the live preview share this ref. Unmounting one
    // must not clear the element that is still on screen, or scorecam has
    // no frame to read.
    if (!node && videoRef.current?.isConnected) return;
    videoRef.current = node || null;
    setPreviewVideoEl(node || null);
  }, []);
  const previewStageRef = useRef(null);
  const settingsPreviewRef = useRef(null);
  const settingsStageRef = useRef(null);
  const recordCanvasRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const liveCaptureRef = useRef(null);
  const sidecarAudioRef = useRef(null);
  const chunksRef = useRef([]);
  const pendingBytesRef = useRef(0);
  const mimeRef = useRef("");
  const recordingDesiredRef = useRef(false);
  const rotatingChunkRef = useRef(false);
  const myTableRef = useRef(1);
  const snapshotRef = useRef(null);
  const pinnedRubberIdRef = useRef(null);
  const clipMetaRef = useRef({
    table: 1,
    rubberId: null,
    angle: "spectator",
    mediaSyncAt: 0,
  });
  const filmingTablesRef = useRef([]);
  const scorecamRecordKeyRef = useRef("");
  const recordStreamRef = useRef(null);
  const chunkWatchRef = useRef(0);
  const segmentOpenedAtRef = useRef(0);
  const rotateRecordingChunkRef = useRef(null);
  const releaseRecordStreamRef = useRef(null);
  const recordingOriginPerfRef = useRef(0);
  const mediaSyncAtRef = useRef(0);
  const segmentStartMsRef = useRef(0);
  const segmentIndexRef = useRef(0);
  const uploadChainRef = useRef(Promise.resolve());
  const recordQualityRef = useRef(recordQuality);
  const uploadModeRef = useRef(uploadMode);
  const recordFpsRef = useRef(recordFps);
  const recordResolutionRef = useRef(recordResolution);
  /** True while this phone is (or is about to be) scorecam — allows 480p encode. */
  const scorecamRoleRef = useRef(false);
  const noteNegotiatedCaptureRef = useRef(() => {});
  const voiceRecorderRef = useRef(null);
  const voiceChunksRef = useRef([]);
  const pendingPointStartRef = useRef(null);
  const captureIdRef = useRef(null);
  const startLockRef = useRef(false);
  const releasingCamRef = useRef(false);
  const releaseCamLockRef = useRef(false);
  const skipDockAfterLeaveRef = useRef(false);
  const cameraLeaveGenRef = useRef(0);
  const cameraTabRef = useRef(tab);
  const leaveRecordingCameraRef = useRef(async () => {});
  const cameraSwitchTokenRef = useRef(0);
  const cameraRestoreTriedRef = useRef(false);
  const cameraFacingRef = useRef(cameraFacingMode);
  const cameraDeviceRef = useRef(cameraDeviceId);
  const cameraZoomRef = useRef(cameraZoom);
  const cameraZoomRangeRef = useRef(null);
  const cameraHardwareZoomRef = useRef(false);
  const lensZoomTailRef = useRef(Promise.resolve());
  const lensZoomQueuedRef = useRef(false);
  const lensZoomAgainRef = useRef(false);
  const lensZoomHoldRef = useRef(0);
  const pushLensZoomRef = useRef(async () => null);
  const recordDrawRafRef = useRef(0);
  const pinchRef = useRef({
    pointers: new Map(),
    startDistance: 0,
    startZoom: DEFAULT_CAMERA_ZOOM,
  });
  cameraFacingRef.current = cameraFacingMode;
  cameraDeviceRef.current = cameraDeviceId;
  cameraZoomRef.current = cameraZoom;
  recordQualityRef.current = recordQuality;
  uploadModeRef.current = uploadMode;
  recordFpsRef.current = recordFps;
  recordResolutionRef.current = recordResolution;
  // Angle is available here; myCamera is synced below once the roster loads.
  scorecamRoleRef.current = normalizeCamPosition(angle) === "scorecam";

  const onSnapshot = useCallback((data) => {
    if (data?.match) setSnapshot(data);
  }, []);

  const onCameras = useCallback(
    (roster) => {
      if (!roster) return;
      if (Array.isArray(roster.cameras)) {
        setSnapshot((prev) =>
          prev ? { ...prev, cameras: roster.cameras } : prev
        );
      }
      const online = Number(roster.online) || 0;
      const total = Number(roster.total) || 0;
      if (roster.reason === "stopped" || roster.reason === "released") {
        setMediaStatus(
          online > 0
            ? t("cmCamerasConnected", "{{count}} camera(s) connected", {
                count: online,
              })
            : t("cmNoCamera", "No camera")
        );
      } else if (roster.reason === "offline") {
        setMediaStatus(
          t(
            "cmCameraReconnecting",
            "A camera went offline — waiting to reconnect…"
          )
        );
      } else if (roster.reason === "claimed" || roster.reason === "rebound") {
        setMediaStatus(
          t("cmCamerasConnected", "{{count}} camera(s) connected", {
            count: online || total,
          })
        );
      }
    },
    [t]
  );

  const [cheerIncoming, setCheerIncoming] = useState(null);
  const cheerOpenRef = useRef(false);
  const onCheer = useCallback(
    (payload) => {
      if (!cheerOpenRef.current) return;
      const id = payload?.championship_match_id;
      if (!id || String(id) !== String(matchId)) return;
      setCheerIncoming(payload);
    },
    [matchId]
  );
  const onSync = useCallback(
    (payload) => {
      if (!cheerOpenRef.current) return;
      const id = payload?.championship_match_id;
      if (!id || String(id) !== String(matchId)) return;
      if (payload.event === "stop" || payload.phase === "stopped") {
        stopCheerKind("fanfare");
        return;
      }
      if (payload.event !== "start" && payload.phase !== "playing") return;
      const sharing = Array.isArray(payload.sharing) ? payload.sharing.map(String) : [];
      if (!userId || !sharing.includes(String(userId))) return;
      playCheerSound("fanfare", cheerDelayMs(payload.play_at));
    },
    [matchId, userId]
  );

  useEffect(() => {
    setCheerIncoming(null);
  }, [matchId]);

  const {
    emit,
    recordingEvent,
    tableRecording,
    camerasRecording,
    recordingTables,
    desiredTables,
    captureState,
    cameraRoster,
    scoreWatchByTable,
    ackCameraRecording,
    publishScoreWatch,
  } = useChampionshipMatchSession(
      matchId,
      userId,
      onSnapshot,
      onCameras,
      phoneId,
      onCheer,
      onSync
    );

  filmingTablesRef.current = recordingTables || [];
  const ackCameraRecordingRef = useRef(ackCameraRecording);
  ackCameraRecordingRef.current = ackCameraRecording;
  const publishScoreWatchRef = useRef(publishScoreWatch);
  publishScoreWatchRef.current = publishScoreWatch;

  useScreenWakeLock(
    Boolean(matchId && (videoFeatureOn || recording || cameraDocked))
  );

  const isScorerRef = useRef(false);
  const emitRef = useRef(emit);
  emitRef.current = emit;

  useEffect(() => {
    if (!matchId || !userId) return undefined;

    const releaseHeldScore = () => {
      if (!isScorerRef.current) return;
      isScorerRef.current = false;
      const sockEmit = emitRef.current;
      if (typeof sockEmit === "function") {
        void sockEmit("cm:release-scorer");
      }
      try {
        const token = localStorage.getItem("token");
        const url = `${String(ip_back || "").replace(/\/?$/, "/") }api/competitions/matches/${encodeURIComponent(matchId)}/scorer/release`;
        void fetch(url, {
          method: "POST",
          headers: {
            Authorization: token || "",
            "Content-Type": "application/json",
          },
          body: "{}",
          keepalive: true,
        });
      } catch {
        void releaseChampionshipScorer(matchId).catch(() => {});
      }
    };

    const onPageHide = () => releaseHeldScore();
    window.addEventListener("pagehide", onPageHide);
    return () => {
      window.removeEventListener("pagehide", onPageHide);
      releaseHeldScore();
    };
  }, [matchId, userId]);

  useEffect(() => {
    if (!matchId) return undefined;
    let cancelled = false;
    fetchChampionshipMatch(matchId)
      .then((data) => {
        if (cancelled) return;
        setSnapshot(data);
        if (Array.isArray(data?.player_notes)) {
          setPlayerNotes(data.player_notes);
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err?.response?.data?.message || err.message);
      });
    return () => {
      cancelled = true;
    };
  }, [matchId]);

  const refreshPlayerNotes = useCallback(async () => {
    if (!matchId) return;
    try {
      const rows = await listChampionshipPlayerNotes(matchId);
      setPlayerNotes(Array.isArray(rows) ? rows : []);
    } catch {
      /* keep current */
    }
  }, [matchId]);

  useEffect(() => {
    if (snapshot?.player_notes && Array.isArray(snapshot.player_notes)) {
      setPlayerNotes(snapshot.player_notes);
    }
  }, [snapshot?.player_notes]);

  useEffect(() => {
    if (!videoFeatureOn && (tab === "camera" || tab === "control")) {
      setTab("score");
    }
  }, [videoFeatureOn, tab]);

  useEffect(() => {
    if (tab !== "lineup") setSwapPick(null);
  }, [tab]);

  const match = snapshot?.match;
  const matchDone = match?.status === "complete";
  const isFeltenFixture = String(match?.fixture_id || "").startsWith("felten:");
  const sheetPhotoAllowed = !matchDone || isFeltenFixture;
  const lineupSaved = Boolean(
    match?.lineup_saved_at ||
      ["home", "away"].some((side) =>
        (snapshot?.lineup?.[side] || []).some(
          (slot) => slot?.order_source === "sheet_photo"
        )
      )
  );
  const hasCapturedMedia = (snapshot?.media || []).some(
    (m) =>
      m &&
      (m.kind === "clip" ||
        m.kind === "photo" ||
        m.kind === "voice" ||
        Boolean(m.file_url))
  );
  /** Completed feuille: results-only chrome (no live camera / score input). */
  const resultsViewOnly = matchDone;
  const isScorer = Boolean(userId && match?.scorer_user_id === userId);
  isScorerRef.current = isScorer;
  const canModerateMatch = canManageChampionshipMatchPresence(user, {
    homeRole: readActiveHomeRole(user),
  });
  useEffect(() => {
    if (!canModerateMatch && tab === "control") setTab("score");
  }, [canModerateMatch, tab]);
  const isMatchModerator =
    userHasModeratorRole(user) ||
    Number(user?.type_user_id) === 1 ||
    user?.isSuperAdmin === true;
  const canLabelMatchScore =
    isMatchModerator ||
    canModerateMatch ||
    user?.hasCoachRole === true ||
    user?.hasAdminRole === true ||
    user?.hasCommitteeRole === true;
  const oppSide = snapshot?.opponent_pool?.side || null;
  const ourSide = useMemo(() => {
    if (oppSide === "home") return "away";
    if (oppSide === "away") return "home";
    const clubId = snapshot?.match?.club_id;
    const homeClub = snapshot?.fixture?.home?.club_id;
    const awayClub = snapshot?.fixture?.away?.club_id;
    if (clubId && homeClub && String(homeClub) === String(clubId)) return "home";
    if (clubId && awayClub && String(awayClub) === String(clubId)) return "away";
    return null;
  }, [
    oppSide,
    snapshot?.match?.club_id,
    snapshot?.fixture?.home?.club_id,
    snapshot?.fixture?.away?.club_id,
  ]);
  const playingThisMatch = Boolean(
    userId &&
      ourSide &&
      (snapshot?.lineup?.[ourSide] || []).some(
        (s) => s.user_id && String(s.user_id) === String(userId)
      )
  );
  const matchDay =
    Boolean(match?.match_day) ||
    (snapshot?.fixture?.match_date &&
      String(snapshot.fixture.match_date).slice(0, 10) ===
        `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, "0")}-${String(new Date().getDate()).padStart(2, "0")}`);
  const canSetOppAvailability =
    !matchDone &&
    (matchDay || match?.status === "live") &&
    (canModerateMatch || playingThisMatch);
  const scorerTaken = Boolean(match?.scorer_user_id && !isScorer);
  const rubber =
    snapshot?.rubbers?.find((r) => r.rubber_id === activeRubberId) ||
    snapshot?.rubbers?.find((r) => r.state === "live") ||
    null;
  const lastPoint = lastPointForRubber(snapshot?.points, rubber?.rubber_id);
  const myCamera = (snapshot?.cameras || []).find((c) =>
    cameraIsThisPhone(c, userId, phoneId)
  );
  scorecamRoleRef.current =
    normalizeCamPosition(angle) === "scorecam" ||
    cameraIsScoreCam(myCamera);
  scoreOnlyPhoneRef.current = myCamera
    ? cameraIsScoreCam(myCamera) && myCamera.record_video === false
    : normalizeCamPosition(angle) === "scorecam" && !scorecamRecordVideo;
  myTableRef.current = Number(myCamera?.table_number || tableNum);
  snapshotRef.current = snapshot;
  const cameraTableRubber = rubberMatchupForTable(
    snapshot,
    myTableRef.current
  )?.rubber;
  if (recording && cameraTableRubber?.state === "live") {
    pinnedRubberIdRef.current = cameraTableRubber.rubber_id;
  }
  clipMetaRef.current = {
    table: myTableRef.current,
    rubberId:
      pinnedRubberIdRef.current || cameraTableRubber?.rubber_id || null,
    angle:
      cameraIsScoreCam(myCamera) && myCamera.record_video === true
        ? SCORECAM_VIDEO_MEDIA_LABEL
        : normalizeCamPosition(myCamera?.angle_label || angle),
    mediaSyncAt: Number(cameraTableRubber?.media_sync_at) || 0,
  };
  // Scorecam OCR follows the rubber on this phone's table — not the feuille focus.
  const scorecamTableNumber = Number(myCamera?.table_number || tableNum);
  const scoreOcrRubber =
    rubberMatchupForTable(snapshot, scorecamTableNumber)?.rubber ||
    (rubber && Number(rubber.table_number) === scorecamTableNumber
      ? rubber
      : null);
  const clipCount = matchPlaybackMedia(snapshot?.media || []).filter(
    (m) => m.kind === "clip"
  ).length;
  const scorecamClipCount = (snapshot?.media || []).filter(
    (m) => m.kind === "clip" && isScorecamMedia(m)
  ).length;
  const photoCount = (snapshot?.media || []).filter((m) => m.kind === "photo").length;
  const liveScore = rubber?.set_scores || {};
  const liveHome = Number(liveScore.live_home) || 0;
  const liveAway = Number(liveScore.live_away) || 0;
  const awaitingNextSet = Boolean(liveScore.awaiting_next_set);
  const currentSetNumber =
    (Array.isArray(liveScore.completed) ? liveScore.completed.length : 0) + 1;
  const lastPointCurrentSet =
    lastPoint && Number(lastPoint.set_number) === currentSetNumber
      ? lastPoint
      : null;
  const setWinner =
    awaitingNextSet || rubber?.state === "done"
      ? liveHome === liveAway
        ? null
        : liveHome > liveAway
          ? "home"
          : "away"
      : null;
  const rubberLive = rubber?.state === "live";
  const rubberDone = rubber?.state === "done";
  const liveRubberOnSameTable = (snapshot?.rubbers || []).find(
    (r) =>
      rubber &&
      Number(r.table_number) === Number(rubber.table_number) &&
      r.state === "live" &&
      String(r.rubber_id) !== String(rubber.rubber_id)
  );
  const canScoreBase =
    isScorer && rubber && rubberLive && !rubberDone && !matchDone && !awaitingNextSet;
  const canScore = canScoreBase && !timeoutActive;
  const canToggleTimeout = canScoreBase;
  const canStartRubber =
    isScorer &&
    rubber &&
    !rubberLive &&
    !rubberDone &&
    !matchDone &&
    !liveRubberOnSameTable;
  const canAssignTable =
    !matchDone && !resultsViewOnly && (isScorer || canModerateMatch);
  const availableTables = championshipTablesFromSnapshot(snapshot, [
    tableNum,
    myCamera?.table_number,
  ]);
  const canStartSet =
    isScorer && rubberLive && awaitingNextSet && !rubberDone && !matchDone;
  const canUndo =
    isScorer &&
    Boolean(lastPointCurrentSet) &&
    rubberLive &&
    !matchDone &&
    !awaitingNextSet &&
    !timeoutActive &&
    (liveHome > 0 || liveAway > 0);
  const canMinusHome =
    canScore &&
    liveHome > 0 &&
    lastPointCurrentSet?.winner_side === "home";
  const canMinusAway =
    canScore &&
    liveAway > 0 &&
    lastPointCurrentSet?.winner_side === "away";
  const showStamps =
    Boolean(canToggleTimeout) &&
    !matchDone &&
    !sheetBrowsing &&
    !(isScorer && rubberLive && tab === "score");
  const focusedOnRubber = Boolean(rubber) && !sheetBrowsing && !resultsViewOnly;
  const scoreCameraArmed = scoreCameraOnTable(
    snapshot?.cameras,
    rubber?.table_number
  );
  const showClaimScore =
    focusedOnRubber &&
    !matchDone &&
    !isScorer &&
    !scorerTaken &&
    !scoreCameraArmed;
  const showScorerTaken =
    focusedOnRubber && !matchDone && !isScorer && scorerTaken;
  const showScoreCameraBlocksClaim =
    focusedOnRubber && !matchDone && !isScorer && scoreCameraArmed;
  const showCloseMatch =
    isScorer && !matchDone && sheetBrowsing && !rubberLive;
  const setsToWin = Number(match?.sets_to_win) || 3;
  const matchCameras = sortMatchCameras(snapshot?.cameras || [], userId, phoneId);
  /** Truthful REC: device-acked capture-state, or this phone's local recorder. */
  const tableIsFilming = (table) => {
    const n = Number(table);
    if (captureTableIsRecording(captureState, n)) return true;
    if (
      recording &&
      Number(myCamera?.table_number) === n &&
      (desiredTables || []).some((t) => Number(t) === n)
    ) {
      return true;
    }
    return Boolean(
      (recordingTables || []).some((t) => Number(t) === n)
    );
  };
  const tableIsStarting = (table) =>
    captureTableIsStarting(captureState, table) && !tableIsFilming(table);
  const filmingTableNumbers = (() => {
    const nums = new Set();
    for (const raw of [...(recordingTables || []), myCamera?.table_number]) {
      const n = Number(raw);
      if (tableIsFilming(n)) nums.add(n);
    }
    return [...nums].sort((a, b) => a - b);
  })();
  const thisPhoneTable = Number(myCamera?.table_number);
  const thisPhoneRecording = tableIsFilming(thisPhoneTable);
  /** A phone with its camera actually connected on this table. */
  const tableHasConnectedCamera = (table) => {
    const n = Number(table);
    if (n !== 1 && n !== 2) return false;
    const listed = (snapshot?.cameras || []).some(
      (cam) => Number(cam?.table_number) === n && cameraIsOnline(cam)
    );
    if (listed) return true;
    return (
      Boolean(myCamera) &&
      Number(myCamera.table_number) === n &&
      Boolean(streamRef.current)
    );
  };
  const cameraPhoneLabel = (cam) => {
    const thisPhone = cameraIsThisPhone(cam, userId, phoneId);
    const mine = cameraIsMine(cam, userId);
    const who = thisPhone
      ? t("cmThisPhoneCamera", "This phone")
      : mine
        ? t("cmYourOtherPhone", "Your other phone")
        : cam.device_label || t("cmConnectedCamera", "Camera connected");
    const online = cameraIsOnline(cam);
    const rec = tableIsFilming(cam.table_number);
    const matchup = rubberMatchupForTable(snapshot, cam.table_number);
    return [
      who,
      `${t("cmTable", "Table")} ${cam.table_number}`,
      cam.angle_label ? camPositionLabel(cam.angle_label, t) : "",
      matchup?.label || "",
      online
        ? t("cmPhoneConnected", "connected")
        : t("cmCameraOfflineShort", "offline"),
      !online
        ? ""
        : rec
          ? t("cmRecording", "Recording")
          : tableIsStarting(cam.table_number)
            ? t("cmRecordingStarting", "Starting…")
            : t("cmCameraWarmup", "warmup"),
    ]
      .filter(Boolean)
      .join(" · ");
  };
  const totalCameras = (snapshot?.cameras || []).length;
  const onlineCameras = (snapshot?.cameras || []).filter(
    (c) => c.online || c.socket_id
  ).length;
  const rosterOnline =
    cameraRoster?.online != null ? Number(cameraRoster.online) : onlineCameras;
  /** Room-wide REC for cheer ducking — only device-acked tables. */
  const roomRecording = Boolean(camerasRecording);
  const showCameraPreview = videoFeatureOn && hasPreviewStream;
  const setsHome = Number(liveScore.home_sets) || 0;
  const setsAway = Number(liveScore.away_sets) || 0;
  const boardScore = rubber?.detected_score;
  const boardReadSum =
    (Number(boardScore?.board_left) || 0) + (Number(boardScore?.board_right) || 0);
  const heldEntry = heldBoardRef.current;
  const heldBoard =
    heldEntry && String(heldEntry.rubberId) === String(rubber?.rubber_id)
      ? heldEntry.board
      : null;
  const heldSum = heldBoard
    ? (Number(heldBoard.board_left) || 0) + (Number(heldBoard.board_right) || 0)
    : 0;
  const boardBlank =
    !boardScore?.visible ||
    (boardReadSum === 0 &&
      !awaitingNextSet &&
      (heldSum > 0 || liveHome + liveAway > 0));
  if (boardScore?.visible && !boardBlank && rubber?.rubber_id) {
    heldBoardRef.current = { rubberId: rubber.rubber_id, board: boardScore };
  }
  const displayBoard = boardBlank && heldBoard ? heldBoard : boardScore;
  const boardFresh = isFreshBoardScore(boardScore);
  const boardSides = displayBoard?.visible
    ? boardAsHomeAway(displayBoard, liveScore)
    : null;
  const useBoardAsLive = Boolean(boardSides) && scoreCameraArmed && !isScorer;
  const shownHome = useBoardAsLive ? boardSides.home : liveHome;
  const shownAway = useBoardAsLive ? boardSides.away : liveAway;
  const shownSetsHome =
    useBoardAsLive && boardSides.gamesHome != null
      ? boardSides.gamesHome
      : setsHome;
  const shownSetsAway =
    useBoardAsLive && boardSides.gamesAway != null
      ? boardSides.gamesAway
      : setsAway;
  const soloCamera = Boolean(myCamera) && !isScorer && !scorerTaken;
  const boardSetOver =
    boardFresh &&
    boardSetIsOver(boardScore?.board_left, boardScore?.board_right);
  const boardClosingSet =
    soloCamera && boardSetOver && !awaitingNextSet && rubberLive;

  useEffect(() => {
    if (focusedOnRubber && tab === "lineup") setTab("score");
  }, [focusedOnRubber, tab]);

  useEffect(() => {
    if (!focusedOnRubber && tab === "replay") setTab("score");
  }, [focusedOnRubber, tab]);

  useEffect(() => {
    if (!resultsViewOnly) return;
    if (!sheetBrowsing) setSheetBrowsing(true);
    if (
      tab === "camera" ||
      tab === "control" ||
      (tab === "replay" && !hasCapturedMedia)
    ) {
      setTab("score");
    }
  }, [resultsViewOnly, hasCapturedMedia, sheetBrowsing, tab]);

  useEffect(() => {
    if (rubber?.rubber_id && rubber.rubber_id !== activeRubberId) {
      setActiveRubberId(rubber.rubber_id);
    }
  }, [rubber?.rubber_id, activeRubberId]);

  const selectRubber = (row) => {
    if (resultsViewOnly) return;
    setActiveRubberId(row.rubber_id);
    setSheetBrowsing(false);
    setScoreMode("input");
    setMoreTags(false);
    setTableEditRubberId(null);
    setTab("score");
  };

  const exitMatch = () => {
    setSheetBrowsing(true);
    setScoreMode("input");
    setMoreTags(false);
    setTableEditRubberId(null);
    setTab("score");
  };

  useEffect(() => {
    if (scoreMode !== "edit" || !rubber) return;
    setEditHome(String(liveHome));
    setEditAway(String(liveAway));
  }, [scoreMode, rubber?.rubber_id, liveHome, liveAway]);

  const uploadRecordingSegment = useCallback(
    async (blob, { announce, finalize, segmentTms, segmentIndex, pointId, audioBlob } = {}) => {
      if (!blob || blob.size < 256 || !matchId) return null;
      const mime = mimeRef.current || blob.type || "video/webm";
      const tms = Math.max(
        0,
        Math.round(
          segmentTms != null
            ? Number(segmentTms)
            : Number(segmentStartMsRef.current) || 0
        )
      );
      const idx =
        segmentIndex != null
          ? Number(segmentIndex)
          : Number(segmentIndexRef.current) || 0;
      const clipMeta = clipMetaRef.current;
      const file = new File(
        [blob],
        `table-${clipMeta.table}-seg${idx}.${recorderExt(mime)}`,
        { type: canonicalEvalVideoMime(blob.type || mime) }
      );
      const fields = {
        kind: "clip",
        table_number: clipMeta.table,
        rubber_id: clipMeta.rubberId || undefined,
        capture_id:
          captureIdRef.current || tableRecording?.capture_id || undefined,
        angle_label: clipMeta.angle,
        // Offset from capture/set start — keeps point seek sync across segments.
        t_ms: tms,
        captured_at: new Date().toISOString(),
        finalize_capture: finalize ? "1" : undefined,
        point_id: pointId || undefined,
      };
      const durationSec = Math.max(
        1,
        (Number(performance.now()) -
          Number(recordingOriginPerfRef.current || 0) -
          Number(tms || 0)) /
          1000
      );
      appendCaptureQos("encode_done", {
        bytes: blob.size,
        bitrate_effective: effectiveBitrateBps(blob.size, Math.min(durationSec, 90)),
        path: liveCaptureRef.current ? "webcodecs" : "mediarecorder",
        segment_index: idx,
        capture_id: fields.capture_id || null,
        container: recorderExt(mime),
      });
      if (!finalize) {
        appendCaptureQos("segment_rotate", {
          segment_index: idx,
          bytes: blob.size,
          capture_id: fields.capture_id || null,
        });
      }
      if (announce) {
        setMediaStatus(
          finalize
            ? t("cmMergingVideo", "Saving & stitching video…")
            : t("cmSavingVideo", "Saving video…")
        );
      }
      const snap = snapshotRef.current;
      const home = snap?.fixture?.home?.display_name;
      const away = snap?.fixture?.away?.display_name;
      const matchup = rubberMatchupForTable(snap, clipMeta.table);
      try {
        const out = await uploadOrKeepMatchClip({
          matchId,
          blob: file,
          fileName: file.name,
          mime: file.type,
          fields,
          audioBlob,
          recording: recordingDesiredRef.current,
          thumb: captureClipThumb(videoRef.current),
          info: {
            matchLabel: home && away ? `${home} – ${away}` : "",
            matchDate: snap?.fixture?.match_date || null,
            rubberLabel: matchup?.label || "",
            rubberCode: matchup?.code || "",
            segmentIndex: idx,
            durationMs: Math.round(Math.min(durationSec, 90) * 1000),
            fps: recordFpsRef.current,
            resolution: recordResolutionRef.current,
            quality: recordQualityRef.current,
          },
        });
        if (out?.held) {
          if (out.error) setError(clipUploadErrorText(out.error, t));
          setMediaStatus(
            out.reason === "rejected"
              ? t(
                  "cmClipKeptAfterError",
                  "Upload failed. The video is saved on this phone."
                )
              : uploadModeForMatchClip(fields, uploadModeRef.current) === "phone"
                ? t("cmClipKeptOnPhone", "Saved on this phone.")
                : t(
                    "cmClipSavedOnPhone",
                    "Saved on this phone. It uploads when Wi‑Fi is on."
                  )
          );
          return out;
        }
        const data = out?.data;
        if (announce) {
          setMediaStatus(
            data?.merged
              ? t("cmVideoMerged", "Video stitched and saved")
              : t("cmVideoSaved", "Video saved")
          );
        }
        return data;
      } catch (err) {
        setError(
          isLocalClipStorageFull(err)
            ? clipUploadErrorText(err, t)
            : mediaUploadErrorMessage(err, t)
        );
        if (announce) {
          setMediaStatus(
            t("cmVideoSaveFailed", "Could not save video — try again")
          );
        }
        return null;
      }
    },
    [matchId, t, tableRecording?.capture_id]
  );

  const enqueueSegmentUpload = useCallback((blob, opts) => {
    const job = () => uploadRecordingSegment(blob, opts);
    uploadChainRef.current = uploadChainRef.current
      .then(job)
      .catch(() => null);
    return uploadChainRef.current;
  }, [uploadRecordingSegment]);

  useEffect(
    () =>
      onChampionshipClipUploaded(({ matchId: uploadedMatchId, data }) => {
        if (String(uploadedMatchId) !== String(matchId)) return;
        if (data?.snapshot?.match) setSnapshot(data.snapshot);
        else if (data?.match) setSnapshot(data);
      }),
    [matchId]
  );

  useEffect(() => {
    setChampionshipRecordingActive(recording);
  }, [recording]);

  useEffect(() => () => setChampionshipRecordingActive(false), []);

  const stopLocalRecording = useCallback(
    async (upload) => {
      recordingDesiredRef.current = false;
      stopCaptureQosSampling();
      appendCaptureQos("recording_stop", {
        upload: Boolean(upload),
        capture_id: captureIdRef.current || null,
        segment_index: segmentIndexRef.current,
      });
      const live = liveCaptureRef.current;
      if (live) {
        liveCaptureRef.current = null;
        if (chunkWatchRef.current) {
          window.clearInterval(chunkWatchRef.current);
          chunkWatchRef.current = 0;
        }
        setRecording(false);
        const sidecar = sidecarAudioRef.current;
        sidecarAudioRef.current = null;
        let audioBlob = null;
        try {
          audioBlob = (await sidecar?.stop()) || null;
        } catch (err) {
          console.warn("Match audio stop failed:", err?.message || err);
        }
        let blob = null;
        try {
          blob = await live.stop();
        } catch (err) {
          console.warn("Live match capture stop failed:", err?.message || err);
        }
        if (!upload || !blob || blob.size < 256 || !matchId) {
          if (blob && blob.size < 256) {
            setMediaStatus(t("cmVideoTooShort", "Clip too short — not saved"));
          }
          pinnedRubberIdRef.current = null;
          return;
        }
        setMediaStatus(t("cmMergingVideo", "Saving & stitching video…"));
        try {
          await uploadChainRef.current.catch(() => null);
          const data = await uploadRecordingSegment(blob, {
            announce: true,
            finalize: true,
            segmentTms: segmentStartMsRef.current,
            segmentIndex: segmentIndexRef.current,
            audioBlob,
          });
          segmentIndexRef.current += 1;
          if (!data) {
            setMediaStatus(
              t("cmVideoSaveFailed", "Could not save video — try again")
            );
          }
        } catch (err) {
          setError(mediaUploadErrorMessage(err, t));
          setMediaStatus(t("cmVideoSaveFailed", "Could not save video — try again"));
        }
        pinnedRubberIdRef.current = null;
        return;
      }
      const rec = recorderRef.current;
      if (!rec) {
        setRecording(false);
        pinnedRubberIdRef.current = null;
        return;
      }
      await new Promise((resolve) => {
        let settled = false;
        const finish = () => {
          if (settled) return;
          settled = true;
          resolve();
        };
        rec.addEventListener?.("stop", finish, { once: true });
        rec.onstop = finish;
        try {
          if (rec.state === "recording") rec.stop();
          else finish();
        } catch {
          finish();
        }
        window.setTimeout(finish, 1500);
      });
      // Let the final dataavailable flush into chunksRef.
      await new Promise((r) => window.setTimeout(r, 80));
      recorderRef.current = null;
      if (chunkWatchRef.current) {
        window.clearInterval(chunkWatchRef.current);
        chunkWatchRef.current = 0;
      }
      if (recordDrawRafRef.current) {
        window.cancelAnimationFrame(recordDrawRafRef.current);
        recordDrawRafRef.current = 0;
      }
      releaseRecordStreamRef.current?.();
      setRecording(false);
      if (!upload || !chunksRef.current.length || !matchId) {
        chunksRef.current = [];
        pendingBytesRef.current = 0;
        pinnedRubberIdRef.current = null;
        return;
      }
      const mime = mimeRef.current || rec.mimeType || "video/webm";
      const blob = blobWithCanonicalEvalMime(
        new Blob(chunksRef.current, { type: mime })
      );
      chunksRef.current = [];
      pendingBytesRef.current = 0;
      if (blob.size < 256) {
        setMediaStatus(t("cmVideoTooShort", "Clip too short — not saved"));
        pinnedRubberIdRef.current = null;
        return;
      }
      setMediaStatus(t("cmMergingVideo", "Saving & stitching video…"));
      try {
        // Wait for mid-set segment uploads, then upload the last part + merge.
        await uploadChainRef.current.catch(() => null);
        const data = await uploadRecordingSegment(blob, {
          announce: true,
          finalize: true,
          segmentTms: segmentStartMsRef.current,
          segmentIndex: segmentIndexRef.current,
        });
        segmentIndexRef.current += 1;
        if (!data) {
          setMediaStatus(
            t("cmVideoSaveFailed", "Could not save video — try again")
          );
        }
      } catch (err) {
        setError(mediaUploadErrorMessage(err, t));
        setMediaStatus(t("cmVideoSaveFailed", "Could not save video — try again"));
      }
      pinnedRubberIdRef.current = null;
    },
    [matchId, t, uploadRecordingSegment]
  );

  const startCanvasRecordStream = useCallback(() => {
    const video = videoRef.current;
    const canvas = recordCanvasRef.current;
    if (!video || !canvas) return null;
    const trackSettings = readMatchTrackCapture(
      streamRef.current?.getVideoTracks?.()?.[0]
    );
    const srcW = video.videoWidth || trackSettings.width || 0;
    const srcH = video.videoHeight || trackSettings.height || 0;
    if (!srcW || !srcH || Math.min(srcW, srcH) < 712) return null;
    const { w, h, fps } = championshipRecordFrameSize(
      srcW,
      srcH,
      recordResolutionRef.current,
      recordFpsRef.current
    );
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    if (recordDrawRafRef.current) {
      window.cancelAnimationFrame(recordDrawRafRef.current);
      recordDrawRafRef.current = 0;
    }
    const draw = () => {
      if (video.readyState >= 2) {
        const z = cameraZoomRef.current;
        if (!cameraHardwareZoomRef.current && z > 1.001) {
          const crop = digitalCropSourceRect(
            video.videoWidth || w,
            video.videoHeight || h,
            z
          );
          ctx.drawImage(
            video,
            crop.sx,
            crop.sy,
            crop.sw,
            crop.sh,
            0,
            0,
            w,
            h
          );
        } else {
          ctx.drawImage(video, 0, 0, w, h);
        }
      }
      recordDrawRafRef.current = window.requestAnimationFrame(draw);
    };
    draw();
    try {
      const captured = canvas.captureStream(fps);
      return mixEvalAudioIntoCaptureStream(captured, streamRef.current);
    } catch {
      if (recordDrawRafRef.current) {
        window.cancelAnimationFrame(recordDrawRafRef.current);
        recordDrawRafRef.current = 0;
      }
      return null;
    }
  }, []);

  const attachMediaRecorder = useCallback(
    (recordStream) => {
      const quality = normalizeChampionshipQuality(
        recordQualityRef.current,
        "low"
      );
      const recordTrack = readMatchTrackCapture(
        recordStream.getVideoTracks?.()?.[0]
      );
      const videoBitRate = matchRecordBitRate(
        quality,
        recordTrack.width && recordTrack.height
          ? matchRecordTierForFrame(recordTrack.width, recordTrack.height)
          : recordResolutionRef.current
      );
      const mimeType = pickEvalVideoMimeType();
      let rec;
      try {
        rec = createEvalMediaRecorder(recordStream, quality, mimeType, undefined, {
          videoBitRate,
        });
      } catch {
        try {
          rec = createEvalMediaRecorder(recordStream, quality, undefined, undefined, {
            videoBitRate,
          });
        } catch {
          const videoBits = Math.max(250_000, videoBitRate);
          try {
            rec = new MediaRecorder(recordStream, {
              videoBitsPerSecond: videoBits,
              audioBitsPerSecond: 64_000,
              ...(mimeType ? { mimeType } : {}),
            });
          } catch {
            rec = new MediaRecorder(recordStream, {
              videoBitsPerSecond: videoBits,
            });
          }
        }
      }
      mimeRef.current = rec.mimeType || mimeType || "video/webm";
      rec.ondataavailable = (e) => {
        if (!e.data?.size) return;
        chunksRef.current.push(e.data);
        pendingBytesRef.current += e.data.size;
        if (
          recordingDesiredRef.current &&
          !rotatingChunkRef.current &&
          shouldRotateMatchRecordingChunk({
            elapsedMs: performance.now() - segmentOpenedAtRef.current,
            pendingBytes: pendingBytesRef.current,
          })
        ) {
          rotatingChunkRef.current = true;
          void rotateRecordingChunkRef.current?.();
        }
      };
      rec.onerror = () => {
        setMediaStatus(t("cmVideoSaveFailed", "Could not save video — try again"));
      };
      try {
        // A 1s timeslice makes Safari stack frames on one timestamp and then
        // hold the picture. Each segment is one continuous take.
        rec.start();
      } catch {
        /* recorder refused to start */
      }
      recorderRef.current = rec;
      return rec;
    },
    [t]
  );

  const releaseRecordStream = useCallback(() => {
    const extra = recordStreamRef.current;
    recordStreamRef.current = null;
    const sourceIds = new Set(
      (streamRef.current?.getTracks?.() || []).map((track) => track.id)
    );
    extra?.getTracks?.().forEach((track) => {
      if (sourceIds.has(track.id)) return;
      try {
        track.stop();
      } catch {
        /* ignore */
      }
    });
    if (recordDrawRafRef.current) {
      window.cancelAnimationFrame(recordDrawRafRef.current);
      recordDrawRafRef.current = 0;
    }
  }, []);

  releaseRecordStreamRef.current = releaseRecordStream;

  const openRecordStream = useCallback(async () => {
    releaseRecordStream();
    const source = streamRef.current;
    if (!source) return null;
    await pushLensZoomRef.current?.();
    lensZoomHoldRef.current += 1;
    try {
      const zoom = normalizeCameraZoom(cameraZoomRef.current);
      const sourceTrack = source.getVideoTracks?.()?.[0];
      if (sourceTrack) {
        const applied = await applyTrackZoom(sourceTrack, zoom);
        cameraHardwareZoomRef.current = Boolean(applied.hardware);
        setCameraHardwareZoom(Boolean(applied.hardware));
        if (applied.range) {
          cameraZoomRangeRef.current = applied.range;
          setCameraZoomRange(applied.range);
        }
      }
      const needsCanvas = matchRecordNeedsCanvas({
        zoom,
        hardwareZoom: cameraHardwareZoomRef.current,
      });
      if (!needsCanvas) {
        const cloned = cloneLiveMediaStream(source);
        if (cloned) {
          const cloneTrack = cloned.getVideoTracks?.()?.[0];
          const stamped = cloneTrack
            ? await applyTrackZoom(cloneTrack, zoom)
            : { hardware: false };
          if (cloneTrack && (stamped.hardware || zoom <= 1.001)) {
            recordStreamRef.current = cloned;
            return cloned;
          }
          cloned.getTracks?.().forEach((track) => {
            try {
              track.stop();
            } catch {
              /* ignore */
            }
          });
        }
        return source;
      }
      const canvasStream = startCanvasRecordStream();
      if (canvasStream) {
        recordStreamRef.current = canvasStream;
        return canvasStream;
      }
      return null;
    } finally {
      lensZoomHoldRef.current = Math.max(0, lensZoomHoldRef.current - 1);
      if (lensZoomAgainRef.current) void pushLensZoomRef.current?.();
    }
  }, [releaseRecordStream, startCanvasRecordStream]);

  const armChunkWatch = useCallback(() => {
    if (chunkWatchRef.current) {
      window.clearInterval(chunkWatchRef.current);
      chunkWatchRef.current = 0;
    }
    segmentOpenedAtRef.current = performance.now();
    chunkWatchRef.current = window.setInterval(() => {
      if (!recordingDesiredRef.current || rotatingChunkRef.current) return;
      if (
        !shouldRotateMatchRecordingChunk({
          elapsedMs: performance.now() - segmentOpenedAtRef.current,
          pendingBytes: pendingBytesRef.current,
        })
      ) {
        return;
      }
      rotatingChunkRef.current = true;
      void rotateRecordingChunkRef.current?.();
    }, 1000);
  }, []);

  const advanceMatchSegment = useCallback(() => {
    const finishedSegmentTms = segmentStartMsRef.current;
    const finishedSegmentIndex = segmentIndexRef.current;
    const syncAt = Number(mediaSyncAtRef.current) || Date.now();
    mediaSyncAtRef.current = syncAt;
    const nextStartMs = Math.max(
      finishedSegmentTms,
      atomicOffsetFromSync(syncAt)
    );
    segmentStartMsRef.current = nextStartMs;
    segmentIndexRef.current = finishedSegmentIndex + 1;
    recordingOriginPerfRef.current = performance.now() - nextStartMs;
    return { finishedSegmentTms, finishedSegmentIndex };
  }, []);

  const rotateRecordingChunk = useCallback(async () => {
    const live = liveCaptureRef.current;
    if (live) {
      if (!recordingDesiredRef.current) {
        rotatingChunkRef.current = false;
        return;
      }
      try {
        let audioBlob = null;
        try {
          audioBlob = (await sidecarAudioRef.current?.rotate()) || null;
        } catch (audioErr) {
          console.warn("Match audio segment failed:", audioErr?.message || audioErr);
        }
        const blob = await live.finishSegment();
        const { finishedSegmentTms, finishedSegmentIndex } = advanceMatchSegment();
        pendingBytesRef.current = 0;
        if (recordingDesiredRef.current) {
          armChunkWatch();
          setMediaStatus(
            t("cmRecordingChunk", "Recording — uploading previous segment…")
          );
        }
        if (blob && blob.size >= 256) {
          void enqueueSegmentUpload(blob, {
            announce: false,
            finalize: false,
            segmentTms: finishedSegmentTms,
            segmentIndex: finishedSegmentIndex,
            audioBlob,
          }).then(() => {
            if (recordingDesiredRef.current) {
              setMediaStatus(t("cmRecording", "Recording"));
            }
          });
        }
      } catch (err) {
        console.warn("Live match segment failed:", err?.message || err);
        liveCaptureRef.current = null;
        try {
          live.close();
        } catch {
          /* ignore */
        }
        try {
          await sidecarAudioRef.current?.stop();
        } catch {
          /* ignore */
        }
        sidecarAudioRef.current = null;
        recordingDesiredRef.current = false;
        setRecording(false);
        setMediaStatus(t("cmVideoSaveFailed", "Could not save video — try again"));
      } finally {
        rotatingChunkRef.current = false;
      }
      return;
    }
    const rec = recorderRef.current;
    if (!rec || !recordingDesiredRef.current) {
      rotatingChunkRef.current = false;
      return;
    }
    try {
      await new Promise((resolve) => {
        let settled = false;
        const finish = () => {
          if (settled) return;
          settled = true;
          resolve();
        };
        rec.addEventListener?.("stop", finish, { once: true });
        const prevOnStop = rec.onstop;
        rec.onstop = () => {
          try {
            prevOnStop?.();
          } catch {
            /* ignore */
          }
          finish();
        };
        try {
          if (rec.state === "recording") rec.stop();
          else finish();
        } catch {
          finish();
        }
        window.setTimeout(finish, 1500);
      });
      await new Promise((r) => window.setTimeout(r, 40));
      const mime = mimeRef.current || rec.mimeType || "video/webm";
      const blob = blobWithCanonicalEvalMime(
        new Blob(chunksRef.current, { type: mime })
      );
      chunksRef.current = [];
      pendingBytesRef.current = 0;
      recorderRef.current = null;
      const { finishedSegmentTms, finishedSegmentIndex } = advanceMatchSegment();

      if (recordingDesiredRef.current && streamRef.current) {
        const recordStream = await openRecordStream();
        if (recordStream) attachMediaRecorder(recordStream);
        armChunkWatch();
        setMediaStatus(
          t("cmRecordingChunk", "Recording — uploading previous segment…")
        );
      } else if (chunkWatchRef.current) {
        window.clearInterval(chunkWatchRef.current);
        chunkWatchRef.current = 0;
      }
      if (blob.size >= 256) {
        void enqueueSegmentUpload(blob, {
          announce: false,
          finalize: false,
          segmentTms: finishedSegmentTms,
          segmentIndex: finishedSegmentIndex,
        }).then(() => {
          if (recordingDesiredRef.current) {
            setMediaStatus(t("cmRecording", "Recording"));
          }
        });
      }
    } finally {
      rotatingChunkRef.current = false;
    }
  }, [
    advanceMatchSegment,
    armChunkWatch,
    attachMediaRecorder,
    enqueueSegmentUpload,
    openRecordStream,
    t,
  ]);

  rotateRecordingChunkRef.current = rotateRecordingChunk;

  const startLiveMatchCapture = useCallback(async () => {
    const video = videoRef.current;
    const stream = streamRef.current;
    if (!video || !stream) return null;
    const trackSettings = readMatchTrackCapture(stream.getVideoTracks?.()?.[0]);
    const srcW = video.videoWidth || trackSettings.width || 0;
    const srcH = video.videoHeight || trackSettings.height || 0;
    if (!srcW || !srcH) return null;
    const { w, h, fps } = championshipRecordFrameSize(
      srcW,
      srcH,
      recordResolutionRef.current,
      recordFpsRef.current
    );
    const quality = normalizeChampionshipQuality(recordQualityRef.current, "low");
    return createLiveH264Session({
      width: w,
      height: h,
      fps,
      bitrate: matchRecordBitRate(quality, matchRecordTierForFrame(w, h)),
      onEncodedBytes(total) {
        pendingBytesRef.current = total;
      },
      paint(ctx, width, height) {
        if (video.readyState < 2) return false;
        const z = cameraZoomRef.current;
        if (!cameraHardwareZoomRef.current && z > 1.001) {
          const crop = digitalCropSourceRect(
            video.videoWidth || width,
            video.videoHeight || height,
            z
          );
          ctx.drawImage(
            video,
            crop.sx,
            crop.sy,
            crop.sw,
            crop.sh,
            0,
            0,
            width,
            height
          );
        } else {
          ctx.drawImage(video, 0, 0, width, height);
        }
        return true;
      },
    });
  }, []);

  const startLocalRecording = useCallback(async () => {
    if (!videoFeatureOn || startLockRef.current) return;
    if (scoreOnlyPhoneRef.current) return;
    const leaveGen = cameraLeaveGenRef.current;
    startLockRef.current = true;
    try {
      if (recorderRef.current || liveCaptureRef.current) {
        await stopLocalRecording(true);
      }
      if (leaveGen !== cameraLeaveGenRef.current) return;
      const stream = streamRef.current;
      if (!stream) return;
      const liveTracks = stream.getVideoTracks?.() || [];
      if (
        liveTracks.length &&
        liveTracks.every((tr) => tr.readyState !== "live")
      ) {
        setMediaStatus(
          t(
            "cmCameraOffline",
            "Camera claimed but offline — reopen the camera phone."
          )
        );
        return;
      }
      chunksRef.current = [];
      pendingBytesRef.current = 0;
      if (!pinnedRubberIdRef.current && clipMetaRef.current.rubberId) {
        pinnedRubberIdRef.current = clipMetaRef.current.rubberId;
      }
      const syncAt =
        Number(mediaSyncAtRef.current) ||
        Number(tableRecording?.media_sync_at) ||
        Number(clipMetaRef.current.mediaSyncAt) ||
        Date.now();
      mediaSyncAtRef.current = syncAt;
      // Offset from the shared atomic epoch — not a local performance.now() zero.
      const startOffset = atomicOffsetFromSync(syncAt);
      segmentStartMsRef.current = startOffset;
      segmentIndexRef.current = 0;
      recordingOriginPerfRef.current = performance.now() - startOffset;
      uploadChainRef.current = Promise.resolve();
      recordingDesiredRef.current = true;
      const live = await startLiveMatchCapture();
      if (leaveGen !== cameraLeaveGenRef.current || !recordingDesiredRef.current) {
        live?.close();
        try {
          await sidecarAudioRef.current?.stop();
        } catch {
          /* ignore */
        }
        sidecarAudioRef.current = null;
        recordingDesiredRef.current = false;
        return;
      }
      if (live) {
        liveCaptureRef.current = live;
        const mic =
          stream.getAudioTracks?.().find((track) => track.readyState === "live") ||
          null;
        sidecarAudioRef.current = createSidecarAudioRecorder(mic);
        mimeRef.current = "video/mp4";
        armChunkWatch();
        setRecording(true);
        setMediaStatus(t("cmRecording", "Recording"));
        setCaptureQosContext({
          flow: "championship",
          session_id: String(matchId || ""),
          match_id: String(matchId || ""),
          capture_id:
            captureIdRef.current || tableRecording?.capture_id || null,
          run_id: resolveCaptureQosRunId(),
        });
        appendCaptureQos("recording_start", {
          path: "webcodecs",
          capture_id:
            captureIdRef.current || tableRecording?.capture_id || null,
        });
        startCaptureQosSampling(() => ({
          segment_index: segmentIndexRef.current,
          queue_depth: pendingBytesRef.current || 0,
        }));
        void ackCameraRecordingRef.current?.({
          captureId: captureIdRef.current || tableRecording?.capture_id,
          cameraId: myCamera?.camera_id,
          state: "recording",
        });
        return;
      }
      const recordStream = await openRecordStream();
      if (!recordStream) {
        recordingDesiredRef.current = false;
        setMediaStatus(
          t(
            "cmRecordNeeds720",
            "Could not start recording at the selected resolution. Reopen the camera and try again."
          )
        );
        void ackCameraRecordingRef.current?.({
          captureId: captureIdRef.current || tableRecording?.capture_id,
          cameraId: myCamera?.camera_id,
          state: "error",
          error: "record_stream_failed",
        });
        return;
      }
      attachMediaRecorder(recordStream);
      armChunkWatch();
      setRecording(true);
      setMediaStatus(t("cmRecording", "Recording"));
      setCaptureQosContext({
        flow: "championship",
        session_id: String(matchId || ""),
        match_id: String(matchId || ""),
        capture_id:
          captureIdRef.current || tableRecording?.capture_id || null,
        run_id: resolveCaptureQosRunId(),
      });
      appendCaptureQos("recording_start", {
        path: "mediarecorder",
        capture_id:
          captureIdRef.current || tableRecording?.capture_id || null,
      });
      startCaptureQosSampling(() => ({
        segment_index: segmentIndexRef.current,
        queue_depth: pendingBytesRef.current || 0,
      }));
      void ackCameraRecordingRef.current?.({
        captureId: captureIdRef.current || tableRecording?.capture_id,
        cameraId: myCamera?.camera_id,
        state: "recording",
      });
    } finally {
      startLockRef.current = false;
    }
  }, [
    armChunkWatch,
    attachMediaRecorder,
    matchId,
    openRecordStream,
    startLiveMatchCapture,
    stopLocalRecording,
    t,
    videoFeatureOn,
    tableRecording?.media_sync_at,
    tableRecording?.capture_id,
    myCamera?.camera_id,
  ]);

  // Heartbeat while this phone is actually recording so Control Room stays truthful.
  useEffect(() => {
    if (!recording || !myCamera) return undefined;
    const beat = () => {
      void ackCameraRecordingRef.current?.({
        captureId: captureIdRef.current || tableRecording?.capture_id,
        cameraId: myCamera.camera_id,
        state: "heartbeat",
      });
    };
    beat();
    const id = window.setInterval(beat, 7000);
    return () => window.clearInterval(id);
  }, [recording, myCamera?.camera_id, tableRecording?.capture_id]);

  // Publish scorecam HUD to the room so staff/spectators share the same status.
  useEffect(() => {
    if (!cameraIsScoreCam(myCamera)) return;
    const phase = scoreWatch?.phase || "idle";
    void publishScoreWatchRef.current?.({
      table_number: myCamera?.table_number || tableNum,
      rubber_id: scoreOcrRubber?.rubber_id || null,
      phase,
      detail: scoreWatch?.detail || "",
      score_text: scoreWatch?.scoreText || "",
    });
  }, [
    myCamera?.camera_id,
    myCamera?.table_number,
    myCamera?.angle_label,
    scoreWatch?.phase,
    scoreWatch?.detail,
    scoreWatch?.scoreText,
    scoreWatch?.n,
    tableNum,
    scoreOcrRubber?.rubber_id,
  ]);

  useEffect(() => {
    if (!videoFeatureOn || !recordingEvent) return;
    if (
      recordingEvent.action === "start" &&
      myCamera &&
      !scoreOnlyPhoneRef.current &&
      thisPhoneShouldRecordTable(recordingEvent, {
        phoneId,
        tableNumber: myCamera.table_number,
      })
    ) {
        captureIdRef.current = recordingEvent.capture_id || captureIdRef.current;
        if (Number(recordingEvent.media_sync_at) > 0) {
          mediaSyncAtRef.current = Number(recordingEvent.media_sync_at);
        }
        const startedRubber =
          recordingEvent.rubber_id ||
          rubberMatchupForTable(
            snapshotRef.current,
            recordingEvent.table_number
          )?.rubber?.rubber_id;
        if (startedRubber) pinnedRubberIdRef.current = startedRubber;
        let cancelled = false;
        const leaveGen = cameraLeaveGenRef.current;
        (async () => {
          try {
            if (!streamRef.current) {
              await openCameraPreview();
            } else {
              await applyZoomToStream(streamRef.current, cameraZoomRef.current);
            }
            if (cancelled || leaveGen !== cameraLeaveGenRef.current) return;
            await startLocalRecording();
            setCameraDocked(true);
            setCamLandscape(false);
            setMediaStatus(
              t("cmDockedRecording", "Recording — screen dimmed to save battery")
            );
          } catch (err) {
            if (!cancelled) {
              setMediaStatus(
                err?.message ||
                  t(
                    "cmCameraOffline",
                    "Camera claimed but offline — reopen the camera phone."
                  )
              );
            }
          }
        })();
        return () => {
          cancelled = true;
        };
    }
    if (recordingEvent.action === "stop") {
      if (
        !recordingEvent.table_number ||
        Number(recordingEvent.table_number) ===
          Number(myCamera?.table_number || tableNum)
      ) {
        // Stay docked between sets — only stop the recorder.
        // Leaving the camera screen releases the phone itself; don't put it
        // back into the docked-waiting state while that release is in flight.
        stopLocalRecording(true);
        if (releasingCamRef.current || skipDockAfterLeaveRef.current) {
          setCameraDocked(false);
          return undefined;
        }
        if (myCamera) {
          setMediaStatus(
            t(
              "cmDockedAfterEnd",
              "Recording ended — tap Start recording when you want to film again"
            )
          );
        } else {
          setCameraDocked(false);
        }
      }
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordingEvent, videoFeatureOn]);

  useEffect(() => {
    if (!cameraRoster?.reason) return;
    const table = cameraRoster.table_number;
    const matchup =
      table != null ? rubberMatchupForTable(snapshot, table) : null;
    if (cameraRoster.reason === "claimed") {
      setMediaStatus(
        matchup
          ? t(
              "cmCameraClaimedMatchup",
              "Camera on table {{n}} — filming {{matchup}}",
              { n: table, matchup: matchup.label }
            )
          : t("cmCameraClaimedTable", "Camera claimed on table {{n}}", {
              n: table,
            })
      );
    } else if (
      cameraRoster.reason === "released" ||
      cameraRoster.reason === "stopped"
    ) {
      setMediaStatus(
        table != null
          ? t("cmCameraRemoved", "Camera removed from table {{n}}", {
              n: table,
            })
          : t("cmCameraReleasedNotify", "A camera was removed from the match")
      );
    }
    // Only react to roster event identity changes, not every snapshot tick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    cameraRoster?.reason,
    cameraRoster?.table_number,
    cameraRoster?.total,
    cameraRoster?.online,
    cameraRoster?.user_id,
  ]);

  useEffect(() => {
    const mine = Number(myCamera?.table_number);
    const eventTable = Number(tableRecording?.table_number);
    if (!Number.isFinite(mine) || mine < 1 || mine !== eventTable) return;
    if (tableRecording?.capture_id) {
      captureIdRef.current = tableRecording.capture_id;
    }
    if (Number(tableRecording?.media_sync_at) > 0) {
      mediaSyncAtRef.current = Number(tableRecording.media_sync_at);
    }
  }, [
    tableRecording?.capture_id,
    tableRecording?.media_sync_at,
    tableRecording?.table_number,
    myCamera?.table_number,
  ]);

  useEffect(() => {
    if (awaitingNextSet || !rubberLive || matchDone) {
      setTimeoutActive(false);
      setPointStartArmed(false);
      pendingPointStartRef.current = null;
    }
  }, [awaitingNextSet, rubberLive, matchDone, rubber?.rubber_id]);

  useEffect(
    () => () => {
      const earlierUploads = uploadChainRef.current;
      // The page may unload any moment: park the last piece on the phone
      // behind this capture's earlier minutes, then let the line send it.
      const keepLeftover = (file, audioBlob, tms) => {
        const clipMeta = clipMetaRef.current;
        const captureId = captureIdRef.current;
        return Promise.resolve(earlierUploads)
          .catch(() => null)
          .then(() =>
            keepMatchClipOnPhone({
              matchId,
              blob: file,
              fileName: file.name,
              mime: file.type,
              audioBlob,
              fields: {
                kind: "clip",
                table_number: clipMeta.table,
                rubber_id: clipMeta.rubberId || undefined,
                capture_id: captureId || undefined,
                angle_label: clipMeta.angle,
                captured_at: new Date().toISOString(),
                t_ms: tms,
                finalize_capture: "1",
              },
            })
          );
      };
      const flushLeftoverClip = () => {
        if (releaseCamLockRef.current) return;
        const live = liveCaptureRef.current;
        if (live) {
          liveCaptureRef.current = null;
          const sidecar = sidecarAudioRef.current;
          sidecarAudioRef.current = null;
          const match = matchId;
          const table = clipMetaRef.current.table;
          const tms = Math.max(0, Math.round(Number(segmentStartMsRef.current) || 0));
          void Promise.resolve()
            .then(() => sidecar?.stop())
            .catch(() => null)
            .then((audioBlob) =>
              live.stop().then((blob) => ({ blob, audioBlob }))
            )
            .then(({ blob, audioBlob }) => {
              if (!match || !blob || blob.size < 256) return null;
              const file = new File([blob], `table-${table}.mp4`, {
                type: "video/mp4",
              });
              return keepLeftover(file, audioBlob, tms);
            })
            .catch(() => {});
          streamRef.current?.getTracks().forEach((track) => track.stop());
          return;
        }
        const rec = recorderRef.current;
        const chunks = chunksRef.current.slice();
        const mime = mimeRef.current || "video/webm";
        const match = matchId;
        const table = clipMetaRef.current.table;
        if (rec && rec.state === "recording") {
          try {
            rec.stop();
          } catch {
            /* ignore */
          }
        }
        streamRef.current?.getTracks().forEach((track) => track.stop());
        if (!match || !chunks.length) return;
        const blob = new Blob(chunks, { type: mime });
        if (blob.size < 256) return;
        const file = new File([blob], `table-${table}.${recorderExt(mime)}`, {
          type: blob.type || mime,
        });
        void keepLeftover(
          file,
          null,
          Math.max(0, Math.round(Number(segmentStartMsRef.current) || 0))
        ).catch(() => {});
      };
      // Leaving the match while this phone is the camera: stop the recording
      // and drop the claim. Skip the leftover flush if that path owns the file.
      void Promise.resolve(leaveRecordingCameraRef.current()).finally(
        flushLeftoverClip
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    if (tab !== "camera" || !hasPreviewStream) return;
    if (videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.play?.().catch(() => {});
    }
  }, [tab, camLandscape, hasPreviewStream]);

  useEffect(() => {
    const mq = window.matchMedia("(orientation: landscape)");
    const sync = () => {
      // Fullscreen landscape chrome only once the phone is the live camera —
      // setup (table + position) stays readable in landscape too.
      const armed =
        tab === "camera" &&
        videoFeatureOn &&
        (Boolean(streamRef.current) || hasPreviewStream || cameraSwitching);
      setCamLandscape(mq.matches && armed);
    };
    sync();
    mq.addEventListener?.("change", sync);
    window.addEventListener("orientationchange", sync);
    return () => {
      mq.removeEventListener?.("change", sync);
      window.removeEventListener("orientationchange", sync);
    };
  }, [tab, videoFeatureOn, hasPreviewStream, cameraSwitching]);

  const cameraArmed =
    tab === "camera" &&
    videoFeatureOn &&
    (hasPreviewStream || cameraSwitching);
  /** Immersive fullscreen score pad while the claimed scorer is in a live rubber. */
  const scorePadActive =
    isScorer &&
    rubberLive &&
    focusedOnRubber &&
    tab === "score" &&
    !cameraArmed &&
    !matchDone;
  const cameraFullscreen = Boolean(isMobileLive && cameraArmed);
  /** Immersive fullscreen score pad — same browser-chrome hide as live training. */
  const scorePadFullscreen = Boolean(isMobileLive && scorePadActive);
  const liveImmersive = cameraFullscreen || scorePadFullscreen;

  useLiveFullscreen(liveImmersive, {
    force: true,
    skipGestureSelector:
      'input, textarea, select, option, [contenteditable="true"], .cm-dock-dim, .cm-cam-settings, .cm-player-notes, .cm-note-dialog, .cm-review, .cm-scorepad__pts-input, .cm-scorepad__sheet, .pingflo-tour-overlay',
  });

  useEffect(() => {
    if (!cameraArmed && !scorePadActive) {
      document.documentElement.classList.remove("cm-immersive");
      return undefined;
    }
    document.documentElement.classList.add("cm-immersive");
    return () => document.documentElement.classList.remove("cm-immersive");
  }, [cameraArmed, scorePadActive]);

  useEffect(() => {
    if (liveImmersive) return undefined;
    exitLiveFullscreen();
    return undefined;
  }, [liveImmersive]);

  useEffect(() => {
    if ((!cameraArmed && !scorePadActive) || !isMobileLive) return;
    requestLiveFullscreen();
  }, [cameraArmed, scorePadActive, isMobileLive]);

  useEffect(() => {
    setScoreSidesManualFlip(false);
    heldBoardRef.current = null;
  }, [rubber?.rubber_id]);

  useEffect(() => {
    const preview = videoRef.current;
    if (!preview || !streamRef.current) return;
    if (preview.srcObject !== streamRef.current) {
      preview.srcObject = streamRef.current;
    }
    preview.muted = true;
    preview.playsInline = true;
    preview.play?.().catch(() => {});
  }, [cameraArmed, hasPreviewStream, tab]);

  const applySnap = (data) => {
    if (data?.match) setSnapshot(data);
    else if (data?.snapshot?.match) setSnapshot(data.snapshot);
  };

  useEffect(() => {
    if (myCamera?.angle_label) {
      setAngle(normalizeCamPosition(myCamera.angle_label));
    }
  }, [myCamera?.camera_id, myCamera?.angle_label]);

  // Keep feuille focus on the scorecam table's rubber so OCR + score stay aligned.
  useEffect(() => {
    if (tab === "camera") return;
    if (!cameraIsScoreCam(myCamera) || !scoreOcrRubber?.rubber_id) return;
    if (String(activeRubberId || "") === String(scoreOcrRubber.rubber_id)) return;
    setActiveRubberId(scoreOcrRubber.rubber_id);
  }, [
    activeRubberId,
    myCamera?.camera_id,
    myCamera?.angle_label,
    scoreOcrRubber?.rubber_id,
    tab,
  ]);

  const scoreRoiLive = normalizeRoi(myCamera?.score_roi);
  const scoreRoiKey = scoreRoiLive
    ? `${scoreRoiLive.x}:${scoreRoiLive.y}:${scoreRoiLive.w}:${scoreRoiLive.h}`
    : "";
  scoreWatchCtxRef.current = {
    scoreRoi: scoreRoiLive,
    ocrRubberId: scoreOcrRubber?.rubber_id || null,
    ocrTable: Number(
      scoreOcrRubber?.table_number || myCamera?.table_number || tableNum
    ),
    recording: Boolean(recording),
    matchId,
    setCloseStableSec,
    rubberMediaSyncAt: Number(scoreOcrRubber?.media_sync_at) || 0,
    tableCaptureId: tableRecording?.capture_id || null,
    tableMediaSyncAt: Number(tableRecording?.media_sync_at) || 0,
  };
  const watchScoreBoard =
    videoFeatureOn &&
    Boolean(myCamera) &&
    cameraIsScoreCam(myCamera) &&
    Boolean(scoreRoiLive) &&
    Boolean(scoreOcrRubber?.rubber_id) &&
    (recording || hasPreviewStream || cameraDocked);

  useEffect(() => {
    const scoreRoi = scoreWatchCtxRef.current?.scoreRoi || null;
    const isScoreCam = cameraIsScoreCam(myCamera);
    const ocrRubberId = scoreWatchCtxRef.current?.ocrRubberId;
    const ocrTable = scoreWatchCtxRef.current?.ocrTable;
    // Scorecam only: change-gate on calibrated ROI, then one AI frame.
    const watchBoard = watchScoreBoard;
    const scorecamDebugOn = () => {
      try {
        return String(localStorage.getItem("scorecam_debug") || "1") !== "0";
      } catch {
        return true;
      }
    };
    const scorecamConsole = (level, msg, extra) => {
      if (!scorecamDebugOn()) return;
      const line = `[scorecam] ${msg}`;
      if (extra != null) {
        // eslint-disable-next-line no-console
        console[level](line, extra);
      } else {
        // eslint-disable-next-line no-console
        console[level](line);
      }
    };
    if (!watchBoard) {
      if (isScoreCam) {
        const why = !scoreRoi
          ? "calibrate score ROI"
          : !ocrRubberId
            ? `no rubber on table ${Number(myCamera?.table_number || tableNum) || "?"}`
            : !videoFeatureOn
              ? "video off"
              : "start preview / dock";
        scorecamConsole("warn", `watch paused · ${why}`, {
          scoreRoi,
          ocrRubberId,
          ocrTable,
          videoFeatureOn,
          recording,
          hasPreviewStream,
          cameraDocked,
        });
        setScoreWatch((prev) => ({
          ...prev,
          phase: "idle",
          detail: why,
          n: prev.n + 1,
          gate: null,
          logs: [
            ...(prev.logs || []),
            { t: Date.now(), level: "warn", msg: `Watch paused · ${why}` },
          ].slice(-24),
        }));
      }
      return undefined;
    }
    let cancelled = false;
    let sampling = false;
    let ocrBusy = false;
    let queued = null;
    let tickCount = 0;
    let readCount = 0;
    let skipCount = 0;
    const gate = createBoardChangeGate();
    const pushScoreLog = (msg, level = "info", extra) => {
      if (cancelled) return;
      scorecamConsole(
        level === "err" ? "error" : level === "warn" ? "warn" : "debug",
        msg,
        extra
      );
      setScoreWatch((prev) => ({
        ...prev,
        logs: [...(prev.logs || []), { t: Date.now(), level, msg }].slice(-24),
      }));
    };
    const setScorePhase = (phase, detail = "", scoreText = "", gateInfo = null) => {
      if (cancelled) return;
      setScoreWatch((prev) => ({
        ...prev,
        phase,
        detail,
        scoreText: scoreText || prev.scoreText,
        n: prev.n + 1,
        gate: gateInfo != null ? gateInfo : prev.gate,
      }));
    };
    const applyOcrResult = (data, via) => {
      const board = data?.detected_score;
      const scoreText =
        board?.visible && board.board_left != null && board.board_right != null
          ? `${board.board_left}–${board.board_right}`
          : "";
scorecamConsole("debug", `${via} OCR result`, {
        skipped: data?.skipped,
        reason: data?.reason,
        needs_confirm: data?.needs_confirm,
        point_added: data?.point_added,
        expected: data?.expected,
        board,
        pending: data?.pending_score,
        message: data?.message,
      });
      if (board?.visible) {
        const rubberId = scoreWatchCtxRef.current?.ocrRubberId;
        if (rubberId) heldBoardRef.current = { rubberId, board };
      }
      if (data?.needs_confirm && data?.pending_score) {
        const p = data.pending_score;
        setScorePhase(
          "confirm",
          `${p.from_home}–${p.from_away} → ${p.home}–${p.away}`,
          scoreText
        );
        pushScoreLog(
          `${via} confirm ${p.from_home}–${p.from_away} → ${p.home}–${p.away}`,
          "warn",
          data
        );
        return;
      }
      if (data?.skipped) {
        const reason = String(data.reason || "skipped");
        if (reason === "not_visible") {
          setScorePhase("occlusion", "board not visible", scoreText);
          pushScoreLog(`${via} occlusion · board not visible`, "warn", data);
          return;
        }
        if (reason === "blank_read") {
          setScorePhase("blank", "ignored 0–0", scoreText);
          pushScoreLog(`${via} blank 0–0 ignored (kept last)`, "warn", data);
          return;
        }
        if (reason === "rate") {
          setScorePhase("watching", "rate limit", scoreText);
          pushScoreLog(`${via} rate-limited`, "info", data);
          return;
        }
        if (reason === "out_of_sequence") {
          const expected = data.expected ? ` · ${data.expected}` : "";
          setScorePhase("watching", `next point in order${expected}`, scoreText);
          pushScoreLog(`${via} not the next point${expected}`, "warn", data);
          return;
        }
        if (reason === "backwards") {
          setScorePhase("watching", "kept the score", scoreText);
          pushScoreLog(`${via} board went backwards · score kept`, "warn", data);
          return;
        }
        if (reason === "implausible") {
          setScorePhase("watching", "ignored impossible score", scoreText);
          pushScoreLog(`${via} ignored impossible score`, "warn", data);
          return;
        }
        setScorePhase("watching", reason, scoreText);
        pushScoreLog(`${via} skipped · ${reason}`, "info", data);
        return;
      }
      if (scoreText) {
        setScorePhase(
          "hit",
          data?.point_added ? `${scoreText} · point` : scoreText,
          scoreText
        );
        pushScoreLog(
          `${via} ${scoreText}${data?.point_added ? " · +1 point" : ""}`,
          "ok",
          data
        );
        return;
      }
      setScorePhase("watching", "no digits", "");
      pushScoreLog(`${via} no digits`, "warn", data);
    };
    const roi = scoreRoi;
    pushScoreLog(
      `Score AI watching · T${ocrTable || "?"} · rubber ${String(ocrRubberId || "").slice(0, 8)} · ROI ${roi ? `${(roi.w * 100).toFixed(0)}×${(roi.h * 100).toFixed(0)}% @${(roi.x * 100).toFixed(0)},${(roi.y * 100).toFixed(0)}` : "none"}`,
      "ok",
      { ocrRubberId, ocrTable, scoreRoi: roi, pollMs: UMPIRE_SCORE_POLL_MS }
    );
    setScorePhase("watching", "armed");
    /** Roll back the change-gate when a board_changed OCR miss would otherwise
     *  lock the fingerprint (e.g. read still says 1–0 while cards show 2–0). */
    const noteFailedRead = (data, readSeq, gateReason) => {
      if (data?.point_added || data?.needs_confirm) return false;
      if (gateReason === "baseline" || gateReason === "heartbeat") return false;
      const ok = gate.rejectRead(readSeq);
      if (ok) {
        scorecamConsole("debug", "gate rollback after OCR miss", {
          reason: data?.reason || "no_point",
          gateReason,
          readSeq,
        });
      }
      return ok;
    };
    const sendJob = async (job) => {
      const ctx = scoreWatchCtxRef.current || {};
      const rubberId = ctx.ocrRubberId;
      const tableNumber = ctx.ocrTable;
      const image = await blobToJpegBase64(job.blob);
      if (!image || cancelled) {
        pushScoreLog(
          `AI abort · empty JPEG (seq ${job.readSeq || 0})`,
          "err",
          { blobBytes: job.blob?.size, gateReason: job.gateReason }
        );
        gate.rejectRead(job.readSeq);
        return;
      }
      setScorePhase("reading", `AI · ${job.gateReason}`);
      pushScoreLog(
        `AI read · ${job.gateReason} · seq ${job.readSeq || 0} · ${(job.blob?.size / 1024).toFixed(1)}KB · lag ${Math.round(job.detectLagMs || 0)}ms`,
        "ok",
        {
          gateReason: job.gateReason,
          readSeq: job.readSeq,
          blobBytes: job.blob?.size,
          detectLagMs: job.detectLagMs,
          rubberId,
          tableNumber,
          imageChars: image.length,
        }
      );
      const syncAt =
        Number(mediaSyncAtRef.current) ||
        Number(ctx.tableMediaSyncAt) ||
        Number(ctx.rubberMediaSyncAt) ||
        0;
      if (syncAt > 0) mediaSyncAtRef.current = syncAt;
      const captureId = captureIdRef.current || ctx.tableCaptureId || null;
      if (captureId) captureIdRef.current = captureId;
      const rawTms = Math.max(
        0,
        syncAt > 0
          ? atomicOffsetFromSync(syncAt)
          : Math.round(
              performance.now() - (recordingOriginPerfRef.current || 0)
            )
      );
      const tMs = Math.max(0, Math.round(rawTms - (job.detectLagMs || 0)));
      scorecamConsole("debug", "OCR request timing", {
        tMs,
        rawTms,
        syncAt,
        captureId,
        detectLagMs: job.detectLagMs,
      });
      const offerScoreJump = (data) => {
        const pending = data?.pending_score;
        if (!data?.needs_confirm || !pending) return;
        const key = `${pending.from_home}-${pending.from_away}:${pending.home}-${pending.away}`;
        if (dismissedJumpRef.current === key) return;
        setScoreJump((cur) =>
          cur?.key === key
            ? cur
            : {
                key,
                rubberId,
                home: pending.home,
                away: pending.away,
                fromHome: pending.from_home,
                fromAway: pending.from_away,
              }
        );
      };
      const kickTableRecording = async (data) => {
        if (!data?.point_added) return;
        const snap = data.snapshot || snapshotRef.current;
        const row =
          rubberMatchupForTable(snap, tableNumber)?.rubber ||
          (snap?.rubbers || []).find(
            (r) => String(r.rubber_id) === String(rubberId)
          );
        if (row?.set_scores?.awaiting_next_set) return;
        const setN =
          (Array.isArray(row?.set_scores?.completed)
            ? row.set_scores.completed.length
            : 0) + 1;
        const key = `${rubberId}:${setN}`;
        if (scorecamRecordKeyRef.current === key) return;
        const already =
          recordingDesiredRef.current ||
          (filmingTablesRef.current || []).some(
            (n) => Number(n) === Number(tableNumber)
          );
        const tableFilms = (snap?.cameras || []).some(
          (cam) =>
            Number(cam.table_number) === Number(tableNumber) &&
            cam.record_video !== false
        );
        if (!tableFilms) return;
        if (already) {
          scorecamRecordKeyRef.current = key;
          return;
        }
        scorecamRecordKeyRef.current = key;
        if (row?.rubber_id || rubberId) {
          pinnedRubberIdRef.current = row?.rubber_id || rubberId;
        }
        const startAck = await emitRef.current("cm:record-start", {
          table_number: tableNumber,
          media_sync_at:
            Number(row?.media_sync_at) ||
            Number(mediaSyncAtRef.current) ||
            Date.now(),
        });
        if (startAck?.capture_id) captureIdRef.current = startAck.capture_id;
        pushScoreLog(
          `record start · T${tableNumber} set ${setN} · ${startAck?.ok ? "ok" : startAck?.message || "no ack"}`,
          startAck?.ok ? "ok" : "warn",
          startAck
        );
      };
      const ack = await emitRef.current("cm:score-ocr", {
        rubber_id: rubberId,
        image_jpeg_base64: image,
        capture_id: captureId,
        t_ms: tMs,
        table_number: tableNumber,
        set_close_stable_sec: ctx.setCloseStableSec,
      });
      if (cancelled) return;
      if (ack?.ok) {
        noteFailedRead(ack.data, job.readSeq, job.gateReason);
        offerScoreJump(ack.data);
        applyOcrResult(ack.data, "socket");
        if (ack.data?.snapshot) setSnapshot(ack.data.snapshot);
        try {
          await kickTableRecording(ack.data);
        } catch (err) {
          pushScoreLog(`record start · ${err?.message || "error"}`, "warn");
        }
        return;
      }
      pushScoreLog(
        `socket fail · ${ack?.message || ack?.status || "retry HTTP"}`,
        "warn",
        ack
      );
      let data = null;
      try {
        data = await postChampionshipScoreOcr(ctx.matchId, rubberId, {
          image_jpeg_base64: image,
          capture_id: captureId,
          t_ms: tMs,
          set_close_stable_sec: ctx.setCloseStableSec,
        });
      } catch (err) {
        gate.rejectRead(job.readSeq);
        setScorePhase("error", err?.message || "HTTP fail");
        pushScoreLog(`HTTP OCR fail · ${err?.message || "error"}`, "err", err);
        return;
      }
      if (cancelled) return;
      if (!data) {
        gate.rejectRead(job.readSeq);
        setScorePhase("error", "HTTP empty");
        pushScoreLog("HTTP OCR empty", "err");
        return;
      }
      noteFailedRead(data, job.readSeq, job.gateReason);
      offerScoreJump(data);
      applyOcrResult(data, "http");
      if (data?.detected_score?.visible && ctx.ocrRubberId) {
        heldBoardRef.current = {
          rubberId: ctx.ocrRubberId,
          board: data.detected_score,
        };
      }
      if (data?.snapshot) setSnapshot(data.snapshot);
      try {
        await kickTableRecording(data);
      } catch (err) {
        pushScoreLog(`record start · ${err?.message || "error"}`, "warn");
      }
    };
    const tick = async () => {
      if (cancelled || sampling) return;
      sampling = true;
      tickCount += 1;
      let job = null;
      try {
        const ctx = scoreWatchCtxRef.current || {};
        let source = null;
        let frameSrc = "none";
        // Prefer the live <video>. WebCodecs recording never paints recordCanvas
        // (it stays at the blank default 300×150), which froze detection after
        // the first point auto-started recording. Only use the canvas when its
        // MediaRecorder draw loop is actually running.
        const canvasLive =
          Boolean(recordDrawRafRef.current) &&
          Number(recordCanvasRef.current?.width) > 16 &&
          Number(recordCanvasRef.current?.height) > 16;
        if (videoRef.current?.videoWidth) {
          source = videoRef.current;
          frameSrc = `video ${source.videoWidth || 0}×${source.videoHeight || 0}`;
        } else if (canvasLive) {
          source = recordCanvasRef.current;
          frameSrc = `canvas ${source.width}×${source.height}`;
        }
        // Fingerprint every tick; the JPEG for Gemini is encoded only on a READ.
        const gray = source ? roiFingerprint(source, ctx.scoreRoi) : null;
        if (!source || cancelled) {
          if (!source) {
            skipCount += 1;
            const now = Date.now();
            if (now - scoreLogAtRef.current > 4000) {
              scoreLogAtRef.current = now;
              pushScoreLog(
                `No ROI frame · src ${frameSrc} · ticks ${tickCount}`,
                "warn",
                {
                  recording: ctx.recording,
                  hasVideo: Boolean(videoRef.current),
                  hasCanvas: Boolean(recordCanvasRef.current?.width),
                  scoreRoi: ctx.scoreRoi,
                  tickCount,
                  skipCount,
                }
              );
              setScorePhase("error", "no frame");
            }
          }
          return;
        }
        let detectLagMs = UMPIRE_SCORE_POLL_MS / 2;
        let gateReason = "direct";
        let readSeq = 0;
        if (gray) {
          const decision = gate.observe(gray);
          gateReason = decision.reason || "gate";
          const vsCommit = summarizeCompare(decision.vsCommit);
          const vsPrev = summarizeCompare(decision.vsPrev);
          const gateInfo = {
            reason: gateReason,
            read: Boolean(decision.read),
            vsCommit,
            vsPrev,
            lagMs: Number(decision.detect_lag_ms) || 0,
            seq: decision.read_seq || 0,
            src: frameSrc,
            ticks: tickCount,
            reads: readCount,
            skips: skipCount,
          };
          if (!decision.read) {
            skipCount += 1;
            const now = Date.now();
            const isMotion = decision.reason === "in_motion";
            const quietReason =
              decision.reason === "same_board" ||
              decision.reason === "empty";
            const logGap = isMotion ? 2500 : quietReason ? 5000 : 3000;
            const shouldLog =
              now - scoreLogAtRef.current > logGap ||
              (quietReason && now - scoreGateQuietAtRef.current > 5000);
            if (shouldLog) {
              scoreLogAtRef.current = now;
              if (quietReason) scoreGateQuietAtRef.current = now;
              const msg = isMotion
                ? `Motion · ${vsCommit || "settling"}`
                : `Gate skip · ${gateReason}${vsCommit ? ` · ${vsCommit}` : ""}`;
              pushScoreLog(msg, isMotion ? "warn" : "info", {
                ...gateInfo,
                decision,
              });
            }
            setScoreWatch((prev) => ({
              ...prev,
              phase: isMotion ? "motion" : prev.phase === "reading" ? prev.phase : "watching",
              detail: isMotion
                ? `motion · ${vsCommit || "settling"}`
                : `${gateReason}${vsCommit ? ` · ${vsCommit}` : ""}`,
              n: prev.n + 1,
              gate: gateInfo,
            }));
            return;
          }
          readCount += 1;
          readSeq = decision.read_seq || 0;
          if (decision.reason === "board_changed") {
            detectLagMs = Number(decision.detect_lag_ms) || detectLagMs;
          } else {
            detectLagMs = 0;
          }
          pushScoreLog(
            `Gate READ · ${gateReason}${vsCommit ? ` · ${vsCommit}` : ""} · src ${frameSrc}`,
            "ok",
            { ...gateInfo, detectLagMs }
          );
          setScorePhase(
            "watching",
            `${gateReason}${vsCommit ? ` · ${vsCommit}` : ""}`,
            "",
            gateInfo
          );
        } else {
          pushScoreLog(
            `Gate bypass · no fingerprint · src ${frameSrc}`,
            "warn",
            { frameSrc }
          );
        }
        const blob = await roiToJpegBlob(source, ctx.scoreRoi);
        if (!blob) {
          gate.rejectRead(readSeq);
          return;
        }
        if (cancelled) return;
        job = { blob, gateReason, detectLagMs, readSeq };
      } catch (err) {
        if (!cancelled) {
          setScorePhase("error", err?.message || "tick fail");
          pushScoreLog(`tick · ${err?.message || "error"}`, "err", err);
        }
      } finally {
        sampling = false;
      }
      if (!job || cancelled) return;
      if (ocrBusy) {
        const first = !queued;
        queued = job;
        if (first) {
          pushScoreLog(
            `Queued behind in-flight OCR · ${job.gateReason}`,
            "info",
            job
          );
        }
        return;
      }
      ocrBusy = true;
      let inflight = null;
      try {
        let current = job;
        while (current && !cancelled) {
          inflight = current;
          await sendJob(current);
          current = queued;
          queued = null;
        }
      } catch (err) {
        if (!cancelled) {
          if (inflight) gate.rejectRead(inflight.readSeq);
          setScorePhase("error", err?.message || "tick fail");
          pushScoreLog(`tick · ${err?.message || "error"}`, "err", err);
        }
      } finally {
        ocrBusy = false;
      }
    };
    const intervalId = window.setInterval(tick, UMPIRE_SCORE_POLL_MS);
    const firstId = window.setTimeout(tick, 120);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      window.clearTimeout(firstId);
      scorecamConsole("debug", "watch loop stopped", {
        ticks: tickCount,
        reads: readCount,
        skips: skipCount,
      });
      setScoreWatch((prev) => ({
        phase: "idle",
        n: 0,
        detail: "",
        scoreText: prev.scoreText || "",
        logs: (prev.logs || []).slice(-8),
        gate: null,
      }));
    };
  }, [matchId, scoreOcrRubber?.rubber_id, scoreRoiKey, watchScoreBoard]);

  const confirmScoreJump = async () => {
    if (!scoreJump) return;
    const pending = scoreJump;
    setScoreJump(null);
    const ack = await emit("cm:score-confirm", {
      rubber_id: pending.rubberId,
      home_score: pending.home,
      away_score: pending.away,
    });
    if (ack?.ok) {
      dismissedJumpRef.current = pending.key;
      if (ack.data?.snapshot) setSnapshot(ack.data.snapshot);
      return;
    }
    dismissedJumpRef.current = "";
    setScoreJump(pending);
    setError(ack?.message || t("cmScoreJumpFailed", "Could not update the score"));
  };

  const saveLineup = async (next) => {
    setBusy(true);
    setError("");
    try {
      applySnap(await saveChampionshipLineup(matchId, next));
    } catch (err) {
      setError(err?.response?.data?.message || err.message);
    } finally {
      setBusy(false);
    }
  };

  const fileToSheetJpeg = (file) =>
    new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        const width = img.naturalWidth || img.width;
        const height = img.naturalHeight || img.height;
        const scale = Math.min(1, 1600 / Math.max(width, height, 1));
        const w = Math.max(1, Math.round(width * scale));
        const h = Math.max(1, Math.round(height * scale));
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          URL.revokeObjectURL(url);
          reject(new Error(t("cmSheetPhotoReadFail", "Could not read that photo.")));
          return;
        }
        ctx.drawImage(img, 0, 0, w, h);
        URL.revokeObjectURL(url);
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error(t("cmSheetPhotoReadFail", "Could not read that photo.")));
              return;
            }
            blobToJpegBase64(blob).then(resolve).catch(reject);
          },
          "image/jpeg",
          0.82
        );
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error(t("cmSheetPhotoReadFail", "Could not read that photo.")));
      };
      img.src = url;
    });

  const onSheetPhoto = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !matchId || !sheetPhotoAllowed) return;
    setSheetPhotoBusy(true);
    setSheetPhotoNote("");
    setError("");
    try {
      const jpeg = await fileToSheetJpeg(file);
      if (!jpeg) {
        throw new Error(t("cmSheetPhotoReadFail", "Could not read that photo."));
      }
      const data = await postChampionshipSheetPhoto(matchId, jpeg);
      if (data?.snapshot) applySnap(data.snapshot);
      const applied = Array.isArray(data?.applied) ? data.applied : [];
      const line = (side) =>
        (data?.snapshot?.lineup?.[side] || [])
          .slice()
          .sort((a, b) => Number(a.seat) - Number(b.seat))
          .map((slot, index) => `${Number(slot.seat) || index + 1}. ${slot.display_name || "—"}`)
          .join("  ");
      const parts = [];
      if (applied.includes("home")) {
        parts.push(`${t("cmHome", "Home")}: ${line("home")}`);
      }
      if (applied.includes("away")) {
        parts.push(`${t("cmAway", "Away")}: ${line("away")}`);
      }
      if (applied.length && applied.length < 2) {
        parts.push(
          t(
            "cmSheetPhotoPartial",
            "The other team was not clear enough — those seats were left as they were."
          )
        );
      }
      const captured = data?.results_applied;
      if (captured?.finalized) {
        parts.push(
          t(
            "cmSheetPhotoResults",
            "Match finished on the sheet: final score {{home}}–{{away}}, sets {{homeSets}}–{{awaySets}}, {{rubbers}} matches updated with their set scores.",
            {
              home: captured.home_team_score,
              away: captured.away_team_score,
              homeSets: captured.home_sets_total,
              awaySets: captured.away_sets_total,
              rubbers: captured.rubbers,
            }
          )
        );
        setSheetBrowsing(true);
        setTab("score");
      } else if (Number(captured?.rubbers) > 0) {
        parts.push(
          t(
            "cmSheetPhotoPartialResults",
            "Saved {{rubbers}} matches already played on the sheet, with set scores where they were written.",
            { rubbers: captured.rubbers }
          )
        );
        setSheetBrowsing(true);
        setTab("score");
      }
      const doneNote =
        parts.join(" · ") || t("cmSheetPhotoDone", "Line-up updated from the photo.");
      setSheetPhotoNote(
        data?.felten_updated
          ? `${doneNote} ${t(
              "cmFeltenSheetPhotoCups",
              "Coupe Felten teams and matches were updated."
            )}`
          : doneNote
      );
    } catch (err) {
      setError(err?.response?.data?.message || err.message);
    } finally {
      setSheetPhotoBusy(false);
    }
  };

  const sheetPhotoAutoRef = useRef(false);
  useEffect(() => {
    if (sheetPhotoAutoRef.current) return;
    if (searchParams.get("sheetPhoto") !== "1") return;
    if (!matchId || !snapshot?.match || !sheetPhotoAllowed) return;
    sheetPhotoAutoRef.current = true;
    setTab("lineup");
    const timer = window.setTimeout(() => {
      sheetCameraRef.current?.click();
      const next = new URLSearchParams(searchParams);
      next.delete("sheetPhoto");
      if (!next.get("tab")) next.set("tab", "lineup");
      navigate(
        { search: next.toString() ? `?${next.toString()}` : "" },
        { replace: true }
      );
    }, 350);
    return () => window.clearTimeout(timer);
  }, [
    matchId,
    navigate,
    searchParams,
    sheetPhotoAllowed,
    snapshot?.match,
  ]);

  const canReorderOurFour =
    !matchDone && Boolean(ourSide) && (playingThisMatch || canModerateMatch);

  const onSwap = (side, a, b) => {
    if (matchDone) return;
    const freeOrder = canReorderOurFour && side === ourSide;
    if (!freeOrder) {
      const groups = snapshot?.tie_groups?.[side] || [];
      if (!seatsInSameTie(groups, a, b)) return;
    }
    saveLineup({ [side]: swapSeats(snapshot.lineup[side], a, b) });
  };

  const onRename = (side, seat, display_name) => {
    if (matchDone) return;
    const slots = snapshot.lineup[side].map((s) =>
      Number(s.seat) === Number(seat) ? { ...s, display_name } : s
    );
    saveLineup({ [side]: slots });
  };

  const claimScore = async () => {
    setBusy(true);
    setError("");
    try {
      const ack = await emit("cm:claim-scorer");
      if (ack?.ok && ack.data) applySnap(ack.data);
      else applySnap(await claimChampionshipScorer(matchId));
    } catch (err) {
      setError(err?.response?.data?.message || err.message);
    } finally {
      setBusy(false);
    }
  };

  const releaseScore = async () => {
    if (!matchId || !isScorer) return;
    setBusy(true);
    setError("");
    try {
      const ack = await emit("cm:release-scorer");
      if (ack?.ok && ack.data) applySnap(ack.data);
      else applySnap(await releaseChampionshipScorer(matchId));
    } catch (err) {
      setError(err?.response?.data?.message || err.message);
    } finally {
      setBusy(false);
    }
  };

  /** Free the live-score claim before this phone docks or records video. */
  const unlockScoreIfHeld = async () => {
    if (!matchId || !isScorer) return false;
    try {
      const ack = await emit("cm:release-scorer");
      if (ack?.ok && ack.data) applySnap(ack.data);
      else applySnap(await releaseChampionshipScorer(matchId));
      return true;
    } catch {
      return false;
    }
  };

  const focusLiveRubberOnTable = (tableNumber, snap) => {
    const live = (snap?.rubbers || snapshot?.rubbers || []).find(
      (r) =>
        Number(r.table_number) === Number(tableNumber) && r.state === "live"
    );
    if (live?.rubber_id) {
      setActiveRubberId(live.rubber_id);
      setSheetBrowsing(false);
    }
    return live || null;
  };

  const assignRubberTable = async (rubberId, tableNumber) => {
    const table = Number(tableNumber);
    if (!rubberId || !Number.isFinite(table) || table < 1) return false;
    setTableAssignBusy(true);
    setError("");
    try {
      const ack = await emit("cm:assign-table", {
        rubber_id: rubberId,
        table_number: table,
      });
      if (ack?.ok && ack.data) {
        applySnap(ack.data);
      } else if (ack && ack.ok === false) {
        setError(
          ack.message ||
            t("cmTableBusy", "That table already has a live match")
        );
        if (ack.live_rubber_id) {
          setActiveRubberId(ack.live_rubber_id);
          setSheetBrowsing(false);
        }
        return false;
      } else {
        applySnap(
          await assignChampionshipRubberTable(matchId, rubberId, {
            table_number: table,
          })
        );
      }
      setTableEditRubberId(null);
      setMediaStatus(
        t("cmMatchMovedToTable", "Match moved to table {{n}}", { n: table })
      );
      return true;
    } catch (err) {
      const status = err?.response?.status;
      const message =
        err?.response?.data?.message ||
        err.message ||
        t("cmTableBusy", "That table already has a live match");
      setError(message);
      if (status === 409) {
        const liveId = err?.response?.data?.live_rubber_id;
        if (liveId) {
          setActiveRubberId(liveId);
          setSheetBrowsing(false);
        }
      }
      return false;
    } finally {
      setTableAssignBusy(false);
    }
  };

  const startRubber = async (row, tableOverride) => {
    setActiveRubberId(row.rubber_id);
    setError("");
    const syncAt = Date.now();
    mediaSyncAtRef.current = syncAt;
    const table =
      tableOverride != null ? Number(tableOverride) : Number(row.table_number);
    try {
      const ack = await emit("cm:start-rubber", {
        rubber_id: row.rubber_id,
        media_sync_at: syncAt,
        ...(Number.isFinite(table) && table > 0
          ? { table_number: table }
          : {}),
      });
      if (ack?.ok && ack.data) {
        applySnap(ack.data);
      } else if (ack && ack.ok === false) {
        setError(
          ack.message ||
            t("cmTableBusy", "That table already has a live match")
        );
        if (ack.live_rubber_id) {
          setActiveRubberId(ack.live_rubber_id);
          setSheetBrowsing(false);
        } else {
          focusLiveRubberOnTable(
            Number.isFinite(table) && table > 0 ? table : row.table_number
          );
        }
        return;
      } else {
        applySnap(
          await startChampionshipRubber(matchId, row.rubber_id, {
            media_sync_at: syncAt,
            ...(Number.isFinite(table) && table > 0
              ? { table_number: table }
              : {}),
          })
        );
        // REST fallback — socket path auto-starts recording when cameras exist.
        const filmTable =
          Number.isFinite(table) && table > 0 ? table : row.table_number;
        if (videoFeatureOn && !tableIsFilming(filmTable)) {
          await emit("cm:record-start", {
            table_number: filmTable,
            media_sync_at: syncAt,
          });
        }
      }
      if (ack?.recording?.cameras > 0) {
        setMediaStatus(
          ack.recording.delivered > 0
            ? t(
                "cmRecordingStartedCams",
                "Recording started on {{count}} camera(s)",
                { count: ack.recording.delivered }
              )
            : t(
                "cmCamerasOffline",
                "Cameras claimed but offline — reopen the camera phone."
              )
        );
      }
    } catch (err) {
      const status = err?.response?.status;
      const message =
        err?.response?.data?.message ||
        err.message ||
        t("cmTableBusy", "That table already has a live match");
      setError(message);
      if (status === 409) {
        const liveId = err?.response?.data?.live_rubber_id;
        if (liveId) {
          setActiveRubberId(liveId);
          setSheetBrowsing(false);
        } else {
          focusLiveRubberOnTable(
            Number.isFinite(table) && table > 0 ? table : row.table_number
          );
        }
      }
    }
  };

  const startSet = async () => {
    if (!rubber || !canStartSet) return;
    setError("");
    const syncAt = Date.now();
    mediaSyncAtRef.current = syncAt;
    try {
      const ack = await emit("cm:start-set", {
        rubber_id: rubber.rubber_id,
        media_sync_at: syncAt,
      });
      if (ack?.ok && ack.data) {
        applySnap(ack.data);
      } else if (ack && ack.ok === false) {
        setError(ack.message || t("cmStartSetFailed", "Could not start the next set"));
        return;
      } else {
        applySnap(
          await startChampionshipSet(matchId, rubber.rubber_id, {
            media_sync_at: syncAt,
          })
        );
        if (videoFeatureOn && !tableIsFilming(rubber.table_number)) {
          await emit("cm:record-start", {
            table_number: rubber.table_number,
            media_sync_at: syncAt,
          });
        }
      }
      setScoreMode("input");
      if (ack?.recording?.cameras > 0) {
        setMediaStatus(
          ack.recording.delivered > 0
            ? t(
                "cmRecordingStartedCams",
                "Recording started on {{count}} camera(s)",
                { count: ack.recording.delivered }
              )
            : t(
                "cmCamerasOffline",
                "Cameras claimed but offline — reopen the camera phone."
              )
        );
      }
    } catch (err) {
      setError(err?.response?.data?.message || err.message);
    }
  };

  const scoreTiming = () => ({
    occurred_at: pendingPointStartRef.current || Date.now(),
    capture_id: captureIdRef.current || tableRecording?.capture_id || undefined,
  });

  const addMarker = async (tag) => {
    if (!rubber || !canToggleTimeout) return null;
    if (timeoutActive && tag !== "timeout_off") return null;
    if (!timeoutActive && tag === "timeout_off") return null;
    const occurredAt = Date.now();
    const timing = {
      occurred_at: occurredAt,
      capture_id: captureIdRef.current || tableRecording?.capture_id || undefined,
    };
    if (tag === "point_start") {
      pendingPointStartRef.current = occurredAt;
      setPointStartArmed(true);
    }
    const ack = await emit("cm:point", {
      rubber_id: rubber.rubber_id,
      tags: [tag],
      kind: "marker",
      ...timing,
    });
    if (ack?.ok && ack.data) {
      applySnap(ack.data);
      return ack.data;
    }
    try {
      const data = await postChampionshipPoint(matchId, rubber.rubber_id, {
        tags: [tag],
        kind: "marker",
        ...timing,
      });
      applySnap(data);
      return data;
    } catch (err) {
      setError(err?.response?.data?.message || err.message);
      return null;
    }
  };

  const toggleTimeout = async () => {
    if (!rubber || !canToggleTimeout || busy) return;
    setBusy(true);
    setError("");
    try {
      const table = Number(rubber.table_number);
      if (!timeoutActive) {
        // Timeout ON — stop cameras and mark the clock.
        if (videoFeatureOn) {
          await emit("cm:record-stop", { table_number: table });
        }
        await addMarker("timeout_on");
        pendingPointStartRef.current = null;
        setPointStartArmed(false);
        setTimeoutActive(true);
        setMediaStatus(
          t("cmTimeoutOn", "Timeout — recording paused")
        );
      } else {
        // Timeout OFF — new media clock + restart set recording.
        const syncAt = Date.now();
        const syncAck = await emit("cm:media-sync", {
          rubber_id: rubber.rubber_id,
          media_sync_at: syncAt,
        });
        if (syncAck?.ok && syncAck.data) applySnap(syncAck.data);
        else {
          applySnap(
            await syncChampionshipMediaClock(matchId, rubber.rubber_id, {
              media_sync_at: syncAt,
            })
          );
        }
        if (videoFeatureOn) {
          const startAck = await emit("cm:record-start", {
            table_number: table,
            media_sync_at: syncAt,
          });
          if (startAck?.capture_id) {
            captureIdRef.current = startAck.capture_id;
          }
        }
        setTimeoutActive(false);
        await addMarker("timeout_off");
        setMediaStatus(
          t("cmTimeoutOff", "Timeout over — recording restarted")
        );
      }
    } catch (err) {
      setError(err?.response?.data?.message || err.message || String(err));
    } finally {
      setBusy(false);
    }
  };

  const addPoint = async (winner_side) => {
    if (!rubber || !canScore) return;
    const timing = scoreTiming();
    const queuedTags = pendingPointTagsRef.current.slice();
    const queuedOutcome = readPointOutcome({ tags: queuedTags });
    pendingPointTagsRef.current = [];
    setPendingPointTags([]);
    pendingPointStartRef.current = null;
    setPointStartArmed(false);
    const payload = {
      rubber_id: rubber.rubber_id,
      winner_side,
      ...(queuedTags.length ? { tags: queuedTags } : {}),
      ...(queuedOutcome ? { outcome: queuedOutcome } : {}),
      ...timing,
    };
    const ack = await emit("cm:point", payload);
    if (ack?.ok && ack.data) applySnap(ack.data);
    else {
      applySnap(
        await postChampionshipPoint(matchId, rubber.rubber_id, {
          winner_side,
          ...(queuedTags.length ? { tags: queuedTags } : {}),
          ...(queuedOutcome ? { outcome: queuedOutcome } : {}),
          ...timing,
        })
      );
    }
  };

  const removePoint = async (winner_side) => {
    if (!rubber || !canScore) return;
    if (winner_side === "home" && liveHome <= 0) return;
    if (winner_side === "away" && liveAway <= 0) return;
    const ack = await emit("cm:remove-point", {
      rubber_id: rubber.rubber_id,
      winner_side,
    });
    if (ack?.ok && ack.data) {
      applySnap(ack.data);
      return;
    }
    if (ack && ack.ok === false && ack.message && ack.message !== "offline") {
      setError(ack.message);
      return;
    }
    try {
      applySnap(
        await removeChampionshipPoint(matchId, rubber.rubber_id, winner_side)
      );
    } catch (err) {
      setError(err?.response?.data?.message || err.message);
    }
  };

  const undo = async () => {
    if (!rubber || !canUndo) return;
    const ack = await emit("cm:undo", { rubber_id: rubber.rubber_id });
    if (ack?.ok && ack.data) applySnap(ack.data);
    else applySnap(await undoChampionshipPoint(matchId, rubber.rubber_id));
  };

  const saveEditScore = async () => {
    if (!rubber || !canScore) return;
    setBusy(true);
    setError("");
    try {
      const timing = scoreTiming();
      const ack = await emit("cm:set-score", {
        rubber_id: rubber.rubber_id,
        home_score: Number(editHome),
        away_score: Number(editAway),
        ...timing,
      });
      if (ack?.ok && ack.data) applySnap(ack.data);
      else {
        applySnap(
          await setChampionshipScore(matchId, rubber.rubber_id, {
            home_score: Number(editHome),
            away_score: Number(editAway),
            ...timing,
          })
        );
      }
      setScoreMode("input");
    } catch (err) {
      setError(err?.response?.data?.message || err.message);
    } finally {
      setBusy(false);
    }
  };

  const endRubber = async ({ home_sets, away_sets, sets_to_win } = {}) => {
    if (!rubber || !isScorer || matchDone) return;
    setBusy(true);
    setError("");
    try {
      const ack = await emit("cm:final-result", {
        rubber_id: rubber.rubber_id,
        home_sets,
        away_sets,
        sets_to_win,
      });
      if (ack?.ok && ack.data) applySnap(ack.data);
      else {
        applySnap(
          await setChampionshipFinalResult(matchId, rubber.rubber_id, {
            home_sets,
            away_sets,
            sets_to_win,
          })
        );
      }
      setScoreMode("input");
      setMediaStatus(t("cmRubberEnded", "Match result saved"));
    } catch (err) {
      setError(err?.response?.data?.message || err.message || String(err));
      throw err;
    } finally {
      setBusy(false);
    }
  };


  const stampOutcomeOn = async (point, outcomeId) => {
    if (!userId || timeoutActive || !point?.point_id) return;
    const next = togglePointOutcome(point, outcomeId);
    const body = {
      replace_tags: next.tags,
      outcome: next.label.outcome,
      label: next.label,
    };
    const ack = await emit("cm:tag", {
      point_id: point.point_id,
      ...body,
    });
    if (ack?.ok && ack.data) applySnap(ack.data);
    else {
      applySnap(
        await annotateChampionshipPoint(matchId, point.point_id, body)
      );
    }
  };

  const stampOutcome = async (outcomeId) => {
    if (!userId || timeoutActive) return;
    if (!lastPoint) {
      if (!canScore) return;
      setPendingPointTags((prev) => {
        const next = togglePointOutcome({ tags: prev }, outcomeId).tags;
        pendingPointTagsRef.current = next;
        return next;
      });
      return;
    }
    await stampOutcomeOn(lastPoint, outcomeId);
  };

  const stamp = async (tag) => {
    if (TIMELINE_MARKER_TAGS.includes(tag) && tag !== "let_serve") {
      await addMarker(tag);
      return;
    }
    if (timeoutActive) return;
    if (!lastPoint) {
      if (!canScore) return;
      setPendingPointTags((prev) => {
        const next = prev.includes(tag)
          ? prev.filter((x) => x !== tag)
          : [...prev, tag];
        pendingPointTagsRef.current = next;
        return next;
      });
      return;
    }
    const tags = lastPoint.tags?.includes(tag)
      ? lastPoint.tags.filter((x) => x !== tag)
      : [...(lastPoint.tags || []), tag];
    const ack = await emit("cm:tag", {
      point_id: lastPoint.point_id,
      replace_tags: tags,
    });
    if (ack?.ok && ack.data) applySnap(ack.data);
    else {
      applySnap(
        await annotateChampionshipPoint(matchId, lastPoint.point_id, {
          replace_tags: tags,
        })
      );
    }
  };

  const setReviewPointOutcome = useCallback(
    async (point, outcomeId) => {
      if (!point?.point_id || !matchId) return;
      const next = togglePointOutcome(point, outcomeId);
      const body = {
        replace_tags: next.tags,
        outcome: next.label.outcome,
        label: next.label,
      };
      const ack = await emit("cm:tag", {
        point_id: point.point_id,
        ...body,
      });
      if (ack?.ok && ack.data) applySnap(ack.data);
      else {
        applySnap(
          await annotateChampionshipPoint(matchId, point.point_id, body)
        );
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [emit, matchId]
  );

  const saveReviewPointScore = useCallback(
    async (point, scores) => {
      if (!point?.point_id || !matchId) return;
      applySnap(
        await annotateChampionshipPoint(matchId, point.point_id, {
          home_score: scores.home_score,
          away_score: scores.away_score,
          ...(scores.sets_home != null && scores.sets_away != null
            ? { sets_home: scores.sets_home, sets_away: scores.sets_away }
            : {}),
        })
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [matchId]
  );

  const stampReviewPoint = useCallback(
    async (rubberId, body) => {
      if (!matchId || !rubberId) {
        throw new Error("Match or rubber missing");
      }
      const data = await postChampionshipReviewPoint(matchId, rubberId, body);
      if (data?.snapshot?.match || data?.match) {
        applySnap(data);
      } else if (data?.point) {
        setSnapshot((prev) => {
          if (!prev?.match) return prev;
          const next = [...(prev.points || [])];
          const idx = next.findIndex(
            (p) => String(p.point_id) === String(data.point.point_id)
          );
          if (idx >= 0) next[idx] = data.point;
          else next.push(data.point);
          return { ...prev, points: next };
        });
      } else {
        throw new Error("Stamp did not return a saved point");
      }
      return data;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [matchId]
  );

  const sendReviewReaction = useCallback(
    async (rubberId, body) => {
      if (!matchId || !rubberId) return;
      applySnap(await postChampionshipReviewReaction(matchId, rubberId, body));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [matchId]
  );

  const clearRubberReviewPoints = useCallback(
    async (rubberId) => {
      if (!isMatchModerator || !matchId || !rubberId) return;
      const ok = window.confirm(
        t(
          "cmClearPointsConfirm",
          "Remove every point and label on this match? The video stays. The sheet score is unchanged."
        )
      );
      if (!ok) return;
      setBusy(true);
      setError("");
      try {
        applySnap(await clearChampionshipRubberPoints(matchId, rubberId));
      } catch (err) {
        setError(err?.response?.data?.message || err.message || String(err));
      } finally {
        setBusy(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isMatchModerator, matchId, t]
  );

  const probeReviewScore = useCallback(
    async (rubberId, payload) => {
      if (!matchId || !rubberId) return null;
      return probeChampionshipScore(matchId, rubberId, payload || {});
    },
    [matchId]
  );

  const fingerprintReviewScore = useCallback(
    async (rubberId, payload) => {
      if (!matchId || !rubberId) return null;
      return fingerprintChampionshipScore(matchId, rubberId, payload || {});
    },
    [matchId]
  );

  const scanReviewScore = useCallback(
    async (rubberId, payload) => {
      if (!matchId || !rubberId) return null;
      return scanChampionshipScoreSegment(matchId, rubberId, payload || {});
    },
    [matchId]
  );

  const labelReviewScore = useCallback(
    async (rubberId, payload) => {
      if (!matchId || !rubberId) return null;
      const data = await labelChampionshipScoreVideo(matchId, rubberId, payload || {});
      if (data?.snapshot) applySnap(data.snapshot);
      return data;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [matchId]
  );

  const readReviewScoreLabel = useCallback(
    async (rubberId, payload) => {
      if (!matchId || !rubberId) return null;
      const data = await getChampionshipScoreLabel(
        matchId,
        rubberId,
        payload?.media_id
      );
      if (data?.snapshot) applySnap(data.snapshot);
      return data;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [matchId]
  );

  const reassignReviewMedia = useCallback(
    async ({ media_ids, media_id, rubber_id }) => {
      if (!isMatchModerator || !matchId || !rubber_id) return;
      const ids = [
        ...new Set(
          (Array.isArray(media_ids) ? media_ids : [])
            .concat(media_id ? [media_id] : [])
            .filter(Boolean)
            .map(String)
        ),
      ];
      if (!ids.length) return;
      let lastSnap = null;
      for (const id of ids) {
        const ack = await emit("cm:reassign-media", {
          media_id: id,
          rubber_id,
        });
        if (!ack?.ok) {
          throw new Error(ack?.message || "Could not move video");
        }
        lastSnap = ack.data || lastSnap;
      }
      if (lastSnap) applySnap(lastSnap);
      // Open the roster match that now owns the file. On a finished feuille
      // the replay tab is closed, so the review dialog has to follow the file.
      const targetId = String(rubber_id);
      pendingMoveRef.current = { mediaId: ids[0], rubberId: targetId };
      setMovedMediaId(ids[0]);
      setReplayRubberFocusId(targetId);
      setReviewRubberId(targetId);
      if (!resultsViewOnly) {
        setActiveRubberId(targetId);
        setSheetBrowsing(false);
        setTab("replay");
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [emit, isMatchModerator, matchId, resultsViewOnly]
  );

  useEffect(() => {
    const pending = pendingMoveRef.current;
    if (!pending) return;
    const row = (snapshot?.media || []).find(
      (item) => String(item?.media_id) === String(pending.mediaId)
    );
    if (!row?.rubber_id || String(row.rubber_id) !== String(pending.rubberId)) {
      return;
    }
    pendingMoveRef.current = null;
    setReplayRubberFocusId(String(pending.rubberId));
    setReviewRubberId(String(pending.rubberId));
  }, [snapshot]);

  const saveComment = async () => {
    if (!lastPoint || !comment.trim()) return;
    const ack = await emit("cm:comment", {
      point_id: lastPoint.point_id,
      comment,
    });
    if (ack?.ok && ack.data) applySnap(ack.data);
    else {
      applySnap(
        await annotateChampionshipPoint(matchId, lastPoint.point_id, { comment })
      );
    }
    setComment("");
  };

  const saveSchema = async (schema_key) => {
    if (!lastPoint) return;
    const ack = await emit("cm:schema", {
      point_id: lastPoint.point_id,
      schema_key,
    });
    if (ack?.ok && ack.data) applySnap(ack.data);
    else {
      applySnap(
        await annotateChampionshipPoint(matchId, lastPoint.point_id, {
          schema_key,
        })
      );
    }
  };

  const stopCanvasRecordLoop = useCallback(() => {
    if (recordDrawRafRef.current) {
      window.cancelAnimationFrame(recordDrawRafRef.current);
      recordDrawRafRef.current = 0;
    }
  }, []);

  const applyZoomToStream = useCallback(async (stream, nextZoom = cameraZoomRef.current) => {
    const track = stream?.getVideoTracks?.()?.[0];
    const range = readTrackZoomRange(track);
    cameraZoomRangeRef.current = range;
    setCameraZoomRange(range);
    const desired = normalizeCameraZoom(nextZoom);
    if (!track) {
      cameraHardwareZoomRef.current = false;
      setCameraHardwareZoom(false);
      return { hardware: false, zoom: desired, range: null };
    }
    const result = await applyTrackZoom(track, desired);
    cameraHardwareZoomRef.current = result.hardware;
    setCameraHardwareZoom(result.hardware);
    if (result.range) {
      cameraZoomRangeRef.current = result.range;
      setCameraZoomRange(result.range);
    }
    return result;
  }, []);

  const pushLensZoom = useCallback(() => {
    if (lensZoomHoldRef.current > 0 || lensZoomQueuedRef.current) {
      lensZoomAgainRef.current = true;
      return lensZoomTailRef.current;
    }
    lensZoomQueuedRef.current = true;
    const job = lensZoomTailRef.current
      .catch(() => null)
      .then(async () => {
        let result = null;
        try {
          if (lensZoomHoldRef.current > 0) return null;
          let zoom = null;
          do {
            lensZoomAgainRef.current = false;
            zoom = cameraZoomRef.current;
            const stream = streamRef.current;
            if (!stream) return null;
            result = await applyZoomToStream(stream, zoom);
          } while (
            lensZoomHoldRef.current === 0 &&
            (lensZoomAgainRef.current || cameraZoomRef.current !== zoom)
          );
          return result;
        } finally {
          lensZoomQueuedRef.current = false;
        }
      });
    lensZoomTailRef.current = job;
    return job;
  }, [applyZoomToStream]);

  pushLensZoomRef.current = pushLensZoom;

  const setCaptureZoom = useCallback(
    (rawZoom, { persist = true } = {}) => {
      const next = clampCameraZoom(
        rawZoom,
        logicalZoomRange(cameraZoomRangeRef.current)
      );
      cameraZoomRef.current = next;
      setCameraZoom(next);
      if (persist) {
        writeCameraPrefs(matchId, {
          facingMode: cameraFacingRef.current,
          deviceId: cameraDeviceRef.current,
          zoom: next,
        });
      }
      void pushLensZoomRef.current?.();
      return next;
    },
    [matchId]
  );

  useEffect(() => {
    // Pinch on the live preview, or the full-screen calibrate overlay.
    if (!hasPreviewStream) return undefined;
    const stage = cameraSettingsOpen
      ? settingsStageRef.current
      : previewStageRef.current;
    if (!stage) return undefined;
    const pinch = pinchRef.current;
    const rangeForPinch = () => logicalZoomRange(cameraZoomRangeRef.current);

    const onPointerDown = (event) => {
      pinch.pointers.set(event.pointerId, event);
      if (pinch.pointers.size === 2) {
        const [a, b] = [...pinch.pointers.values()];
        pinch.startDistance = pinchDistance(a, b);
        pinch.startZoom = cameraZoomRef.current;
        try {
          stage.setPointerCapture?.(event.pointerId);
        } catch {
          /* ignore */
        }
      }
    };
    const onPointerMove = (event) => {
      if (!pinch.pointers.has(event.pointerId)) return;
      pinch.pointers.set(event.pointerId, event);
      if (pinch.pointers.size !== 2 || !(pinch.startDistance > 0)) return;
      event.preventDefault();
      const [a, b] = [...pinch.pointers.values()];
      setCaptureZoom(
        nextPinchZoom(
          pinch.startZoom,
          pinch.startDistance,
          pinchDistance(a, b),
          rangeForPinch()
        ),
        { persist: false }
      );
    };
    const onPointerUp = (event) => {
      pinch.pointers.delete(event.pointerId);
      if (pinch.pointers.size < 2) {
        pinch.startDistance = 0;
        setCaptureZoom(cameraZoomRef.current);
      }
    };
    const onWheel = (event) => {
      if (!event.ctrlKey && !event.metaKey) return;
      event.preventDefault();
      const factor = event.deltaY < 0 ? 1.08 : 1 / 1.08;
      setCaptureZoom(cameraZoomRef.current * factor);
    };
    const onDblClick = () => {
      setCaptureZoom(DEFAULT_CAMERA_ZOOM);
    };

    stage.addEventListener("pointerdown", onPointerDown);
    stage.addEventListener("pointermove", onPointerMove, { passive: false });
    stage.addEventListener("pointerup", onPointerUp);
    stage.addEventListener("pointercancel", onPointerUp);
    stage.addEventListener("wheel", onWheel, { passive: false });
    stage.addEventListener("dblclick", onDblClick);
    return () => {
      stage.removeEventListener("pointerdown", onPointerDown);
      stage.removeEventListener("pointermove", onPointerMove);
      stage.removeEventListener("pointerup", onPointerUp);
      stage.removeEventListener("pointercancel", onPointerUp);
      stage.removeEventListener("wheel", onWheel);
      stage.removeEventListener("dblclick", onDblClick);
      pinch.pointers.clear();
    };
  }, [hasPreviewStream, cameraArmed, cameraSettingsOpen, setCaptureZoom]);

  useEffect(() => {
    if (!cameraSettingsOpen || !hasPreviewStream) return;
    const preview = settingsPreviewRef.current;
    if (!preview || !streamRef.current) return;
    preview.srcObject = streamRef.current;
    preview.muted = true;
    preview.playsInline = true;
    preview.play?.().catch(() => {});
  }, [cameraSettingsOpen, hasPreviewStream]);

  const attachPreview = (stream, { resetZoom = false } = {}) => {
    streamRef.current = stream;
    setHasPreviewStream(Boolean(stream));
    if (videoRef.current) {
      videoRef.current.srcObject = stream;
      videoRef.current.play?.().catch(() => {});
    }
    if (settingsPreviewRef.current) {
      settingsPreviewRef.current.srcObject = stream;
      settingsPreviewRef.current.play?.().catch(() => {});
    }
    if (resetZoom) {
      cameraZoomRef.current = DEFAULT_CAMERA_ZOOM;
      setCameraZoom(DEFAULT_CAMERA_ZOOM);
    }
    const syncOpenedCamera = () => {
      if (streamRef.current !== stream) return;
      void applyZoomToStream(stream, cameraZoomRef.current);
      const latest =
        stream?.getVideoTracks?.()?.[0]?.getSettings?.() || {};
      const actualId = latest.deviceId ? String(latest.deviceId) : "";
      const actualFacing = normalizeWebEvalFacingMode(
        latest.facingMode,
        cameraFacingRef.current
      );
      if (actualFacing && actualFacing !== cameraFacingRef.current) {
        setCameraFacingMode(actualFacing);
        cameraFacingRef.current = actualFacing;
      }
      if (actualId && actualId !== cameraDeviceRef.current) {
        setCameraDeviceId(actualId);
        cameraDeviceRef.current = actualId;
      }
      writeCameraPrefs(matchId, {
        facingMode: cameraFacingRef.current,
        deviceId: cameraDeviceRef.current,
        zoom: cameraZoomRef.current,
      });
    };
    syncOpenedCamera();
    const videoTrack = stream?.getVideoTracks?.()?.[0];
    videoTrack?.addEventListener?.("unmute", syncOpenedCamera, { once: true });
    const previewEl = settingsPreviewRef.current || videoRef.current;
    previewEl?.addEventListener?.("loadedmetadata", syncOpenedCamera, {
      once: true,
    });
    window.setTimeout(syncOpenedCamera, 120);
  };

  const stopPreviewTracks = () => {
    stopCanvasRecordLoop();
    const stream = streamRef.current;
    if (!stream) return;
    stopStreamTracks(stream);
    streamRef.current = null;
    setHasPreviewStream(false);
    if (videoRef.current) videoRef.current.srcObject = null;
    if (settingsPreviewRef.current) settingsPreviewRef.current.srcObject = null;
  };

  const releaseCam = useCallback(async () => {
    if (releaseCamLockRef.current) return;
    releaseCamLockRef.current = true;
    releasingCamRef.current = true;
    cameraLeaveGenRef.current += 1;
    const table = myCamera?.table_number || tableNum;
    const plan = planCameraLeave({
      cameras: snapshot?.cameras,
      isThisPhone: (cam) => cameraIsThisPhone(cam, userId, phoneId),
      recording,
      tableRecording,
    });
    setCameraSettingsOpen(false);
    setCameraDocked(false);
    if (plan.release || plan.stopLocal || recording) {
      skipDockAfterLeaveRef.current = true;
    }
    try {
      persistCameraClaim(matchId, null);
      if (recording || plan.stopLocal) {
        await stopLocalRecording(true);
      }
      if (plan.stopTable) {
        try {
          await emit("cm:record-stop", {
            table_number: plan.table || table,
          });
        } catch {
          /* room stop is best-effort; the local recorder is already closed */
        }
      }
      stopPreviewTracks();
      const ack = await emit("cm:release-camera", { table_number: table });
      if (ack?.ok && ack.data) applySnap(ack.data);
      else {
        applySnap(
          await releaseChampionshipCamera(matchId, {
            table_number: table,
            device_id: phoneId,
          })
        );
      }
      setMediaStatus(
        t("cmCameraRemoved", "Camera removed from table {{n}}", { n: table })
      );
    } catch (err) {
      setError(err?.response?.data?.message || err.message);
    } finally {
      releasingCamRef.current = false;
      releaseCamLockRef.current = false;
    }
  }, [
    emit,
    matchId,
    myCamera?.table_number,
    phoneId,
    recording,
    snapshot?.cameras,
    stopLocalRecording,
    t,
    tableNum,
    tableRecording,
    userId,
  ]);

  // Leaving the camera screen (another tab, the feuille, or the match) while
  // this phone is filming: save the clip, end the table recording when this
  // was the only live camera, and take the phone off that table.
  leaveRecordingCameraRef.current = async () => {
    const plan = planCameraLeave({
      cameras: snapshot?.cameras,
      isThisPhone: (cam) => cameraIsThisPhone(cam, userId, phoneId),
      recording,
      tableRecording,
    });
    if (!plan.release && !plan.stopLocal) return;
    cameraLeaveGenRef.current += 1;
    setCameraSettingsOpen(false);
    setCameraDocked(false);
    if (plan.release) {
      await releaseCam();
      return;
    }
    await stopLocalRecording(true);
    stopPreviewTracks();
  };

  useEffect(() => {
    const prev = cameraTabRef.current;
    cameraTabRef.current = tab;
    if (prev === "camera" && tab !== "camera") {
      void leaveRecordingCameraRef.current();
    }
  }, [tab]);

  // If the live track dies, drop the claim so every client updates the count.
  useEffect(() => {
    const stream = streamRef.current;
    if (!stream || !myCamera) return undefined;
    const tracks = stream.getVideoTracks?.() || [];
    const onEnded = () => {
      void releaseCam();
    };
    tracks.forEach((tr) => tr.addEventListener("ended", onEnded));
    return () => {
      tracks.forEach((tr) => tr.removeEventListener("ended", onEnded));
    };
  }, [hasPreviewStream, myCamera?.camera_id, releaseCam]);

  const refreshCameraOptions = useCallback(async () => {
    try {
      if (!navigator?.mediaDevices?.enumerateDevices) {
        setCameraOptions(buildWebEvalCameraOptions([]));
        return;
      }
      const devices = await navigator.mediaDevices.enumerateDevices();
      setCameraOptions(buildWebEvalCameraOptions(devices));
    } catch {
      setCameraOptions(buildWebEvalCameraOptions([]));
    }
  }, []);

  const clearStaleCameraDevice = useCallback(() => {
    cameraDeviceRef.current = "";
    setCameraDeviceId("");
    writeCameraPrefs(matchId, {
      facingMode: cameraFacingRef.current || "environment",
      deviceId: "",
      zoom: cameraZoomRef.current,
    });
  }, [matchId]);

  const openCameraPreview = useCallback(async () => {
    if (!videoFeatureOn) return null;
    if (streamRef.current) {
      attachPreview(streamRef.current);
      await refreshCameraOptions();
      return streamRef.current;
    }
    let facing = cameraFacingRef.current || "environment";
    let deviceId = cameraDeviceRef.current || "";
    if (!navigator.mediaDevices?.getUserMedia) {
      throw new Error("Camera unavailable");
    }
    // First await from the Connect tap. This is the call that shows Allow.
    const permissionStream = await navigator.mediaDevices.getUserMedia(
      plainCameraConstraints(facing)
    );
    const releasePermissionStream = () => {
      if (permissionStream && permissionStream !== streamRef.current) {
        stopStreamTracks(permissionStream);
      }
    };
    try {
      if (navigator.mediaDevices.enumerateDevices) {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const options = buildWebEvalCameraOptions(devices);
        setCameraOptions(options);
        const knownIds = new Set(
          options.map((o) => String(o.deviceId || "")).filter(Boolean)
        );
        // Drop a saved lens id that is no longer on this phone.
        if (deviceId && knownIds.size && !knownIds.has(deviceId)) {
          deviceId = "";
          clearStaleCameraDevice();
        }
        if (!deviceId) {
          const preferred = preferDefaultBackCamera(options);
          if (preferred) {
            deviceId = String(preferred.deviceId || "");
            facing = normalizeWebEvalFacingMode(
              preferred.facingMode || "environment",
              "environment"
            );
            setCameraDeviceId(deviceId);
            setCameraFacingMode(facing);
            cameraDeviceRef.current = deviceId;
            cameraFacingRef.current = facing;
            writeCameraPrefs(matchId, {
              facingMode: facing,
              deviceId,
              zoom: cameraZoomRef.current,
            });
          } else {
            facing = "environment";
            cameraFacingRef.current = facing;
            setCameraFacingMode(facing);
          }
        }
      }
    } catch {
      /* labels are optional once the camera is already allowed */
    }
    const openOnce = (id) =>
      openChampionshipCameraStream({
        facing,
        deviceId: id,
        resolution: clampResolutionForCameraRole(
          recordResolutionRef.current,
          scorecamRoleRef.current
        ),
        fps: recordFpsRef.current,
      });
    const keepPlainPreview = async () => {
      attachPreview(permissionStream);
      await refreshCameraOptions();
      return permissionStream;
    };
    let opened;
    try {
      opened = await openOnce(deviceId);
    } catch (err) {
      const notFound =
        err?.name === "NotFoundError" ||
        err?.name === "DevicesNotFoundError" ||
        /device not found/i.test(String(err?.message || ""));
      if (isCameraBusyError(err)) {
        releasePermissionStream();
        opened = await openOnce(deviceId);
      } else if (notFound && deviceId) {
        clearStaleCameraDevice();
        try {
          opened = await openOnce("");
        } catch (retryErr) {
          if (isCameraPermissionDenied(retryErr) || isCameraBusyError(retryErr)) {
            return keepPlainPreview();
          }
          releasePermissionStream();
          throw retryErr;
        }
      } else if (
        permissionStream
          .getVideoTracks()
          .some((track) => track.readyState === "live")
      ) {
        return keepPlainPreview();
      } else {
        releasePermissionStream();
        throw err;
      }
    }
    if (opened?.stream && opened.stream !== permissionStream) {
      stopStreamTracks(permissionStream);
    }
    noteNegotiatedCaptureRef.current(opened);
    attachPreview(opened.stream);
    await refreshCameraOptions();
    return opened.stream;
  }, [clearStaleCameraDevice, matchId, refreshCameraOptions, videoFeatureOn]);

  const switchCaptureCamera = async (option) => {
    if (!option || recording || cameraSwitching) return;
    const nextDeviceId = String(option.deviceId || "");
    const nextFacing = normalizeWebEvalFacingMode(
      option.facingMode ||
        (option.lens === "front" ? "user" : "environment"),
      "environment"
    );
    const alreadyOn =
      nextDeviceId && nextDeviceId === cameraDeviceRef.current;
    if (alreadyOn && streamRef.current) return;

    const previousDeviceId = cameraDeviceRef.current;
    const previousFacing = cameraFacingRef.current;
    const previous = streamRef.current;
    const preferWideZoom =
      option?.lens === "ultrawide" &&
      (option.zoom > 0 && option.zoom < 1
        ? normalizeCameraZoom(option.zoom)
        : isWideArrayCameraLabel(option.label)
          ? 0.5
          : null);
    const nextZoom = preferWideZoom || DEFAULT_CAMERA_ZOOM;
    const token = (cameraSwitchTokenRef.current += 1);
    setCameraDeviceId(nextDeviceId);
    setCameraFacingMode(nextFacing);
    cameraDeviceRef.current = nextDeviceId;
    cameraFacingRef.current = nextFacing;
    cameraZoomRef.current = nextZoom;
    setCameraZoom(nextZoom);
    cameraZoomRangeRef.current = null;
    setCameraZoomRange(null);
    cameraHardwareZoomRef.current = false;
    setCameraHardwareZoom(false);
    writeCameraPrefs(matchId, {
      facingMode: nextFacing,
      deviceId: nextDeviceId,
      zoom: nextZoom,
    });

    setCameraSwitching(true);
    try {
      if (previous) {
        streamRef.current = null;
        setHasPreviewStream(false);
        if (videoRef.current) videoRef.current.srcObject = null;
        if (settingsPreviewRef.current) settingsPreviewRef.current.srcObject = null;
        stopStreamTracks(previous);
        await new Promise((resolve) => window.setTimeout(resolve, 60));
        if (token !== cameraSwitchTokenRef.current) return;
      }
      const opened = await openChampionshipCameraStream({
        facing: nextFacing,
        deviceId: nextDeviceId,
        fps: recordFpsRef.current,
        resolution: clampResolutionForCameraRole(
          recordResolutionRef.current,
          scorecamRoleRef.current
        ),
      });
      if (token !== cameraSwitchTokenRef.current) {
        stopStreamTracks(opened.stream);
        return;
      }
      // Keep nextZoom (e.g. Dual Wide at 0.5×) instead of forcing 1×.
      attachPreview(opened.stream, { resetZoom: false });
      await refreshCameraOptions();
      setMediaStatus(
        t("cmCameraSwitched", "Camera: {{name}}", {
          name: webEvalCameraButtonLabel(option, cameraOptions, t),
        })
      );
      noteNegotiatedCapture(opened);
    } catch (err) {
      if (token !== cameraSwitchTokenRef.current) return;
      setError(err?.message || String(err));
      cameraDeviceRef.current = previousDeviceId;
      cameraFacingRef.current = previousFacing;
      setCameraDeviceId(previousDeviceId);
      setCameraFacingMode(previousFacing);
      writeCameraPrefs(matchId, {
        facingMode: previousFacing,
        deviceId: previousDeviceId,
        zoom: DEFAULT_CAMERA_ZOOM,
      });
      try {
        const restored = await openChampionshipCameraStream({
          facing: previousFacing,
          deviceId: previousDeviceId,
          fps: recordFpsRef.current,
          resolution: clampResolutionForCameraRole(
            recordResolutionRef.current,
            scorecamRoleRef.current
          ),
        });
        if (token !== cameraSwitchTokenRef.current) {
          stopStreamTracks(restored.stream);
          return;
        }
        noteNegotiatedCapture(restored);
        attachPreview(restored.stream, { resetZoom: true });
      } catch {
        /* preview may stay dark until the user retries */
      }
    } finally {
      if (token === cameraSwitchTokenRef.current) {
        setCameraSwitching(false);
      }
    }
  };

  const openCameraSettings = async () => {
    if (!hasPreviewStream) {
      try {
        await openCameraPreview();
      } catch (err) {
        setError(
          err?.message ||
            t("cmCalibNeedPreview", "Start the camera preview first.")
        );
        return;
      }
    }
    await refreshCameraOptions();
    setCameraSettingsOpen(true);
  };

  const persistEncodePrefs = (patch = {}) => {
    writeEncodePrefs({
      quality: recordQualityRef.current,
      fps: recordFpsRef.current,
      resolution: recordResolutionRef.current,
      setCloseStableSec,
      uploadMode: uploadModeRef.current,
      scorecamRecordVideo: scorecamRecordVideoRef.current,
      ...patch,
    });
  };

  const noteNegotiatedCapture = (opened) => {
    if (!opened) return;
    if (opened.fps) {
      recordFpsRef.current = opened.fps;
      setRecordFps(opened.fps);
    }
    if (opened.resolution) {
      recordResolutionRef.current = opened.resolution;
      setRecordResolution(opened.resolution);
    }
    writeEncodePrefs({
      quality: recordQualityRef.current,
      fps: opened.fps || recordFpsRef.current,
      resolution: opened.resolution || recordResolutionRef.current,
      setCloseStableSec,
      uploadMode: uploadModeRef.current,
      scorecamRecordVideo: scorecamRecordVideoRef.current,
    });
    const warning = matchCaptureWarningText(opened.warnings || []);
    if (warning) setMediaStatus(warning);
  };
  noteNegotiatedCaptureRef.current = noteNegotiatedCapture;

  const applyUploadMode = (mode) => {
    const next = normalizeMatchUploadMode(mode);
    uploadModeRef.current = next;
    setUploadMode(next);
    persistEncodePrefs({ uploadMode: next });
    kickChampionshipClipUploads();
  };

  const applyScorecamRecordVideo = async (enabled) => {
    const on = Boolean(enabled);
    scorecamRecordVideoRef.current = on;
    setScorecamRecordVideo(on);
    persistEncodePrefs({ scorecamRecordVideo: on });
    if (!myCamera || !cameraIsScoreCam(myCamera)) return;
    const table = myCamera.table_number || tableNum;
    persistCameraClaim(matchId, {
      table_number: table,
      angle_label: "scorecam",
      device_id: phoneId,
      camera_role: "score",
      record_video: on,
      ...(myCamera.score_roi ? { score_roi: myCamera.score_roi } : {}),
    });
    const ack = await emit("cm:claim-camera", {
      table_number: table,
      angle_label: "scorecam",
      camera_role: "score",
      record_video: on,
    });
    if (ack?.ok && ack.data) applySnap(ack.data);
  };

  const renderScorecamCaptureMode = (variant) => {
    if (!thisPhoneIsScorecam && normalizeCamPosition(angle) !== "scorecam") {
      return null;
    }
    const options = [
      { id: "score", on: false, label: t("cmScorecamScoreOnly", "Score only") },
      { id: "video", on: true, label: t("cmScorecamScoreVideo", "Score + video") },
    ];
    const note = scorecamRecordVideo
      ? t(
          "cmScorecamScoreVideoNote",
          "This phone also films the players — its clip plays with the referee/spectator videos."
        )
      : t(
          "cmScorecamScoreOnlyNote",
          "Only the score is read and updated — no video is recorded or uploaded from this phone."
        );
    if (variant === "chips") {
      return (
        <div className="cm-scorecam-mode">
          <div
            className="cm-picks"
            role="group"
            aria-label={t("cmScorecamMode", "Scorecam mode")}
          >
            {options.map((opt) => (
              <button
                key={opt.id}
                type="button"
                className={`cm-chip ${scorecamRecordVideo === opt.on ? "is-on" : ""}`}
                onClick={() => void applyScorecamRecordVideo(opt.on)}
                aria-pressed={scorecamRecordVideo === opt.on}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <p className="cm-scorecam-mode__note">{note}</p>
        </div>
      );
    }
    return (
      <div className="cm-cam-settings__field">
        <span className="cm-cam-settings__seg-label">
          {t("cmScorecamMode", "Scorecam mode")}
        </span>
        <div
          className="cm-cam-settings__seg"
          role="group"
          aria-label={t("cmScorecamMode", "Scorecam mode")}
        >
          {options.map((opt) => (
            <button
              key={opt.id}
              type="button"
              className={`cm-cam-settings__seg-btn${
                scorecamRecordVideo === opt.on ? " is-on" : ""
              }`}
              onClick={() => void applyScorecamRecordVideo(opt.on)}
              aria-pressed={scorecamRecordVideo === opt.on}
              disabled={recording}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <p className="cm-cam-settings__note">{note}</p>
      </div>
    );
  };

  const reopenPreviewIfLive = async () => {
    if (!streamRef.current && !hasPreviewStream) return;
    const wasLive = Boolean(streamRef.current);
    stopPreviewTracks();
    if (!wasLive && !hasPreviewStream) return;
    try {
      await openCameraPreview();
    } catch (err) {
      setError(err?.message || String(err));
    }
  };

  const applyRecordQuality = (next) => {
    if (recording) return;
    const quality = normalizeChampionshipQuality(next, "low");
    recordQualityRef.current = quality;
    setRecordQuality(quality);
    persistEncodePrefs({ quality });
  };

  const applyRecordFps = async (next) => {
    if (recording) return;
    const fps = normalizeMatchRecordFps(next, MATCH_RECORD_DEFAULT_FPS);
    recordFpsRef.current = fps;
    setRecordFps(fps);
    persistEncodePrefs({ fps });
    await reopenPreviewIfLive();
  };

  const applyRecordResolution = async (next) => {
    if (recording) return;
    const resolution = clampResolutionForCameraRole(
      next,
      scorecamRoleRef.current
    );
    recordResolutionRef.current = resolution;
    setRecordResolution(resolution);
    persistEncodePrefs({ resolution });
    await reopenPreviewIfLive();
  };

  /** Leave scorecam → drop 480p (match cameras stay ≥720). Enter scorecam keeps prefs. */
  const selectCameraPosition = (nextPos) => {
    const position = normalizeCamPosition(nextPos);
    setAngle(position);
    scorecamRoleRef.current = position === "scorecam";
    if (position !== "scorecam" && recordResolutionRef.current === 480) {
      recordResolutionRef.current = MATCH_RECORD_MIN_SHORT;
      setRecordResolution(MATCH_RECORD_MIN_SHORT);
      persistEncodePrefs({ resolution: MATCH_RECORD_MIN_SHORT });
    }
    return position;
  };

  const applySetCloseStableSec = (next) => {
    const sec = normalizeSetCloseStableSec(next, DEFAULT_SET_CLOSE_STABLE_SEC);
    setSetCloseStableSec(sec);
    persistEncodePrefs({ setCloseStableSec: sec });
  };

  const claimCam = async () => {
    if (!videoFeatureOn) return;
    skipDockAfterLeaveRef.current = false;
    setError("");
    const position = normalizeCamPosition(angle);
    const role = camRoleFromPosition(position);
    setAngle(position);
    // Open hardware first — never register this phone if the camera fails.
    try {
      await openCameraPreview();
    } catch (err) {
      persistCameraClaim(matchId, null);
      stopPreviewTracks();
      // Clear any leftover roster row from an earlier failed claim.
      try {
        const releaseAck = await emit("cm:release-camera", {
          table_number: tableNum,
        });
        if (releaseAck?.ok && releaseAck.data) applySnap(releaseAck.data);
        else {
          applySnap(
            await releaseChampionshipCamera(matchId, {
              table_number: tableNum,
              device_id: phoneId,
            })
          );
        }
      } catch {
        /* best-effort */
      }
      if (isCameraPermissionDenied(err)) {
        const state = await queryCameraPermissionState();
        // "denied" is the browser site setting. A dismissed prompt stays "prompt"
        // and can still be allowed on the next tap.
        setCameraPermissionBlocked(state === "denied");
        setCameraPermissionPrompt(true);
        setError("");
        return;
      }
      setCameraPermissionPrompt(false);
      setError(
        err?.message ||
          t("cmCameraUnavailable", "Camera unavailable on this phone.")
      );
      return;
    }
    await unlockScoreIfHeld();
    let ack = null;
    try {
      const deviceLabel = `${camPositionLabel(position, t)} · ${t("cmTable", "Table")} ${tableNum}`;
      ack = await emit("cm:claim-camera", {
        table_number: tableNum,
        angle_label: position,
        camera_role: role,
        device_label: deviceLabel,
        record_video: scorecamRecordVideoRef.current,
      });
      if (ack?.ok && ack.data) applySnap(ack.data);
      else {
        applySnap(
          await claimChampionshipCamera(matchId, {
            table_number: tableNum,
            angle_label: position,
            device_id: phoneId,
            camera_role: role,
            device_label: deviceLabel,
            record_video: scorecamRecordVideoRef.current,
          })
        );
        // Re-claim over socket so the backend stores this phone's socket_id.
        ack = await emit("cm:claim-camera", {
          table_number: tableNum,
          angle_label: position,
          camera_role: role,
          device_label: deviceLabel,
          record_video: scorecamRecordVideoRef.current,
        });
        if (ack?.ok && ack.data) applySnap(ack.data);
      }
    } catch (err) {
      stopPreviewTracks();
      persistCameraClaim(matchId, null);
      setError(err?.response?.data?.message || err.message);
      return;
    }
    if (!streamRef.current) {
      // Stream died during claim — do not leave a ghost roster entry.
      try {
        const releaseAck = await emit("cm:release-camera", {
          table_number: tableNum,
        });
        if (releaseAck?.ok && releaseAck.data) applySnap(releaseAck.data);
        else {
          applySnap(
            await releaseChampionshipCamera(matchId, {
              table_number: tableNum,
              device_id: phoneId,
            })
          );
        }
      } catch {
        /* best-effort */
      }
      persistCameraClaim(matchId, null);
      setError(t("cmCameraUnavailable", "Camera unavailable on this phone."));
      return;
    }
    persistCameraClaim(matchId, {
      table_number: tableNum,
      angle_label: position,
      device_id: phoneId,
      camera_role: role,
    });
    setCameraPermissionPrompt(false);
    setCameraPermissionBlocked(false);
    setTab("camera");
    if (position === "scorecam") {
      setMediaStatus(
        t(
          "cmScoreCameraWarmup",
          "Scorecam on table {{n}} — draw the scoreboard box to start detecting.",
          { n: tableNum }
        )
      );
      setScoreRoiMode(true);
    } else {
      const matchup = rubberMatchupForTable(ack?.data || snapshot, tableNum);
      setMediaStatus(
        matchup
          ? t(
              "cmCameraWarmupMatchup",
              "Connected to table {{n}} — {{matchup}}. This camera records the match that starts here.",
              { n: tableNum, matchup: matchup.label }
            )
          : t(
              "cmCameraWarmupOnly",
              "Connected to table {{n}}. This camera records the match that starts here, manually or with scorecam.",
              { n: tableNum }
            )
      );
    }
  };

  const saveScoreRoi = async (roi) => {
    const box = normalizeRoi(roi);
    if (!box || !matchId) return;
    const position = "scorecam";
    setAngle(position);
    const ack = await emit("cm:claim-camera", {
      table_number: myCamera?.table_number || tableNum,
      angle_label: position,
      camera_role: "score",
      score_roi: box,
      record_video: scorecamRecordVideoRef.current,
    });
    if (ack?.ok && ack.data) applySnap(ack.data);
    else {
      applySnap(
        await claimChampionshipCamera(matchId, {
          table_number: myCamera?.table_number || tableNum,
          angle_label: position,
          device_id: phoneId,
          camera_role: "score",
          score_roi: box,
          record_video: scorecamRecordVideoRef.current,
        })
      );
    }
    persistCameraClaim(matchId, {
      table_number: myCamera?.table_number || tableNum,
      angle_label: position,
      device_id: phoneId,
      camera_role: "score",
      score_roi: box,
      record_video: scorecamRecordVideoRef.current,
    });
    setMediaStatus(
      t(
        "cmScoreRoiSaved",
        "Scoreboard region saved — detecting score changes."
      )
    );
  };

  const finishLiveScoreRoi = async (roi) => {
    setScoreRoiMode(false);
    const box = normalizeRoi(roi);
    if (!box) {
      if (roi) {
        setError(
          t("cmScoreRoiTooSmall", "Draw a larger box around the scoreboard.")
        );
      }
      return;
    }
    try {
      await saveScoreRoi(box);
    } catch (err) {
      setError(err?.response?.data?.message || err.message);
    }
  };

  // Soft-rebind after refresh: only keep the claim if this phone can open the camera.
  useEffect(() => {
    if (!videoFeatureOn || !matchId || !userId || !snapshot || cameraRestoreTriedRef.current) {
      return;
    }
    cameraRestoreTriedRef.current = true;
    const saved = readPersistedCameraClaim(matchId);
    if (!saved) return;
    let cancelled = false;
    (async () => {
      const table = Number(saved.table_number) || tableNum;
      const position = normalizeCamPosition(
        saved.angle_label ||
          (saved.camera_role === "score" ? "scorecam" : angle)
      );
      if (saved.table_number) setTableNum(table);
      setAngle(position);
      try {
        await openCameraPreview();
        if (cancelled) return;
        if (!streamRef.current) {
          throw new Error("Camera unavailable");
        }
        const ack = await emit("cm:claim-camera", {
          table_number: table,
          angle_label: position,
          camera_role: camRoleFromPosition(position),
          ...(saved.score_roi ? { score_roi: saved.score_roi } : {}),
          record_video: scorecamRecordVideoRef.current,
        });
        if (cancelled) return;
        if (ack?.ok && ack.data) applySnap(ack.data);
        persistCameraClaim(matchId, {
          table_number: table,
          angle_label: position,
          device_id: phoneId,
          camera_role: camRoleFromPosition(position),
          ...(saved.score_roi ? { score_roi: saved.score_roi } : {}),
          record_video: scorecamRecordVideoRef.current,
        });
      } catch {
        if (cancelled) return;
        // Drop ghost roster rows from a prior failed "Use as camera".
        persistCameraClaim(matchId, null);
        stopPreviewTracks();
        try {
          const releaseAck = await emit("cm:release-camera", {
            table_number: table,
          });
          if (releaseAck?.ok && releaseAck.data) applySnap(releaseAck.data);
          else {
            applySnap(
              await releaseChampionshipCamera(matchId, {
                table_number: table,
                device_id: phoneId,
              })
            );
          }
        } catch {
          /* best-effort cleanup */
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoFeatureOn, matchId, userId, snapshot?.match?.championship_match_id]);

  const snapPhoto = async () => {
    if (!videoFeatureOn) return;
    const video = videoRef.current;
    if (!video || !streamRef.current) {
      setError(
        t("cmPhotoNeedPreview", "Start the camera preview before taking a photo.")
      );
      return;
    }
    let width = Number(video.videoWidth) || 0;
    let height = Number(video.videoHeight) || 0;
    if (!width || !height) {
      await new Promise((resolve) => {
        const started = Date.now();
        const tick = () => {
          width = Number(video.videoWidth) || 0;
          height = Number(video.videoHeight) || 0;
          if ((width && height) || Date.now() - started > 1500) {
            resolve();
            return;
          }
          requestAnimationFrame(tick);
        };
        tick();
      });
    }
    if (!width || !height) {
      setError(
        t("cmPhotoNotReady", "Camera frame not ready — try again in a moment.")
      );
      return;
    }
    // Cap canvas size — full 4K landscape canvases can OOM on phones.
    const maxSide = 1920;
    const scale = Math.min(1, maxSide / Math.max(width, height));
    const canvasW = Math.max(1, Math.round(width * scale));
    const canvasH = Math.max(1, Math.round(height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = canvasW;
    canvas.height = canvasH;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      setError(t("cmPhotoFailed", "Could not capture photo."));
      return;
    }
    try {
      ctx.drawImage(video, 0, 0, canvasW, canvasH);
    } catch (err) {
      setError(err?.message || t("cmPhotoFailed", "Could not capture photo."));
      return;
    }
    const blob = await new Promise((resolve) => {
      try {
        canvas.toBlob(resolve, "image/jpeg", 0.85);
      } catch {
        resolve(null);
      }
    });
    if (!blob) {
      setError(t("cmPhotoFailed", "Could not capture photo."));
      return;
    }
    setMediaStatus(t("cmSavingPhoto", "Saving photo…"));
    const clipMeta = clipMetaRef.current;
    const file = new File([blob], `table-${clipMeta.table}.jpg`, {
      type: "image/jpeg",
    });
    try {
      const data = await uploadChampionshipMedia(matchId, file, {
        kind: "photo",
        table_number: clipMeta.table,
        rubber_id: clipMeta.rubberId || undefined,
        point_id: lastPoint?.point_id,
        angle_label: clipMeta.angle,
        capture_id: tableRecording?.capture_id || undefined,
      });
      if (data?.snapshot?.match) setSnapshot(data.snapshot);
      setMediaStatus(t("cmPhotoSaved", "Photo saved"));
    } catch (err) {
      setError(mediaUploadErrorMessage(err, t));
      setMediaStatus("");
    }
  };

  const closeMatch = async () => {
    if (videoFeatureOn) {
      await emit("cm:record-stop", {});
      await stopLocalRecording(true);
    }
    applySnap(await completeChampionshipMatch(matchId));
  };

  const resetMatch = async () => {
    if (!canModerateMatch) return;
    const ok = window.confirm(
      t(
        "cmResetConfirm",
        "Reset this match? Scores, points, clips and camera claims will be deleted. Line-up names are kept."
      )
    );
    if (!ok) return;
    setBusy(true);
    setError("");
    try {
      if (videoFeatureOn) {
        await emit("cm:record-stop", {});
        await stopLocalRecording(true);
      }
      setCameraDocked(false);
      stopPreviewTracks();
      const ack = await emit("cm:reset-match", {});
      if (ack?.ok && ack.data) applySnap(ack.data);
      else applySnap(await resetChampionshipMatch(matchId));
      setActiveRubberId(null);
      setSheetBrowsing(true);
      setMediaStatus(
        t("cmMatchReset", "Match reset — scores and media cleared")
      );
    } catch (err) {
      setError(err?.response?.data?.message || err.message);
    } finally {
      setBusy(false);
    }
  };

  const applyOpponentPoolPlayer = (player, seat, sideOverride) => {
    if (matchDone) return;
    const side =
      sideOverride || snapshot?.opponent_pool?.side || "away";
    const slots = [...(snapshot?.lineup?.[side] || [])];
    if (!slots.length) return;
    const targetSeat = Number(seat) || Number(slots[0]?.seat) || 1;
    const next = slots.map((s) =>
      Number(s.seat) === targetSeat
        ? {
            ...s,
            user_id: player.user_id || null,
            player_id: player.player_id || null,
            display_name: player.display_name || player.name || "",
            classement: player.classement || null,
            ranking_points:
              player.ranking_points != null ? Number(player.ranking_points) : null,
            ranking_place:
              player.ranking_place != null ? Number(player.ranking_place) : null,
            average_points:
              player.average_points != null ? Number(player.average_points) : null,
            order_source: player.order_source || "fltt_roster_estimate",
          }
        : s
    );
    setSwapPick(null);
    void saveLineup({ [side]: next });
  };

  const togglePoolSwap = (side, player) => {
    if (matchDone) return;
    const key = opponentPoolPlayerKey(player);
    setSwapPick((current) =>
      current && current.side === side && current.key === key
        ? null
        : { side, key, player }
    );
  };

  const setOpponentAvailability = async (player, status) => {
    if (matchDone || !canSetOppAvailability) return;
    setBusy(true);
    setError("");
    try {
      applySnap(
        await setChampionshipOpponentAvailability(matchId, {
          status,
          side: snapshot?.opponent_pool?.side,
          player,
        })
      );
    } catch (err) {
      setError(err?.response?.data?.message || err.message);
    } finally {
      setBusy(false);
    }
  };

  const tagLabel = (key) =>
    ({
      point_start: t("cmTagPointStart", "Point start"),
      amazing_rally: t("cmTagAmazingShort", "Rally"),
      service_missed: t("cmTagMissedServeShort", "Miss"),
      service_ace: t("cmTagAceShort", "ACE"),
      service_attack_3rd: t("cmTagThirdShort", "3rd"),
      unforced_error: t("cmTagUnforcedShort", "Error"),
      forced_error: t("cmTagForcedShort", "Forced"),
      winner_open_play: t("cmTagWinnerShort", "Winner"),
      return_winner: t("cmTagReturnWinnerShort", "Ret. win"),
      return_error: t("cmTagReturnErrorShort", "Ret. err"),
      net: t("cmTagNet", "Net"),
      edge: t("cmTagEdge", "Edge"),
      let_serve: t("cmTagLet", "Let"),
      timeout_on: t("cmTagTimeoutOn", "Timeout on"),
      timeout_off: t("cmTagTimeoutOff", "Timeout off"),
      set_point: t("cmTagSetPointShort", "Set pt"),
      match_point: t("cmTagMatchPointShort", "Match pt"),
      court_coupe: t("cmSchemaCourtCoupeShort", "Short BS"),
      long_coupe: t("cmSchemaLongCoupeShort", "Long BS"),
      long_rapide: t("cmSchemaLongFastShort", "Long fast"),
      court_lateral: t("cmSchemaLateralShort", "Side"),
      long_varie: t("cmSchemaLongMixShort", "Mixed"),
    }[key] || key);

  const scorePadOutcome = readPointOutcome(
    lastPoint || { tags: pendingPointTags }
  );
  const mode = themeMode === "light" ? "light" : "dark";
  const inMatch = focusedOnRubber;
  const cameraTableSetup = tab === "camera" && !cameraArmed;
  const cheerTable = Number(rubber?.table_number);
  const cameraTableNumber = Number(myCamera?.table_number || tableNum);
  const thisPhoneIsScorecam = Boolean(myCamera) && cameraIsScoreCam(myCamera);
  /** Scorecam always uploads live — Wi‑Fi only and Save on phone are hidden. */
  const scorecamUploadLiveOnly =
    thisPhoneIsScorecam || normalizeCamPosition(angle) === "scorecam";
  const uploadModeChoices = scorecamUploadLiveOnly
    ? ["live"]
    : MATCH_UPLOAD_MODES;
  const shownUploadMode = scorecamUploadLiveOnly ? "live" : uploadMode;
  const scorecamDetectingOnTable = (snapshot?.cameras || []).some(
    (cam) =>
      Boolean(cam?.score_camera_armed) &&
      Number(cam.table_number) === cameraTableNumber &&
      !cameraIsThisPhone(cam, userId, phoneId)
  );
  const tableLiveRubber = (snapshot?.rubbers || []).find(
    (row) =>
      Number(row.table_number) === cameraTableNumber && row.state === "live"
  );
  const scorecamPoint = lastPointForRubber(
    snapshot?.points,
    tableLiveRubber?.rubber_id
  );
  /** Side camera can label points the scorecam already created. */
  const tagScorecamPoints =
    Boolean(userId) &&
    cameraArmed &&
    !thisPhoneIsScorecam &&
    scorecamDetectingOnTable &&
    Boolean(tableLiveRubber);
  const showCheer =
    Boolean(userId) &&
    tab === "score" &&
    inMatch &&
    rubberLive &&
    (cheerTable === 1 || cheerTable === 2);
  cheerOpenRef.current = showCheer || tagScorecamPoints;
  const showCameraTab = videoFeatureOn && !resultsViewOnly;
  const showControlTab =
    canModerateMatch && videoFeatureOn && !resultsViewOnly;
  /** Timeline only while focused on a rubber — not on the feuille overview. */
  const showTimelineTab =
    inMatch && (!resultsViewOnly || hasCapturedMedia);
  const tabs = inMatch
    ? [
        { id: "score", label: t("cmSheet", "Score"), Icon: LayoutGrid },
        ...(showCameraTab
          ? [{ id: "camera", label: t("cmCamera", "Video"), Icon: Camera }]
          : []),
        ...(showControlTab
          ? [
              {
                id: "control",
                label: t("cmControlRoomTab", "Control"),
                Icon: LayoutDashboard,
              },
            ]
          : []),
        ...(showTimelineTab
          ? [{ id: "replay", label: t("cmVideoPlayer", "Video player"), Icon: Video }]
          : []),
      ]
    : [
        { id: "lineup", label: t("cmLineup", "Line-up"), Icon: Users },
        { id: "score", label: t("cmSheet", "Sheet"), Icon: LayoutGrid },
        ...(showCameraTab
          ? [{ id: "camera", label: t("cmCamera", "Camera"), Icon: Camera }]
          : []),
        ...(showControlTab
          ? [
              {
                id: "control",
                label: t("cmControlRoomTab", "Control"),
                Icon: LayoutDashboard,
              },
            ]
          : []),
      ];

  const reviewRubber = useMemo(
    () =>
      (snapshot?.rubbers || []).find(
        (r) => String(r.rubber_id) === String(reviewRubberId)
      ) || null,
    [snapshot?.rubbers, reviewRubberId]
  );

  useEffect(() => {
    if (showCheer || tagScorecamPoints) return undefined;
    stopMatchSounds();
    setCheerIncoming(null);
    return undefined;
  }, [showCheer, tagScorecamPoints]);

  const renderScorecamPointTools = () => {
    if (!tagScorecamPoints) return null;
    return (
      <div className="cm-cam-tags">
        <p className="cm-cam-tags__label">
          {scorecamPoint
            ? t("cmTagScorecamPoint", "Tag the point scorecam just read")
            : t("cmTagScorecamWait", "Scorecam is reading — tags open on the next point")}
        </p>
        <PointOutcomeBar
          t={t}
          className="cm-outcome--on-cam"
          activeOutcome={scorecamPoint ? readPointOutcome(scorecamPoint) : null}
          disabled={!scorecamPoint || timeoutActive}
          onSelect={(outcomeId) => void stampOutcomeOn(scorecamPoint, outcomeId)}
        />
        <div
          className="cm-cam-tags__emojis"
          role="group"
          aria-label={t("cmCheerEmojis", "Emojis")}
        >
          {CHEER_EMOJIS.map((item) => (
            <button
              key={item.kind}
              type="button"
              className="cm-cam-tags__emoji"
              aria-label={t(item.labelKey, item.label)}
              onClick={() => emit("cm:cheer", { kind: item.kind })}
            >
              {item.emoji}
            </button>
          ))}
        </div>
      </div>
    );
  };

  if (error && !snapshot) {
    return (
      <div className={`cm-page cm-page--${mode}`}>
        <div className="cm-status">
          <p>{error}</p>
          <Link to="/competition">{t("cmBack", "Back to calendar")}</Link>
        </div>
      </div>
    );
  }

  if (!snapshot) {
    return (
      <div className={`cm-page cm-page--${mode}`}>
        <div className="cm-status">
          <p>{t("cmLoading", "Opening the feuille…")}</p>
        </div>
      </div>
    );
  }

  const homeName = snapshot.fixture?.home?.display_name || t("competitionHome", "Home");
  const awayName = snapshot.fixture?.away?.display_name || t("competitionAway", "Away");
  const brandLogo =
    resolveClubImageUrl(brand?.logo_url) ||
    resolveClubImageUrl(brand?.logo_light_url) ||
    null;
  const matchClubId = snapshot?.match?.club_id;
  const homeClubId = snapshot.fixture?.home?.club_id;
  const awayClubId = snapshot.fixture?.away?.club_id;
  const homeLogo =
    resolveClubImageUrl(snapshot.fixture?.home?.club_image) ||
    (matchClubId &&
    homeClubId &&
    String(homeClubId) === String(matchClubId)
      ? brandLogo
      : null);
  const awayLogo =
    resolveClubImageUrl(snapshot.fixture?.away?.club_image) ||
    (matchClubId &&
    awayClubId &&
    String(awayClubId) === String(matchClubId)
      ? brandLogo
      : null);
  const homeClubName =
    snapshot.fixture?.home?.club_short_name ||
    snapshot.fixture?.home?.display_name ||
    homeName;
  const awayClubName =
    snapshot.fixture?.away?.club_short_name ||
    snapshot.fixture?.away?.display_name ||
    awayName;
  const overlayLogo = brandLogo || homeLogo;
  const overlayClubName = brandLogo
    ? brand?.brand_short_name || brand?.brand_name || homeClubName
    : homeClubName;
  const points = (snapshot.points || []).filter((p) => {
    if (filterTag && !pointMatchesOutcome(p, filterTag)) return false;
    if (inMatch && rubber && p.rubber_id !== rubber.rubber_id) return false;
    return true;
  });
  const rubberHomeName =
    shortNames(rubber?.home_names, rubber?.home_seats, "") || homeName;
  const rubberAwayName =
    shortNames(rubber?.away_names, rubber?.away_seats, "") || awayName;
  const homePlayers = lineupPlayersForSeats(
    snapshot.lineup?.home,
    rubber?.home_seats,
    rubber?.home_names
  );
  const awayPlayers = lineupPlayersForSeats(
    snapshot.lineup?.away,
    rubber?.away_seats,
    rubber?.away_names
  );

  const endManualRecording = async (tableOverride) => {
    if (recordActionRef.current) return;
    recordActionRef.current = true;
    setRecordBusy(true);
    const table = Number(tableOverride) || Number(tableNum);
    setCameraDocked(false);
    try {
      await emit("cm:record-stop", {
        table_number: Number.isFinite(table) && table > 0 ? table : tableNum,
      });
      void ackCameraRecordingRef.current?.({
        captureId: captureIdRef.current || tableRecording?.capture_id,
        cameraId: myCamera?.camera_id,
        state: "stopped",
      });
    } catch {
      /* best-effort room stop */
    }
    try {
      await stopLocalRecording(true);
      setMediaStatus(t("cmRecordingEnded", "Recording ended"));
    } finally {
      recordActionRef.current = false;
      setRecordBusy(false);
    }
  };

  const startManualRecording = async (tableOverride) => {
    if (!videoFeatureOn || recordActionRef.current) return;
    const table = Number(tableOverride) || Number(tableNum);
    if (!Number.isFinite(table) || table < 1) return;
    if (tableIsFilming(table) || tableIsStarting(table)) return;
    const claimedHere = (snapshot?.cameras || []).some(
      (cam) => Number(cam.table_number) === table
    );
    const thisPhone =
      Boolean(myCamera) && Number(myCamera.table_number) === table;
    if (!claimedHere && !thisPhone) {
      setError(
        t(
          "cmStartRecordingNeedCamera",
          "Use a phone as the camera on this table, then start recording."
        )
      );
      return;
    }
    recordActionRef.current = true;
    setRecordBusy(true);
    setError("");
    const syncAt = Date.now();
    mediaSyncAtRef.current = syncAt;
    try {
      const ack = await emit("cm:record-start", {
        table_number: table,
        media_sync_at: syncAt,
      });
      if (ack && ack.ok === false) {
        setError(
          ack.message ||
            t("cmStartRecordingFailed", "Could not start recording")
        );
        return;
      }
      if (ack?.capture_id) captureIdRef.current = ack.capture_id;
      const delivered = Number(ack?.delivered) || 0;
      const cameras = Number(ack?.cameras) || (claimedHere || thisPhone ? 1 : 0);
      if (cameras > 0 && delivered === 0 && !thisPhone) {
        setMediaStatus(
          t(
            "cmCamerasOffline",
            "Cameras claimed but offline — reopen the camera phone."
          )
        );
        return;
      }
      setMediaStatus(
        delivered > 0
          ? t(
              "cmRecordingStartedCams",
              "Recording started on {{count}} camera(s) — waiting for confirm…",
              { count: delivered }
            )
          : t("cmRecordingStarting", "Starting…")
      );
    } catch (err) {
      setError(err?.message || String(err));
    } finally {
      recordActionRef.current = false;
      setRecordBusy(false);
    }
  };

  const startAllLiveTables = async () => {
    if (!videoFeatureOn || recordActionRef.current) return;
    const liveTables = [
      ...new Set(
        (snapshot?.rubbers || [])
          .filter((r) => r.state === "live" && !r.set_scores?.awaiting_next_set)
          .map((r) => Number(r.table_number))
          .filter((n) => Number.isFinite(n) && n > 0)
      ),
    ];
    for (const n of liveTables) {
      if (!tableIsFilming(n) && !tableIsStarting(n)) {
        await startManualRecording(n);
      }
    }
  };

  const forceReleaseCamera = async (payload = {}) => {
    const ack = await emit("cm:force-release-camera", payload);
    if (ack?.ok === false) {
      setError(ack.message || t("cmForceReleaseFailed", "Could not release camera"));
    }
  };

  const renderMatchRecordButton = ({
    table,
    icon = false,
    block = false,
  }) => {
    if (!videoFeatureOn || resultsViewOnly) return null;
    const n = Number(table);
    if (!Number.isFinite(n) || n < 1) return null;
    const filming = tableIsFilming(n);
    const starting = tableIsStarting(n);
    const active = filming || starting;
    if (!filming && !starting && !tableHasConnectedCamera(n)) return null;
    const label = filming
      ? t("cmEndRecording", "End recording")
      : starting
        ? t("cmRecordingStarting", "Starting…")
        : t("cmStartRecording", "Start recording");
    return (
      <button
        key={`match-rec-${n}`}
        type="button"
        className={[
          "cm-btn",
          block ? "cm-btn--block" : "",
          icon ? "cm-btn--icon" : "",
          filming ? "cm-btn--danger" : starting ? "cm-btn--ghost" : "cm-btn--rec",
        ]
          .filter(Boolean)
          .join(" ")}
        disabled={recordBusy || starting}
        onClick={() =>
          void (active && filming
            ? endManualRecording(n)
            : starting
              ? null
              : startManualRecording(n))
        }
        aria-label={label}
        title={label}
      >
        {filming ? (
          <Square size={icon ? 18 : 14} aria-hidden />
        ) : (
          <Video size={icon ? 18 : 14} aria-hidden />
        )}
        {icon ? (
          <ActionLabel landscape>{label}</ActionLabel>
        ) : (
          <span className="cm-btn__label">{label}</span>
        )}
      </button>
    );
  };

  const dockCameraPhone = async () => {
    await unlockScoreIfHeld();
    if (!streamRef.current) {
      await claimCam();
    }
    if (!streamRef.current) return;
    setCameraDocked(true);
    setCamLandscape(false);
    setCameraSettingsOpen(false);

    const table = Number(myCamera?.table_number || tableNum);
    const liveOnTable = (snapshot?.rubbers || []).find(
      (r) => Number(r.table_number) === table && r.state === "live"
    );
    const setOpen =
      liveOnTable && !liveOnTable.set_scores?.awaiting_next_set;
    // Dock stays in warmup until a set is already open. Start recording is
    // also available on its own, before or between sets.
    if (videoFeatureOn && setOpen && !tableIsFilming(table)) {
      const ack = await emit("cm:record-start", { table_number: table });
      if (ack?.capture_id) captureIdRef.current = ack.capture_id;
      if (
        ack?.ok &&
        Number(ack.cameras) > 0 &&
        Number(ack.delivered) === 0
      ) {
        setMediaStatus(
          t(
            "cmCameraOffline",
            "Camera claimed but offline — reopen the camera phone."
          )
        );
        return;
      }
      if (scoreOnlyPhoneRef.current) {
        setMediaStatus(
          t(
            "cmScoreOnlyDocked",
            "Score only — reading the board, no video upload from this phone"
          )
        );
        return;
      }
      if (!recording) void startLocalRecording();
      setMediaStatus(
        t("cmDockedRecording", "Recording — screen dimmed to save battery")
      );
      return;
    }

    const matchup = rubberMatchupForTable(snapshot, table);
    setMediaStatus(
      matchup
        ? t(
            "cmDockedWaitingMatchup",
            "Docked on table {{n}} for {{matchup}} — waiting for the set to start",
            { n: table, matchup: matchup.label }
          )
        : t(
            "cmDockedWaiting",
            "Docked — waiting for the scorer to start the match"
          )
    );
  };

  const wakeFromDock = () => {
    setCameraDocked(false);
    if (!recording) {
      setMediaStatus(
        t("cmCameraIdleHint", "Tap “Use as camera” to start the live preview.")
      );
    }
  };

  const startVoiceComment = async () => {
    if (!lastPoint || voiceRecording) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      voiceChunksRef.current = [];
      const rec = new MediaRecorder(stream);
      voiceRecorderRef.current = rec;
      rec.ondataavailable = (e) => {
        if (e.data?.size) voiceChunksRef.current.push(e.data);
      };
      rec.onstop = async () => {
        stream.getTracks().forEach((tr) => tr.stop());
        setVoiceRecording(false);
        const blob = new Blob(voiceChunksRef.current, {
          type: rec.mimeType || "audio/webm",
        });
        voiceChunksRef.current = [];
        if (blob.size < 32 || !matchId) return;
        const ext = String(rec.mimeType || "").includes("mp4") ? "m4a" : "webm";
        const file = new File([blob], `voice-${lastPoint.point_id}.${ext}`, {
          type: blob.type,
        });
        setMediaStatus(t("cmSavingVoice", "Saving voice comment…"));
        try {
          const data = await uploadChampionshipMedia(matchId, file, {
            kind: "voice",
            table_number: rubber?.table_number || tableNum,
            rubber_id: rubber?.rubber_id,
            point_id: lastPoint.point_id,
            t_ms: lastPoint.t_ms != null ? lastPoint.t_ms : undefined,
          });
          if (data?.snapshot?.match) setSnapshot(data.snapshot);
          setMediaStatus(t("cmVoiceSaved", "Voice comment saved"));
        } catch (err) {
          setError(mediaUploadErrorMessage(err, t));
          setMediaStatus("");
        }
      };
      rec.start();
      setVoiceRecording(true);
      setMediaStatus(t("cmVoiceRecording", "Recording voice… tap again to stop"));
    } catch (err) {
      setError(err?.message || String(err));
    }
  };

  const stopVoiceComment = () => {
    const rec = voiceRecorderRef.current;
    if (!rec) {
      setVoiceRecording(false);
      return;
    }
    if (rec.state === "recording") rec.stop();
    voiceRecorderRef.current = null;
  };

  const renderPlayerBlock = (players, align = "left") => (
    <div className={`cm-scoredeck__players cm-scoredeck__players--${align}`}>
      {(players.length ? players : [{ name: "—", classement: null, ranking_points: null }]).map(
        (player, idx) => {
          const meta = formatPlayerMeta(player, t);
          return (
            <div
              key={`${player.seat || idx}-${player.name}`}
              className="cm-scoredeck__player"
            >
              <strong className="cm-scoredeck__fullname">{player.name}</strong>
              {meta ? <span className="cm-scoredeck__meta">{meta}</span> : null}
            </div>
          );
        }
      )}
    </div>
  );

  const renderRubberSheetList = () => (
    <div className="cm-rubbers cm-rubbers--sheet">
      {snapshot.rubbers.map((row) => {
        const score = row.set_scores || {};
        const home = lineupPlayersForSeats(
          snapshot.lineup?.home,
          row.home_seats,
          row.home_names
        );
        const away = lineupPlayersForSeats(
          snapshot.lineup?.away,
          row.away_seats,
          row.away_names
        );
        const homeSets = Number(score.home_sets) || 0;
        const awaySets = Number(score.away_sets) || 0;
        const pointLabels = (Array.isArray(score.completed) ? score.completed : [])
          .map((cell) => formatSetCell(cell))
          .filter(Boolean);
        const tapeCount = rubberReviewTapes(snapshot.media, row.rubber_id).length;
        const isLive = row.state === "live";
        const isDone = row.state === "done";
        const winnerSide =
          isDone && homeSets !== awaySets
            ? homeSets > awaySets
              ? "home"
              : "away"
            : null;
        // Prefer rubber.home_names first (server-resolved), then lineup slots.
        const homeLabel =
          (Array.isArray(row.home_names)
            ? row.home_names.map((n) => String(n || "").trim()).filter(Boolean)
            : []
          ).join(" / ") ||
          home
            .map((p) => p.name)
            .filter((n) => n && !/^Seat\s/i.test(n))
            .join(" / ") ||
          home.map((p) => p.name).filter(Boolean).join(" / ") ||
          "—";
        const awayLabel =
          (Array.isArray(row.away_names)
            ? row.away_names.map((n) => String(n || "").trim()).filter(Boolean)
            : []
          ).join(" / ") ||
          away
            .map((p) => p.name)
            .filter((n) => n && !/^Seat\s/i.test(n))
            .join(" / ") ||
          away.map((p) => p.name).filter(Boolean).join(" / ") ||
          "—";
        const canReview = rubberReviewReady(row, snapshot.points, snapshot.media, {
          videoFeatureOn,
          recording,
          tableRecording,
        });
        const storedClips = rubberHasStoredClips(
          row,
          snapshot.points,
          snapshot.media,
          { recording, tableRecording }
        );
        const reviewUploading = rubberReviewUploading(
          row,
          snapshot.points,
          snapshot.media,
          { videoFeatureOn, recording, tableRecording }
        );
        return (
          <div
            key={row.rubber_id}
            role={resultsViewOnly ? undefined : "button"}
            tabIndex={resultsViewOnly ? undefined : 0}
            className={`cm-rubber-card ${isLive ? "is-live" : ""} ${
              row.rubber_id === rubber?.rubber_id ? "is-on" : ""
            } ${isDone ? "is-done" : ""} ${
              winnerSide ? `is-winner-${winnerSide}` : ""
            }${resultsViewOnly ? " is-readonly" : ""}`}
            onClick={resultsViewOnly ? undefined : () => selectRubber(row)}
            onKeyDown={
              resultsViewOnly
                ? undefined
                : (e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      selectRubber(row);
                    }
                  }
            }
          >
            <div className="cm-rubber-card__matchup" aria-label={`${homeLabel} vs ${awayLabel}`}>
              <div
                className={`cm-rubber-card__fighter${
                  winnerSide === "home" ? " is-winner" : ""
                }${winnerSide === "away" ? " is-loser" : ""}`}
              >
                {winnerSide === "home" ? (
                  <span className="cm-rubber-card__result cm-rubber-card__result--win">
                    <Trophy size={12} aria-hidden />
                    {t("cmWin", "Win")}
                  </span>
                ) : null}
                {winnerSide === "away" ? (
                  <span className="cm-rubber-card__result cm-rubber-card__result--lose">
                    {t("cmLoss", "Loss")}
                  </span>
                ) : null}
                <strong className="cm-rubber-card__names-line">{homeLabel}</strong>
                {home.some((p) => formatPlayerMeta(p, t)) ? (
                  <span className="cm-rubber-card__meta-line">
                    {home
                      .map((p) => formatPlayerMeta(p, t))
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                ) : null}
                {resultsViewOnly && isDone && ourSide === "home" ? (
                  <ChampionshipPlayerNotes
                    matchId={matchId}
                    rubberId={row.rubber_id}
                    side="home"
                    players={home.filter(
                      (p) => p?.name && !/^Seat\s/i.test(String(p.name))
                    )}
                    opponents={away.filter(
                      (p) => p?.name && !/^Seat\s/i.test(String(p.name))
                    )}
                    notes={playerNotes}
                    onChanged={refreshPlayerNotes}
                    primaryColor={brand?.color_primary}
                    align="left"
                    viewerUserId={userId}
                  />
                ) : null}
              </div>
              <span className="cm-rubber-card__sep">{t("competitionVs", "Vs.")}</span>
              <div
                className={`cm-rubber-card__fighter cm-rubber-card__fighter--away${
                  winnerSide === "away" ? " is-winner" : ""
                }${winnerSide === "home" ? " is-loser" : ""}`}
              >
                {winnerSide === "away" ? (
                  <span className="cm-rubber-card__result cm-rubber-card__result--win">
                    <Trophy size={12} aria-hidden />
                    {t("cmWin", "Win")}
                  </span>
                ) : null}
                {winnerSide === "home" ? (
                  <span className="cm-rubber-card__result cm-rubber-card__result--lose">
                    {t("cmLoss", "Loss")}
                  </span>
                ) : null}
                <strong className="cm-rubber-card__names-line">{awayLabel}</strong>
                {away.some((p) => formatPlayerMeta(p, t)) ? (
                  <span className="cm-rubber-card__meta-line">
                    {away
                      .map((p) => formatPlayerMeta(p, t))
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                ) : null}
                {resultsViewOnly && isDone && ourSide === "away" ? (
                  <ChampionshipPlayerNotes
                    matchId={matchId}
                    rubberId={row.rubber_id}
                    side="away"
                    players={away.filter(
                      (p) => p?.name && !/^Seat\s/i.test(String(p.name))
                    )}
                    opponents={home.filter(
                      (p) => p?.name && !/^Seat\s/i.test(String(p.name))
                    )}
                    notes={playerNotes}
                    onChanged={refreshPlayerNotes}
                    primaryColor={brand?.color_primary}
                    align="right"
                    viewerUserId={userId}
                  />
                ) : null}
              </div>
            </div>

            <div className="cm-rubber-card__top">
              <em>{rubberCodeLabel(row.code)}</em>
              {canAssignTable && row.state !== "done" ? (
                <button
                  type="button"
                  className={`cm-rubber-card__table-btn${
                    tableHasOnlineCamera(snapshot?.cameras, row.table_number)
                      ? " has-cam"
                      : ""
                  }${
                    tableEditRubberId === row.rubber_id ? " is-on" : ""
                  }`}
                  onClick={(event) => {
                    event.stopPropagation();
                    setTableEditRubberId((current) =>
                      current === row.rubber_id ? null : row.rubber_id
                    );
                  }}
                >
                  {t("cmTable", "Table")} {row.table_number}
                  {tableHasOnlineCamera(snapshot?.cameras, row.table_number) ? (
                    <Camera size={11} aria-hidden />
                  ) : null}
                </button>
              ) : (
                <span
                  className={`cm-rubber-card__table${
                    tableHasOnlineCamera(snapshot?.cameras, row.table_number)
                      ? " has-cam"
                      : ""
                  }`}
                >
                  {t("cmTable", "Table")} {row.table_number}
                  {tableHasOnlineCamera(snapshot?.cameras, row.table_number) ? (
                    <Camera size={11} aria-hidden />
                  ) : null}
                </span>
              )}
              {isLive ? (
                <span className="cm-rubber-card__badge cm-rubber-card__badge--live">
                  <i aria-hidden />
                  {t("cmLive", "Live")}
                </span>
              ) : null}
              {isDone ? (
                <span className="cm-rubber-card__badge cm-rubber-card__badge--final">
                  <CheckCircle2 size={12} aria-hidden />
                  {t("cmFinal", "Final")}
                </span>
              ) : null}
              <div
                className="cm-rubber-card__scoreline"
                aria-label={`${homeSets}–${awaySets}`}
              >
                <span
                  className={`cm-rubber-card__setn${
                    winnerSide === "home" ? " is-win" : ""
                  }${winnerSide === "away" ? " is-lose" : ""}`}
                >
                  {homeSets}
                </span>
                <span className="cm-rubber-card__score-sep">–</span>
                <span
                  className={`cm-rubber-card__setn${
                    winnerSide === "away" ? " is-win" : ""
                  }${winnerSide === "home" ? " is-lose" : ""}`}
                >
                  {awaySets}
                </span>
              </div>
              {pointLabels.length ? (
                <p className="cm-rubber-card__points">{pointLabels.join(" · ")}</p>
              ) : null}
            </div>
            {canAssignTable &&
            tableEditRubberId === row.rubber_id &&
            row.state !== "done" ? (
              <div className="cm-rubber-card__assign">
                <ChampionshipTablePicker
                  rubber={row}
                  snapshot={snapshot}
                  tables={availableTables}
                  busy={tableAssignBusy}
                  compact
                  onAssign={assignRubberTable}
                />
              </div>
            ) : null}
            {(tapeCount > 0 ||
            storedClips ||
            (isDone && (canReview || reviewUploading))) ? (
              <div className="cm-rubber-card__review">
                {tapeCount > 0 || canReview || storedClips ? (
                  <button
                    type="button"
                    className="cm-rubber-card__review-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      pendingMoveRef.current = null;
                      setReviewRubberId(row.rubber_id);
                    }}
                    aria-label={t("cmMatchReview", "Match review")}
                    title={t("cmMatchReview", "Match review")}
                  >
                    <Video size={16} aria-hidden />
                    {tapeCount === 1
                      ? t("cmReviewOneVideo", "Review video")
                      : tapeCount > 1
                        ? t("cmReviewVideoCount", "Review {{n}} videos", {
                            n: tapeCount,
                          })
                        : t("cmMatchReview", "Match review")}
                  </button>
                ) : (
                  <span className="cm-rubber-card__review-wait">
                    {t(
                      "cmReviewWaitingUploads",
                      "Waiting for videos to finish uploading…"
                    )}
                  </span>
                )}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );

  return (
    <div
      className={`cm-page cm-page--${mode}${inMatch ? " cm-page--in-match" : ""}${
        cameraArmed ? " cm-page--cam-armed" : ""
      }${camLandscape ? " cm-page--cam-landscape" : ""}${
        cameraDocked ? " cm-page--cam-docked" : ""
      }${
        cameraSettingsOpen && cameraArmed ? " cm-page--cam-settings" : ""
      }${scorePadActive ? " cm-page--score-pad" : ""}${
        showCheer ? " cm-page--cheer" : ""
      }${tab === "replay" ? " cm-page--timeline" : ""}`}
    >
      <ChampionshipFullscreenGate enabled={cameraFullscreen || scorePadFullscreen} />
      <header className="cm-head">
        <button
          type="button"
          className="cm-head__back"
          onClick={() => (inMatch ? exitMatch() : navigate("/competition"))}
          aria-label={
            inMatch ? t("cmAllMatches", "All matches") : t("cmBack", "Back to calendar")
          }
        >
          <ChevronLeft size={20} />
        </button>
        <div className="cm-head__vs">
          <p className="cm-head__kicker">
            {inMatch
              ? `${rubberCodeLabel(rubber.code)} · ${t("cmTable", "Table")} ${rubber.table_number}`
              : t("cmFeuille", "Feuille de match")}
          </p>
          <h1>
            {inMatch
              ? `${rubberHomeName} ${t("competitionVs", "vs")} ${rubberAwayName}`
              : `${homeName} ${t("competitionVs", "vs")} ${awayName}`}
          </h1>
          {!inMatch ? (
            <Link
              className="cm-head__prep"
              to={`/tactical/prep?name=${encodeURIComponent(awayName)}&matchId=${encodeURIComponent(matchId || "")}`}
            >
              {t("schemaPrepCta", "Prepare an opponent")}
            </Link>
          ) : (
            <p className="cm-head__match-hint">
              {awaitingNextSet
                ? tableIsFilming(rubber?.table_number)
                  ? t(
                      "cmSetOverHintRecording",
                      "Set over — recording stays on until you end it"
                    )
                  : t(
                      "cmSetOverHint",
                      "Set over — start the next set when you are ready"
                    )
                : rubberLive
                ? t("cmInMatchLive", "Live match — score and film from here")
                : rubberDone
                  ? t("cmRubberDone", "This match is finished.")
                  : t("cmInMatchReady", "In this match — start, score, or film")}
            </p>
          )}
        </div>
        <div className="cm-head__score">
          {inMatch ? (
            <>
              <span className="cm-head__live-score">
                {shownHome}–{shownAway}
              </span>
              <span>
                {t("cmSets", "Sets")} {shownSetsHome}–{shownSetsAway}
              </span>
              {boardFresh ? (
                <BoardScoreLabel
                  reading={boardScore}
                  t={t}
                  className={
                    useBoardAsLive
                      ? "cm-head__board cm-head__board--live"
                      : "cm-head__board"
                  }
                />
              ) : null}
              {boardClosingSet ? (
                <span className="cm-head__board-hint">
                  {t(
                    "cmBoardSetWillClose",
                    "Board {{score}} — set closes if unchanged for {{n}}s",
                    {
                      score: `${boardScore.board_left}–${boardScore.board_right}`,
                      n: setCloseStableSec,
                    }
                  )}
                </span>
              ) : null}
            </>
          ) : (
            <>
              {match.home_team_score}–{match.away_team_score}
              <span>
                {t("cmSets", "Sets")} {match.home_sets_total}–{match.away_sets_total}
              </span>
            </>
          )}
          {videoFeatureOn && filmingTableNumbers.length ? (
            <span
              className="cm-rec cm-head__rec"
              title={recordingTablesLabel(filmingTableNumbers, t)}
            >
              <Video size={12} aria-hidden />
              <i />
              {recordingTablesLabel(filmingTableNumbers, t)}
            </span>
          ) : null}
        </div>
      </header>

      {error || mediaStatus ? <p className="cm-banner">{error || mediaStatus}</p> : null}

      {videoFeatureOn && !resultsViewOnly && matchCameras.length > 0 && tab !== "camera" ? (
        <ul className="cm-cam-status" aria-live="polite">
          {matchCameras.map((cam) => {
            const thisPhone = cameraIsThisPhone(cam, userId, phoneId);
            const online = cameraIsOnline(cam);
            return (
              <li
                key={cam.camera_id || `${cam.user_id}-${cam.device_id}-${cam.table_number}`}
                className={`cm-cam-status__item${
                  thisPhone ? " is-this" : ""
                }${online ? " is-online" : " is-offline"}`}
              >
                <Camera size={14} aria-hidden />
                <span>{cameraPhoneLabel(cam)}</span>
                {thisPhone ? (
                  <button
                    type="button"
                    className="cm-cam-status__remove"
                    onClick={() => void releaseCam()}
                  >
                    {t("cmRemoveCamera", "Remove")}
                  </button>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : null}

      {(!inMatch || cameraTableSetup) &&
      !resultsViewOnly &&
      !cameraArmed ? (
      <div
        className={`cm-tables${tab === "camera" ? " cm-tables--pick" : ""}`}
        role={tab === "camera" ? "group" : "status"}
        aria-label={
          tab === "camera" ? t("cmPickTable", "Choose a table") : undefined
        }
      >
        {[
          ...new Set([
            1,
            2,
            ...(snapshot?.cameras || [])
              .map((c) => Number(c.table_number))
              .filter((n) => Number.isFinite(n) && n > 0),
            ...(snapshot?.rubbers || [])
              .map((r) => Number(r.table_number))
              .filter((n) => Number.isFinite(n) && n > 0),
          ]),
        ]
          .sort((a, b) => a - b)
          .map((n) => {
          const live = snapshot.rubbers.find(
            (r) => Number(r.table_number) === n && r.state === "live"
          );
          const cams = (snapshot.cameras || []).filter(
            (c) => Number(c.table_number) === n
          );
          const camsOnline = cams.filter((c) => c.online || c.socket_id);
          const matchup = rubberMatchupForTable(snapshot, n);
          const tableIsRec = tableIsFilming(n);
          const selected = tab === "camera" && Number(tableNum) === n;
          const TableTag = tab === "camera" ? "button" : "div";
          return (
            <TableTag
              key={n}
              type={tab === "camera" ? "button" : undefined}
              className={`cm-table${live ? " is-live" : ""}${
                camsOnline.length ? " has-cam" : ""
              }${selected ? " is-selected" : ""}`}
              onClick={
                tab === "camera" ? () => setTableNum(n) : undefined
              }
              aria-pressed={tab === "camera" ? selected : undefined}
            >
              <strong>
                {t("cmTable", "Table")} {n}
                {videoFeatureOn && tableIsRec ? (
                  <span className="cm-rec cm-table__rec" aria-label={t("cmRecording", "Recording")}>
                    <i />
                  </span>
                ) : null}
              </strong>
              <span className="cm-table__meta">
                {live || cams.length
                  ? matchup?.label ||
                    (live
                      ? `${shortNames(live.home_names, live.home_seats, "")} · ${shortNames(live.away_names, live.away_seats, "")}`
                      : t("cmTableFree", "Free"))
                  : t("cmTableFree", "Free")}
                {live
                  ? ` · ${Number(live.set_scores?.live_home) || 0}–${Number(live.set_scores?.live_away) || 0}`
                  : ""}
              </span>
              {scoreWatchByTable?.[n]?.phase &&
              scoreWatchByTable[n].phase !== "idle" ? (
                <span className="cm-table__scorewatch">
                  {t("cmScoreWatchRoom", "Scorecam")}
                  {scoreWatchByTable[n].score_text
                    ? ` · ${scoreWatchByTable[n].score_text}`
                    : ` · ${scoreWatchByTable[n].phase}`}
                </span>
              ) : null}
              {videoFeatureOn ? (
                <span
                  className={`cm-table__cams ${camsOnline.length ? "is-on" : ""}`}
                  title={
                    camsOnline.length
                      ? t("cmCamerasConnected", "{{count}} camera(s) connected", {
                          count: camsOnline.length,
                        })
                      : cams.length
                        ? t(
                            "cmCamerasOffline",
                            "Cameras claimed but offline — reopen the camera phone."
                          )
                        : t("cmNoCamera", "No camera")
                  }
                >
                  <Camera size={12} aria-hidden />
                  {camsOnline.length
                    ? t("cmCamCount", "{{count}} cam", { count: camsOnline.length })
                    : cams.length
                      ? t("cmCamOfflineShort", "Cam offline")
                      : t("cmNoCameraShort", "No cam")}
                </span>
              ) : null}
            </TableTag>
          );
        })}
      </div>
      ) : inMatch && !cameraTableSetup && !cameraArmed && !scorePadActive ? (
        <div className="cm-match-status" role="status">
          <span>
            {t("cmTable", "Table")} {rubber.table_number}
          </span>
          {tableIsFilming(rubber.table_number) ? (
            <span className="cm-rec">
              <i />
              {recordingTablesLabel([rubber.table_number], t)}
            </span>
          ) : null}
          {(() => {
            const watch = scoreWatchByTable?.[Number(rubber.table_number)];
            if (!watch?.phase || watch.phase === "idle" || cameraIsScoreCam(myCamera)) {
              return null;
            }
            return (
            <span className="cm-match-status__scorewatch" role="status">
              {t("cmScoreWatchRoom", "Scorecam")} · {watch.phase}
              {watch.score_text ? ` · ${watch.score_text}` : ""}
            </span>
            );
          })()}
        </div>
      ) : null}

      {(tab === "score" || tab === "lineup") &&
      !scorePadActive &&
      showCloseMatch ? (
        <div className="cm-actions">
          <button type="button" className="cm-btn cm-btn--ghost" onClick={closeMatch}>
            {t("cmComplete", "Close")}
          </button>
        </div>
      ) : null}

      <div className="cm-body">
        {tab === "lineup" ? (
          <div className="cm-panel">
            <div
              className={`cm-lineup${
                sheetPhotoAllowed ? " has-sheet-photo" : " is-locked"
              }`}
            >
              {matchDone && !isFeltenFixture ? (
                <p className="cm-lineup__locked" role="status">
                  {t(
                    "cmLineupFinal",
                    "Final line-up from the official result — players cannot be changed."
                  )}
                </p>
              ) : null}
              {sheetPhotoAllowed ? (
                <div className="cm-sheet-photo">
                  {matchDone && isFeltenFixture ? (
                    <p className="cm-lineup__locked" role="status">
                      {t(
                        "cmFeltenSheetPhotoComplete",
                        "Match finished — you can still photograph the feuille to update Felten lineups and scores."
                      )}
                    </p>
                  ) : null}
                  <button
                    type="button"
                    className="cm-btn"
                    disabled={busy || sheetPhotoBusy}
                    onClick={() => sheetCameraRef.current?.click()}
                  >
                    <Camera size={16} aria-hidden />
                    {sheetPhotoBusy
                      ? t("cmSheetPhotoWorking", "Reading the sheet…")
                      : t("cmSheetPhoto", "Photograph match sheet")}
                  </button>
                  <button
                    type="button"
                    className="cm-btn cm-btn--ghost"
                    disabled={busy || sheetPhotoBusy}
                    onClick={() => sheetFileRef.current?.click()}
                  >
                    {t("cmSheetPhotoFile", "Choose photo")}
                  </button>
                  <input
                    ref={sheetCameraRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    hidden
                    onChange={onSheetPhoto}
                  />
                  <input
                    ref={sheetFileRef}
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={onSheetPhoto}
                  />
                  <p className="cm-sheet-photo__note">
                    {sheetPhotoNote ||
                      t(
                        "cmSheetPhotoHint",
                        "Photograph the feuille so the four players and their positions replace the lineup. If results are already written, each match is updated with its final score and its set scores."
                      )}
                  </p>
                </div>
              ) : null}
              {["home", "away"].map((side) => {
                const sidePool =
                  snapshot.lineup_pools?.[side] ||
                  (snapshot.opponent_pool?.side === side
                    ? snapshot.opponent_pool
                    : null);
                const poolPlayers = sidePool?.players || [];
                const pickingSwap =
                  !matchDone && swapPick?.side === side ? swapPick : null;
                const canFreeOrder = canReorderOurFour && side === ourSide;
                return (
                  <section key={side}>
                    <h2>{side === "home" ? homeName : awayName}</h2>
                    <p className="cm-lineup__section-label">
                      {matchDone
                        ? t("cmFinalTeam", "Final team")
                        : lineupSaved
                          ? t("cmSavedTeam", "Saved line-up")
                          : t("cmTitularTeam", "Titular team (theoretical)")}
                    </p>
                    {pickingSwap ? (
                      <p className="cm-lineup__swap-hint">
                        {t(
                          "cmPoolSwapHint",
                          "Tap a starter to swap with {{name}}.",
                          {
                            name:
                              pickingSwap.player.display_name ||
                              pickingSwap.player.name ||
                              "—",
                          }
                        )}
                      </p>
                    ) : null}
                    <ol>
                      {(snapshot.lineup?.[side] || []).map((slot, idx, all) => (
                        <li
                          key={`${side}-${slot.seat}`}
                          className={pickingSwap ? "is-swap-target" : undefined}
                          onClick={
                            pickingSwap
                              ? (event) => {
                                  if (event.target.closest("button")) return;
                                  applyOpponentPoolPlayer(
                                    pickingSwap.player,
                                    slot.seat,
                                    side
                                  );
                                }
                              : undefined
                          }
                        >
                          <span className="pos">{slot.seat}</span>
                          <div>
                            <input
                              value={slot.display_name || ""}
                              readOnly={matchDone || Boolean(pickingSwap)}
                              disabled={matchDone}
                              onChange={(e) => {
                                if (matchDone) return;
                                const next = snapshot.lineup[side].map((s) =>
                                  s.seat === slot.seat
                                    ? { ...s, display_name: e.target.value }
                                    : s
                                );
                                setSnapshot({
                                  ...snapshot,
                                  lineup: { ...snapshot.lineup, [side]: next },
                                });
                              }}
                              onBlur={(e) =>
                                onRename(side, slot.seat, e.target.value)
                              }
                            />
                            <small>
                              {slot.classement || "—"}
                              {slot.ranking_points != null
                                ? ` · ${slot.ranking_points}`
                                : ""}
                            </small>
                          </div>
                          {!matchDone &&
                          ((canSetOppAvailability &&
                            side === oppSide &&
                            (slot.display_name ||
                              slot.user_id ||
                              slot.player_id)) ||
                            (idx < all.length - 1 &&
                              (canFreeOrder ||
                                seatsInSameTie(
                                  snapshot.tie_groups?.[side],
                                  slot.seat,
                                  all[idx + 1].seat
                                )))) ? (
                            <span className="cm-lineup__acts">
                              {!matchDone &&
                              canSetOppAvailability &&
                              side === oppSide &&
                              (slot.display_name ||
                                slot.user_id ||
                                slot.player_id) ? (
                                <button
                                  type="button"
                                  className="cm-opp-avail cm-opp-avail--bench cm-opp-avail--icon"
                                  disabled={busy}
                                  onClick={() =>
                                    void setOpponentAvailability(slot, "bench")
                                  }
                                  aria-label={t(
                                    "cmOpponentNotPresent",
                                    "Not present"
                                  )}
                                  title={t(
                                    "cmOpponentNotPresent",
                                    "Not present — fill with next ranked player"
                                  )}
                                >
                                  <X size={14} strokeWidth={2.4} aria-hidden />
                                </button>
                              ) : null}
                              {!matchDone &&
                              idx < all.length - 1 &&
                              (canFreeOrder ||
                                seatsInSameTie(
                                  snapshot.tie_groups?.[side],
                                  slot.seat,
                                  all[idx + 1].seat
                                )) ? (
                                <button
                                  type="button"
                                  className="cm-swap"
                                  onClick={() =>
                                    onSwap(side, slot.seat, all[idx + 1].seat)
                                  }
                                  aria-label={t(
                                    "cmMoveSeatDown",
                                    "Move to the next position"
                                  )}
                                  title={t(
                                    "cmMoveSeatDown",
                                    "Move to the next position"
                                  )}
                                >
                                  ↕
                                </button>
                              ) : null}
                            </span>
                          ) : null}
                        </li>
                      ))}
                    </ol>
                    {!matchDone &&
                    oppSide &&
                    side === oppSide &&
                    poolPlayers.length ? (
                      <OpponentPoolPanel
                        poolPlayers={poolPlayers}
                        lineupSide={snapshot.lineup?.[side]}
                        onToggleSwap={(player) => togglePoolSwap(side, player)}
                        onSetAvailability={(player, status) =>
                          void setOpponentAvailability(player, status)
                        }
                        t={t}
                        extrasOnly={Boolean(
                          (snapshot.lineup?.[side] || []).some(
                            (s) => s.display_name || s.user_id || s.player_id
                          )
                        )}
                        availabilityMode={
                          canSetOppAvailability && side === oppSide
                        }
                        swapPlayerKey={
                          swapPick?.side === side ? swapPick.key : null
                        }
                      />
                    ) : null}
                  </section>
                );
              })}
              {!matchDone ? (
                <>
                  {lineupSaved ? (
                    <p className="cm-hint">
                      {t(
                        "cmLineupSaved",
                        "This line-up is saved on the match. Anyone who opens it later sees these players and positions."
                      )}
                    </p>
                  ) : null}
                  <p className="cm-hint">
                    {canReorderOurFour
                      ? t(
                          "cmOrderHint",
                          "Move your four players between positions 1 and 4."
                        )
                      : t(
                          "cmTieHint",
                          "Players with the same classement can be swapped. Different levels stay in rating order."
                        )}
                  </p>
                  {oppSide && canSetOppAvailability ? (
                    <p className="cm-hint">
                      {t(
                        "cmOpponentPoolHint",
                        "Mark an opponent with × if they are not present — the next highest ranking player fills the fourth seat. For another player, tap Swap, then the starter to replace."
                      )}
                    </p>
                  ) : null}
                </>
              ) : null}
            </div>
          </div>
        ) : null}

        {tab === "score" ? (
          <div className="cm-panel">
            {sheetBrowsing || !rubber ? (
              <>
                <p className="cm-sheet-label">
                  {resultsViewOnly
                    ? t("cmFinalSheet", "Final results — scores only.")
                    : t("cmPickRubber", "Tap a rubber to start.")}
                </p>
                {sheetPhotoNote ? (
                  <p className="cm-sheet-photo__note" role="status">
                    {sheetPhotoNote}
                  </p>
                ) : null}
                <div className="cm-scroll cm-scroll--sheet">
                  {renderRubberSheetList()}
                </div>
              </>
            ) : (
              <div
                className={`cm-live${
                  scoreMode === "edit" ? " cm-live--score-edit" : ""
                }`}
              >
                {scorePadActive ? (
                  <ChampionshipScorePad
                    t={t}
                    mode={mode}
                    brandPrimary={brand?.color_primary || brand?.primary || "#0877bc"}
                    brandSecondary={
                      brand?.color_secondary ||
                      brand?.color_accent ||
                      brand?.secondary ||
                      "#c2410c"
                    }
                    homeLogo={homeLogo}
                    awayLogo={awayLogo}
                    homeClubName={homeClubName}
                    awayClubName={awayClubName}
                    tableNumber={rubber.table_number}
                    rubberCode={rubberCodeLabel(rubber.code)}
                    homeName={rubberHomeName}
                    awayName={rubberAwayName}
                    homePlayers={homePlayers}
                    awayPlayers={awayPlayers}
                    liveHome={shownHome}
                    liveAway={shownAway}
                    setsHome={shownSetsHome}
                    setsAway={shownSetsAway}
                    setsToWin={setsToWin}
                    completedSets={Array.isArray(liveScore.completed) ? liveScore.completed : []}
                    setWinner={setWinner}
                    canScore={canScore}
                    canUndo={canUndo}
                    canMinusHome={canMinusHome}
                    canMinusAway={canMinusAway}
                    canStartSet={canStartSet}
                    canToggleTimeout={canToggleTimeout}
                    timeoutActive={timeoutActive}
                    onToggleTimeout={() => void toggleTimeout()}
                    scoreMode={scoreMode}
                    editHome={editHome}
                    editAway={editAway}
                    busy={busy}
                    onBack={exitMatch}
                    onAddPoint={(side) => void addPoint(side)}
                    onRemovePoint={(side) => void removePoint(side)}
                    onUndo={() => void undo()}
                    onStartSet={() => void startSet()}
                    onScoreMode={setScoreMode}
                    onEditHome={setEditHome}
                    onEditAway={setEditAway}
                    onSaveEdit={() => void saveEditScore()}
                    onThemeToggle={() =>
                      setThemeMode(mode === "light" ? "dark" : "light")
                    }
                    manualFlip={scoreSidesManualFlip}
                    onToggleManualFlip={() =>
                      setScoreSidesManualFlip((v) => !v)
                    }
                    canTagOutcome={Boolean(userId)}
                    activeOutcome={scorePadOutcome}
                    onOutcome={(outcomeId) => void stampOutcome(outcomeId)}
                    canEndRubber={
                      isScorer &&
                      rubberLive &&
                      !matchDone &&
                      !rubberDone
                    }
                    onEndRubber={endRubber}
                    canResetMatch={canModerateMatch && !matchDone}
                    onResetMatch={resetMatch}
                    canReleaseScore={isScorer && !matchDone}
                    onReleaseScore={releaseScore}
                    showMatchRecording={
                      videoFeatureOn &&
                      !resultsViewOnly &&
                      (tableHasConnectedCamera(rubber.table_number) ||
                        tableIsFilming(rubber.table_number))
                    }
                    matchRecording={tableIsFilming(rubber.table_number)}
                    recordBusy={recordBusy}
                    onStartRecording={() =>
                      void startManualRecording(rubber.table_number)
                    }
                    onEndRecording={() =>
                      void endManualRecording(rubber.table_number)
                    }
                  />
                ) : (
                <>
                <div className="cm-focus-bar">
                  <button
                    type="button"
                    className="cm-focus-bar__back"
                    onClick={exitMatch}
                  >
                    <ChevronLeft size={18} aria-hidden />
                    {t("cmAllMatches", "All matches")}
                  </button>
                  <div className="cm-focus-bar__meta">
                    <em>
                      {t("cmTable", "Table")} {rubber.table_number}
                      {tableHasOnlineCamera(
                        snapshot?.cameras,
                        rubber.table_number
                      )
                        ? ` · ${t("cmCamReadyShort", "cam")}`
                        : ""}
                    </em>
                    <span>
                      {rubberCodeLabel(rubber.code)}
                      {rubberLive
                        ? ` · ${t("cmLive", "Live")}`
                        : rubberDone
                          ? ` · ${t("cmDone", "Done")}`
                          : ""}
                    </span>
                  </div>
                  {canUndo ? (
                    <button
                      type="button"
                      className="cm-icon-btn"
                      onClick={undo}
                      aria-label={t("cmUndo", "Undo")}
                      title={t("cmUndo", "Undo")}
                    >
                      <Undo2 size={16} />
                    </button>
                  ) : (
                    <span className="cm-icon-btn cm-icon-btn--ghost" aria-hidden />
                  )}
                </div>

                <div
                  className={`cm-scoredeck${setWinner ? " is-set-over" : ""}${
                    scoreMode === "edit" ? " cm-scoredeck--score-edit" : ""
                  }`}
                  aria-live="polite"
                >
                  <div
                    className={`cm-scoredeck__lane${
                      setWinner === "home" ? " is-winner" : ""
                    }`}
                  >
                    {renderPlayerBlock(homePlayers, "left")}
                    <strong className="cm-scoredeck__pts">{shownHome}</strong>
                    {canScore && rubberLive && scoreMode === "input" ? (
                      <div className="cm-scoredeck__btns">
                        <button
                          type="button"
                          className="cm-scoredeck__btn"
                          disabled={!canMinusHome}
                          onClick={() => removePoint("home")}
                          aria-label={t("cmMinusPoint", "Minus one point")}
                        >
                          <Minus size={28} strokeWidth={3} aria-hidden />
                        </button>
                        <button
                          type="button"
                          className="cm-scoredeck__btn cm-scoredeck__btn--plus cm-scoredeck__btn--home"
                          onClick={() => addPoint("home")}
                          aria-label={`${t("cmPlusPoint", "Plus one point")} ${rubberHomeName}`}
                        >
                          <Plus size={28} strokeWidth={3} aria-hidden />
                        </button>
                      </div>
                    ) : null}
                  </div>

                  <div className="cm-scoredeck__sets" aria-label={t("cmSets", "Sets")}>
                    <strong>
                      {shownSetsHome}–{shownSetsAway}
                    </strong>
                    <span>{t("cmSets", "Sets")}</span>
                  </div>

                  <div
                    className={`cm-scoredeck__lane cm-scoredeck__lane--away${
                      setWinner === "away" ? " is-winner" : ""
                    }`}
                  >
                    {renderPlayerBlock(awayPlayers, "right")}
                    <strong className="cm-scoredeck__pts">{shownAway}</strong>
                    {canScore && rubberLive && scoreMode === "input" ? (
                      <div className="cm-scoredeck__btns">
                        <button
                          type="button"
                          className="cm-scoredeck__btn"
                          disabled={!canMinusAway}
                          onClick={() => removePoint("away")}
                          aria-label={t("cmMinusPoint", "Minus one point")}
                        >
                          <Minus size={28} strokeWidth={3} aria-hidden />
                        </button>
                        <button
                          type="button"
                          className="cm-scoredeck__btn cm-scoredeck__btn--plus cm-scoredeck__btn--away"
                          onClick={() => addPoint("away")}
                          aria-label={`${t("cmPlusPoint", "Plus one point")} ${rubberAwayName}`}
                        >
                          <Plus size={28} strokeWidth={3} aria-hidden />
                        </button>
                      </div>
                    ) : null}
                  </div>
                </div>

                {setWinner ? (
                  <p
                    className={`cm-set-banner${
                      setWinner === "home" ? " is-home" : " is-away"
                    }`}
                  >
                    {t("cmSetWinner", "{{name}} wins the set {{score}}", {
                      name: setWinner === "home" ? rubberHomeName : rubberAwayName,
                      score: `${shownHome}–${shownAway}`,
                    })}
                  </p>
                ) : null}

                {canStartSet ? (
                  <button
                    type="button"
                    className="cm-btn cm-btn--block cm-btn--start-set"
                    onClick={() => void startSet()}
                  >
                    {t("cmStartSet", "Start Set")}
                  </button>
                ) : null}

                {userId && (lastPoint || canScore) ? (
                  <PointOutcomeBar
                    t={t}
                    className="cm-outcome--score"
                    activeOutcome={scorePadOutcome}
                    disabled={timeoutActive || (!lastPoint && !canScore)}
                    onSelect={(outcomeId) => void stampOutcome(outcomeId)}
                  />
                ) : null}

                {canScore && rubberLive ? (
                  <>
                    <div className="cm-score-modes" role="tablist">
                      <button
                        type="button"
                        className={scoreMode === "input" ? "is-on" : ""}
                        onClick={() => setScoreMode("input")}
                      >
                        {t("cmInputScore", "Input ±")}
                      </button>
                      <button
                        type="button"
                        className={scoreMode === "edit" ? "is-on" : ""}
                        onClick={() => setScoreMode("edit")}
                      >
                        <Pencil size={12} aria-hidden />
                        {t("cmEditScore", "Edit score")}
                      </button>
                    </div>
                    {scoreMode === "edit" ? (
                      <div className="cm-edit-score">
                        <label>
                          {rubberHomeName}
                          <input
                            inputMode="numeric"
                            value={editHome}
                            onChange={(e) =>
                              setEditHome(e.target.value.replace(/[^\d]/g, ""))
                            }
                          />
                        </label>
                        <span>–</span>
                        <label>
                          {rubberAwayName}
                          <input
                            inputMode="numeric"
                            value={editAway}
                            onChange={(e) =>
                              setEditAway(e.target.value.replace(/[^\d]/g, ""))
                            }
                          />
                        </label>
                        <button
                          type="button"
                          className="cm-btn"
                          disabled={busy}
                          onClick={saveEditScore}
                        >
                          {t("cmSaveScore", "Set score")}
                        </button>
                      </div>
                    ) : null}
                  </>
                ) : null}

                {canAssignTable && !rubberDone ? (
                  <ChampionshipTablePicker
                    rubber={rubber}
                    snapshot={snapshot}
                    tables={availableTables}
                    busy={tableAssignBusy}
                    onAssign={assignRubberTable}
                  />
                ) : null}

                <div className="cm-score-cta">
                  {renderMatchRecordButton({
                    table: Number(rubber.table_number),
                    block: true,
                  })}
                  {showClaimScore ? (
                    <button
                      type="button"
                      className="cm-btn cm-btn--block"
                      disabled={busy}
                      onClick={() => void claimScore()}
                    >
                      {t("cmClaimScore", "Claim live score")}
                    </button>
                  ) : null}
                  {showScoreCameraBlocksClaim ? (
                    <p className="cm-status-chip cm-status-chip--block">
                      {t(
                        "cmScoreCameraBlocksClaim",
                        "Score camera is reading the points. You can still tag the last point or send an emoji."
                      )}
                    </p>
                  ) : null}
                  {showScorerTaken ? (
                    <p className="cm-status-chip cm-status-chip--block">
                      {t("cmScorerTaken", "Score input is taken")}
                    </p>
                  ) : null}
                  {canStartRubber ? (
                    <button
                      type="button"
                      className="cm-btn cm-btn--block"
                      onClick={() => void startRubber(rubber)}
                    >
                      {t("cmStartRubber", "Start this match")}
                    </button>
                  ) : null}
                  {isScorer && focusedOnRubber && !matchDone ? (
                    <button
                      type="button"
                      className="cm-btn cm-btn--ghost cm-btn--block"
                      disabled={busy}
                      onClick={() => void releaseScore()}
                    >
                      {t("cmReleaseScore", "Release score")}
                    </button>
                  ) : null}
                </div>

                {isScorer && liveRubberOnSameTable && !rubberLive && !matchDone ? (
                  <p className="cm-follow cm-follow--compact">
                    {t(
                      "cmTableBusyHint",
                      "Table {{table}} already has a live match — finish or switch to it before starting this one.",
                      { table: rubber?.table_number }
                    )}
                    <button
                      type="button"
                      className="cm-btn cm-btn--ghost"
                      style={{ marginTop: "0.45rem" }}
                      onClick={() => {
                        setActiveRubberId(liveRubberOnSameTable.rubber_id);
                        setSheetBrowsing(false);
                        setError("");
                      }}
                    >
                      {t("cmGoToLiveRubber", "Open live match on this table")}
                    </button>
                  </p>
                ) : null}

                {!canScore &&
                !canStartRubber &&
                !canStartSet &&
                !showClaimScore &&
                !isScorer &&
                !liveRubberOnSameTable ? (
                  <p className="cm-follow cm-follow--compact">
                    {matchDone
                      ? t("cmMatchClosed", "This rencontre is closed.")
                      : rubberDone
                        ? t("cmRubberDone", "This match is finished.")
                        : showScoreCameraBlocksClaim
                          ? t(
                              "cmScoreCameraBlocksClaim",
                              "Score camera is reading the points. You can still tag the last point or send an emoji."
                            )
                          : showScorerTaken || scorerTaken
                            ? t("cmFollowScore", "Live score is entered by the claimed scorer.")
                            : t("cmClaimToScore", "Claim live score to enter points.")}
                  </p>
                ) : null}

                {canModerateMatch && !matchDone && !scorePadActive ? (
                  <button
                    type="button"
                    className="cm-reset-discrete"
                    disabled={busy}
                    onClick={() => void resetMatch()}
                  >
                    {t("cmResetMatch", "Reset match")}
                  </button>
                ) : null}
                </>
                )}
              </div>
            )}
          </div>
        ) : null}

        {tab === "control" && showControlTab ? (
          <div className="cm-panel cm-panel--control">
            <ChampionshipControlRoom
              snapshot={snapshot}
              captureState={captureState}
              scoreWatchByTable={scoreWatchByTable}
              canModerate={canModerateMatch}
              canAssign={canAssignTable}
              assignBusy={tableAssignBusy}
              recordBusy={recordBusy}
              onStartTable={(n) => void startManualRecording(n)}
              onStopTable={(n) => void endManualRecording(n)}
              onStartAllLive={() => void startAllLiveTables()}
              onForceRelease={(payload) => void forceReleaseCamera(payload)}
              onAssignMatch={assignRubberTable}
            />
          </div>
        ) : null}

        {tab === "camera" && videoFeatureOn ? (
          <div className={`cm-cam${cameraArmed ? " cm-cam--armed" : ""}`}>
            {!cameraArmed ? (
              <div className="cm-picks" role="group" aria-label={t("cmCamPosition", "Camera position")}>
                {["spectator", "referee", "scorecam"].map((pos) => (
                  <button
                    key={pos}
                    type="button"
                    className={`cm-chip ${
                      normalizeCamPosition(angle) === pos ? "is-on" : ""
                    }`}
                    onClick={() => selectCameraPosition(pos)}
                  >
                    {camPositionLabel(pos, t)}
                  </button>
                ))}
              </div>
            ) : null}
            {!cameraArmed ? renderScorecamCaptureMode("chips") : null}
            {canAssignTable ? (
              <ChampionshipMatchOnTable
                tableNumber={myCamera?.table_number || tableNum}
                snapshot={snapshot}
                busy={tableAssignBusy}
                onAssign={assignRubberTable}
              />
            ) : null}
            {matchCameras.length ? (
              <ul className="cm-cam__roster" aria-live="polite">
                {matchCameras.map((cam) => {
                  const thisPhone = cameraIsThisPhone(cam, userId, phoneId);
                  const online = cameraIsOnline(cam);
                  return (
                    <li
                      key={cam.camera_id || `${cam.user_id}-${cam.device_id}-${cam.table_number}`}
                      className={`${thisPhone ? "is-this" : ""} ${online ? "is-online" : "is-offline"}`}
                    >
                      <Camera size={12} aria-hidden />
                      <span>{cameraPhoneLabel(cam)}</span>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="cm-cam__idle-phones">
                {t(
                  "cmNoPhonesYet",
                  "No phones claimed yet — pick a table and use as camera."
                )}
              </p>
            )}
            <div
              ref={previewStageRef}
              className={`cm-cam__preview${hasPreviewStream ? " is-live" : ""}`}
              aria-label={
                hasPreviewStream ? t("cmPinchZoom", "Pinch to zoom") : undefined
              }
            >
              {hasPreviewStream ? (
                <video
                  ref={bindPreviewVideo}
                  autoPlay
                  playsInline
                  muted
                  style={{
                    transform: previewZoomTransform(
                      cameraZoom,
                      cameraHardwareZoom,
                      Boolean(cameraZoomRange)
                    ),
                  }}
                />
              ) : (
                <div
                  className={`cm-cam__idle${tableGuideOn ? " cm-cam__idle--guide" : ""}`}
                  role="status"
                >
                  <Camera size={28} aria-hidden />
                  <p>
                    {t(
                      "cmCameraIdleHint",
                      "Pick a table and a camera position, then connect."
                    )}
                  </p>
                </div>
              )}
              <TableCameraGuide
                position={normalizeCamPosition(myCamera?.angle_label || angle)}
                players={scorecamRecordVideo}
                show={tableGuideOn}
                onToggle={toggleTableGuide}
              />
              {thisPhoneRecording ? (
                <span className="cm-cam__badge cm-rec">
                  <Video size={12} aria-hidden />
                  <i />
                  {recordingTablesLabel([thisPhoneTable], t)}
                </span>
              ) : showCameraPreview && !cameraArmed ? (
                <span className="cm-cam__badge cm-preview-badge">
                  {t("cmPreview", "Preview")}
                </span>
              ) : null}
              {hasPreviewStream && cameraZoom > 1.02 ? (
                <span className="cm-cam__badge cm-cam__badge--zoom" aria-live="polite">
                  {formatCameraZoomLabel(cameraZoom)}
                </span>
              ) : null}
              {hasPreviewStream && scoreRoiMode ? (
                <ScoreBoxLayer
                  videoEl={previewVideoEl}
                  fit="cover"
                  drawing={scoreRoiMode}
                  roi={normalizeRoi(myCamera?.score_roi)}
                  tone={scoreWatch.phase || ""}
                  onCommit={(box) => void finishLiveScoreRoi(box)}
                />
              ) : null}
              {cameraArmed && cameraIsScoreCam(myCamera) ? (
                <div
                  className={`cm-score-ai-hud is-${scoreWatch.phase || "idle"}`}
                  aria-live="polite"
                >
                  {(() => {
                    const meta = scoreAiStatusMeta(scoreWatch.phase, t);
                    const Icon = meta.icon;
                    return (
                      <div className="cm-score-ai-hud__status">
                        <Icon
                          size={15}
                          aria-hidden
                          className={meta.spin ? "is-spin" : undefined}
                        />
                        <span>
                          {meta.label}
                          {scoreWatch.detail ? ` · ${scoreWatch.detail}` : ""}
                        </span>
                        {scoreWatch.scoreText ? (
                          <strong>{scoreWatch.scoreText}</strong>
                        ) : null}
                      </div>
                    );
                  })()}
                  {scoreWatch.gate ? (
                    <div className="cm-score-ai-hud__gate" aria-hidden>
                      <span>
                        gate {scoreWatch.gate.reason}
                        {scoreWatch.gate.vsCommit
                          ? ` · ${scoreWatch.gate.vsCommit}`
                          : ""}
                      </span>
                      <span>
                        {scoreWatch.gate.src || ""}
                        {scoreWatch.gate.blobKb != null
                          ? ` · ${scoreWatch.gate.blobKb}KB`
                          : ""}
                        {` · t${scoreWatch.gate.ticks || 0}/r${scoreWatch.gate.reads || 0}`}
                      </span>
                    </div>
                  ) : null}
                  {(scoreWatch.logs || []).length ? (
                    <ol className="cm-score-ai-hud__log">
                      {[...(scoreWatch.logs || [])]
                        .slice()
                        .reverse()
                        .map((row, idx) => (
                          <li
                            key={`${row.t}-${idx}`}
                            className={`is-${row.level || "info"}`}
                          >
                            <time>{formatScoreAiLogTime(row.t)}</time>
                            <span>{row.msg}</span>
                          </li>
                        ))}
                    </ol>
                  ) : null}
                </div>
              ) : null}
              {scoreRoiMode && hasPreviewStream ? (
                <div className="cm-chrono__roi cm-cam__score-roi-draw">
                  <p className="cm-chrono__roi-hint">
                    {t(
                      "cmDrawScoreBoxLive",
                      "One finger draws the box. Two fingers zoom and pan."
                    )}
                  </p>
                  <button
                    type="button"
                    className="cm-chrono__roi-cancel"
                    onPointerDown={(event) => event.stopPropagation()}
                    onClick={(event) => {
                      event.stopPropagation();
                      setScoreRoiMode(false);
                    }}
                  >
                    {t("cancel", "Cancel")}
                  </button>
                </div>
              ) : null}
            </div>
            {cameraArmed ? (
              <p className="cm-cam__armed-info" role="status">
                <Camera size={14} aria-hidden />
                <span>
                {(() => {
                  const table = myCamera?.table_number || tableNum;
                  const matchup = rubberMatchupForTable(snapshot, table);
                  const pos = camPositionLabel(
                    myCamera?.angle_label || angle,
                    t
                  );
                  if (cameraIsScoreCam(myCamera)) {
                    if (!myCamera?.score_roi) {
                      return t(
                        "cmScorecamChecklistRoi",
                        "Scorecam · table {{n}} · 1) Draw scoreboard box",
                        { n: table }
                      );
                    }
                    if (
                      !scoreWatch?.scoreText &&
                      scoreWatch?.phase !== "hit" &&
                      scoreWatch?.phase !== "confirm"
                    ) {
                      return t(
                        "cmScorecamChecklistFirstRead",
                        "Scorecam · table {{n}} · 2) Wait for first digits, then dock",
                        { n: table }
                      );
                    }
                    return t(
                      "cmYouAreScoreCameraArmed",
                      "Scorecam · table {{n}} · detecting",
                      { n: table }
                    );
                  }
                  if (matchup) {
                    return t(
                      "cmYouAreCameraMatchupPos",
                      "{{pos}} · table {{n}} · {{matchup}}",
                      { pos, n: table, matchup: matchup.label }
                    );
                  }
                  return t(
                    "cmYouAreCameraPos",
                    "{{pos}} · table {{n}}",
                    { pos, n: table }
                  );
                })()}
                </span>
              </p>
            ) : null}
            {cameraDocked ? null : renderScorecamPointTools()}
            {cameraPermissionPrompt && !cameraArmed ? (
              <p className="cm-cam-perm" role="status">
                {cameraPermissionBlocked
                  ? t(
                      "cmCameraPermissionBlocked",
                      "Camera is blocked for this site. Enable it in the browser settings, then tap Allow camera so this phone can record the table."
                    )
                  : t(
                      "cmCameraPermissionAsk",
                      "Camera access was denied. Allow the camera so this phone can record the table."
                    )}
              </p>
            ) : null}
            <div className={`cm-actions${cameraArmed ? " cm-actions--edge" : ""}`}>
              {!cameraArmed ? (
                <button
                  type="button"
                  className="cm-btn"
                  onClick={claimCam}
                  aria-label={
                    cameraPermissionPrompt
                      ? t("cmAllowCamera", "Allow camera")
                      : t("cmConnectTable", "Connect to table {{n}}", {
                          n: tableNum,
                        })
                  }
                  title={
                    cameraPermissionPrompt
                      ? t("cmAllowCamera", "Allow camera")
                      : t("cmConnectTable", "Connect to table {{n}}", {
                          n: tableNum,
                        })
                  }
                >
                  <Camera size={14} aria-hidden />
                  {cameraPermissionPrompt
                    ? t("cmAllowCamera", "Allow camera")
                    : t("cmConnectTable", "Connect to table {{n}}", {
                        n: tableNum,
                      })}
                </button>
              ) : null}
              {cameraArmed && cameraIsScoreCam(myCamera) ? (
                <button
                  type="button"
                  className="cm-btn cm-btn--ghost cm-btn--icon"
                  onClick={() => {
                    setCameraSettingsOpen(false);
                    setScoreRoiMode(true);
                  }}
                  aria-label={t("cmCalibrateScoreboard", "Calibrate scoreboard")}
                  title={t("cmCalibrateScoreboard", "Calibrate scoreboard")}
                >
                  <Crop size={20} aria-hidden />
                  <ActionLabel landscape>
                    {t("cmCalibrateScoreboard", "Calibrate scoreboard")}
                  </ActionLabel>
                </button>
              ) : null}
              {cameraArmed ? (
                <button
                  type="button"
                  className="cm-btn cm-btn--ghost cm-btn--icon"
                  onClick={() => void openCameraSettings()}
                  aria-label={t("cmCalibrate", "Calibrate")}
                  title={t("cmCalibrate", "Calibrate")}
                >
                  <SwitchCamera size={20} aria-hidden />
                  <ActionLabel landscape>{t("cmCalibrate", "Calibrate")}</ActionLabel>
                </button>
              ) : null}
              {cameraArmed ? (
                <button
                  type="button"
                  className="cm-btn cm-btn--ghost cm-btn--icon cm-btn--exit-preview"
                  onClick={() => {
                    setCameraSettingsOpen(false);
                    setTab("score");
                  }}
                  aria-label={t("cmExitPreview", "Exit live preview")}
                  title={t("cmExitPreview", "Exit live preview")}
                >
                  <CameraOff size={20} aria-hidden />
                  <ActionLabel landscape>
                    {t("cmExitPreview", "Exit live preview")}
                  </ActionLabel>
                </button>
              ) : null}
              {cameraArmed ? (
                <button
                  type="button"
                  className="cm-btn cm-btn--ghost cm-btn--icon"
                  onClick={snapPhoto}
                  aria-label={t("cmPhoto", "Photo")}
                  title={t("cmPhoto", "Photo")}
                >
                  <ImageIcon size={20} aria-hidden />
                  <ActionLabel landscape>{t("cmPhoto", "Photo")}</ActionLabel>
                </button>
              ) : null}
              {myCamera
                ? renderMatchRecordButton({
                    table: Number(myCamera.table_number),
                    icon: cameraArmed,
                  })
                : null}
              {cameraArmed &&
              myCamera &&
              !tableIsFilming(myCamera.table_number) ? (
                <button
                  type="button"
                  className="cm-btn cm-btn--dock cm-btn--icon"
                  onClick={() => void dockCameraPhone()}
                  aria-label={t("cmDock", "Dock")}
                  title={t("cmDock", "Dock")}
                >
                  <Moon size={20} aria-hidden />
                  <ActionLabel landscape>{t("cmDock", "Dock")}</ActionLabel>
                </button>
              ) : null}
            </div>
            {!cameraArmed ? (
              <p className="cm-hint">
                {t(
                  "cmCameraHint",
                  "Pick a table, connect this phone, then put a match on that table. The camera stays put — change the match when the next one starts."
                )}
                {` · ${clipCount} ${t("cmClips", "clips")}${
                  isMatchModerator && scorecamClipCount
                    ? ` · ${scorecamClipCount} ${t("cmScorecamShort", "scorecam")}`
                    : ""
                } · ${photoCount} ${t("cmPhotos", "photos")}`}
              </p>
            ) : null}
          </div>
        ) : null}

        {tab === "replay" ? (
          <div className="cm-panel">
            <div className="cm-replay__filters">
              <button
                type="button"
                className={`cm-tagchip cm-tagchip--moment${!filterTag ? " is-on" : ""}`}
                onClick={() => setFilterTag("")}
              >
                <span className="cm-tagchip__text">{t("cmAllPoints", "All")}</span>
              </button>
              {POINT_OUTCOMES.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={`cm-outcome__btn cm-outcome__btn--${item.id}${
                    filterTag === item.id ? " is-on" : ""
                  }`}
                  aria-pressed={filterTag === item.id}
                  onClick={() =>
                    setFilterTag((current) => (current === item.id ? "" : item.id))
                  }
                >
                  <span className="cm-outcome__mark" aria-hidden>
                    {item.mark}
                  </span>
                  <span className="cm-outcome__text">
                    {t(item.labelKey, item.label)}
                  </span>
                </button>
              ))}
            </div>
            <div className="cm-replay cm-chrono-shell">
              {(() => {
                const rows = (snapshot.rubbers || []).map((row) => ({
                  rubber: row,
                  rubberPoints: (snapshot.points || []).filter(
                    (p) =>
                      p.rubber_id === row.rubber_id &&
                      (!filterTag || pointMatchesOutcome(p, filterTag))
                  ),
                }));
                const focusId = rows.some(
                  (r) =>
                    String(r.rubber.rubber_id) === String(replayRubberFocusId)
                )
                  ? replayRubberFocusId
                  : rows.some(
                        (r) =>
                          String(r.rubber.rubber_id) === String(rubber?.rubber_id)
                      )
                    ? rubber.rubber_id
                    : rows[0]?.rubber?.rubber_id;
                const active =
                  rows.find(
                    (r) => String(r.rubber.rubber_id) === String(focusId)
                  ) || rows[0];
                if (!active) {
                  return (
                    <p className="cm-chrono__empty">
                      {t(
                        "cmNoTimelineYet",
                        "No points or media for this match yet."
                      )}
                    </p>
                  );
                }
                const homeLabel =
                  fullNames(
                    active.rubber.home_names,
                    active.rubber.home_seats,
                    ""
                  ) || "—";
                const awayLabel =
                  fullNames(
                    active.rubber.away_names,
                    active.rubber.away_seats,
                    ""
                  ) || "—";
                const activeHomePlayers = lineupPlayersForSeats(
                  snapshot.lineup?.home,
                  active.rubber.home_seats,
                  active.rubber.home_names
                );
                const activeAwayPlayers = lineupPlayersForSeats(
                  snapshot.lineup?.away,
                  active.rubber.away_seats,
                  active.rubber.away_names
                );
                return (
                  <>
                    {rows.length > 1 ? (
                      <div className="cm-chrono__rubber-tabs" role="tablist">
                        {rows.map(({ rubber: row, rubberPoints }) => {
                          const tapeCount = rubberReviewTapes(
                            snapshot.media,
                            row.rubber_id
                          ).length;
                          const tapeLabel = rubberPoints.length
                            ? tapeCount > 1
                              ? t(
                                  "cmPointsAndVideos",
                                  "{{n}} pts · {{videos}} videos",
                                  {
                                    n: rubberPoints.length,
                                    videos: tapeCount,
                                  }
                                )
                              : t("cmPointCount", "{{n}} pts", {
                                  n: rubberPoints.length,
                                })
                            : tapeCount === 1
                              ? t("cmOneVideo", "1 video")
                              : tapeCount > 1
                                ? t("cmVideoCount", "{{n}} videos", {
                                    n: tapeCount,
                                  })
                                : t("cmNoClipShort", "No tape");
                          return (
                          <button
                            key={row.rubber_id}
                            type="button"
                            role="tab"
                            aria-selected={
                              String(row.rubber_id) === String(focusId)
                            }
                            className={
                              String(row.rubber_id) === String(focusId)
                                ? "cm-chrono__rubber-tab is-on"
                                : "cm-chrono__rubber-tab"
                            }
                            onClick={() => setReplayRubberFocusId(row.rubber_id)}
                          >
                            <strong>{rubberCodeLabel(row.code)}</strong>
                            <span>{tapeLabel}</span>
                          </button>
                          );
                        })}
                      </div>
                    ) : null}
                    {!active.rubberPoints.length &&
                    !orphanMediaForRubber(
                      snapshot.media,
                      active.rubber.rubber_id,
                      []
                    ).length ? (
                      <section className="cm-chrono">
                        <header className="cm-chrono__rubber">
                          <strong>{rubberCodeLabel(active.rubber.code)}</strong>
                          <span>
                            {homeLabel} vs {awayLabel}
                          </span>
                        </header>
                        <p className="cm-chrono__empty">
                          {t(
                            "cmNoTimelineYet",
                            "No points or media for this match yet."
                          )}
                        </p>
                      </section>
                    ) : (
                      <ChronoTimelineViewer
                        key={active.rubber.rubber_id}
                        rubber={active.rubber}
                        points={active.rubberPoints}
                        media={snapshot.media}
                        rubbers={snapshot.rubbers || []}
                        tagLabel={tagLabel}
                        homeLabel={homeLabel}
                        awayLabel={awayLabel}
                        homePlayers={activeHomePlayers}
                        awayPlayers={activeAwayPlayers}
                        showRubberHead={!inMatch && rows.length === 1}
                        canTag={Boolean(userId)}
                        canModerate={isMatchModerator}
                        canLabelScore={canLabelMatchScore}
                        onSetPointOutcome={setReviewPointOutcome}
                        onSavePointScore={saveReviewPointScore}
                        onStampReviewPoint={(body) =>
                          stampReviewPoint(active.rubber.rubber_id, body)
                        }
                        onSendReaction={(body) =>
                          sendReviewReaction(active.rubber.rubber_id, body)
                        }
                        onClearPoints={() =>
                          clearRubberReviewPoints(active.rubber.rubber_id)
                        }
                        onProbeScore={(payload) =>
                          probeReviewScore(active.rubber.rubber_id, payload)
                        }
                        onFingerprintFrame={(payload) =>
                          fingerprintReviewScore(active.rubber.rubber_id, payload)
                        }
                        onScanScore={(payload) =>
                          scanReviewScore(active.rubber.rubber_id, payload)
                        }
                        onLabelScore={(payload) =>
                          labelReviewScore(active.rubber.rubber_id, payload)
                        }
                        onLabelStatus={(payload) =>
                          readReviewScoreLabel(active.rubber.rubber_id, payload)
                        }
                        onReassignMedia={
                          isMatchModerator ? reassignReviewMedia : undefined
                        }
                        preferredMediaId={movedMediaId}
                        clubLogo={overlayLogo}
                        clubName={overlayClubName}
                        brandColor={brand?.color_primary || brand?.primary || ""}
                        homeClubName={homeClubName}
                        awayClubName={awayClubName}
                        homeTeamScore={snapshot.match?.home_team_score}
                        awayTeamScore={snapshot.match?.away_team_score}
                        matchPoints={snapshot.points}
                        setsToWin={snapshot.match?.sets_to_win}
                        division={
                          snapshot.fixture?.division ||
                          snapshot.fixture?.home?.division ||
                          snapshot.fixture?.away?.division ||
                          ""
                        }
                        t={t}
                      />
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        ) : null}
      </div>

      {tab === "score" && showStamps ? (
        moreTags ? (
          <div className="cm-tagdock" role="dialog" aria-label={t("cmMore", "More")}>
            <div className="cm-tagdock__head">
              <strong>{t("cmTagPoint", "Tag this point")}</strong>
              <button
                type="button"
                className="cm-tagdock__close"
                onClick={() => setMoreTags(false)}
                aria-label={t("close", "Close")}
              >
                <X size={16} aria-hidden />
              </button>
            </div>
            <div className="cm-comment">
              <input
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder={t("cmComment", "Comment this point")}
                disabled={timeoutActive || !lastPoint}
              />
              <button
                type="button"
                className="cm-btn cm-btn--ghost"
                onClick={saveComment}
                disabled={timeoutActive || !lastPoint}
              >
                {t("cmSaveComment", "Save")}
              </button>
              <button
                type="button"
                className={`cm-btn cm-btn--ghost${voiceRecording ? " cm-btn--danger" : ""}`}
                onClick={() =>
                  voiceRecording ? stopVoiceComment() : void startVoiceComment()
                }
                aria-label={t("cmVoiceComment", "Voice comment")}
                title={t("cmVoiceComment", "Voice comment")}
              >
                <Mic size={14} aria-hidden />
                {voiceRecording
                  ? t("cmStopVoice", "Stop")
                  : t("cmVoice", "Voice")}
              </button>
            </div>
            <div className="cm-tagdock__scroll">
              <section className="cm-tagdock__group">
                <p className="cm-tagdock__label">
                  {t("cmTagGroupCore", "Quick tags")}
                </p>
                <div className="cm-tagdock__chips">
                  {CORE_POINT_TAGS.map((tag) => (
                    <PointTagChip
                      key={tag}
                      tagKey={tag}
                      label={tagLabel(tag)}
                      active={
                        tag === "point_start"
                          ? pointStartArmed
                          : lastPoint?.tags?.includes(tag)
                      }
                      onClick={() => stamp(tag)}
                      disabled={
                        timeoutActive ||
                        (tag !== "point_start" && !lastPoint)
                      }
                    />
                  ))}
                </div>
              </section>
              <section className="cm-tagdock__group">
                <p className="cm-tagdock__label">
                  {t("cmTagGroupExtra", "Outcome")}
                </p>
                <div className="cm-tagdock__chips">
                  {EXTRA_POINT_TAGS.filter(
                    (tag) => tag !== "timeout_on" && tag !== "timeout_off"
                  ).map((tag) => (
                    <PointTagChip
                      key={tag}
                      tagKey={tag}
                      label={tagLabel(tag)}
                      active={lastPoint?.tags?.includes(tag)}
                      onClick={() => stamp(tag)}
                      disabled={timeoutActive || (!lastPoint && tag !== "let_serve")}
                    />
                  ))}
                </div>
              </section>
              <section className="cm-tagdock__group">
                <p className="cm-tagdock__label">
                  {t("cmTagGroupSchema", "Game pattern")}
                </p>
                <div className="cm-tagdock__chips">
                  {GAME_SCHEMAS.map((key) => (
                    <PointTagChip
                      key={key}
                      tagKey={key}
                      label={tagLabel(key)}
                      active={lastPoint?.schema_key === key}
                      onClick={() => saveSchema(key)}
                    />
                  ))}
                </div>
              </section>
            </div>
          </div>
        ) : (
          <div className="cm-stamps-wrap">
            <button
              type="button"
              className={`cm-timeout-toggle${timeoutActive ? " is-on" : ""}`}
              onClick={() => void toggleTimeout()}
              disabled={busy || !canToggleTimeout}
              aria-pressed={timeoutActive}
            >
              <span className="cm-timeout-toggle__mark" aria-hidden>
                {timeoutActive ? (
                  <Play size={14} strokeWidth={2.4} />
                ) : (
                  <Pause size={14} strokeWidth={2.4} />
                )}
              </span>
              <span className="cm-timeout-toggle__text">
                {timeoutActive
                  ? t("cmTimeoutResume", "Timeout off — resume")
                  : t("cmTimeoutStart", "Timeout")}
              </span>
            </button>
            {timeoutActive ? (
              <p className="cm-timeout-banner" role="status">
                {t(
                  "cmTimeoutBanner",
                  "Timeout — scoring paused, cameras stopped. Tap Timeout off to restart recording in sync."
                )}
              </p>
            ) : null}
            <div className="cm-stamps" aria-label={t("cmTagPoint", "Tag this point")}>
              {CORE_POINT_TAGS.map((tag) => (
                <PointTagChip
                  key={tag}
                  tagKey={tag}
                  label={tagLabel(tag)}
                  active={
                    tag === "point_start"
                      ? pointStartArmed
                      : lastPoint?.tags?.includes(tag)
                  }
                  onClick={() => stamp(tag)}
                  disabled={
                    timeoutActive || (tag !== "point_start" && !lastPoint)
                  }
                />
              ))}
              <button
                type="button"
                className="cm-tagchip cm-tagchip--more"
                onClick={() => setMoreTags(true)}
                disabled={timeoutActive}
              >
                <span className="cm-tagchip__mark" aria-hidden>
                  <Plus size={14} strokeWidth={2.4} />
                </span>
                <span className="cm-tagchip__text">{t("cmMore", "More")}</span>
              </button>
            </div>
          </div>
        )
      ) : null}

      <nav
        className="cm-tabs"
        style={{
          gridTemplateColumns: `repeat(${tabs.length}, 1fr)`,
        }}
      >
        {tabs.map(({ id, label, Icon }) => (
          <button
            key={id}
            type="button"
            className={tab === id ? "is-on" : ""}
            onClick={() => {
              if (id === "camera") {
                void (async () => {
                  await unlockScoreIfHeld();
                  setTab("camera");
                })();
                return;
              }
              setTab(id);
            }}
          >
            <span className="cm-tabs__icon">
              <Icon size={16} />
              {id === "camera" && thisPhoneRecording ? (
                <i className="cm-tabs__rec" />
              ) : null}
              {id === "control" && roomRecording ? (
                <i className="cm-tabs__rec" />
              ) : null}
            </span>
            {label}
          </button>
        ))}
      </nav>

      {cameraSettingsOpen && (hasPreviewStream || cameraSwitching) ? (
        <div
          className={`cm-cam-settings cm-cam-settings--ios${
            camLandscape ? " is-landscape" : ""
          }`}
          role="dialog"
          aria-modal="true"
          aria-label={t("cmCameraSettings", "Camera settings")}
        >
          <div ref={settingsStageRef} className="cm-cam-settings__stage-full">
            <video
              ref={settingsPreviewRef}
              className="cm-cam-settings__preview-full"
              autoPlay
              playsInline
              muted
              style={{
                transform: previewZoomTransform(
                  cameraZoom,
                  cameraHardwareZoom,
                  Boolean(cameraZoomRange)
                ),
              }}
            />
            {cameraZoom > 1.02 ? (
              <span className="cm-cam-settings__zoom-pill" aria-live="polite">
                {formatCameraZoomLabel(cameraZoom)}
              </span>
            ) : null}
            <TableCameraGuide
              position={normalizeCamPosition(myCamera?.angle_label || angle)}
              players={scorecamRecordVideo}
              show={tableGuideOn}
              placement="settings"
              onToggle={toggleTableGuide}
            />
          </div>
          <header className="cm-cam-settings__ios-top">
            <div className="cm-cam-settings__title-row">
              <Video size={18} strokeWidth={2.2} aria-hidden />
              <strong>{t("cmCameraSettings", "Camera")}</strong>
            </div>
            <button
              type="button"
              className="cm-cam-settings__icon-btn"
              onClick={() => setCameraSettingsOpen(false)}
              aria-label={t("close", "Close")}
            >
              <X size={20} strokeWidth={2.2} />
            </button>
          </header>
          <div className="cm-cam-settings__ios-bar">
            <p className="cm-cam-settings__ios-hint">
              {t(
                "cmCameraSettingsHint",
                "Pinch to set the lens zoom. Tap Wide, Back, or Front. Double-tap to reset."
              )}
            </p>
            <div
              className="cm-cam-settings__seg cm-cam-settings__seg--positions"
              role="group"
              aria-label={t("cmCamPosition", "Camera position")}
            >
              {[
                {
                  id: "spectator",
                  icon: Eye,
                  label: camPositionLabel("spectator", t),
                },
                {
                  id: "referee",
                  icon: Shield,
                  label: camPositionLabel("referee", t),
                },
                {
                  id: "scorecam",
                  icon: Hash,
                  label: camPositionLabel("scorecam", t),
                },
              ].map((pos) => {
                const active = normalizeCamPosition(angle) === pos.id;
                const Icon = pos.icon;
                return (
                  <button
                    key={pos.id}
                    type="button"
                    className={`cm-cam-settings__seg-btn cm-cam-settings__seg-btn--icon${
                      active ? " is-on" : ""
                    }`}
                    onClick={() => {
                      const position = selectCameraPosition(pos.id);
                      if (myCamera) {
                        const table = myCamera.table_number || tableNum;
                        const role = camRoleFromPosition(position);
                        persistCameraClaim(matchId, {
                          table_number: table,
                          angle_label: position,
                          device_id: phoneId,
                          camera_role: role,
                          ...(position === "scorecam" && myCamera.score_roi
                            ? { score_roi: myCamera.score_roi }
                            : {}),
                        });
                        void emit("cm:claim-camera", {
                          table_number: table,
                          angle_label: position,
                          camera_role: role,
                          record_video: scorecamRecordVideoRef.current,
                          ...(position === "scorecam" && myCamera.score_roi
                            ? { score_roi: myCamera.score_roi }
                            : {}),
                        }).then((ack) => {
                          if (ack?.ok && ack.data) applySnap(ack.data);
                          if (position === "scorecam" && !myCamera.score_roi) {
                            setScoreRoiMode(true);
                          }
                        });
                      }
                    }}
                    aria-pressed={active}
                    aria-label={pos.label}
                    title={pos.label}
                    disabled={recording}
                  >
                    <Icon size={18} strokeWidth={2.2} aria-hidden />
                    <span>{pos.label}</span>
                  </button>
                );
              })}
            </div>
            {renderScorecamCaptureMode("settings")}
            <div
              className="cm-cam-settings__lenses"
              role="group"
              aria-label={t("cmCalibrate", "Camera")}
            >
              {cameraOptions.map((option) => {
                const active = isWebEvalCameraSelected(option, {
                  deviceId: cameraDeviceId,
                  facingMode: cameraFacingMode,
                });
                const label = webEvalCameraSetupLabel(option, t);
                const mark = webEvalCameraLensMark(option);
                const isFront = mark === "F";
                return (
                  <button
                    key={option.id}
                    type="button"
                    className={`cm-cam-settings__lens${active ? " is-on" : ""}`}
                    onClick={() => void switchCaptureCamera(option)}
                    aria-pressed={active}
                    aria-label={label}
                    title={label}
                    disabled={recording || cameraSwitching}
                  >
                    <span className="cm-cam-settings__lens-mark">
                      {isFront ? (
                        <User size={14} strokeWidth={2.2} aria-hidden />
                      ) : (
                        mark
                      )}
                    </span>
                    <span className="cm-cam-settings__lens-name">{label}</span>
                  </button>
                );
              })}
            </div>
            <div className="cm-cam-settings__encode">
              <div
                className="cm-cam-settings__seg cm-cam-settings__seg--encode"
                role="group"
                aria-label={t("cmRecordFps", "Frame rate")}
              >
                {WEB_EVAL_FPS_OPTIONS.map((rate) => (
                  <button
                    key={rate}
                    type="button"
                    className={`cm-cam-settings__seg-btn${
                      recordFps === rate ? " is-on" : ""
                    }`}
                    onClick={() => void applyRecordFps(rate)}
                    aria-pressed={recordFps === rate}
                    disabled={recording}
                  >
                    {rate}
                  </button>
                ))}
                {(thisPhoneIsScorecam ||
                normalizeCamPosition(angle) === "scorecam"
                  ? CM_SCORECAM_RES_OPTIONS
                  : CM_RECORD_RES_OPTIONS
                ).map((res) => (
                  <button
                    key={res}
                    type="button"
                    className={`cm-cam-settings__seg-btn${
                      recordResolution === res ? " is-on" : ""
                    }`}
                    onClick={() => void applyRecordResolution(res)}
                    aria-pressed={recordResolution === res}
                    disabled={recording}
                  >
                    {res}p
                  </button>
                ))}
                {CM_RECORD_QUALITY_OPTIONS.map((q) => (
                  <button
                    key={`q-${q}`}
                    type="button"
                    className={`cm-cam-settings__seg-btn${
                      recordQuality === q ? " is-on" : ""
                    }`}
                    onClick={() => applyRecordQuality(q)}
                    aria-pressed={recordQuality === q}
                    disabled={recording}
                  >
                    {q === "low"
                      ? t("cmRecordQualityLowShort", "Low")
                      : t("cmRecordQualityMediumShort", "Med")}
                  </button>
                ))}
              </div>
              <p className="cm-cam-settings__note cm-cam-settings__note--encode">
                {t(
                  "cmRecordEncodeSummary",
                  "{{res}}p · {{fps}} fps · {{mbps}} Mbps · about {{mb}} MB per minute",
                  {
                    res: recordResolution,
                    fps: recordFps,
                    mbps: (matchRecordBitRate(recordQuality, recordResolution) / 1e6).toFixed(1),
                    mb: Math.round(
                      matchRecordMegabytesPerMinute(
                        matchRecordBitRate(recordQuality, recordResolution)
                      )
                    ),
                  }
                )}
              </p>
            </div>
            <div className="cm-cam-settings__field cm-cam-settings__field--wait">
              <span className="cm-cam-settings__seg-label">
                {t("cmSetCloseWait", "Set close wait")}
              </span>
              <div
                className="cm-cam-settings__seg"
                role="group"
                aria-label={t("cmSetCloseWait", "Set close wait")}
              >
                {SET_CLOSE_STABLE_SEC_OPTIONS.map((sec) => (
                  <button
                    key={sec}
                    type="button"
                    className={`cm-cam-settings__seg-btn${
                      setCloseStableSec === sec ? " is-on" : ""
                    }`}
                    onClick={() => applySetCloseStableSec(sec)}
                    aria-pressed={setCloseStableSec === sec}
                  >
                    {sec}s
                  </button>
                ))}
              </div>
            </div>
            <div className="cm-cam-settings__field cm-cam-settings__field--upload">
              <span className="cm-cam-settings__seg-label">
                {t("cmUploadNetwork", "Upload")}
              </span>
              <div
                className="cm-cam-settings__seg"
                role="group"
                aria-label={t("cmUploadNetwork", "Upload")}
              >
                {uploadModeChoices.map((mode) => {
                  const Icon =
                    mode === "live" ? CloudUpload : mode === "wifi" ? Wifi : Smartphone;
                  return (
                    <button
                      key={mode}
                      type="button"
                      className={`cm-cam-settings__seg-btn cm-cam-settings__seg-btn--icon${
                        shownUploadMode === mode ? " is-on" : ""
                      }`}
                      onClick={() => {
                        if (!scorecamUploadLiveOnly) applyUploadMode(mode);
                      }}
                      aria-pressed={shownUploadMode === mode}
                    >
                      <Icon size={14} strokeWidth={2.2} aria-hidden />
                      {mode === "live"
                        ? t("cmUploadModeLive", "Live")
                        : mode === "wifi"
                          ? t("cmWifiOnlyUpload", "Wi‑Fi only")
                          : t("cmUploadModePhone", "Save on phone")}
                    </button>
                  );
                })}
              </div>
              <p className="cm-cam-settings__note">
                {shownUploadMode === "wifi"
                  ? t(
                      "cmWifiOnlyUploadOn",
                      "On mobile data, clips stay on this phone and upload when Wi‑Fi is on."
                    )
                  : shownUploadMode === "phone"
                    ? t(
                        "cmUploadModePhoneNote",
                        "Every clip stays on this phone while it films. Uploads start on Wi‑Fi once filming stops, or from Videos on this phone."
                      )
                    : t(
                        "cmWifiOnlyUploadOff",
                        "Clips upload as soon as each one finishes recording."
                      )}
              </p>
              <div className="cm-cam-settings__queue">
                {shownUploadMode !== "live" && getNetworkTransport() === "unknown" ? (
                  <p className="cm-cam-settings__note">
                    {t(
                      "cmUploadNetworkUnknown",
                      "This phone does not tell the browser whether it is on Wi‑Fi. On mobile data, use Save on phone and tap Upload now once you are on Wi‑Fi."
                    )}
                  </p>
                ) : null}
                {clipUploads.count > 0 ? (
                  <p className="cm-cam-settings__note">
                    {t("cmClipsOnPhone", "{{count}} clips on this phone · {{size}}", {
                      count: clipUploads.count,
                      size: formatClipBytes(clipUploads.bytes),
                    })}
                  </p>
                ) : null}
                <div className="cm-cam-settings__seg">
                  {clipUploads.count > 0 ? (
                    <button
                      type="button"
                      className="cm-cam-settings__seg-btn"
                      disabled={clipUploads.busy && clipUploads.forced}
                      onClick={() => void flushChampionshipClips({ force: true })}
                    >
                      {clipUploads.busy && clipUploads.forced
                        ? t("cmUploadingClips", "Uploading…")
                        : t("cmUploadClipsNow", "Upload now")}
                    </button>
                  ) : null}
                  {recording ? (
                    <button
                      type="button"
                      className="cm-cam-settings__seg-btn cm-cam-settings__seg-btn--icon"
                      disabled
                      title={t(
                        "cmPhoneVideosAfterRecording",
                        "Open after this phone stops filming"
                      )}
                    >
                      <Film size={14} strokeWidth={2.2} aria-hidden />
                      {t("cmPhoneVideosOpen", "Videos on this phone")}
                    </button>
                  ) : (
                    <Link
                      to="/competition/phone-videos"
                      className="cm-cam-settings__seg-btn cm-cam-settings__seg-btn--icon"
                    >
                      <Film size={14} strokeWidth={2.2} aria-hidden />
                      {t("cmPhoneVideosOpen", "Videos on this phone")}
                    </Link>
                  )}
                </div>
                {clipUploadStatusText(clipUploads, t) ? (
                  <p className="cm-cam-settings__note" role="status">
                    {clipUploadStatusText(clipUploads, t)}
                  </p>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {hasPreviewStream && !cameraArmed ? (
        <video
          ref={bindPreviewVideo}
          className="cm-cam-hidden"
          autoPlay
          playsInline
          muted
          aria-hidden
        />
      ) : null}
      <canvas ref={recordCanvasRef} className="cm-cam-record-canvas" aria-hidden />

      {reviewRubber ? (
        <MatchReviewPlayer
          rubber={reviewRubber}
          points={snapshot?.points || []}
          media={snapshot?.media || []}
          rubbers={snapshot?.rubbers || []}
          tagLabel={tagLabel}
          homeLabel={
            fullNames(reviewRubber.home_names, reviewRubber.home_seats, "") ||
            "—"
          }
          awayLabel={
            fullNames(reviewRubber.away_names, reviewRubber.away_seats, "") ||
            "—"
          }
          homePlayers={lineupPlayersForSeats(
            snapshot?.lineup?.home,
            reviewRubber.home_seats,
            reviewRubber.home_names
          )}
          awayPlayers={lineupPlayersForSeats(
            snapshot?.lineup?.away,
            reviewRubber.away_seats,
            reviewRubber.away_names
          )}
          canTag={Boolean(userId)}
          canModerate={isMatchModerator}
          canLabelScore={canLabelMatchScore}
          onSetPointOutcome={setReviewPointOutcome}
          onSavePointScore={saveReviewPointScore}
          onStampReviewPoint={(body) =>
            stampReviewPoint(reviewRubber.rubber_id, body)
          }
          onSendReaction={(body) =>
            sendReviewReaction(reviewRubber.rubber_id, body)
          }
          onClearPoints={() => clearRubberReviewPoints(reviewRubber.rubber_id)}
          onProbeScore={(payload) =>
            probeReviewScore(reviewRubber.rubber_id, payload)
          }
          onFingerprintFrame={(payload) =>
            fingerprintReviewScore(reviewRubber.rubber_id, payload)
          }
          onScanScore={(payload) =>
            scanReviewScore(reviewRubber.rubber_id, payload)
          }
          onLabelScore={(payload) =>
            labelReviewScore(reviewRubber.rubber_id, payload)
          }
          onLabelStatus={(payload) =>
            readReviewScoreLabel(reviewRubber.rubber_id, payload)
          }
          onReassignMedia={
            isMatchModerator ? reassignReviewMedia : undefined
          }
          preferredMediaId={movedMediaId}
          clubLogo={overlayLogo}
          clubName={overlayClubName}
          brandColor={brand?.color_primary || brand?.primary || ""}
          homeClubName={homeClubName}
          awayClubName={awayClubName}
          homeTeamScore={snapshot.match?.home_team_score}
          awayTeamScore={snapshot.match?.away_team_score}
          matchPoints={snapshot?.points || []}
          setsToWin={snapshot.match?.sets_to_win}
          division={
            snapshot.fixture?.division ||
            snapshot.fixture?.home?.division ||
            snapshot.fixture?.away?.division ||
            ""
          }
          onClose={() => setReviewRubberId(null)}
          t={t}
        />
      ) : null}

      {cameraDocked ? (
        <div className="cm-dock-dim" role="dialog" aria-modal="true">
          {thisPhoneRecording ? (
            <span className="cm-dock-dim__rec">
              <i />
              {recordingTablesLabel([thisPhoneTable], t)}
            </span>
          ) : (
            <span className="cm-dock-dim__wait">
              {t("cmDockNotRecording", "Not recording")}
            </span>
          )}
          {cameraIsScoreCam(myCamera) ||
          normalizeCamPosition(angle) === "scorecam" ? (
            <div
              className={`cm-dock-dim__score${
                scoreWatch.phase === "reading" ? " is-reading" : ""
              }${scoreWatch.phase === "read" ? " is-read" : ""}`}
              aria-live="polite"
            >
              <span className="cm-dock-dim__score-kicker">
                <i
                  key={scoreWatch.n}
                  className={`cm-dock-dim__pulse cm-dock-dim__pulse--${scoreWatch.phase}`}
                  aria-hidden
                />
                {scoreWatch.phase === "reading"
                  ? t("cmDockScoreReading", "Reading score")
                  : t("cmDockScoreDetected", "Detected score")}
              </span>
              <strong>
                {displayBoard?.board_left != null && displayBoard?.board_right != null
                  ? `${displayBoard.board_left}–${displayBoard.board_right}`
                  : "–"}
              </strong>
              {displayBoard?.games_left != null && displayBoard?.games_right != null ? (
                <span className="cm-dock-dim__score-sets">
                  {t("cmSets", "Sets")} {displayBoard.games_left}–{displayBoard.games_right}
                </span>
              ) : null}
            </div>
          ) : null}
          {renderScorecamPointTools()}
          <strong>
            {t("cmTable", "Table")} {myCamera?.table_number || tableNum}
          </strong>
          <p className="cm-dock-dim__hint">
            {tableIsFilming(myCamera?.table_number || tableNum)
              ? t("cmDockedRecording", "Recording — screen dimmed to save battery")
              : t(
                  "cmDockedReady",
                  "Docked — start or end recording whenever you want"
                )}
          </p>
          <div className="cm-dock-dim__actions">
            {renderMatchRecordButton({
              table: Number(myCamera?.table_number || tableNum),
            })}
            <button type="button" className="cm-btn cm-btn--ghost" onClick={wakeFromDock}>
              {t("cmDockWake", "Tap to wake")}
            </button>
          </div>
        </div>
      ) : null}
      {scoreJump ? (
        <div className="cm-score-jump" role="dialog" aria-modal="true">
          <div className="cm-score-jump__card">
            <p>{t("cmScoreJumpTitle", "Score jumped")}</p>
            <strong>
              {scoreJump.fromHome}–{scoreJump.fromAway}
              <span> → </span>
              {scoreJump.home}–{scoreJump.away}
            </strong>
            <p>
              {t(
                "cmScoreJumpBody",
                "The board moved by more than one point. Update the match?"
              )}
            </p>
            <div className="cm-score-jump__acts">
              <button
                type="button"
                className="cm-btn cm-btn--ghost"
                onClick={() => {
                  dismissedJumpRef.current = scoreJump.key;
                  setScoreJump(null);
                }}
              >
                {t("cmScoreJumpDismiss", "Keep {{from}}", {
                  from: `${scoreJump.fromHome}–${scoreJump.fromAway}`,
                })}
              </button>
              <button
                type="button"
                className="cm-btn"
                onClick={() => void confirmScoreJump()}
              >
                {t("cmScoreJumpConfirm", "Update score")}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {showCheer || tagScorecamPoints ? (
        <ChampionshipCheerBar
          controls={showCheer}
          incoming={cheerIncoming}
          pointId={(tagScorecamPoints ? scorecamPoint : lastPoint)?.point_id || ""}
          onSend={(kind) => emit("cm:cheer", { kind })}
        />
      ) : null}
    </div>
  );
}
